import {
  createElement,
  createRef,
  startTransition,
  useDeferredValue,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type SyntheticEvent,
} from "react";

import type {
  PatientPortalFeatureFlagsV1,
  TenantAdminAiModelContextFlagV1,
  TenantAdminAiModelKeyV1,
  TenantAdminAiModelPromptArtifactV1,
  TenantAdminAiModelSourceDocumentV1,
  TenantAdminClinicianOperationsResponseV1,
  TenantAdminCommandInputV1,
  TenantAdminCommandResponseV1,
  TenantAdminPatientOperationsCareTypeFilter,
  TenantAdminPatientOperationsResponseV1,
  TenantAdminPatientPortalRoute,
  TenantAdminOutboundCallCommandInputV1,
  TenantAdminOutboundCallsCommandResponseV1,
  TenantAdminOutboundCallsSummaryResponseV1,
  TenantAdminOutboundCallsQuestionnairesResponseV1,
  TenantAdminOutboundCallsTargetsResponseV1,
  TenantAdminOutboundCampaignDialerStatusV1,
  TenantAdminOutboundCampaignQuestionnaireImpactReviewV1,
  TenantAdminOutboundCampaignV1,
  TenantAdminOutboundConsentStateV1,
  TenantAdminOutboundDialerModeV1,
  TenantAdminOutboundQuestionAnswerTypeV1,
  TenantAdminOutboundQuestionnaireQuestionInputV1,
  TenantAdminOutboundQuestionnaireReviewPolicyV1,
  TenantAdminOutboundQuestionnaireTemplateV1,
  TenantAdminOutboundQuestionnaireVersionV1,
  TenantAdminOutboundQuestionnaireVoicePolicyV1,
  TenantAdminReviewQueueId,
  TenantAdminReviewQueueItemsResponseV1,
  TenantAdminSummaryConnectorRegistrationV1,
  TenantAdminSummaryFeatureFlagV1,
  TenantAdminSummaryResponseV1,
  WorkforceSessionSummaryV1,
} from "@amplecare/contracts";
import {
  patientPortalDefaultFeatureFlagsV1,
  patientPortalFeatureDefinitionsV1,
} from "@amplecare/contracts";
import { designTokens } from "@amplecare/design-tokens";
import type { TranslationKey } from "@amplecare/i18n";
import {
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type LayoutChangeEvent,
  type ViewStyle,
} from "react-native";

import type { MobileRuntimeConfig } from "../../config/runtime-config.js";
import { styles } from "../AppShell.styles.js";
import {
  loadTenantAdminClinicianOperations,
  loadTenantAdminOutboundCallsCampaigns,
  loadTenantAdminOutboundCallsQuestionnaires,
  loadTenantAdminOutboundCallsSummary,
  loadTenantAdminOutboundCallsTargets,
  loadTenantAdminPatientOperations,
  loadTenantAdminReviewQueueItems,
  submitTenantAdminOutboundCallCommand,
} from "../admin-console-client.js";
import { useWorkspaceRefreshScope } from "../refresh/useWorkspaceRefreshScope.js";
import {
  buildClinicianWorkbenchRefreshAliases,
  buildPatientWorkspaceRefreshAlias,
  buildTenantAdminClinicianOperationsRefreshAlias,
  buildTenantAdminClinicianOperationsRefreshKey,
  buildTenantAdminOutboundRefreshAlias,
  buildTenantAdminOutboundRefreshKey,
  buildTenantAdminPatientOperationsRefreshAlias,
  buildTenantAdminPatientOperationsRefreshKey,
  buildTenantAdminReviewQueueRefreshAlias,
  buildTenantAdminReviewQueueRefreshKey,
  buildTenantAdminSummaryRefreshAlias,
  buildWorkspaceRefreshAliasesForChangedScopes,
  invalidateWorkspaceRefreshScopes,
  workspaceRefreshCadenceMs,
} from "../refresh/workspace-refresh-service.js";
import {
  humanizeWorkspaceKey,
  resolveLegacyCompactLeadText,
  resolvePatientAccessModeLabel,
  resolvePatientInitials,
  resolveWorkforceSessionRoleLabel,
  type AppShellTheme,
  type LegacySectionChromeTone,
} from "../shell-core.js";
import { LineIcon, type LineIconName } from "../line-icons.js";
import {
  renderLegacyPreviewListRow,
  renderLegacySectionSummaryRail,
} from "../shell-legacy-ui.js";
import {
  createWebViewportMinHeightStyle,
  renderLegacySurfaceHeader,
  renderWorkbenchActionButton,
  renderWorkspaceFilterChip,
} from "../shell-primitives.js";
import type {
  DetailSectionViewModel,
  DetailTabId,
  PatientListTabId,
  PatientListSummaryViewModel,
  PatientWorkspaceViewModel,
  VoiceCallSummaryViewModel,
  WorkbenchPanelId,
  WorkbenchPanelViewModel,
} from "../types.js";
import {
  adminConnectorFamilyOptions,
  adminConnectorSourceAuthorityOptions,
  adminConnectorStatusOptions,
  adminOperationalStatusOptions,
  adminPatientStatusOptions,
  buildAdminPatientLifecycleIdempotencyKey,
  buildAdminCommandKey,
  buildAdminClinicianOperationalRows,
  buildAdminOutboundFollowUpGateOptions,
  buildAdminOutboundFollowUpSkipLogic,
  buildAdminOutboundFollowUpTriggerOptions,
  buildAdminPatientOperationalRows,
  buildAdminOperationalSubjectRows,
  buildAdminWorkflowQueueRows,
  describeAdminOutboundFollowUpTrigger,
  findAdminOutboundFollowUpGateQuestion,
  formatAdminDateInputValue,
  formatAdminSummaryTimestamp,
  isAdminPatientArchived,
  matchesAdminPatientQuickFilter,
  paginateRows,
  parseAdminDateInputValue,
  resolveAdminAnalyticsRangeSelection,
  resolveAdminOutboundFollowUpGateSelection,
  truncateAdminOutboundFollowUpPromptText,
  resolveAdminClinicianCrudStatus,
  resolveAdminClinicianOperationsSort,
  resolveAdminPatientOperationsSort,
  resolveAdminTimestampSortValue,
  sampleAdminAnalyticsSeries,
  sortAdminConnectorRegistrations,
  sortAdminFeatureFlagRows,
  sortAdminOperationalSubjectRows,
  sortAdminReviewQueueRows,
  type AdminAnalyticsRangeKey,
  type AdminAnalyticsRangeSelection,
  type AdminAnalyticsSnapshot,
  type AdminConnectorCrudStatus,
  type AdminConnectorFamily,
  type AdminConnectorSourceAuthority,
  type AdminCrudFormState,
  type AdminOperationalCrudStatus,
  type AdminOperationalSubjectRow,
  type AdminOutboundFollowUpTriggerKind,
  type AdminPreviewPatientCareType,
  type AdminPreviewPatientRow,
  type AdminPatientQuickFilter,
  type AdminReviewQueueRow,
} from "./admin-shared.js";
import {
  adminConfigurationDialog,
  adminOperationalLayout,
  adminOperationalTypography,
} from "./admin-design-system.js";

const smsDispatchSystemFlagKey = "messaging.sms.dispatch.enabled";

function buildAdminWorkspaceMutationRefreshAliases(input: {
  clinicianUserId: string;
  tenantId: string;
}): string[] {
  return [
    buildTenantAdminSummaryRefreshAlias(input.tenantId),
    buildTenantAdminPatientOperationsRefreshAlias(input.tenantId),
    buildTenantAdminClinicianOperationsRefreshAlias(input.tenantId),
    buildTenantAdminReviewQueueRefreshAlias(input.tenantId),
    buildTenantAdminOutboundRefreshAlias(input.tenantId),
    buildPatientWorkspaceRefreshAlias(input.tenantId),
    ...buildClinicianWorkbenchRefreshAliases({
      clinicianUserId: input.clinicianUserId,
      tenantId: input.tenantId,
    }),
  ];
}

type AdminFeatureFlagDraftState = {
  enabledState: "enabled" | "disabled";
  flagKey: string;
  flagKind: "feature" | "system";
  flagVariant: string;
  rolloutNote: string;
  scope: "tenant" | "site" | "department" | "program";
  scopeKey: string;
};

type AdminAiModelEditorModelKey = "chat" | "voice" | "shared";

type AdminDashboardTabId =
  | "patients"
  | "clinicians"
  | "connectors"
  | "reviews"
  | "outbound-calls"
  | "patient-portal"
  | "flags"
  | "ai-models"
  | "provisioning"
  | "departments"
  | "analytics";

type AdminReviewQueueDetailFilter = "pending" | "high" | "completed" | "all";

type AdminOutboundTabId =
  | "overview"
  | "questionnaire"
  | "patients"
  | "campaign"
  | "activity";

type AdminOutboundPatientFilter =
  | "all"
  | "ready"
  | "no-phone"
  | "suppressed"
  | "targeted";

function resolveAdminPatientPortalFeatureFlags(
  featureFlags: readonly TenantAdminSummaryFeatureFlagV1[] | undefined,
): PatientPortalFeatureFlagsV1 {
  const resolvedFlags: PatientPortalFeatureFlagsV1 = {
    ...patientPortalDefaultFeatureFlagsV1,
  };

  for (const definition of patientPortalFeatureDefinitionsV1) {
    const row = featureFlags?.find(
      (flag) =>
        flag.flagKey === definition.flagKey &&
        flag.flagVariant == null &&
        flag.scope === "tenant" &&
        !flag.isSystemFlag,
    );

    if (row) {
      resolvedFlags[definition.flagKey] = row.enabled;
    }
  }

  return resolvedFlags;
}

function patientPortalFeatureFlagsAreEqual(
  left: PatientPortalFeatureFlagsV1,
  right: PatientPortalFeatureFlagsV1,
): boolean {
  return patientPortalFeatureDefinitionsV1.every(
    (definition) => left[definition.flagKey] === right[definition.flagKey],
  );
}

type AdminAiModelArtifactEditorState =
  | {
      kind: "prompt";
      labelText: string;
      mode: "create" | "edit";
      modelKey: AdminAiModelEditorModelKey;
      promptId: string | null;
      promptText: string;
      purposeText: string;
    }
  | {
      kind: "source";
      mode: "create" | "edit";
      modelKey: AdminAiModelEditorModelKey;
      sourceDocumentId: string | null;
      sourceText: string;
      summaryText: string;
      titleText: string;
    };

type AdminOutboundQuestionnaireRevisionDraft = {
  bulkQuestionText: string;
  clinicalPurpose: string;
  defaultEstimatedMinutesText: string;
  defaultResumePolicy: Record<string, unknown>;
  defaultReviewPolicy: TenantAdminOutboundQuestionnaireReviewPolicyV1;
  displayName: string;
  locale: string;
  ownerDepartmentCode: string;
  questionnaireTemplateId: string;
  questionnaireTemplateKey: string;
  questions: TenantAdminOutboundQuestionnaireQuestionInputV1[];
  resumePolicy: Record<string, unknown>;
  reviewPolicy: TenantAdminOutboundQuestionnaireReviewPolicyV1;
  sourceQuestionnaireVersionId: string | null;
  versionLabel: string;
};

type AdminOutboundQuestionnaireRevisionStep = "basics" | "policy" | "questions";

type AdminOutboundDialerConfigDraft = {
  campaignId: string;
  campaignName: string;
  connectCampaignArn: string;
  connectCampaignId: string;
  connectCampaignRegion: string;
  connectContactFlowId: string;
  connectInstanceId: string;
  connectSourcePhoneNumber: string;
  currentDialerStatus: TenantAdminOutboundCampaignDialerStatusV1;
  currentReadinessBlockers: string[];
  dialerMode: TenantAdminOutboundDialerModeV1;
  managedTelephony: boolean;
  managedSetupRequestedAt: string;
  showAdvancedProviderFields: boolean;
};

function readAdminOutboundMetadataText(
  metadata: Record<string, unknown> | null | undefined,
  key: string,
): string {
  const value = metadata?.[key];

  return typeof value === "string" ? value.trim() : "";
}

function readAdminOutboundRecord(value: unknown): Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readAdminOutboundNumber(value: unknown, fallback: number): number {
  const numberValue =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim().length > 0
        ? Number.parseFloat(value)
        : Number.NaN;

  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function readAdminOutboundBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function readAdminOutboundText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function setAdminOutboundRecordValue(
  source: unknown,
  key: string,
  value: unknown,
): Record<string, unknown> {
  const nextRecord = { ...readAdminOutboundRecord(source) };
  const shouldDelete =
    value == null ||
    (typeof value === "string" && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0);

  if (shouldDelete) {
    delete nextRecord[key];
  } else {
    nextRecord[key] = value;
  }

  return nextRecord;
}

function parseAdminOutboundKeywordList(value: string): string[] {
  return value
    .split(/[,\n]/u)
    .map((keyword) => keyword.trim())
    .filter((keyword, index, keywords) => {
      if (keyword.length === 0) {
        return false;
      }

      return keywords.findIndex((candidate) => candidate === keyword) === index;
    })
    .slice(0, 20);
}

function formatAdminOutboundKeywordList(value: unknown): string {
  return Array.isArray(value)
    ? value
        .map((keyword) => (typeof keyword === "string" ? keyword.trim() : ""))
        .filter((keyword) => keyword.length > 0)
        .join(", ")
    : "";
}

function resolveAdminOutboundAnswerHintBehavior(
  voicePhraseConfig: Record<string, unknown>,
): "default" | "always" | "hide" | "none" {
  const configuredBehavior = readAdminOutboundText(
    voicePhraseConfig.answerHintBehavior,
  );

  if (
    configuredBehavior === "always" ||
    configuredBehavior === "hide" ||
    configuredBehavior === "none"
  ) {
    return configuredBehavior;
  }

  if (
    readAdminOutboundBoolean(voicePhraseConfig.suppressAnswerHint, false) ||
    readAdminOutboundBoolean(voicePhraseConfig.hideAnswerHint, false) ||
    readAdminOutboundBoolean(voicePhraseConfig.omitAnswerHint, false)
  ) {
    return "hide";
  }

  if (
    readAdminOutboundBoolean(voicePhraseConfig.includeAnswerHint, false) ||
    readAdminOutboundBoolean(voicePhraseConfig.showAnswerHint, false) ||
    readAdminOutboundBoolean(voicePhraseConfig.repeatAnswerHint, false) ||
    readAdminOutboundBoolean(voicePhraseConfig.forceAnswerHint, false)
  ) {
    return "always";
  }

  return "default";
}

function normalizeAdminOutboundVoicePhraseConfig(
  value: unknown,
): Record<string, unknown> {
  const source = readAdminOutboundRecord(value);
  const leadIn =
    readAdminOutboundText(source.leadIn) ||
    readAdminOutboundText(source.promptLeadIn) ||
    readAdminOutboundText(source.voiceLeadIn);
  const answerHint =
    readAdminOutboundText(source.answerHint) ||
    readAdminOutboundText(source.answerHintText) ||
    readAdminOutboundText(source.binaryAnswerHint);
  const answerHintBehavior = resolveAdminOutboundAnswerHintBehavior(source);
  const normalized: Record<string, unknown> = { ...source };

  [
    "promptLeadIn",
    "voiceLeadIn",
    "answerHintText",
    "binaryAnswerHint",
    "includeAnswerHint",
    "showAnswerHint",
    "repeatAnswerHint",
    "forceAnswerHint",
    "suppressAnswerHint",
    "hideAnswerHint",
    "omitAnswerHint",
  ].forEach((key) => {
    delete normalized[key];
  });

  if (leadIn.trim()) {
    normalized.leadIn = leadIn.trim();
  } else {
    delete normalized.leadIn;
  }

  if (answerHint.trim()) {
    normalized.answerHint = answerHint.trim();
  } else {
    delete normalized.answerHint;
  }

  normalized.answerHintBehavior = answerHintBehavior;

  return normalized;
}

function normalizeAdminOutboundRedFlagRules(
  value: unknown,
): Record<string, unknown> {
  const source = readAdminOutboundRecord(value);
  const keywordText = readAdminOutboundText(source.keywordsInputText);
  const keywords =
    keywordText.length > 0
      ? parseAdminOutboundKeywordList(keywordText)
      : Array.isArray(source.keywords)
        ? source.keywords
        : Array.isArray(source.containsAny)
          ? source.containsAny
          : [];
  const normalized: Record<string, unknown> = { ...source };

  delete normalized.keywordsInputText;
  delete normalized.containsAny;

  if (keywords.length > 0) {
    normalized.keywords = keywords;
  } else {
    delete normalized.keywords;
  }

  normalized.unsureRequiresReview = source.unsureRequiresReview === false ? false : true;

  return normalized;
}

const adminOutboundDefaultResumePolicy = {
  allowOutsideCampaignWindow: false,
  allowPatientRequestedTime: true,
  afternoonLocalTime: "13:00",
  defaultResumeDelayMinutes: 240,
  eveningLocalTime: "16:00",
  maxPausedHours: 72,
  maxResumeAttempts: 3,
  mode: "continue_from_last_unanswered_question",
  morningLocalTime: "09:00",
  onDisconnectPauseDelayMinutes: 240,
  onNoInputPauseDelayMinutes: 240,
};

const adminOutboundDefaultVoicePolicy: TenantAdminOutboundQuestionnaireVoicePolicyV1 = {
  answerInstructionText:
    "You can answer naturally; yes, no, not sure, repeat that, go back, pause, or call me later all work.",
  briefIntroText:
    "Hello, this is AmpleCare calling on behalf of your care team to continue your questionnaire.",
  clinicianReviewDisclosureText:
    "Some answers may be sent to your care team for clinician review.",
  customToneInstructions: "",
  disclosureVersion: "post-verification-v1",
  fullIntroText:
    "Hello, this is AmpleCare calling on behalf of your care team about your health questionnaire. Your health information is kept private and handled securely, and clinicians can review anything that needs attention.",
  introMode: "provider_first_powered_by_amplecare",
  privacyDisclosureText:
    "For privacy, I will confirm I am speaking with the right person before discussing questionnaire details.",
  tonePreset: "warm_clinical",
};

function normalizeAdminOutboundQuestionnaireVoicePolicy(
  value: unknown,
): TenantAdminOutboundQuestionnaireVoicePolicyV1 {
  const source = readAdminOutboundRecord(value);
  const tonePreset = readAdminOutboundText(source.tonePreset);
  const introMode = readAdminOutboundText(source.introMode);

  return {
    answerInstructionText:
      readAdminOutboundText(source.answerInstructionText) ||
      adminOutboundDefaultVoicePolicy.answerInstructionText,
    briefIntroText:
      readAdminOutboundText(source.briefIntroText) ||
      adminOutboundDefaultVoicePolicy.briefIntroText,
    clinicianReviewDisclosureText:
      readAdminOutboundText(source.clinicianReviewDisclosureText) ||
      adminOutboundDefaultVoicePolicy.clinicianReviewDisclosureText,
    customToneInstructions: readAdminOutboundText(
      source.customToneInstructions,
    ),
    disclosureVersion:
      readAdminOutboundText(source.disclosureVersion) ||
      adminOutboundDefaultVoicePolicy.disclosureVersion,
    fullIntroText:
      readAdminOutboundText(source.fullIntroText) ||
      adminOutboundDefaultVoicePolicy.fullIntroText,
    introMode:
      introMode === "provider_only_white_label" ||
      introMode === "amplecare_first"
        ? introMode
        : adminOutboundDefaultVoicePolicy.introMode,
    privacyDisclosureText:
      readAdminOutboundText(source.privacyDisclosureText) ||
      adminOutboundDefaultVoicePolicy.privacyDisclosureText,
    tonePreset:
      tonePreset === "gentle_concise" ||
      tonePreset === "formal_concise" ||
      tonePreset === "custom"
        ? tonePreset
        : adminOutboundDefaultVoicePolicy.tonePreset,
  };
}

const adminOutboundDefaultReviewPolicy: TenantAdminOutboundQuestionnaireReviewPolicyV1 = {
  clinicianReviewDisclosureRequired: true,
  policyKey: "standard_clinician_review",
  redFlagRouting: "stop_and_review",
  redFlagStopAutomationDefault: true,
  reviewLabels: {
    changedAnswer: "Changed answer",
    medicationReview: "Medication review",
    notSure: "Not sure",
    redFlag: "Red flag",
    yellowFlag: "Yellow flag",
  },
  reviewPriorityMapping: {
    changedAnswer: "routine",
    medicationReview: "soon",
    notSure: "routine",
    redFlag: "urgent",
    yellowFlag: "soon",
  },
  reviewQueueKey: "outbound-questionnaire-review",
  unsureRequiresReviewDefault: true,
  unsureRouting: "yellow_flag",
  voicePolicy: adminOutboundDefaultVoicePolicy,
  yellowFlagRouting: "clinician_review_required",
};

function normalizeAdminOutboundQuestionnaireReviewPolicy(
  value: unknown,
): TenantAdminOutboundQuestionnaireReviewPolicyV1 {
  const source = readAdminOutboundRecord(value);
  const unsureRouting = readAdminOutboundText(source.unsureRouting);
  const yellowFlagRouting = readAdminOutboundText(source.yellowFlagRouting);
  const redFlagRouting = readAdminOutboundText(source.redFlagRouting);
  const normalizedRedFlagRouting =
    redFlagRouting === "none" ||
    redFlagRouting === "clinician_review_required" ||
    redFlagRouting === "stop_and_review"
      ? redFlagRouting
      : adminOutboundDefaultReviewPolicy.redFlagRouting;
  const reviewPriorityMapping = readAdminOutboundRecord(
    source.reviewPriorityMapping,
  );
  const reviewLabels = readAdminOutboundRecord(source.reviewLabels);

  return {
    ...source,
    clinicianReviewDisclosureRequired:
      source.clinicianReviewDisclosureRequired === false ? false : true,
    policyKey:
      readAdminOutboundText(source.policyKey) ||
      adminOutboundDefaultReviewPolicy.policyKey,
    redFlagRouting: normalizedRedFlagRouting,
    redFlagStopAutomationDefault:
      normalizedRedFlagRouting === "stop_and_review",
    reviewLabels: {
      ...adminOutboundDefaultReviewPolicy.reviewLabels,
      ...reviewLabels,
    },
    reviewPriorityMapping: {
      ...adminOutboundDefaultReviewPolicy.reviewPriorityMapping,
      ...reviewPriorityMapping,
    },
    reviewQueueKey:
      readAdminOutboundText(source.reviewQueueKey) ||
      adminOutboundDefaultReviewPolicy.reviewQueueKey,
    unsureRequiresReviewDefault:
      source.unsureRequiresReviewDefault === false ? false : true,
    unsureRouting:
      unsureRouting === "none" ||
      unsureRouting === "yellow_flag" ||
      unsureRouting === "red_flag"
        ? unsureRouting
        : adminOutboundDefaultReviewPolicy.unsureRouting,
    voicePolicy: normalizeAdminOutboundQuestionnaireVoicePolicy(
      source.voicePolicy,
    ),
    yellowFlagRouting:
      yellowFlagRouting === "none" ||
      yellowFlagRouting === "clinician_review_required" ||
      yellowFlagRouting === "queue_review"
        ? yellowFlagRouting
        : adminOutboundDefaultReviewPolicy.yellowFlagRouting,
  };
}

const legacyOutboundTelephonyVendorName = ["Connect"].join("");
const legacyOutboundTelephonyCampaignIdPattern = new RegExp(
  `\\bCampaign outbound ${legacyOutboundTelephonyVendorName} campaign id\\b`,
  "giu",
);
const legacyOutboundTelephonyInstanceIdPattern = new RegExp(
  `\\bCampaign outbound ${legacyOutboundTelephonyVendorName} instance id\\b`,
  "giu",
);
const legacyOutboundTelephonyFlowIdPattern = new RegExp(
  `\\bCampaign outbound ${legacyOutboundTelephonyVendorName} contact flow id\\b`,
  "giu",
);
const legacyOutboundTelephonyCampaignPattern = new RegExp(
  `\\b${legacyOutboundTelephonyVendorName} campaign\\b`,
  "giu",
);
const legacyOutboundTelephonyInstancePattern = new RegExp(
  `\\b${legacyOutboundTelephonyVendorName} instance\\b`,
  "giu",
);
const legacyOutboundTelephonyFlowPattern = new RegExp(
  `\\b${legacyOutboundTelephonyVendorName} contact flow\\b`,
  "giu",
);

function resolveAdminOutboundCallingBlockerText(value: string): string {
  return value
    .replace(legacyOutboundTelephonyCampaignIdPattern, "Batch campaign id")
    .replace(legacyOutboundTelephonyInstanceIdPattern, "Telephony instance id")
    .replace(legacyOutboundTelephonyFlowIdPattern, "Questionnaire flow id")
    .replace(
      /\bCampaign outbound source phone number\b/giu,
      "Campaign calling line",
    )
    .replace(legacyOutboundTelephonyCampaignPattern, "batch campaign")
    .replace(legacyOutboundTelephonyInstancePattern, "telephony instance")
    .replace(legacyOutboundTelephonyFlowPattern, "questionnaire flow");
}

type AdminOutboundCreateCampaignDraft = {
  displayName: string;
  purposeText: string;
  questionnaireTemplateVersionId: string;
};

type AdminOutboundCampaignQuestionnaireImpactDraft = {
  campaign: TenantAdminOutboundCampaignV1;
  impactReview: TenantAdminOutboundCampaignQuestionnaireImpactReviewV1 | null;
  migrateEligibleQueuedSessions: boolean;
  targetQuestionnaireTemplateVersionId: string;
};

const adminOutboundQuestionnaireAnswerTypeOptions: Array<{
  labelText: string;
  value: TenantAdminOutboundQuestionAnswerTypeV1;
}> = [
  { labelText: "Short text", value: "short_text" },
  { labelText: "Yes/no", value: "yes_no" },
  { labelText: "Confirmation", value: "confirmation" },
  { labelText: "Number", value: "number" },
  { labelText: "Date", value: "date" },
  { labelText: "Single choice", value: "single_choice" },
  { labelText: "Multi choice", value: "multi_choice" },
];

const adminOutboundCustomQuestionSectionKey = "custom";
const adminOutboundCustomQuestionSectionLabel = "Custom questions";

function formatAdminOutboundQuestionAnswerType(
  answerType: TenantAdminOutboundQuestionAnswerTypeV1,
): string {
  return (
    adminOutboundQuestionnaireAnswerTypeOptions.find(
      (option) => option.value === answerType,
    )?.labelText ?? "Short text"
  );
}

function nextAdminOutboundQuestionAnswerType(
  answerType: TenantAdminOutboundQuestionAnswerTypeV1,
): TenantAdminOutboundQuestionAnswerTypeV1 {
  const currentIndex = adminOutboundQuestionnaireAnswerTypeOptions.findIndex(
    (option) => option.value === answerType,
  );
  const nextIndex =
    currentIndex < 0
      ? 0
      : (currentIndex + 1) % adminOutboundQuestionnaireAnswerTypeOptions.length;

  return adminOutboundQuestionnaireAnswerTypeOptions[nextIndex]?.value ?? "short_text";
}

function normalizeAdminOutboundQuestionKeySlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "_")
    .replace(/^_+|_+$/gu, "")
    .slice(0, 96);
}

function createUniqueAdminOutboundQuestionKey(
  baseValue: string,
  existingKeys: Set<string>,
): string {
  const fallback = `question_${String(existingKeys.size + 1)}`;
  const baseSlug = normalizeAdminOutboundQuestionKeySlug(baseValue) || fallback;
  const baseKey = `custom_${baseSlug}`.slice(0, 112);
  let candidate = baseKey;
  let suffix = 2;

  while (existingKeys.has(candidate)) {
    candidate = `${baseKey.slice(0, 112 - String(suffix).length - 1)}_${String(
      suffix,
    )}`;
    suffix += 1;
  }

  existingKeys.add(candidate);

  return candidate;
}

function resequenceAdminOutboundQuestions(
  questions: TenantAdminOutboundQuestionnaireQuestionInputV1[],
): TenantAdminOutboundQuestionnaireQuestionInputV1[] {
  return questions.map((question, index) => ({
    ...question,
    sequenceNumber: index + 1,
  }));
}

function parseAdminOutboundQuestionPasteLines(value: string): string[] {
  return value
    .split(/\r?\n/u)
    .map((line) =>
      line
        .replace(/^\s*(?:[-*\u2022]+|\d+[.)])\s*/u, "")
        .trim(),
    )
    .filter((line) => line.length > 0);
}

function buildAdminOutboundCustomQuestion(
  promptText: string,
  existingKeys: Set<string>,
  sequenceNumber: number,
): TenantAdminOutboundQuestionnaireQuestionInputV1 {
  return {
    answerNormalization: {},
    answerType: "short_text",
    clinicianReviewLabel: null,
    dtmfConfig: {},
    promptText,
    questionKey: createUniqueAdminOutboundQuestionKey(promptText, existingKeys),
    redFlagRules: {},
    required: true,
    sectionKey: adminOutboundCustomQuestionSectionKey,
    sectionLabel: adminOutboundCustomQuestionSectionLabel,
    sequenceNumber,
    skipLogic: {},
    validationRules: {},
    voicePhraseConfig: {},
    questionMetadata: {},
  };
}

type AdminCommandRunResult = {
  ok: boolean;
  result?: TenantAdminCommandResponseV1["result"] | null;
  summaryText: string | null;
};

type AdminPatientLifecycleDraft = {
  action: "archive" | "restore";
  displayName: string;
  expectedLifecycleVersion: number;
  idempotencyKey: string;
  notes: string;
  patientId: string;
  reasonKey: string;
  restoreStatus: "active" | "draft" | "inactive";
};

const adminPatientArchiveReasonOptions = [
  { labelText: "Care completed", value: "care-completed" },
  { labelText: "Patient transferred", value: "patient-transferred" },
  { labelText: "Duplicate record", value: "duplicate-record" },
  { labelText: "Created in error", value: "created-in-error" },
  { labelText: "Other", value: "other" },
] as const;

type AdminOutboundCallsRefreshPayload = {
  summary: TenantAdminOutboundCallsSummaryResponseV1;
  targetsPage: TenantAdminOutboundCallsTargetsResponseV1;
};

function hasLiveAdminOutboundCall(
  summary: TenantAdminOutboundCallsSummaryResponseV1 | null,
  targetsPage: TenantAdminOutboundCallsTargetsResponseV1 | null,
): boolean {
  if (targetsPage?.items.some((target) => target.targetStatus === "calling")) {
    return true;
  }

  return (
    summary?.recentSessions.some(
      (session) =>
        session.attempts?.some(
          (attempt) =>
            attempt.attemptStatus === "dialing" ||
            attempt.attemptStatus === "live_answer",
        ) === true,
    ) ?? false
  );
}

function isAdminSessionUnauthorizedError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes("failed-401") ||
      error.message.includes("failed-403"))
  );
}

export type AdminConsolePageProps = {
  activeWorkforceSession: WorkforceSessionSummaryV1 | null;
  adminAnalyticsRangeSelection: AdminAnalyticsRangeSelection;
  adminSummary: TenantAdminSummaryResponseV1 | null;
  beginWorkspaceSelection: () => void;
  canSwitchWorkspaces: boolean;
  hasAdminSummaryError: boolean;
  isAdminCommandSubmitting: boolean;
  isAdminSummaryLoading: boolean;
  isMediumLayout: boolean;
  isWideLayout: boolean;
  layout: typeof designTokens.semanticLayout;
  legacyHospitalLogoSource: { uri: string };
  onRunAdminCommand: (
    command: TenantAdminCommandInputV1,
  ) => Promise<AdminCommandRunResult>;
  onLoadAdminReviewQueueItems: (request: {
    queueId: TenantAdminReviewQueueId;
    offset?: number | null;
    limit?: number | null;
  }) => Promise<TenantAdminReviewQueueItemsResponseV1 | null>;
  onSessionExpired: () => void;
  onSignOut: () => void;
  onUpdateAdminAnalyticsRange: (value: AdminAnalyticsRangeSelection) => void;
  runtimeConfig: MobileRuntimeConfig;
  showsLegacyWebAssets: boolean;
  theme: AppShellTheme;
  tokens: typeof designTokens;
  viewportHeight: number;
  viewportWidth: number;
  legacyBackgroundTextureSource: { uri: string };
  patientDesktopPalette: typeof designTokens.semanticTheme.patientDesktopWorkspace;
  renderLegacyWorkbenchFooter: (input: {
    layout: typeof designTokens.semanticLayout;
    palette: typeof designTokens.semanticTheme.patientDesktopWorkspace;
    position?: "absolute" | "relative";
    viewportWidth: number;
  }) => ReactNode;
};

export function AdminConsolePage(context: AdminConsolePageProps) {
  const {
    activeWorkforceSession,
    adminAnalyticsRangeSelection,
    adminSummary,
    hasAdminSummaryError,
    isAdminCommandSubmitting,
    isAdminSummaryLoading,
    isMediumLayout,
    isWideLayout,
    layout,
    legacyBackgroundTextureSource,
    onLoadAdminReviewQueueItems,
    onRunAdminCommand,
    onSessionExpired,
    onSignOut,
    onUpdateAdminAnalyticsRange,
    patientDesktopPalette,
    renderLegacyWorkbenchFooter,
    runtimeConfig,
    showsLegacyWebAssets,
    theme,
    tokens,
    viewportHeight,
    viewportWidth,
  } = context;
  const summary = adminSummary?.summary ?? null;
  const hasAdminSummary = summary != null;
  const activeWorkforceSessionSiteId = activeWorkforceSession?.siteId ?? null;
  const latestProvisioningRun = summary?.provisioning.latestRun ?? null;
  const latestProvisioningRunId =
    summary?.provisioning.recentRuns?.[0]?.runId ?? null;
  const pageMaxWidth = 1680;
  const legacyWorkbenchFooterHeight = showsLegacyWebAssets ? 32 : 0;
  const adminControlRadius = adminOperationalLayout.controlRadius;
  const isAdminMobileLayout = viewportWidth < 640;
  const usesScrollableAdminMetrics = viewportWidth < 760;
  const usesAdminWideCommandLayout = isWideLayout;
  const usesAdminCommandRailLayout = isWideLayout && viewportWidth >= 1560;
  const adminButtonMinWidth = isMediumLayout
    ? 132
    : isAdminMobileLayout
      ? 104
      : 118;
  const adminCompactButtonMinWidth = isMediumLayout
    ? 118
    : isAdminMobileLayout
      ? 92
      : 104;
  const adminToolbarActionButtonWidth = isMediumLayout
    ? 176
    : isAdminMobileLayout
      ? 116
      : 152;
  const adminHeaderControlHeight = isAdminMobileLayout
    ? 40
    : adminOperationalLayout.controlHeight;
  const adminTableActionButtonWidth = isMediumLayout
    ? 108
    : isAdminMobileLayout
      ? 96
      : 104;
  const adminNarrowTableActionButtonWidth = isMediumLayout
    ? 82
    : isAdminMobileLayout
      ? 72
      : 78;
  const legacyAdminSurfaceMinHeight =
    showsLegacyWebAssets && isWideLayout
      ? Math.max(360, Math.floor(viewportHeight - 200))
      : undefined;
  const [adminDashboardTabId, setAdminDashboardTabId] =
    useState<AdminDashboardTabId>("patients");
  const [adminSearchQueriesByTab, setAdminSearchQueriesByTab] = useState<
    Record<AdminDashboardTabId, string>
  >({
    "ai-models": "",
    analytics: "",
    clinicians: "",
    connectors: "",
    departments: "",
    flags: "",
    "outbound-calls": "",
    "patient-portal": "",
    patients: "",
    provisioning: "",
    reviews: "",
  });
  const adminSearchQuery = adminSearchQueriesByTab[adminDashboardTabId] ?? "";
  const setAdminSearchQuery = (nextQuery: string) => {
    setAdminSearchQueriesByTab((current) =>
      current[adminDashboardTabId] === nextQuery
        ? current
        : {
            ...current,
            [adminDashboardTabId]: nextQuery,
          },
    );
  };
  const deferredAdminSearchQuery = useDeferredValue(adminSearchQuery.trim());
  const [adminPatientOperationsPage, setAdminPatientOperationsPage] =
    useState<TenantAdminPatientOperationsResponseV1 | null>(null);
  const [isAdminPatientOperationsLoading, setIsAdminPatientOperationsLoading] =
    useState(false);
  const [hasAdminPatientOperationsError, setHasAdminPatientOperationsError] =
    useState(false);
  const [adminClinicianOperationsPage, setAdminClinicianOperationsPage] =
    useState<TenantAdminClinicianOperationsResponseV1 | null>(null);
  const [
    isAdminClinicianOperationsLoading,
    setIsAdminClinicianOperationsLoading,
  ] = useState(false);
  const [
    hasAdminClinicianOperationsError,
    setHasAdminClinicianOperationsError,
  ] = useState(false);
  const [adminOutboundCallsPage, setAdminOutboundCallsPage] =
    useState<TenantAdminOutboundCallsSummaryResponseV1 | null>(null);
  const [
    adminOutboundQuestionnairesPage,
    setAdminOutboundQuestionnairesPage,
  ] = useState<TenantAdminOutboundQuestionnaireTemplateV1[] | null>(null);
  const [adminOutboundTargetsPage, setAdminOutboundTargetsPage] =
    useState<TenantAdminOutboundCallsTargetsResponseV1 | null>(null);
  const [isAdminOutboundCallsLoading, setIsAdminOutboundCallsLoading] =
    useState(false);
  const [hasAdminOutboundCallsError, setHasAdminOutboundCallsError] =
    useState(false);
  const [
    isAdminOutboundCommandSubmitting,
    setIsAdminOutboundCommandSubmitting,
  ] = useState(false);
  const [adminConnectorStatusFilter, setAdminConnectorStatusFilter] =
    useState<string>("all");
  const [adminSiteFilter, setAdminSiteFilter] = useState<string>("all");
  const [adminReviewPriorityFilter, setAdminReviewPriorityFilter] =
    useState<string>("all");
  const [adminConnectorSortKey, setAdminConnectorSortKey] = useState<
    "name" | "status" | "sync"
  >("status");
  const [adminPatientSortKey, setAdminPatientSortKey] = useState<
    "name" | "access"
  >("name");
  const [adminPatientQuickFilter, setAdminPatientQuickFilter] =
    useState<AdminPatientQuickFilter>("all");
  const [adminClinicianSortKey, setAdminClinicianSortKey] = useState<
    "name" | "department" | "role"
  >("department");
  const [adminShowInactiveClinicians, setAdminShowInactiveClinicians] =
    useState(false);
  const [adminReviewSortKey, setAdminReviewSortKey] = useState<
    "priority" | "count" | "signal"
  >("priority");
  const [adminFeatureFlagSortKey, setAdminFeatureFlagSortKey] = useState<
    "key" | "scope" | "updated"
  >("updated");
  const [adminConnectorPageIndex, setAdminConnectorPageIndex] = useState(0);
  const [adminSelectedConnectorId, setAdminSelectedConnectorId] = useState<
    string | null
  >(null);
  const [adminPatientPageIndex, setAdminPatientPageIndex] = useState(0);
  const [adminSelectedPatientRowId, setAdminSelectedPatientRowId] = useState<
    string | null
  >(null);
  const [adminClinicianPageIndex, setAdminClinicianPageIndex] = useState(0);
  const [adminReviewPageIndex, setAdminReviewPageIndex] = useState(0);
  const [adminSelectedReviewQueueId, setAdminSelectedReviewQueueId] = useState<
    string | null
  >(null);
  const [adminReviewQueueDetailFilter, setAdminReviewQueueDetailFilter] =
    useState<AdminReviewQueueDetailFilter>("all");
  const [adminFeatureFlagPageIndex, setAdminFeatureFlagPageIndex] = useState(0);
  const [adminProvisioningPageIndex, setAdminProvisioningPageIndex] =
    useState(0);
  const [adminProvisioningViewFilter, setAdminProvisioningViewFilter] =
    useState<"all" | "runs" | "allocations" | "clusters" | "failed">("all");
  const [adminSelectedProvisioningRunId, setAdminSelectedProvisioningRunId] =
    useState<string | null>(null);
  const [adminOpsNotice, setAdminOpsNotice] = useState<string | null>(null);
  const [adminOutboundTargetToast, setAdminOutboundTargetToast] = useState<
    string | null
  >(null);
  const [adminReviewQueueItemsById, setAdminReviewQueueItemsById] = useState<
    Record<string, TenantAdminReviewQueueItemsResponseV1>
  >({});
  const [adminReviewQueueItemsLoadingId, setAdminReviewQueueItemsLoadingId] =
    useState<string | null>(null);
  const [adminReviewQueueItemsErrorById, setAdminReviewQueueItemsErrorById] =
    useState<Record<string, string>>({});
  const [adminCrudForm, setAdminCrudForm] = useState<AdminCrudFormState | null>(
    null,
  );
  const [adminPatientLifecycleDraft, setAdminPatientLifecycleDraft] =
    useState<AdminPatientLifecycleDraft | null>(null);
  const [adminFeatureFlagDraft, setAdminFeatureFlagDraft] =
    useState<AdminFeatureFlagDraftState | null>(null);
  // Phase O-6 callback scheduler card (outbound section): null renders the
  // saved settings; a draft holds unsaved edits until save succeeds.
  const [adminCallbackSchedulerDraft, setAdminCallbackSchedulerDraft] =
    useState<{
      breakMinutesText: string;
      enabled: boolean;
      maxAttemptsText: string;
    } | null>(null);
  const [adminPatientPortalFeatureDraft, setAdminPatientPortalFeatureDraft] =
    useState<PatientPortalFeatureFlagsV1 | null>(null);
  const [adminAiModelArtifactEditor, setAdminAiModelArtifactEditor] =
    useState<AdminAiModelArtifactEditorState | null>(null);
  const [
    adminOutboundQuestionnaireRevisionDraft,
    setAdminOutboundQuestionnaireRevisionDraft,
  ] = useState<AdminOutboundQuestionnaireRevisionDraft | null>(null);
  const [
    adminOutboundExpandedPolicyQuestionKeys,
    setAdminOutboundExpandedPolicyQuestionKeys,
  ] = useState<ReadonlySet<string>>(() => new Set());
  // Floating field-help tooltip rendered in a panel-level overlay so it is never
  // clipped by later rows. Positioned from the "?" icon's window coordinates.
  const [activeFieldHelp, setActiveFieldHelp] = useState<{
    text: string;
    left: number;
    iconTop: number;
    iconBottom: number;
  } | null>(null);
  const [adminOutboundTab, setAdminOutboundTab] =
    useState<AdminOutboundTabId>("overview");
  const [adminOutboundPatientSearch, setAdminOutboundPatientSearch] =
    useState<string>("");
  const [adminOutboundPatientFilter, setAdminOutboundPatientFilter] =
    useState<AdminOutboundPatientFilter>("all");
  const [adminOutboundPatientPageIndex, setAdminOutboundPatientPageIndex] =
    useState(0);
  const [adminOutboundSelectedTargetIds, setAdminOutboundSelectedTargetIds] =
    useState<readonly string[]>([]);
  const [
    adminOutboundAdvanceNoticePolicyAttestationKey,
    setAdminOutboundAdvanceNoticePolicyAttestationKey,
  ] = useState<string | null>(null);
  const [adminOutboundDrawerCandidateId, setAdminOutboundDrawerCandidateId] =
    useState<string | null>(null);
  const [adminOutboundRevisionStep, setAdminOutboundRevisionStep] =
    useState<AdminOutboundQuestionnaireRevisionStep>("basics");
  const [adminOutboundSelectedCampaignId, setAdminOutboundSelectedCampaignId] =
    useState<string | null>(null);
  const [adminOutboundCampaignPickerOpen, setAdminOutboundCampaignPickerOpen] =
    useState(false);
  const [adminOutboundAnalyticsScope, setAdminOutboundAnalyticsScope] =
    useState<"all" | "campaign">("all");
  const [adminOutboundDialerDraft, setAdminOutboundDialerDraft] =
    useState<AdminOutboundDialerConfigDraft | null>(null);
  const [adminOutboundCreateDraft, setAdminOutboundCreateDraft] =
    useState<AdminOutboundCreateCampaignDraft | null>(null);
  const [
    adminOutboundCampaignQuestionnaireImpactDraft,
    setAdminOutboundCampaignQuestionnaireImpactDraft,
  ] = useState<AdminOutboundCampaignQuestionnaireImpactDraft | null>(null);
  const [adminOutboundCampaignAnalytics, setAdminOutboundCampaignAnalytics] =
    useState<TenantAdminOutboundCallsSummaryResponseV1 | null>(null);
  const [adminActivityPopoverOpen, setAdminActivityPopoverOpen] =
    useState(false);
  const [adminFeatureFlagStatusFilter, setAdminFeatureFlagStatusFilter] =
    useState<
      "all" | "enabled" | "disabled" | "unset" | "system" | "feature"
    >("all");
  const [adminPatientCareTypeFilter, setAdminPatientCareTypeFilter] =
    useState<TenantAdminPatientOperationsCareTypeFilter>("all");
  const [adminAnalyticsFunnelMode, setAdminAnalyticsFunnelMode] = useState<
    "overall" | "step-by-step"
  >("overall");
  const [isAdminAnalyticsCustomRangeOpen, setIsAdminAnalyticsCustomRangeOpen] =
    useState(false);
  const [adminAnalyticsCustomRangeDraft, setAdminAnalyticsCustomRangeDraft] =
    useState({
      endDate: adminAnalyticsRangeSelection.endDate,
      startDate: adminAnalyticsRangeSelection.startDate,
    });
  const selectAdminPatientQuickFilter = (
    nextFilter: AdminPatientQuickFilter,
  ): void => {
    setAdminPatientQuickFilter(nextFilter);
    setAdminPatientPageIndex(0);
    setAdminSelectedPatientRowId(null);
  };

  useEffect(() => {
    setAdminAnalyticsCustomRangeDraft({
      endDate: adminAnalyticsRangeSelection.endDate,
      startDate: adminAnalyticsRangeSelection.startDate,
    });
  }, [
    adminAnalyticsRangeSelection.endDate,
    adminAnalyticsRangeSelection.startDate,
  ]);

  useEffect(() => {
    setAdminPatientPortalFeatureDraft(null);
  }, [summary?.generatedAt]);

  const adminPatientPortalSavedFeatureFlags =
    resolveAdminPatientPortalFeatureFlags(summary?.featureFlags);
  const adminPatientPortalFeatureFlags =
    adminPatientPortalFeatureDraft ?? adminPatientPortalSavedFeatureFlags;
  const adminPatientPortalHasUnsavedChanges =
    !patientPortalFeatureFlagsAreEqual(
      adminPatientPortalFeatureFlags,
      adminPatientPortalSavedFeatureFlags,
    );
  const adminPatientPortalEnabledCount =
    patientPortalFeatureDefinitionsV1.filter(
      (definition) => adminPatientPortalFeatureFlags[definition.flagKey],
    ).length;
  // Preview runs the patient route in scaffold mode (sample logged-in patient)
  // so the configurator shows the patient view directly without an OTP login.
  // NOTE: on hosted deploys these scaffold params are intentionally ignored
  // (disableHostedScaffoldRuntime), so the hosted preview still requires login.
  const adminPatientPortalPreviewUrl =
    `/patient/?acTenantId=${encodeURIComponent(
      runtimeConfig.requestTenantId,
    )}&acBootstrapMode=scaffold-header&acRoleKeys=patient&acTransport=scaffold&patientPortalFeatureFlags=${encodeURIComponent(
      JSON.stringify(adminPatientPortalFeatureFlags),
    )}`;

  useEffect(() => {
    setAdminCrudForm(null);
    setAdminPatientLifecycleDraft(null);
    setAdminAiModelArtifactEditor(null);
    setAdminFeatureFlagDraft(null);
    setAdminOutboundQuestionnaireRevisionDraft(null);
    setAdminOpsNotice(null);
    setAdminSelectedConnectorId(null);
    setAdminSelectedPatientRowId(null);
    setAdminSelectedProvisioningRunId(null);
    if (adminDashboardTabId !== "reviews") {
      setAdminSelectedReviewQueueId(null);
    }
    setIsAdminAnalyticsCustomRangeOpen(false);
  }, [adminDashboardTabId]);

  useEffect(() => {
    if (
      adminAiModelArtifactEditor == null &&
      adminCrudForm == null &&
      adminPatientLifecycleDraft == null &&
      adminFeatureFlagDraft == null &&
      adminOutboundQuestionnaireRevisionDraft == null
    ) {
      return;
    }

    setAdminSelectedConnectorId(null);
    setAdminSelectedPatientRowId(null);
    setAdminSelectedProvisioningRunId(null);
    setAdminSelectedReviewQueueId(null);
  }, [
    adminAiModelArtifactEditor,
    adminCrudForm,
    adminPatientLifecycleDraft,
    adminFeatureFlagDraft,
    adminOutboundQuestionnaireRevisionDraft,
  ]);

  const runAdminOpsCommand = async (
    command: TenantAdminCommandInputV1,
    messages: {
      failureText: string;
      pendingText: string;
      successText: string;
    },
  ): Promise<boolean> => {
    if (isAdminCommandSubmitting) {
      return false;
    }

    setAdminOpsNotice(messages.pendingText);

    try {
      const commandResult = await onRunAdminCommand(command);

      setAdminOpsNotice(
        commandResult.summaryText ??
          (commandResult.ok ? messages.successText : messages.failureText),
      );

      return commandResult.ok;
    } catch (error) {
      if (handleAdminSessionError(error)) {
        return false;
      }

      setAdminOpsNotice(
        error instanceof Error ? error.message : messages.failureText,
      );

      return false;
    }
  };
  const adminCallbackSchedulerSettings =
    summary?.callbackSchedulerSettings ?? null;
  const adminCallbackSchedulerBaseline = adminCallbackSchedulerSettings ?? {
    callbacksEnabled: true,
    retryBreakMinutes: 60,
    maxAttemptsPerRequest: 3,
    updatedAt: null,
    configurable: true,
  };
  const adminCallbackSchedulerView = adminCallbackSchedulerDraft ?? {
    breakMinutesText: String(adminCallbackSchedulerBaseline.retryBreakMinutes),
    enabled: adminCallbackSchedulerBaseline.callbacksEnabled,
    maxAttemptsText: String(
      adminCallbackSchedulerBaseline.maxAttemptsPerRequest,
    ),
  };
  const updateAdminCallbackSchedulerDraft = (
    update: Partial<{
      breakMinutesText: string;
      enabled: boolean;
      maxAttemptsText: string;
    }>,
  ) => {
    setAdminCallbackSchedulerDraft({
      ...adminCallbackSchedulerView,
      ...update,
    });
  };
  const saveAdminCallbackSchedulerSettings = async () => {
    const breakMinutes = Number.parseInt(
      adminCallbackSchedulerView.breakMinutesText.trim(),
      10,
    );
    const maxAttempts = Number.parseInt(
      adminCallbackSchedulerView.maxAttemptsText.trim(),
      10,
    );

    if (
      !Number.isInteger(breakMinutes) ||
      breakMinutes < 0 ||
      breakMinutes > 10080
    ) {
      setAdminOpsNotice("Break between tries must be 0-10080 minutes.");
      return;
    }

    if (!Number.isInteger(maxAttempts) || maxAttempts < 1 || maxAttempts > 10) {
      setAdminOpsNotice("Tries per request must be 1-10.");
      return;
    }

    const saved = await runAdminOpsCommand(
      {
        kind: "update-callback-scheduler-settings",
        callbacksEnabled: adminCallbackSchedulerView.enabled,
        retryBreakMinutes: breakMinutes,
        maxAttemptsPerRequest: maxAttempts,
      },
      {
        failureText: "Callback scheduler settings could not be saved.",
        pendingText: "Saving callback scheduler settings...",
        successText: "Callback scheduler settings saved.",
      },
    );

    if (saved) {
      setAdminCallbackSchedulerDraft(null);
    }
  };
  const setAdminPatientPortalFeatureEnabled = (
    flagKey: keyof PatientPortalFeatureFlagsV1,
    enabled: boolean,
  ) => {
    setAdminPatientPortalFeatureDraft((current) => ({
      ...(current ?? adminPatientPortalSavedFeatureFlags),
      [flagKey]: enabled,
    }));
  };
  const saveAdminPatientPortalFeatureFlags = () => {
    void runAdminOpsCommand(
      {
        featureFlags: adminPatientPortalFeatureFlags,
        kind: "set-patient-portal-feature-flags",
        rolloutNote: "Admin updated patient portal module visibility.",
      },
      {
        failureText: "Patient portal module settings could not be saved.",
        pendingText: "Saving patient portal module settings...",
        successText: "Patient portal module settings saved and summary refreshed.",
      },
    ).then((wasSaved) => {
      if (wasSaved) {
        setAdminPatientPortalFeatureDraft(null);
      }
    });
  };
  const reloadAdminOutboundCalls = async (): Promise<void> => {
    const [outboundSummary, questionnairesPage, targetsPage] =
      await Promise.all([
        loadTenantAdminOutboundCallsSummary(runtimeConfig),
        loadTenantAdminOutboundCallsQuestionnaires(runtimeConfig, {
          includeQuestions: true,
          limit: 100,
          offset: 0,
          status: "all",
        }),
        loadTenantAdminOutboundCallsTargets(runtimeConfig, {
          limit: 100,
          offset: 0,
          status: "all",
        }),
      ]);

    startTransition(() => {
      setAdminOutboundCallsPage(outboundSummary);
      setAdminOutboundQuestionnairesPage(questionnairesPage.items);
      setAdminOutboundTargetsPage(targetsPage);
      setHasAdminOutboundCallsError(false);
    });
  };
  const runAdminOutboundCommand = async (
    command: TenantAdminOutboundCallCommandInputV1,
    pendingText: string,
    failureText: string,
  ): Promise<TenantAdminOutboundCallsCommandResponseV1 | null> => {
    if (isAdminOutboundCommandSubmitting) {
      return null;
    }

    setAdminOpsNotice(pendingText);
    setIsAdminOutboundCommandSubmitting(true);

    try {
      const result = await submitTenantAdminOutboundCallCommand(
        runtimeConfig,
        command,
      );

      startTransition(() => {
        setAdminOutboundCallsPage(result.summary);
      });
      await reloadAdminOutboundCalls();
      const changedRefreshAliases = buildWorkspaceRefreshAliasesForChangedScopes(
        result.changedScopes,
      );
      invalidateWorkspaceRefreshScopes(
        changedRefreshAliases.length > 0
          ? changedRefreshAliases
          : adminWorkspaceMutationRefreshAliases,
        "tenant-admin-outbound-command-success",
        { local: false, tenantId: runtimeConfig.requestTenantId },
      );
      setAdminOpsNotice(result.summaryText);

      return result;
    } catch (error) {
      if (handleAdminSessionError(error)) {
        return null;
      }

      setHasAdminOutboundCallsError(true);
      setAdminOpsNotice(error instanceof Error ? error.message : failureText);

      return null;
    } finally {
      setIsAdminOutboundCommandSubmitting(false);
    }
  };
  const updateAdminCrudFormField = (fieldName: string, value: string): void => {
    setAdminCrudForm((current) => {
      if (current == null) {
        return current;
      }

      if (current.mode === "retire") {
        return {
          ...current,
          [fieldName]: value,
        } as AdminCrudFormState;
      }

      const nextForm = {
        ...current,
        [fieldName]: value,
      } as AdminCrudFormState;

      if (current.entityKind === "clinician") {
        const nextClinicianForm = nextForm as Extract<
          AdminCrudFormState,
          { entityKind: "clinician" }
        >;
        const withLoginEmail =
          fieldName === "emailAddress" &&
          current.testLoginEmail.trim().length === 0
            ? {
                ...nextClinicianForm,
                testLoginEmail: value,
              }
            : nextClinicianForm;

        if (
          current.mode === "create" &&
          (fieldName === "displayName" || fieldName === "emailAddress")
        ) {
          return {
            ...withLoginEmail,
            clinicianUserId: "",
            previewProfileKey: "",
          } as AdminCrudFormState;
        }

        if (
          fieldName === "clinicianUserId" &&
          current.previewProfileKey.trim().length === 0
        ) {
          return {
            ...withLoginEmail,
            previewProfileKey: buildGeneratedAdminClinicianPreviewProfileKey(
              value.trim(),
            ),
          } as AdminCrudFormState;
        }

        if (withLoginEmail !== nextClinicianForm) {
          return withLoginEmail as AdminCrudFormState;
        }
      }

      if (current.entityKind === "clinician" && fieldName === "teamKey") {
        const selectedTeam = previewTeamRows.find(
          (team) => team.teamKey === value,
        );

        return {
          ...nextForm,
          departmentCode: selectedTeam?.departmentCode ?? "",
        } as AdminCrudFormState;
      }

      if (current.entityKind === "connector" && fieldName === "teamKey") {
        const selectedTeam = previewTeamRows.find(
          (team) => team.teamKey === value,
        );

        return {
          ...nextForm,
          departmentCode:
            selectedTeam?.departmentCode ?? current.departmentCode,
        } as AdminCrudFormState;
      }

      if (
        current.entityKind === "clinician" &&
        fieldName === "departmentCode"
      ) {
        const currentTeam = previewTeamRows.find(
          (team) => team.teamKey === current.teamKey,
        );
        const nextTeamKey =
          value.length > 0 && currentTeam?.departmentCode === value
            ? current.teamKey
            : "";

        return {
          ...nextForm,
          teamKey: nextTeamKey,
        } as AdminCrudFormState;
      }

      if (current.entityKind === "connector" && fieldName === "departmentCode") {
        const currentTeam = previewTeamRows.find(
          (team) => team.teamKey === current.teamKey,
        );
        const nextTeamKey =
          value.length > 0 && currentTeam?.departmentCode !== value
            ? ""
            : current.teamKey;

        return {
          ...nextForm,
          teamKey: nextTeamKey,
        } as AdminCrudFormState;
      }

      return nextForm;
    });
  };
  const resolveAdminPatientCrudStatus = (
    statusLabel: string,
  ): (typeof adminPatientStatusOptions)[number] => {
    const normalizedStatus = statusLabel.trim().toLowerCase();

    return adminPatientStatusOptions.includes(
      normalizedStatus as (typeof adminPatientStatusOptions)[number],
    )
      ? (normalizedStatus as (typeof adminPatientStatusOptions)[number])
      : "active";
  };
  const buildGeneratedAdminPatientId = (
    form: Extract<AdminCrudFormState, { entityKind: "patient" }>,
  ): string => {
    const phoneDigits = form.phoneNumber.replace(/\D/gu, "");
    const sourceValue =
      form.displayName.trim() ||
      form.emailAddress.trim().split("@")[0] ||
      phoneDigits ||
      "patient";
    const slug = buildAdminCommandKey(sourceValue, "patient");
    const suffix = Date.now().toString(36);

    return `patient-${slug}-${suffix}`.slice(0, 128);
  };
  const buildAdminClinicianIdentitySource = (
    form: Extract<AdminCrudFormState, { entityKind: "clinician" }>,
  ): string =>
    form.emailAddress.trim().split("@")[0] ||
    form.displayName.trim() ||
    "clinician";
  const buildGeneratedAdminClinicianUserId = (
    form: Extract<AdminCrudFormState, { entityKind: "clinician" }>,
  ): string =>
    buildAdminCommandKey(buildAdminClinicianIdentitySource(form), "clinician");
  const buildGeneratedAdminClinicianPreviewProfileKey = (
    clinicianUserId: string,
  ): string => `${buildAdminCommandKey(clinicianUserId, "clinician")}-web`;
  const resolveAdminClinicianUserId = (
    form: Extract<AdminCrudFormState, { entityKind: "clinician" }>,
  ): string =>
    form.clinicianUserId.trim() || buildGeneratedAdminClinicianUserId(form);
  const resolveAdminClinicianPreviewProfileKey = (
    form: Extract<AdminCrudFormState, { entityKind: "clinician" }>,
    clinicianUserId: string = resolveAdminClinicianUserId(form),
  ): string =>
    form.previewProfileKey.trim() ||
    buildGeneratedAdminClinicianPreviewProfileKey(clinicianUserId);
  const runCreatePatientCommand = () => {
    setAdminCrudForm({
      addressCity: "",
      addressState: "",
      addressStreet: "",
      addressZip: "",
      alternateEmergencyContactPhone: "",
      contactWindowText: "24h",
      dateOfBirth: "",
      displayName: "",
      emailAddress: "",
      emergencyContactPhone: "",
      entityKind: "patient",
      expectedLifecycleVersion: null,
      familyName: "",
      givenName: "",
      homePhoneNumber: "",
      identifier: "",
      leadingClinicianUserId: "",
      mode: "create",
      onboardingState: "open",
      patientId: "",
      phoneNumber: "",
      programLabel: "",
      secondaryPhoneNumber: "",
      sex: "",
      siteId: activeWorkforceSessionSiteId ?? runtimeConfig.requestSiteId ?? "",
      status: "active",
      workPhoneNumber: "",
    });
    setAdminOpsNotice(
      "Fill the patient form and save. The system assigns the patient reference automatically.",
    );
  };
  const formatAdminPatientImportNotice = (
    commandResult: AdminCommandRunResult,
  ): string => {
    const importResult =
      commandResult.result?.kind === "patient-import"
        ? commandResult.result
        : null;

    if (!importResult) {
      return commandResult.summaryText ?? "Patient import finished.";
    }

    const issueLines = importResult.issues.slice(0, 6).map((issue) => {
      const prefix = issue.severity === "error" ? "Error" : "Warning";
      const fieldText = issue.field ? ` ${issue.field}` : "";

      return `${prefix} row ${issue.rowNumber}${fieldText}: ${issue.message}`;
    });
    const issueSuffix =
      importResult.issues.length > issueLines.length
        ? ` ${importResult.issues.length - issueLines.length} more issue(s).`
        : "";
    const body = issueLines.length > 0 ? ` ${issueLines.join(" ")}` : "";

    return `${commandResult.summaryText ?? "Patient import finished."}${body}${issueSuffix}`;
  };
  const runImportPatientsCsvCommand = () => {
    if (isAdminCommandSubmitting) {
      return;
    }

    if (Platform.OS !== "web" || typeof document === "undefined") {
      setAdminOpsNotice("CSV import is available in the web admin console.");
      return;
    }

    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".csv,text/csv";
    fileInput.onchange = () => {
      const file = fileInput.files?.[0] ?? null;

      if (!file) {
        return;
      }

      setAdminOpsNotice(`Reading ${file.name}...`);
      void file
        .text()
        .then((csvText) => {
          setAdminOpsNotice(`Validating ${file.name} before import...`);

          return onRunAdminCommand({
            csvText,
            fileName: file.name,
            kind: "import-patients-csv",
            mode: "create-only",
            onboardingInitialFlowComplete: false,
            siteId: activeWorkforceSessionSiteId ?? runtimeConfig.requestSiteId ?? null,
            status: "active",
          });
        })
        .then((commandResult) => {
          setAdminOpsNotice(formatAdminPatientImportNotice(commandResult));
        })
        .catch((error) => {
          setAdminOpsNotice(
            error instanceof Error
              ? error.message
              : `Patient import for ${file.name} could not be completed.`,
          );
        });
    };
    fileInput.click();
  };
  const runUpdatePatientCommand = (row: AdminPreviewPatientRow) => {
    setAdminCrudForm({
      addressCity: row.addressCity ?? "",
      addressState: row.addressState ?? "",
      addressStreet: row.addressStreet ?? "",
      addressZip: row.addressZip ?? "",
      alternateEmergencyContactPhone:
        row.alternateEmergencyContactPhone ?? "",
      contactWindowText: row.contactWindowText ?? "",
      dateOfBirth: row.dateOfBirth ?? "",
      displayName: row.displayName,
      emailAddress: row.emailAddress ?? "",
      emergencyContactPhone: row.emergencyContactPhone ?? "",
      entityKind: "patient",
      expectedLifecycleVersion: Math.max(1, row.lifecycleVersion),
      familyName: row.familyName ?? "",
      givenName: row.givenName ?? "",
      homePhoneNumber: row.homePhoneNumber ?? "",
      identifier: row.identifier ?? "",
      leadingClinicianUserId: row.leadingClinicianUserId ?? "",
      mode: "update",
      onboardingState: row.onboardingInitialFlowComplete ? "complete" : "open",
      patientId: row.patientId ?? row.subjectKey,
      phoneNumber: row.phoneNumber ?? "",
      programLabel: row.programLabel ?? "",
      secondaryPhoneNumber: row.secondaryPhoneNumber ?? "",
      sex: row.sex ?? "",
      siteId: row.siteId ?? "",
      status: resolveAdminPatientCrudStatus(row.statusLabel),
      workPhoneNumber: row.workPhoneNumber ?? "",
    });
    setAdminOpsNotice(`Editing patient ${row.displayName}.`);
  };
  const openAdminPatientLifecycleDialog = (
    row: AdminPreviewPatientRow,
    action: "archive" | "restore",
  ): void => {
    const patientId = row.patientId ?? row.subjectKey;
    const previousStatus = row.preArchiveStatus;
    const restoreStatus =
      previousStatus === "active" ||
      previousStatus === "draft" ||
      previousStatus === "inactive"
        ? previousStatus
        : "active";

    setAdminPatientLifecycleDraft({
      action,
      displayName: row.displayName,
      expectedLifecycleVersion: Math.max(1, row.lifecycleVersion),
      idempotencyKey: buildAdminPatientLifecycleIdempotencyKey(
        action,
        patientId,
      ),
      notes: "",
      patientId,
      reasonKey: action === "restore" ? "admin-restore" : "",
      restoreStatus,
    });
    setAdminOpsNotice(
      action === "archive"
        ? `Review the archive reason for ${row.displayName}.`
        : `Choose the restored state for ${row.displayName}.`,
    );
  };
  const submitAdminPatientLifecycleCommand = (): void => {
    if (!adminPatientLifecycleDraft || isAdminCommandSubmitting) {
      return;
    }

    if (!adminPatientLifecycleDraft.reasonKey.trim()) {
      setAdminOpsNotice("Choose an archive reason before continuing.");
      return;
    }

    const command =
      adminPatientLifecycleDraft.action === "archive"
        ? ({
            expectedLifecycleVersion:
              adminPatientLifecycleDraft.expectedLifecycleVersion,
            idempotencyKey: adminPatientLifecycleDraft.idempotencyKey,
            kind: "archive-patient",
            notes: adminPatientLifecycleDraft.notes.trim() || null,
            patientId: adminPatientLifecycleDraft.patientId,
            reasonKey: adminPatientLifecycleDraft.reasonKey.trim(),
          } satisfies TenantAdminCommandInputV1)
        : ({
            expectedLifecycleVersion:
              adminPatientLifecycleDraft.expectedLifecycleVersion,
            idempotencyKey: adminPatientLifecycleDraft.idempotencyKey,
            kind: "restore-patient",
            notes: adminPatientLifecycleDraft.notes.trim() || null,
            patientId: adminPatientLifecycleDraft.patientId,
            reasonKey: adminPatientLifecycleDraft.reasonKey.trim(),
            restoreStatus: adminPatientLifecycleDraft.restoreStatus,
          } satisfies TenantAdminCommandInputV1);
    const actionLabel =
      adminPatientLifecycleDraft.action === "archive" ? "archived" : "restored";

    void runAdminOpsCommand(command, {
      failureText: `Patient ${adminPatientLifecycleDraft.displayName} could not be ${actionLabel}.`,
      pendingText: `${
        adminPatientLifecycleDraft.action === "archive"
          ? "Archiving"
          : "Restoring"
      } patient ${adminPatientLifecycleDraft.displayName}...`,
      successText: `Patient ${actionLabel} and admin records refreshed.`,
    }).then((didSave) => {
      if (!didSave) {
        return;
      }

      setAdminPatientLifecycleDraft(null);
      setAdminSelectedPatientRowId(null);
      setAdminPatientPageIndex(0);
    });
  };
  const retryAdminPatientAccessSync = (
    row: AdminPreviewPatientRow,
  ): void => {
    const patientId = row.patientId ?? row.subjectKey;

    void runAdminOpsCommand(
      {
        expectedLifecycleVersion: Math.max(1, row.lifecycleVersion),
        kind: "reconcile-patient-access",
        patientId,
      },
      {
        failureText: `Access synchronization for ${row.displayName} could not be retried.`,
        pendingText: `Retrying access synchronization for ${row.displayName}...`,
        successText:
          "Patient access synchronized and admin records refreshed.",
      },
    );
  };
  const runCreateClinicianCommand = () => {
    setAdminCrudForm({
      clinicianUserId: "",
      departmentCode: "",
      displayName: "",
      emailAddress: "",
      entityKind: "clinician",
      isProtectedOwner: false,
      mode: "create",
      phoneNumber: "",
      protectedOwnerLabel: null,
      previewProfileKey: "",
      teamKey: "",
      testLoginEmail: "",
      testLoginPassword: "",
      status: "active",
    });
    setAdminOpsNotice("Fill the clinician profile and sign-in fields, then save.");
  };
  const runUpdateClinicianCommand = (row: AdminOperationalSubjectRow) => {
    setAdminCrudForm({
      clinicianUserId: row.clinicianUserId,
      departmentCode: row.departmentCode ?? "",
      displayName: row.displayName,
      emailAddress: row.emailAddress ?? "",
      entityKind: "clinician",
      isProtectedOwner: row.isProtectedOwner,
      mode: "update",
      phoneNumber: row.phoneNumber ?? "",
      protectedOwnerLabel: row.protectedOwnerLabel,
      previewProfileKey: "",
      teamKey: row.teamKey ?? "",
      testLoginEmail: row.emailAddress ?? "",
      testLoginPassword: "",
      status: resolveAdminClinicianCrudStatus(row),
    });
    setAdminOpsNotice(`Editing clinician ${row.displayName}.`);
  };
  const runCreateDepartmentCommand = () => {
    setAdminCrudForm({
      departmentCode: "",
      displayName: "",
      entityKind: "department",
      mode: "create",
      siteCode: "",
      status: "active",
    });
    setAdminOpsNotice("Fill the department form and save.");
  };
  const runCreateTeamCommand = () => {
    setAdminCrudForm({
      departmentCode: "",
      displayName: "",
      entityKind: "team",
      mode: "create",
      status: "active",
      teamKey: "",
    });
    setAdminOpsNotice("Fill the team form and save.");
  };
  const runCreateConnectorCommand = () => {
    setAdminCrudForm({
      connectorFamily: "mssql_direct",
      connectorId: "",
      departmentCode: "",
      displayName: "",
      entityKind: "connector",
      metadataSummary: "admin-created",
      mode: "create",
      researchPackSlug: "mssql-direct",
      siteCode: "",
      sourceAuthorityMode: "incremental",
      sourceSystemKey: "",
      status: "active",
      teamKey: "",
    });
    setAdminOpsNotice(
      "Fill the connector form and save to register a real connector row.",
    );
  };
  const runUpdateConnectorCommand = (
    row: TenantAdminSummaryConnectorRegistrationV1,
  ) => {
    const connectorFamily = adminConnectorFamilyOptions.includes(
      row.connectorFamily as AdminConnectorFamily,
    )
      ? (row.connectorFamily as AdminConnectorFamily)
      : "custom";
    const connectorStatus = adminConnectorStatusOptions.includes(
      row.status as AdminConnectorCrudStatus,
    )
      ? (row.status as AdminConnectorCrudStatus)
      : "active";
    const sourceAuthorityMode = adminConnectorSourceAuthorityOptions.includes(
      row.sourceAuthorityMode as AdminConnectorSourceAuthority,
    )
      ? (row.sourceAuthorityMode as AdminConnectorSourceAuthority)
      : "incremental";

    setAdminCrudForm({
      connectorFamily,
      connectorId: row.connectorId,
      departmentCode: row.departmentCode ?? "",
      displayName: row.displayName,
      entityKind: "connector",
      metadataSummary: row.metadataSummary ?? "",
      mode: "update",
      researchPackSlug: connectorFamily.replaceAll("_", "-"),
      siteCode: row.siteCode ?? "",
      sourceAuthorityMode,
      sourceSystemKey: row.sourceSystemKey,
      status: connectorStatus,
      teamKey: row.teamKey ?? "",
    });
    setAdminOpsNotice(`Editing connector ${row.displayName}.`);
  };
  const runRetireConnectorCommand = (
    row: TenantAdminSummaryConnectorRegistrationV1,
  ) => {
    setAdminCrudForm({
      displayName: row.displayName,
      entityKind: "connector",
      mode: "retire",
      targetKey: row.connectorId,
    });
    setAdminOpsNotice(`Review retire action for connector ${row.displayName}.`);
  };
  const runAdminConnectorSyncCommand = (
    row: TenantAdminSummaryConnectorRegistrationV1,
  ) => {
    void runAdminOpsCommand(
      {
        connectorId: row.connectorId,
        kind: "connector-sync-now",
      },
      {
        failureText: "Integration sync could not be queued.",
        pendingText: `Queueing sync for ${row.displayName}...`,
        successText: "Connector sync request queued and summary refreshed.",
      },
    );
  };
  const runAdminConnectorAuthReviewCommand = (
    row: TenantAdminSummaryConnectorRegistrationV1,
    authStatus: "reviewed" | "needs_attention" | "expired" | "blocked",
    status: "draft" | "active" | "paused" | "retired" | null,
    labelText: string,
  ) => {
    void runAdminOpsCommand(
      {
        authStatus,
        connectorId: row.connectorId,
        kind: "connector-auth-review",
        notes: `${labelText} from the admin connector workspace.`,
        status,
      },
      {
        failureText: `${labelText} could not be saved for ${row.displayName}.`,
        pendingText: `${labelText} for ${row.displayName}...`,
        successText: `${labelText} saved and admin summary refreshed.`,
      },
    );
  };
  const openAdminReviewQueueItems = (row: AdminReviewQueueRow) => {
    setAdminReviewQueueDetailFilter("all");

    if (row.count < 1) {
      setAdminSelectedReviewQueueId(row.id);
      setAdminOpsNotice(`${row.queueLabel} has no live items right now.`);
      return;
    }

    if (adminReviewQueueItemsLoadingId === row.id) {
      return;
    }

    setAdminSelectedReviewQueueId(row.id);
    setAdminReviewQueueItemsLoadingId(row.id);
    setAdminReviewQueueItemsErrorById((current) => {
      const next = { ...current };
      delete next[row.id];
      return next;
    });
    setAdminOpsNotice(`Loading ${row.queueLabel.toLowerCase()}...`);

    void onLoadAdminReviewQueueItems({
      limit: 25,
      offset: 0,
      queueId: row.id as TenantAdminReviewQueueId,
    }).then((response) => {
      setAdminReviewQueueItemsLoadingId((current) =>
        current === row.id ? null : current,
      );

      if (!response) {
        setAdminReviewQueueItemsErrorById((current) => ({
          ...current,
          [row.id]: `${row.queueLabel} could not be loaded right now.`,
        }));
        setAdminOpsNotice(
          `${row.queueLabel} could not be loaded right now.`,
        );
        return;
      }

      setAdminReviewQueueItemsById((current) => ({
        ...current,
        [row.id]: response,
      }));
      setAdminOpsNotice(
        `${row.queueLabel} loaded with ${response.page.totalCount} live item${response.page.totalCount === 1 ? "" : "s"}.`,
      );
    });
  };
  const runAdminPatientPortalControl = (
    row: AdminOperationalSubjectRow,
    command: Omit<
      Extract<TenantAdminCommandInputV1, { kind: "patient-portal-control" }>,
      "kind" | "patientId"
    >,
    labelText: string,
  ) => {
    if (!row.patientId) {
      setAdminOpsNotice(`${row.displayName} is missing a patient id.`);
      return;
    }

    void runAdminOpsCommand(
      {
        ...command,
        kind: "patient-portal-control",
        patientId: row.patientId,
      },
      {
        failureText: `${labelText} could not be saved for ${row.displayName}.`,
        pendingText: `${labelText} for ${row.displayName}...`,
        successText: `${labelText} saved and admin summary refreshed.`,
      },
    );
  };
  const runAdminSystemFlagTransition = (
    flagKey: string,
    nextState: "enabled" | "disabled" | "unset",
  ) => {
    const rolloutNote =
      nextState === "unset"
        ? `Admin unset ${flagKey} from the system settings surface.`
        : `Admin ${nextState} ${flagKey} from the system settings surface.`;
    const command =
      nextState === "unset"
        ? ({
            flagKey,
            kind: "unset-system-flag",
            rolloutNote,
          } satisfies TenantAdminCommandInputV1)
        : ({
            enabled: nextState === "enabled",
            flagKey,
            kind: "set-system-flag",
            rolloutNote,
          } satisfies TenantAdminCommandInputV1);

    void runAdminOpsCommand(command, {
      failureText: `System setting ${flagKey} could not be updated.`,
      pendingText: `${humanizeWorkspaceKey(nextState)} ${flagKey}...`,
      successText: `System setting ${flagKey} saved and admin summary refreshed.`,
    });
  };
  const runAdminFeatureFlagTransition = (
    flag: TenantAdminSummaryFeatureFlagV1,
    nextState: "enabled" | "disabled" | "unset",
  ) => {
    if (flag.isSystemFlag) {
      runAdminSystemFlagTransition(flag.flagKey, nextState);
      return;
    }

    const rolloutNote =
      nextState === "unset"
        ? `Admin unset ${flag.flagKey} from the flags table.`
        : `Admin ${nextState} ${flag.flagKey} from the flags table.`;
    const command =
      nextState === "unset"
        ? ({
            flagKey: flag.flagKey,
            flagVariant: flag.flagVariant,
            kind: "unset-feature-flag",
            rolloutNote,
            scope: flag.scope,
            scopeKey: flag.scopeKey,
          } satisfies TenantAdminCommandInputV1)
        : ({
            enabled: nextState === "enabled",
            flagKey: flag.flagKey,
            flagVariant: flag.flagVariant,
            kind: "set-feature-flag",
            rolloutNote,
            scope: flag.scope,
            scopeKey: flag.scopeKey,
          } satisfies TenantAdminCommandInputV1);

    void runAdminOpsCommand(command, {
      failureText: `Flag ${flag.flagKey} could not be updated.`,
      pendingText: `${humanizeWorkspaceKey(nextState)} ${flag.flagKey}...`,
      successText: `Flag ${flag.flagKey} saved and admin summary refreshed.`,
    });
  };
  const runAdminAiModelContextFlagTransition = (
    flag: TenantAdminAiModelContextFlagV1,
    modelKey: TenantAdminAiModelKeyV1,
    enabled: boolean,
  ) => {
    if (
      modelKey === "chat" &&
      flag.flagKey === "patientDocumentation" &&
      !enabled
    ) {
      setAdminOpsNotice("Patient documentation must remain enabled for Chat.");
      return;
    }

    void runAdminOpsCommand(
      {
        enabled,
        flagKey: flag.flagKey,
        kind: "set-ai-model-context-flag",
        modelKey,
      },
      {
        failureText: `${flag.labelText} could not be updated for ${modelKey}.`,
        pendingText: `${enabled ? "Enabling" : "Disabling"} ${flag.labelText.toLowerCase()} for ${modelKey}...`,
        successText: `${flag.labelText} saved for ${modelKey} and admin summary refreshed.`,
      },
    );
  };
  const runAdminAiModelPromptArtifactEditor = (
    modelKey: AdminAiModelEditorModelKey,
    artifact: TenantAdminAiModelPromptArtifactV1 | null = null,
  ) => {
    setAdminCrudForm(null);
    setAdminFeatureFlagDraft(null);
    setAdminAiModelArtifactEditor({
      kind: "prompt",
      labelText:
        artifact?.labelText ??
        `${humanizeWorkspaceKey(modelKey)} response guidance`,
      mode: artifact == null ? "create" : "edit",
      modelKey: artifact?.modelKey ?? modelKey,
      promptId: artifact?.promptId ?? null,
      promptText:
        artifact?.promptText ??
        "Use this approved care-team guidance when drafting patient-facing answers.",
      purposeText:
        artifact?.purposeText ?? "Approved care-team response guidance.",
    });
  };
  const archiveAdminAiModelPromptArtifact = (
    artifact: TenantAdminAiModelPromptArtifactV1,
  ) => {
    void runAdminOpsCommand(
      {
        kind: "archive-ai-model-prompt-artifact",
        promptId: artifact.promptId,
      },
      {
        failureText: "AI response guidance could not be archived.",
        pendingText: "Archiving AI response guidance...",
        successText: "AI response guidance archived and admin summary refreshed.",
      },
    );
  };
  const runAdminAiModelSourceDocumentEditor = (
    modelKey: AdminAiModelEditorModelKey,
    document: TenantAdminAiModelSourceDocumentV1 | null = null,
  ) => {
    setAdminCrudForm(null);
    setAdminFeatureFlagDraft(null);
    setAdminAiModelArtifactEditor({
      kind: "source",
      mode: document == null ? "create" : "edit",
      modelKey: document?.modelKey ?? modelKey,
      sourceDocumentId: document?.sourceDocumentId ?? null,
      sourceText:
        document?.sourceText ??
        "Use this approved clinical knowledge source to support patient-facing answers.",
      summaryText:
        document?.summaryText ?? "Approved clinical knowledge source.",
      titleText:
        document?.titleText ?? `${humanizeWorkspaceKey(modelKey)} knowledge source`,
    });
  };
  const archiveAdminAiModelSourceDocument = (
    document: TenantAdminAiModelSourceDocumentV1,
  ) => {
    void runAdminOpsCommand(
      {
        kind: "archive-ai-model-source-document",
        sourceDocumentId: document.sourceDocumentId,
      },
      {
        failureText: "AI knowledge source could not be archived.",
        pendingText: "Archiving AI knowledge source...",
        successText: "AI knowledge source archived and admin summary refreshed.",
      },
    );
  };
  const resolveAdminAiModelProductText = (
    valueText: string | null | undefined,
    fallbackText: string,
  ): string => {
    const trimmedText = valueText?.trim() ?? "";
    const normalizedText = trimmedText || fallbackText;

    return normalizedText
      .replace(/\bprompt artifacts\b/giu, "response guidance")
      .replace(/\bprompt artifact\b/giu, "response guidance")
      .replace(/\bsource documents\b/giu, "knowledge sources")
      .replace(/\bsource document\b/giu, "knowledge source")
      .replace(/\btenant-approved\b/giu, "approved");
  };
  const resolveAdminProductCopyText = (
    valueText: string | null | undefined,
    fallbackText: string,
  ): string => {
    const trimmedText = valueText?.trim() ?? "";
    const normalizedText = trimmedText || fallbackText;
    const hasInternalDemoProbe =
      /\bcodex\b/iu.test(normalizedText) ||
      /\bclinician-workbench\b/iu.test(normalizedText) ||
      /\bdb-only\b/iu.test(normalizedText) ||
      /\bsmoke\s+\d{4}/iu.test(normalizedText) ||
      /\bsmoke\/probe\b/iu.test(normalizedText) ||
      /^\s*json\s*:/iu.test(normalizedText);

    if (hasInternalDemoProbe) {
      return fallbackText;
    }

    return normalizedText
      .replace(/\bprompt artifacts\b/giu, "response guidance")
      .replace(/\bprompt artifact\b/giu, "response guidance")
      .replace(/\bsource documents\b/giu, "knowledge sources")
      .replace(/\bsource document\b/giu, "knowledge source")
      .replace(/\btenant-approved\b/giu, "approved");
  };
  const updateAdminAiModelArtifactEditorField = (
    fieldName: string,
    value: string,
  ) => {
    setAdminAiModelArtifactEditor((current) =>
      current == null
        ? current
        : ({
            ...current,
            [fieldName]: value,
          } as AdminAiModelArtifactEditorState),
    );
  };
  const submitAdminAiModelArtifactEditor = () => {
    if (adminAiModelArtifactEditor == null || isAdminCommandSubmitting) {
      return;
    }

    if (adminAiModelArtifactEditor.kind === "prompt") {
      const labelText = adminAiModelArtifactEditor.labelText.trim();
      const promptText = adminAiModelArtifactEditor.promptText.trim();

      if (!labelText || !promptText) {
        setAdminOpsNotice("Prompt label and guidance text are required.");
        return;
      }

      void runAdminOpsCommand(
        {
          kind: "upsert-ai-model-prompt-artifact",
          modelKey: adminAiModelArtifactEditor.modelKey,
          promptId: adminAiModelArtifactEditor.promptId,
          labelText,
          purposeText: adminAiModelArtifactEditor.purposeText.trim(),
          promptText,
          status: "active",
        },
        {
          failureText: "AI response guidance could not be saved.",
          pendingText: "Saving AI response guidance...",
          successText: "AI response guidance saved and admin summary refreshed.",
        },
      ).then((didSave) => {
        if (didSave) {
          setAdminAiModelArtifactEditor(null);
        }
      });
      return;
    }

    const titleText = adminAiModelArtifactEditor.titleText.trim();
    const sourceText = adminAiModelArtifactEditor.sourceText.trim();

    if (!titleText || !sourceText) {
      setAdminOpsNotice("Source title and source text are required.");
      return;
    }

    void runAdminOpsCommand(
      {
        kind: "upsert-ai-model-source-document",
        modelKey: adminAiModelArtifactEditor.modelKey,
        sourceDocumentId: adminAiModelArtifactEditor.sourceDocumentId,
        sourceText,
        status: "active",
        summaryText: adminAiModelArtifactEditor.summaryText.trim(),
        titleText,
      },
      {
        failureText: "AI knowledge source could not be saved.",
        pendingText: "Saving AI knowledge source...",
        successText: "AI knowledge source saved and admin summary refreshed.",
      },
    ).then((didSave) => {
      if (didSave) {
        setAdminAiModelArtifactEditor(null);
      }
    });
  };
  const openCreateFeatureFlagDraft = () => {
    setAdminCrudForm(null);
    setAdminAiModelArtifactEditor(null);
    setAdminFeatureFlagDraft({
      enabledState: "enabled",
      flagKey: "",
      flagKind: "feature",
      flagVariant: "",
      rolloutNote: "Admin-created flag from the Flags workspace.",
      scope: "tenant",
      scopeKey: "",
    });
    setAdminOpsNotice("Fill the flag form and save to create a real flag row.");
  };
  const updateAdminFeatureFlagDraftField = (
    fieldName: keyof AdminFeatureFlagDraftState,
    value: string,
  ) => {
    setAdminFeatureFlagDraft((current) => {
      if (current == null) {
        return current;
      }

      const nextDraft = {
        ...current,
        [fieldName]: value,
      } as AdminFeatureFlagDraftState;

      if (fieldName === "flagKind") {
        return {
          ...nextDraft,
          flagKind: value === "system" ? "system" : "feature",
          flagVariant: value === "system" ? "" : nextDraft.flagVariant,
          scope: "tenant",
          scopeKey: "",
        };
      }

      if (fieldName === "scope") {
        return {
          ...nextDraft,
          scope:
            value === "site" ||
            value === "department" ||
            value === "program"
              ? value
              : "tenant",
          scopeKey: "",
        };
      }

      if (fieldName === "enabledState") {
        return {
          ...nextDraft,
          enabledState: value === "disabled" ? "disabled" : "enabled",
        };
      }

      return nextDraft;
    });
  };
  const submitAdminFeatureFlagDraft = () => {
    if (!adminFeatureFlagDraft || isAdminCommandSubmitting) {
      return;
    }

    const flagKey = adminFeatureFlagDraft.flagKey.trim();

    if (!flagKey) {
      setAdminOpsNotice("Flag key is required.");
      return;
    }

    if (
      adminFeatureFlagDraft.flagKind === "feature" &&
      adminFeatureFlagDraft.scope !== "tenant" &&
      !adminFeatureFlagDraft.scopeKey.trim()
    ) {
      setAdminOpsNotice("Choose a scope key or switch the flag scope to all sites.");
      return;
    }

    const rolloutNote =
      adminFeatureFlagDraft.rolloutNote.trim() ||
      `Admin ${adminFeatureFlagDraft.enabledState} ${flagKey} from the flags table.`;
    const enabled = adminFeatureFlagDraft.enabledState === "enabled";
    const command =
      adminFeatureFlagDraft.flagKind === "system"
        ? ({
            enabled,
            flagKey,
            kind: "set-system-flag",
            rolloutNote,
          } satisfies TenantAdminCommandInputV1)
        : ({
            enabled,
            flagKey,
            flagVariant: adminFeatureFlagDraft.flagVariant.trim() || null,
            kind: "set-feature-flag",
            rolloutNote,
            scope: adminFeatureFlagDraft.scope,
            scopeKey: adminFeatureFlagDraft.scopeKey.trim() || null,
          } satisfies TenantAdminCommandInputV1);

    void runAdminOpsCommand(command, {
      failureText: `Flag ${flagKey} could not be created.`,
      pendingText: `Saving flag ${flagKey}...`,
      successText: `Flag ${flagKey} saved and admin summary refreshed.`,
    }).then((didSave) => {
      if (didSave) {
        setAdminFeatureFlagDraft(null);
      }
    });
  };
  const resolveAdminFeatureFlagState = (
    flag: TenantAdminSummaryFeatureFlagV1,
  ): "enabled" | "disabled" | "unset" =>
    flag.state ?? (flag.enabled ? "enabled" : "disabled");
  const resolveAdminFlagStatePalette = (
    flagState: "enabled" | "disabled" | "unset",
  ) => ({
    backgroundColor:
      flagState === "enabled"
        ? theme.backgroundSubtle
        : flagState === "disabled"
          ? theme.surfaceMuted
          : theme.surface,
    borderColor:
      flagState === "enabled"
        ? theme.info
        : flagState === "disabled"
          ? theme.warning
          : theme.borderSubtle,
    textColor:
      flagState === "enabled"
        ? theme.info
        : flagState === "disabled"
          ? theme.warning
          : theme.textSecondary,
  });
  const rawSummaryPatientRows = buildAdminOperationalSubjectRows(
    summary,
    "patient",
  );
  const adminPatientOperationsReturnedEmptyPage =
    adminPatientOperationsPage != null &&
    adminPatientOperationsPage.items.length === 0 &&
    adminPatientOperationsPage.page.totalCount > 0 &&
    rawSummaryPatientRows.length > 0;
  const rawPatientRows =
    adminPatientOperationsPage != null && !adminPatientOperationsReturnedEmptyPage
      ? buildAdminPatientOperationalRows(adminPatientOperationsPage.items)
      : rawSummaryPatientRows;
  const adminPatientOperationsTotalCount =
    adminPatientOperationsPage?.page.totalCount ?? rawSummaryPatientRows.length;
  const rawSummaryClinicianRows = buildAdminOperationalSubjectRows(
    summary,
    "clinician",
  );
  const adminClinicianOperationsReturnedEmptyPage =
    adminClinicianOperationsPage != null &&
    adminClinicianOperationsPage.items.length === 0 &&
    adminClinicianOperationsPage.page.totalCount > 0 &&
    rawSummaryClinicianRows.length > 0;
  const rawClinicianRows =
    adminClinicianOperationsPage != null &&
    !adminClinicianOperationsReturnedEmptyPage
      ? buildAdminClinicianOperationalRows(adminClinicianOperationsPage.items)
      : rawSummaryClinicianRows;
  const adminClinicianOperationsTotalCount =
    adminClinicianOperationsPage?.page.totalCount ??
    rawSummaryClinicianRows.length;
  const rawReviewRows = buildAdminWorkflowQueueRows(summary);
  const summaryMetrics = [
    {
      id: "patients",
      label: "Patient lanes",
      value: String(adminPatientOperationsTotalCount),
    },
    {
      id: "clinicians",
      label: "Clinician coverage",
      value: String(adminClinicianOperationsTotalCount),
    },
    {
      id: "reviews",
      label: "Pending reviews",
      value: String(summary?.workflow.pendingMessageReviewCount ?? 0),
    },
    {
      id: "escalations",
      label: "Open escalations",
      value: String(summary?.workflow.activeEscalationCount ?? 0),
    },
  ];
  const sectionSummaries = [
    {
      id: "patients",
      title: "Patients",
      supportText: "Patient access, site routing, and self-service lanes.",
      leadText: "PT",
      countText: String(adminPatientOperationsTotalCount),
      tone: "info" as const,
      isSelected: true,
    },
    {
      id: "clinicians",
      title: "Clinician coverage",
      supportText: "Workbench ownership, access posture, and site coverage.",
      leadText: "CLN",
      countText: String(adminClinicianOperationsTotalCount),
      tone: "accent" as const,
    },
    {
      id: "reviews",
      title: "Review backlog",
      supportText: "Pending approvals, tasks, escalations, and audit queues.",
      leadText: "RVW",
      countText: String(summary?.workflow.pendingMessageReviewCount ?? 0),
      tone: "warning" as const,
    },
  ];
  const adminPriorityRows = sortAdminReviewQueueRows(
    rawReviewRows.filter((row) => row.count > 0),
    "priority",
  ).slice(0, 4);
  const adminCommandActions = [
    {
      id: "ops-review",
      title: "View review backlog",
      subtitle:
        "Triage pending approvals, task-review lanes, escalations, and audit queues from one starting point.",
      label: "View queues",
      onPress: () => setAdminDashboardTabId("reviews"),
      tone: "accent" as const,
    },
    {
      id: "ops-workforce",
      title: "Review workforce coverage",
      subtitle:
        "Check clinician ownership, patient access posture, and site routing from the command rail.",
      label: "Review coverage",
      onPress: () =>
        setAdminDashboardTabId("clinicians"),
      tone: "accent" as const,
    },
    {
      id: "ops-outbound-calls",
      title: "Review outbound calls",
      subtitle:
        "Check readiness blockers before enabling patient questionnaire calling.",
      label: "Outbound calls",
      onPress: () => setAdminDashboardTabId("outbound-calls"),
      tone: "accent" as const,
    },
  ];
  const normalizedAdminSearchQuery = adminSearchQuery.trim().toLowerCase();
  const selectedAdminSiteLabel =
    adminSiteFilter === "all"
      ? null
      : adminSiteFilter === "tenant-wide"
        ? "All sites"
        : humanizeWorkspaceKey(adminSiteFilter);
  const siteFilterOptions = [
    "all",
    ...Array.from(
      new Set(
        (summary?.workforceSubjects ?? [])
          .map((subject) => subject.siteId ?? "tenant-wide")
          .filter((siteId) => siteId.length > 0),
      ),
    ).sort((left, right) => left.localeCompare(right)),
  ];
  const connectorRows = sortAdminConnectorRegistrations(
    (summary?.connectors.registrations ?? []).filter((registration) => {
      if (
        adminConnectorStatusFilter !== "all" &&
        registration.status !== adminConnectorStatusFilter
      ) {
        return false;
      }

      if (normalizedAdminSearchQuery.length === 0) {
        return true;
      }

      return [
        registration.displayName,
        registration.connectorFamily,
        registration.status,
        registration.scopeLabel,
        registration.sourceSystemKey,
        registration.sourceAuthorityMode,
        registration.syncStatus ?? "",
        registration.syncInvocationStatus ?? "",
        registration.authReviewStatus ?? "",
        registration.authReviewNotes ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedAdminSearchQuery);
    }),
    adminConnectorSortKey,
  );
  const patientRows =
    adminPatientOperationsPage != null
      ? rawPatientRows.filter((row) =>
          selectedAdminSiteLabel
            ? row.siteLabel === selectedAdminSiteLabel
            : true,
        )
      : sortAdminOperationalSubjectRows(
          rawPatientRows.filter((row) => {
            if (
              selectedAdminSiteLabel &&
              row.siteLabel !== selectedAdminSiteLabel
            ) {
              return false;
            }

            if (normalizedAdminSearchQuery.length === 0) {
              return true;
            }

            return [
              row.displayName,
              row.accessLabel,
              row.siteLabel,
              row.subjectKey,
              row.supportText,
            ]
              .join(" ")
              .toLowerCase()
              .includes(normalizedAdminSearchQuery);
          }),
          adminPatientSortKey,
        );
  const clinicianRowsSource =
    adminClinicianOperationsPage != null
      ? rawClinicianRows.filter((row) =>
          selectedAdminSiteLabel
            ? row.siteLabel === selectedAdminSiteLabel
            : true,
        )
      : sortAdminOperationalSubjectRows(
          rawClinicianRows.filter((row) => {
            if (
              selectedAdminSiteLabel &&
              row.siteLabel !== selectedAdminSiteLabel
            ) {
              return false;
            }

            if (normalizedAdminSearchQuery.length === 0) {
              return true;
            }

            return [
              row.displayName,
              row.roleLabel,
              row.accessLabel,
              row.siteLabel,
              row.subjectKey,
              row.supportText,
            ]
              .join(" ")
              .toLowerCase()
              .includes(normalizedAdminSearchQuery);
          }),
          adminClinicianSortKey,
        );
  const clinicianRows = adminShowInactiveClinicians
    ? clinicianRowsSource
    : clinicianRowsSource.filter(
        (row) => resolveAdminClinicianCrudStatus(row) === "active",
      );
  const reviewRows = sortAdminReviewQueueRows(
    rawReviewRows.filter((row) => {
      if (
        adminReviewPriorityFilter !== "all" &&
        row.priorityLabel.toLowerCase() !== adminReviewPriorityFilter
      ) {
        return false;
      }

      if (normalizedAdminSearchQuery.length === 0) {
        return true;
      }

      return [
        row.queueLabel,
        row.ownerLabel,
        row.sourceArea,
        row.supportingText,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedAdminSearchQuery);
    }),
    adminReviewSortKey,
  );
  const featureFlagSourceRows = summary?.featureFlags ?? [];
  const featureFlagRows = sortAdminFeatureFlagRows(
    featureFlagSourceRows.filter((flag) => {
      const flagState = resolveAdminFeatureFlagState(flag);

      if (
        (adminFeatureFlagStatusFilter === "enabled" &&
          flagState !== "enabled") ||
        (adminFeatureFlagStatusFilter === "disabled" &&
          flagState !== "disabled") ||
        (adminFeatureFlagStatusFilter === "unset" && flagState !== "unset") ||
        (adminFeatureFlagStatusFilter === "system" && !flag.isSystemFlag) ||
        (adminFeatureFlagStatusFilter === "feature" && flag.isSystemFlag)
      ) {
        return false;
      }

      if (normalizedAdminSearchQuery.length === 0) {
        return true;
      }

      return [
        flag.flagKey,
        flag.flagVariant ?? "",
        flag.scopeLabel,
        flag.scopeKey ?? "",
        flag.rolloutNote ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedAdminSearchQuery);
    }),
    adminFeatureFlagSortKey,
  );
  const allProvisioningRuns = summary?.provisioning.recentRuns ?? [];
  const provisioningRuns = allProvisioningRuns.filter(
    (run) => {
      if (
        adminProvisioningViewFilter === "failed" &&
        run.runStatus !== "failed"
      ) {
        return false;
      }

      if (normalizedAdminSearchQuery.length === 0) {
        return true;
      }

      return [
        run.runKind,
        run.runStatus,
        run.environmentKey,
        run.targetLabel,
        run.databaseName ?? "",
        run.clusterIdentifier ?? "",
        run.requestedBy ?? "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedAdminSearchQuery);
    },
  );
  const databaseAllocationRows = (
    summary?.provisioning.allocations ?? []
  ).filter((allocation) => {
    if (
      adminProvisioningViewFilter === "failed" &&
      allocation.allocationStatus !== "failed"
    ) {
      return false;
    }

    if (normalizedAdminSearchQuery.length === 0) {
      return true;
    }

    return [
      allocation.databaseName,
      allocation.allocationStatus,
      allocation.isolationModel,
      allocation.migrationBundleKey,
      allocation.lastMigrationVersion ?? "",
      allocation.clusterIdentifier,
      allocation.clusterStatus,
      allocation.awsRegion,
    ]
      .join(" ")
      .toLowerCase()
      .includes(normalizedAdminSearchQuery);
  });
  const databaseClusterRows = (summary?.provisioning.clusters ?? []).filter(
    (cluster) => {
      if (
        adminProvisioningViewFilter === "failed" &&
        cluster.clusterStatus !== "failed"
      ) {
        return false;
      }

      if (normalizedAdminSearchQuery.length === 0) {
        return true;
      }

      return [
        cluster.clusterIdentifier,
        cluster.environmentKey,
        cluster.awsRegion,
        cluster.clusterRole,
        cluster.engineFamily,
        cluster.engineVersion,
        cluster.clusterStatus,
      ]
        .join(" ")
        .toLowerCase()
        .includes(normalizedAdminSearchQuery);
    },
  );
  const connectorPageRows = paginateRows(
    connectorRows,
    adminConnectorPageIndex,
    5,
  );
  const selectedAdminConnector =
    adminSelectedConnectorId == null
      ? null
      : (connectorRows.find(
          (row) => row.connectorId === adminSelectedConnectorId,
        ) ?? null);
  const patientPageRows =
    adminPatientOperationsPage != null
      ? patientRows
      : paginateRows(patientRows, adminPatientPageIndex, 5);
  const clinicianPageRows =
    adminClinicianOperationsPage != null
      ? clinicianRows
      : paginateRows(clinicianRows, adminClinicianPageIndex, 5);
  const reviewPageRows = paginateRows(reviewRows, adminReviewPageIndex, 5);
  const selectedAdminReviewQueue =
    adminSelectedReviewQueueId == null
      ? null
      : (reviewRows.find((row) => row.id === adminSelectedReviewQueueId) ??
        null);
  const selectedAdminReviewQueueItems =
    selectedAdminReviewQueue == null
      ? null
      : adminReviewQueueItemsById[selectedAdminReviewQueue.id] ?? null;
  const selectedAdminReviewQueueItemsError =
    selectedAdminReviewQueue == null
      ? null
      : adminReviewQueueItemsErrorById[selectedAdminReviewQueue.id] ?? null;
  const isSelectedAdminReviewQueueLoading =
    selectedAdminReviewQueue != null &&
    adminReviewQueueItemsLoadingId === selectedAdminReviewQueue.id;
  const featureFlagPageRows = paginateRows(
    featureFlagRows,
    adminFeatureFlagPageIndex,
    8,
  );
  const provisioningRunPageRows = paginateRows(
    provisioningRuns,
    adminProvisioningPageIndex,
    8,
  );
  const selectedAdminProvisioningRun =
    adminSelectedProvisioningRunId == null
      ? null
      : (provisioningRuns.find(
          (run) => run.runId === adminSelectedProvisioningRunId,
        ) ?? null);
  const activeConnectorCount = connectorRows.filter(
    (row) => row.status === "active",
  ).length;
  const inactiveConnectorCount = connectorRows.length - activeConnectorCount;
  const connectorScopeCount = new Set(
    connectorRows.map((row) => row.scopeLabel),
  ).size;
  const latestConnectorSyncAt = (
    summary?.connectors.registrations ?? []
  ).reduce<string | null>(
    (latest, row) =>
      resolveAdminTimestampSortValue(row.lastSyncAt) >
      resolveAdminTimestampSortValue(latest)
        ? (row.lastSyncAt ?? null)
        : latest,
    null,
  );
  const latestConnectorSyncText =
    latestConnectorSyncAt != null
      ? formatAdminSummaryTimestamp(latestConnectorSyncAt)
      : "No sync available";
  const patientSiteCount = new Set(patientRows.map((row) => row.siteLabel))
    .size;
  const patientLinkedCount = patientRows.filter(
    (row) => row.patientId != null,
  ).length;
  const patientAttentionCount = patientRows.filter(
    (row) => row.requiresAttention,
  ).length;
  const patientPendingDocumentCount = patientRows.reduce(
    (sum, row) => sum + row.pendingDocumentCount,
    0,
  );
  const patientUnreadCount = patientRows.reduce(
    (sum, row) => sum + row.unreadMessageCount,
    0,
  );
  const patientAccessModeCount = new Set(
    patientRows.map((row) => row.accessLabel),
  ).size;
  const patientSummaryPendingCount = patientRows.filter(
    (row) => row.summaryRefreshStatus == null,
  ).length;
  const clinicianSiteCount = new Set(clinicianRows.map((row) => row.siteLabel))
    .size;
  const clinicianRoleCount = new Set(clinicianRows.map((row) => row.roleLabel))
    .size;
  const clinicianTenantWideCount = clinicianRows.filter(
    (row) => row.siteLabel === "Tenant-wide",
  ).length;
  const latestReviewSignalAt = rawReviewRows.reduce<string | null>(
    (latest, row) =>
      resolveAdminTimestampSortValue(row.latestSignalAt) >
      resolveAdminTimestampSortValue(latest)
        ? row.latestSignalAt
        : latest,
    null,
  );
  const reviewLiveQueueCount = reviewRows.filter((row) => row.count > 0).length;
  const highPriorityReviewCount = reviewRows
    .filter((row) => row.priorityLabel === "High")
    .reduce((sum, row) => sum + row.count, 0);
  const reviewOwnerCount = new Set(reviewRows.map((row) => row.ownerLabel))
    .size;
  const latestReviewSignalText =
    latestReviewSignalAt != null
      ? formatAdminSummaryTimestamp(latestReviewSignalAt)
      : "No review signal available";
  const enabledFeatureFlagCount = featureFlagRows.filter(
    (flag) => resolveAdminFeatureFlagState(flag) === "enabled",
  ).length;
  const disabledFeatureFlagCount = featureFlagRows.filter(
    (flag) => resolveAdminFeatureFlagState(flag) === "disabled",
  ).length;
  const unsetFeatureFlagCount = featureFlagRows.filter(
    (flag) => resolveAdminFeatureFlagState(flag) === "unset",
  ).length;
  const systemFeatureFlagCount = featureFlagRows.filter(
    (flag) => flag.isSystemFlag,
  ).length;
  const configuredFeatureFlagRows = featureFlagRows.filter(
    (flag) => resolveAdminFeatureFlagState(flag) !== "unset",
  );
  const latestFeatureFlagText =
    configuredFeatureFlagRows.length > 0
      ? formatAdminSummaryTimestamp(
          configuredFeatureFlagRows[0]?.updatedAt ?? null,
        )
      : "No flags saved";
  const resolveAdminSystemFlagState = (
    flagKey: string,
  ): "enabled" | "disabled" | "unset" => {
    const flag = featureFlagSourceRows.find(
      (candidate) => candidate.isSystemFlag && candidate.flagKey === flagKey,
    );

    return flag ? resolveAdminFeatureFlagState(flag) : "unset";
  };
  const smsDispatchSystemFlagState = resolveAdminSystemFlagState(
    smsDispatchSystemFlagKey,
  );
  const adminFlagStateSummaryRows = [
    {
      labelText: "Admin actions",
      state: resolveAdminSystemFlagState("admin.ops.commands.enabled"),
    },
    {
      labelText: "SMS dispatch",
      state: smsDispatchSystemFlagState,
    },
  ];
  const aiModelParameters = summary?.aiModelParameters ?? null;
  const aiModelContextFlags = aiModelParameters?.contextFlags ?? [];
  const aiModelPromptArtifacts = aiModelParameters?.promptArtifacts ?? [];
  const aiModelSourceDocuments = aiModelParameters?.sourceDocuments ?? [];
  const aiModelChatEnabledCount = aiModelContextFlags.filter(
    (flag) => flag.chatEnabled,
  ).length;
  const aiModelVoiceEnabledCount = aiModelContextFlags.filter(
    (flag) => flag.voiceEnabled,
  ).length;
  const activeAiModelPromptArtifactCount = aiModelPromptArtifacts.filter(
    (artifact) => artifact.status === "active",
  ).length;
  const activeAiModelSourceDocumentCount = aiModelSourceDocuments.filter(
    (document) => document.status === "active",
  ).length;
  const aiModelLatestChangeText =
    aiModelParameters?.updatedAt != null
      ? formatAdminSummaryTimestamp(aiModelParameters.updatedAt)
      : "Defaults active";
  const failedProvisioningRunCount = provisioningRuns.filter(
    (run) => run.runStatus === "failed",
  ).length;
  const failedDatabaseAllocationCount = databaseAllocationRows.filter(
    (allocation) => allocation.allocationStatus === "failed",
  ).length;
  const failedDatabaseClusterCount = databaseClusterRows.filter(
    (cluster) => cluster.clusterStatus === "failed",
  ).length;
  const adminPatientOperationsPageSize = 10;
  const adminPatientOperationsSort =
    resolveAdminPatientOperationsSort(adminPatientSortKey);
  const adminClinicianOperationsPageSize = 10;
  const adminClinicianOperationsSort = resolveAdminClinicianOperationsSort(
    adminClinicianSortKey,
  );
  const adminPatientOperationsRequest = {
    careTypeFilter: adminPatientCareTypeFilter,
    lifecycleFilter:
      adminPatientQuickFilter === "archived"
        ? ("archived" as const)
        : ("current" as const),
    limit: adminPatientOperationsPageSize,
    offset: adminPatientPageIndex * adminPatientOperationsPageSize,
    searchQuery: deferredAdminSearchQuery,
    sortDirection: adminPatientOperationsSort.sortDirection,
    sortKey: adminPatientOperationsSort.sortKey,
  };
  const adminClinicianOperationsRequest = {
    includeInactive: adminShowInactiveClinicians,
    limit: adminClinicianOperationsPageSize,
    offset: adminClinicianPageIndex * adminClinicianOperationsPageSize,
    searchQuery: deferredAdminSearchQuery,
    sortDirection: adminClinicianOperationsSort.sortDirection,
    sortKey: adminClinicianOperationsSort.sortKey,
  };
  const adminPatientOperationsRefreshKey =
    buildTenantAdminPatientOperationsRefreshKey({
      requestKey: JSON.stringify(adminPatientOperationsRequest),
      tenantId: runtimeConfig.requestTenantId,
    });
  const adminClinicianOperationsRefreshKey =
    buildTenantAdminClinicianOperationsRefreshKey({
      requestKey: JSON.stringify(adminClinicianOperationsRequest),
      tenantId: runtimeConfig.requestTenantId,
    });
  const adminOutboundRefreshKey = buildTenantAdminOutboundRefreshKey({
    requestKey: "outbound-bundle:all",
    tenantId: runtimeConfig.requestTenantId,
  });
  const adminOutboundQuestionnaireDefinitionsRefreshKey =
    buildTenantAdminOutboundRefreshKey({
      requestKey: "questionnaire-definitions:all",
      tenantId: runtimeConfig.requestTenantId,
    });
  const adminSelectedReviewQueueRefreshKey =
    buildTenantAdminReviewQueueRefreshKey({
      queueId: adminSelectedReviewQueueId ?? "none",
      requestKey: JSON.stringify({
        limit: 25,
        offset: 0,
        queueId: adminSelectedReviewQueueId,
      }),
      tenantId: runtimeConfig.requestTenantId,
    });
  const adminWorkspaceMutationRefreshAliases =
    buildAdminWorkspaceMutationRefreshAliases({
      clinicianUserId: runtimeConfig.requestClinicianUserId,
      tenantId: runtimeConfig.requestTenantId,
    });
  const adminOutboundPollMs = hasLiveAdminOutboundCall(
    adminOutboundCallsPage,
    adminOutboundTargetsPage,
  )
    ? workspaceRefreshCadenceMs.liveCall
    : workspaceRefreshCadenceMs.standard;
  const handleAdminSessionError = (error: unknown): boolean => {
    if (!isAdminSessionUnauthorizedError(error)) {
      return false;
    }

    setAdminOpsNotice("Your session expired. Sign in again.");
    setIsAdminPatientOperationsLoading(false);
    setIsAdminClinicianOperationsLoading(false);
    setIsAdminOutboundCallsLoading(false);
    setIsAdminOutboundCommandSubmitting(false);
    setAdminReviewQueueItemsLoadingId(null);
    onSessionExpired();

    return true;
  };

  useWorkspaceRefreshScope<TenantAdminPatientOperationsResponseV1>({
    key: adminPatientOperationsRefreshKey,
    aliases: [
      buildTenantAdminPatientOperationsRefreshAlias(runtimeConfig.requestTenantId),
    ],
    enabled: hasAdminSummary && adminDashboardTabId === "patients",
    loader: (signal) =>
      loadTenantAdminPatientOperations(
        runtimeConfig,
        adminPatientOperationsRequest,
        signal,
      ),
    apply: (page) => {
      startTransition(() => {
        setAdminPatientOperationsPage(page);
        setHasAdminPatientOperationsError(false);
        setIsAdminPatientOperationsLoading(false);
      });
    },
    onError: (error) => {
      if (handleAdminSessionError(error)) {
        return;
      }

      if (adminPatientOperationsPage != null) {
        return;
      }

      startTransition(() => {
        setHasAdminPatientOperationsError(true);
        setIsAdminPatientOperationsLoading(false);
      });
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: (payload) => payload.generatedAt,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  useWorkspaceRefreshScope<TenantAdminClinicianOperationsResponseV1>({
    key: adminClinicianOperationsRefreshKey,
    aliases: [
      buildTenantAdminClinicianOperationsRefreshAlias(
        runtimeConfig.requestTenantId,
      ),
    ],
    enabled: hasAdminSummary && adminDashboardTabId === "clinicians",
    loader: (signal) =>
      loadTenantAdminClinicianOperations(
        runtimeConfig,
        adminClinicianOperationsRequest,
        signal,
      ),
    apply: (page) => {
      startTransition(() => {
        setAdminClinicianOperationsPage(page);
        setHasAdminClinicianOperationsError(false);
        setIsAdminClinicianOperationsLoading(false);
      });
    },
    onError: (error) => {
      if (handleAdminSessionError(error)) {
        return;
      }

      if (adminClinicianOperationsPage != null) {
        return;
      }

      startTransition(() => {
        setHasAdminClinicianOperationsError(true);
        setIsAdminClinicianOperationsLoading(false);
      });
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: (payload) => payload.generatedAt,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  useWorkspaceRefreshScope<AdminOutboundCallsRefreshPayload>({
    key: adminOutboundRefreshKey,
    aliases: [
      buildTenantAdminOutboundRefreshAlias(runtimeConfig.requestTenantId),
    ],
    enabled:
      hasAdminSummary &&
      adminDashboardTabId === "outbound-calls" &&
      !isAdminOutboundCommandSubmitting,
    loader: async (signal) => {
      const [outboundSummary, targetsPage] = await Promise.all([
        loadTenantAdminOutboundCallsSummary(runtimeConfig, {}, signal),
        loadTenantAdminOutboundCallsTargets(
          runtimeConfig,
          {
            limit: 100,
            offset: 0,
            status: "all",
          },
          signal,
        ),
      ]);

      return {
        summary: outboundSummary,
        targetsPage,
      };
    },
    apply: (payload) => {
      startTransition(() => {
        setAdminOutboundCallsPage(payload.summary);
        setAdminOutboundTargetsPage(payload.targetsPage);
        setHasAdminOutboundCallsError(false);
        setIsAdminOutboundCallsLoading(false);
      });
    },
    onError: (error) => {
      if (handleAdminSessionError(error)) {
        return;
      }

      if (adminOutboundCallsPage != null) {
        return;
      }

      startTransition(() => {
        setHasAdminOutboundCallsError(true);
        setIsAdminOutboundCallsLoading(false);
      });
    },
    readGeneratedAt: (payload) => payload.summary.generatedAt,
    readRevision: (payload) =>
      [
        payload.summary.generatedAt,
        payload.targetsPage.generatedAt,
      ].join(":"),
    minStaleMs: 5_000,
    activePollMs: adminOutboundPollMs,
  });

  useWorkspaceRefreshScope<TenantAdminOutboundCallsQuestionnairesResponseV1>({
    key: adminOutboundQuestionnaireDefinitionsRefreshKey,
    aliases: [
      buildTenantAdminOutboundRefreshAlias(runtimeConfig.requestTenantId),
    ],
    enabled:
      hasAdminSummary &&
      adminDashboardTabId === "outbound-calls" &&
      !isAdminOutboundCommandSubmitting,
    loader: (signal) =>
      loadTenantAdminOutboundCallsQuestionnaires(
        runtimeConfig,
        {
          includeQuestions: true,
          limit: 100,
          offset: 0,
          status: "all",
        },
        signal,
      ),
    apply: (page) => {
      setAdminOutboundQuestionnairesPage(page.items);
    },
    onError: (error) => {
      if (handleAdminSessionError(error)) {
        return;
      }

      if (adminOutboundQuestionnairesPage == null) {
        setHasAdminOutboundCallsError(true);
      }
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: (payload) => payload.generatedAt,
    minStaleMs: workspaceRefreshCadenceMs.standard,
    activePollMs: workspaceRefreshCadenceMs.staticRecovery,
    refreshOnRegister: true,
  });

  useWorkspaceRefreshScope<TenantAdminReviewQueueItemsResponseV1>({
    key: adminSelectedReviewQueueRefreshKey,
    aliases: [
      buildTenantAdminReviewQueueRefreshAlias(runtimeConfig.requestTenantId),
    ],
    enabled:
      hasAdminSummary &&
      adminDashboardTabId === "reviews" &&
      adminSelectedReviewQueueId != null,
    loader: (signal) =>
      loadTenantAdminReviewQueueItems(
        runtimeConfig,
        {
          limit: 25,
          offset: 0,
          queueId: adminSelectedReviewQueueId as TenantAdminReviewQueueId,
        },
        signal,
      ),
    apply: (response) => {
      setAdminReviewQueueItemsById((current) => ({
        ...current,
        [response.queueId]: response,
      }));
      setAdminReviewQueueItemsLoadingId((current) =>
        current === response.queueId ? null : current,
      );
      setAdminReviewQueueItemsErrorById((current) => {
        const next = { ...current };
        delete next[response.queueId];
        return next;
      });
    },
    onError: (error) => {
      if (handleAdminSessionError(error)) {
        return;
      }

      if (adminSelectedReviewQueueId == null) {
        return;
      }

      setAdminReviewQueueItemsErrorById((current) => ({
        ...current,
        [adminSelectedReviewQueueId]: "Review queue items could not refresh.",
      }));
      setAdminReviewQueueItemsLoadingId((current) =>
        current === adminSelectedReviewQueueId ? null : current,
      );
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: (payload) => payload.generatedAt,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  useEffect(() => {
    if (!hasAdminSummary) {
      setAdminPatientOperationsPage(null);
      setHasAdminPatientOperationsError(false);
      setIsAdminPatientOperationsLoading(false);
      return;
    }

    if (adminDashboardTabId !== "patients") {
      return;
    }

    const abortController = new AbortController();

    setIsAdminPatientOperationsLoading(true);
    setHasAdminPatientOperationsError(false);

    void loadTenantAdminPatientOperations(
      runtimeConfig,
      adminPatientOperationsRequest,
      abortController.signal,
    )
      .then((page) => {
        if (abortController.signal.aborted) {
          return;
        }

        startTransition(() => {
          setAdminPatientOperationsPage(page);
          setIsAdminPatientOperationsLoading(false);
        });
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (handleAdminSessionError(error)) {
          return;
        }

        startTransition(() => {
          setHasAdminPatientOperationsError(true);
          setIsAdminPatientOperationsLoading(false);
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    adminDashboardTabId,
    adminPatientCareTypeFilter,
    adminPatientOperationsSort.sortDirection,
    adminPatientOperationsSort.sortKey,
    adminPatientPageIndex,
    adminPatientQuickFilter,
    deferredAdminSearchQuery,
    runtimeConfig.requestTenantId,
    runtimeConfig.sessionApiBaseUrl,
    runtimeConfig.workforceSessionSubjectKey,
    hasAdminSummary,
  ]);

  useEffect(() => {
    if (!hasAdminSummary) {
      setAdminClinicianOperationsPage(null);
      setHasAdminClinicianOperationsError(false);
      setIsAdminClinicianOperationsLoading(false);
      return;
    }

    if (adminDashboardTabId !== "clinicians") {
      return;
    }

    const abortController = new AbortController();

    setIsAdminClinicianOperationsLoading(true);
    setHasAdminClinicianOperationsError(false);

    void loadTenantAdminClinicianOperations(
      runtimeConfig,
      adminClinicianOperationsRequest,
      abortController.signal,
    )
      .then((page) => {
        if (abortController.signal.aborted) {
          return;
        }

        startTransition(() => {
          setAdminClinicianOperationsPage(page);
          setIsAdminClinicianOperationsLoading(false);
        });
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (handleAdminSessionError(error)) {
          return;
        }

        startTransition(() => {
          setHasAdminClinicianOperationsError(true);
          setIsAdminClinicianOperationsLoading(false);
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    adminClinicianOperationsSort.sortDirection,
    adminClinicianOperationsSort.sortKey,
    adminClinicianPageIndex,
    adminDashboardTabId,
    adminShowInactiveClinicians,
    deferredAdminSearchQuery,
    runtimeConfig.requestTenantId,
    runtimeConfig.sessionApiBaseUrl,
    runtimeConfig.workforceSessionSubjectKey,
    hasAdminSummary,
  ]);

  useEffect(() => {
    if (!hasAdminSummary) {
      setAdminOutboundCallsPage(null);
      setAdminOutboundQuestionnairesPage(null);
      setAdminOutboundTargetsPage(null);
      setHasAdminOutboundCallsError(false);
      setIsAdminOutboundCallsLoading(false);
      return;
    }

    if (adminDashboardTabId !== "outbound-calls") {
      return;
    }

    const abortController = new AbortController();

    setIsAdminOutboundCallsLoading(true);
    setHasAdminOutboundCallsError(false);

    void Promise.all([
      loadTenantAdminOutboundCallsSummary(runtimeConfig, {}, abortController.signal),
      loadTenantAdminOutboundCallsTargets(
        runtimeConfig,
        {
          limit: 100,
          offset: 0,
          status: "all",
        },
        abortController.signal,
      ),
    ])
      .then(([outboundSummary, targetsPage]) => {
        if (abortController.signal.aborted) {
          return;
        }

        startTransition(() => {
          setAdminOutboundCallsPage(outboundSummary);
          setAdminOutboundTargetsPage(targetsPage);
          setIsAdminOutboundCallsLoading(false);
        });
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (handleAdminSessionError(error)) {
          return;
        }

        startTransition(() => {
          setHasAdminOutboundCallsError(true);
          setIsAdminOutboundCallsLoading(false);
        });
      });

    return () => {
      abortController.abort();
    };
  }, [
    adminDashboardTabId,
    runtimeConfig.requestTenantId,
    runtimeConfig.sessionApiBaseUrl,
    runtimeConfig.workforceSessionSubjectKey,
    hasAdminSummary,
  ]);

  const adminOutboundAnalyticsCampaignId =
    adminOutboundAnalyticsScope === "campaign"
      ? (adminOutboundSelectedCampaignId ??
        adminOutboundCallsPage?.campaigns[0]?.campaignId ??
        null)
      : null;
  const adminOutboundCampaignAnalyticsRefreshKey =
    buildTenantAdminOutboundRefreshKey({
      requestKey: JSON.stringify({
        campaignId: adminOutboundAnalyticsCampaignId,
        scope: "campaign-analytics",
      }),
      tenantId: runtimeConfig.requestTenantId,
    });

  useWorkspaceRefreshScope<TenantAdminOutboundCallsSummaryResponseV1>({
    key: adminOutboundCampaignAnalyticsRefreshKey,
    aliases: [
      buildTenantAdminOutboundRefreshAlias(runtimeConfig.requestTenantId),
    ],
    enabled:
      hasAdminSummary &&
      adminDashboardTabId === "outbound-calls" &&
      adminOutboundAnalyticsCampaignId != null,
    loader: (signal) =>
      loadTenantAdminOutboundCallsSummary(
        runtimeConfig,
        { campaignId: adminOutboundAnalyticsCampaignId },
        signal,
      ),
    apply: (scopedSummary) => {
      setAdminOutboundCampaignAnalytics(scopedSummary);
    },
    onError: (error) => {
      if (handleAdminSessionError(error)) {
        return;
      }
    },
    readGeneratedAt: (payload) => payload.generatedAt,
    readRevision: (payload) => payload.generatedAt,
    minStaleMs: 5_000,
    activePollMs: workspaceRefreshCadenceMs.standard,
  });

  useEffect(() => {
    if (adminOutboundAnalyticsCampaignId == null) {
      setAdminOutboundCampaignAnalytics(null);

      return;
    }

    const abortController = new AbortController();

    void loadTenantAdminOutboundCallsSummary(
      runtimeConfig,
      { campaignId: adminOutboundAnalyticsCampaignId },
      abortController.signal,
    )
      .then((scopedSummary) => {
        if (abortController.signal.aborted) {
          return;
        }

        setAdminOutboundCampaignAnalytics(scopedSummary);
      })
      .catch((error: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }

        if (handleAdminSessionError(error)) {
          return;
        }

        // Fall back to the aggregate analytics if the scoped fetch fails.
      });

    return () => {
      abortController.abort();
    };
  }, [
    adminOutboundAnalyticsCampaignId,
    runtimeConfig.requestTenantId,
    runtimeConfig.sessionApiBaseUrl,
    runtimeConfig.workforceSessionSubjectKey,
  ]);

  useEffect(() => {
    setAdminOutboundAdvanceNoticePolicyAttestationKey(null);
    setAdminOutboundSelectedTargetIds([]);
  }, [adminOutboundSelectedCampaignId]);

  useEffect(() => {
    if (
      adminPatientOperationsPage != null &&
      adminPatientOperationsPage.items.length === 0 &&
      adminPatientOperationsPage.page.totalCount > 0 &&
      adminPatientPageIndex > 0
    ) {
      setAdminPatientPageIndex(0);
    }
  }, [adminPatientOperationsPage, adminPatientPageIndex]);

  useEffect(() => {
    if (
      adminClinicianOperationsPage != null &&
      adminClinicianOperationsPage.items.length === 0 &&
      adminClinicianOperationsPage.page.totalCount > 0 &&
      adminClinicianPageIndex > 0
    ) {
      setAdminClinicianPageIndex(0);
    }
  }, [adminClinicianOperationsPage, adminClinicianPageIndex]);

  useEffect(() => {
    setAdminConnectorPageIndex(0);
    setAdminPatientPageIndex(0);
    setAdminClinicianPageIndex(0);
    setAdminReviewPageIndex(0);
    setAdminFeatureFlagPageIndex(0);
    setAdminProvisioningPageIndex(0);
  }, [
    adminSearchQuery,
    adminConnectorStatusFilter,
    adminSiteFilter,
    adminPatientCareTypeFilter,
    adminPatientQuickFilter,
    adminReviewPriorityFilter,
    adminFeatureFlagStatusFilter,
    adminProvisioningViewFilter,
    adminShowInactiveClinicians,
    adminConnectorSortKey,
    adminPatientSortKey,
    adminClinicianSortKey,
    adminReviewSortKey,
    adminFeatureFlagSortKey,
  ]);

  const formatAdminLegacyName = (value: string) => {
    const trimmedValue = value.trim();

    if (trimmedValue.length === 0) {
      return value;
    }

    if (trimmedValue.includes(",")) {
      const normalizedParts = trimmedValue
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

      if (normalizedParts.length >= 2) {
        return `${normalizedParts[0]}, ${normalizedParts.slice(1).join(" ")}`;
      }

      return normalizedParts[0] ?? trimmedValue;
    }

    const parts = trimmedValue
      .split(/\s+/u)
      .filter((part) => part.length > 0);

    if (parts.length <= 1) {
      return trimmedValue;
    }

    const lastName = parts[parts.length - 1] ?? trimmedValue;
    const firstNames = parts.slice(0, -1).join(" ");

    return `${lastName}, ${firstNames}`;
  };

  const formatAdminLegacyDateLabel = (value: Date) =>
    new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(value);

  const adminDepartmentPalette = {
    backgroundColor: theme.surfaceMuted,
    borderColor: theme.borderSubtle,
    textColor: theme.accentStrong,
  };

  const resolveAdminTeamPalette = (teamName: string) => {
    if (teamName === "Radiology") {
      return {
        backgroundColor: theme.surfaceMuted,
        borderColor: theme.borderSubtle,
        textColor: theme.accentStrong,
      };
    }

    if (teamName === "Pain Management") {
      return {
        backgroundColor: theme.surfaceMuted,
        borderColor: theme.borderSubtle,
        textColor: theme.accentStrong,
      };
    }

    return {
      backgroundColor: theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.accentStrong,
    };
  };

  const resolveAdminCareTypePalette = (
    careTypeLabel: AdminPreviewPatientCareType,
  ) => {
    if (careTypeLabel === "Procedure") {
      return {
        backgroundColor: theme.backgroundSubtle,
        borderColor: theme.borderSubtle,
        textColor: theme.accentStrong,
      };
    }

    if (careTypeLabel === "Pain Mgmt") {
      return {
        backgroundColor: theme.surfaceMuted,
        borderColor: theme.borderSubtle,
        textColor: theme.accentStrong,
      };
    }

    if (careTypeLabel === "Synced") {
      return {
        backgroundColor: theme.backgroundSubtle,
        borderColor: theme.borderSubtle,
        textColor: theme.accentStrong,
      };
    }

    return {
      backgroundColor: theme.surfaceMuted,
      borderColor: theme.borderSubtle,
      textColor: theme.accentStrong,
    };
  };

  const resolveAdminPreviewPatientCareType = (
    row: AdminOperationalSubjectRow,
  ): AdminPreviewPatientCareType => {
    const normalizedText = [
      row.displayName,
      row.supportText,
      row.subjectKey,
      row.accessLabel,
      row.programLabel ?? "",
    ]
      .join(" ")
      .toLowerCase();

    if (
      normalizedText.includes("procedure") ||
      normalizedText.includes("imaging") ||
      normalizedText.includes("pre-op") ||
      normalizedText.includes("post-op")
    ) {
      return "Procedure";
    }

    if (normalizedText.includes("pain") || normalizedText.includes("mgmt")) {
      return "Pain Mgmt";
    }

    if (normalizedText.includes("chronic")) {
      return "Chronic";
    }

    return "Synced";
  };

  const resolveAdminPreviewTeamName = (row: AdminOperationalSubjectRow) => {
    if (row.teamKey) {
      return humanizeWorkspaceKey(row.teamKey);
    }

    return row.focusArea === "clinician"
      ? "No team assigned"
      : row.roleLabel;
  };

  const resolveAdminActionIconName = (input: {
    labelText: string;
    prefixText?: string;
    tone?: "primary" | "danger" | "surface";
  }): LineIconName | null => {
    const normalizedLabel = input.labelText.trim().toLowerCase();

    if (input.prefixText === "+") {
      return "plus";
    }

    if (
      normalizedLabel.includes("create") ||
      normalizedLabel.includes("add") ||
      normalizedLabel.includes("new ")
    ) {
      return "plus";
    }

    if (normalizedLabel.includes("sync") || normalizedLabel.includes("refresh")) {
      return "sync";
    }

    if (normalizedLabel.includes("edit") || normalizedLabel.includes("update")) {
      return "edit";
    }

    if (
      normalizedLabel.includes("view") ||
      normalizedLabel.includes("open") ||
      normalizedLabel.includes("detail") ||
      normalizedLabel.includes("inspect")
    ) {
      return "eye";
    }

    if (normalizedLabel.includes("approve") || normalizedLabel.includes("save")) {
      return "check";
    }

    if (
      normalizedLabel.includes("complete") ||
      normalizedLabel.includes("activate") ||
      normalizedLabel.includes("apply")
    ) {
      return "check";
    }

    if (normalizedLabel.includes("archive")) {
      return "archive";
    }

    if (
      normalizedLabel.includes("reject") ||
      normalizedLabel.includes("close") ||
      normalizedLabel.includes("cancel") ||
      normalizedLabel.includes("retire") ||
      normalizedLabel.includes("block")
    ) {
      return "x";
    }

    if (input.tone === "danger") {
      return "x";
    }

    return null;
  };

  // Suppress the default browser focus ring on interactive admin elements.
  // These rows/buttons already render an intentional focus state (accent
  // border + background), so the stray blue/teal UA outline is redundant.
  const adminWebOutlineReset: ViewStyle | null =
    Platform.OS === "web"
      ? ({ outlineStyle: "none", outlineWidth: 0 } as unknown as ViewStyle)
      : null;

  const renderAdminPrimaryButton = (input: {
    density?: "compact" | "default";
    disabled?: boolean;
    iconName?: LineIconName | null;
    iconRotationDegrees?: number;
    labelText: string;
    labelLines?: 1 | 2;
    minWidth?: number;
    onPress: () => void;
    prefixText?: string;
    stretch?: boolean;
    tone?: "primary" | "danger" | "surface";
    width?: number;
  }) => {
    const density = input.density ?? "default";
    const tone = input.tone ?? "primary";
    const backgroundColor =
      tone === "danger"
        ? theme.danger
        : tone === "surface"
          ? theme.surface
          : theme.accent;
    const borderColor =
      tone === "danger"
        ? theme.danger
        : tone === "surface"
          ? theme.borderSubtle
          : theme.accent;
    const textColor =
      tone === "surface" ? theme.accentStrong : theme.accentContrast;
    const resolvedTextColor =
      input.disabled === true ? theme.textSecondary : textColor;
    const resolvedIconName =
      input.iconName === undefined
        ? resolveAdminActionIconName(input)
        : input.iconName;

    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: input.disabled === true }}
        key={`${input.prefixText ?? "+"}-${input.labelText}-${tone}-${resolvedIconName ?? "text"}`}
        onPress={input.onPress}
        disabled={input.disabled}
        style={(state) => {
          const stateRecord =
            state && typeof state === "object"
              ? (state as unknown as Record<string, unknown>)
              : {};
          const hovered = stateRecord.hovered === true;
          const focused = stateRecord.focused === true;
          const pressed = stateRecord.pressed === true;
          const disabled = input.disabled === true;

          return [
          styles.inlinePill,
          adminWebOutlineReset,
          {
            alignItems: "center",
            alignSelf: input.stretch ? "stretch" : "flex-start",
            backgroundColor:
              disabled
                ? theme.backgroundSubtle
                : pressed || (tone === "surface" && (hovered || focused))
                ? theme.backgroundSubtle
                : backgroundColor,
            borderColor:
              disabled
                ? theme.borderSubtle
                : focused || hovered
                  ? theme.accentStrong
                  : borderColor,
            borderRadius: adminControlRadius,
            flexDirection: "row",
            gap: resolvedIconName == null ? 0 : 6,
            justifyContent: "center",
            minHeight: density === "compact" ? 32 : 36,
            minWidth:
              input.minWidth ??
              (density === "compact"
                ? adminCompactButtonMinWidth
                : adminButtonMinWidth),
            opacity: disabled ? 0.55 : pressed ? 0.9 : 1,
            paddingHorizontal: density === "compact" ? 12 : 16,
            paddingVertical: density === "compact" ? 5 : 7,
            shadowColor: theme.accentStrong,
            shadowOffset: {
              height: !disabled && (hovered || focused) ? 2 : 0,
              width: 0,
            },
            shadowOpacity: !disabled && (hovered || focused) ? 0.06 : 0,
            shadowRadius: !disabled && (hovered || focused) ? 6 : 0,
            width: input.stretch ? undefined : input.width,
          },
        ];
        }}
      >
        {resolvedIconName == null ? null : (
          <View
            style={
              input.iconRotationDegrees == null
                ? undefined
                : {
                    transform: [
                      { rotate: `${String(input.iconRotationDegrees)}deg` },
                    ],
                  }
            }
          >
            <LineIcon
              color={resolvedTextColor}
              name={resolvedIconName}
              size={density === "compact" ? 14 : 16}
              strokeWidth={2.45}
            />
          </View>
        )}
        <Text
          ellipsizeMode="tail"
          numberOfLines={input.labelLines ?? 1}
          style={[
            styles.badgeText,
            {
              color: resolvedTextColor,
              fontFamily: layout.bodyFontFamily,
              fontSize: density === "compact" ? 11 : 12,
              fontWeight: adminTypography.buttonWeight,
              lineHeight: density === "compact" ? 14 : 16,
              textAlign: "center",
            },
          ]}
        >
          {resolvedIconName == null && input.prefixText !== ""
            ? `${input.prefixText ?? "+"} ${input.labelText}`
            : input.prefixText === ""
            ? input.labelText
            : input.labelText}
        </Text>
      </Pressable>
    );
  };

  const renderAdminTableActionButton = (input: {
    disabled?: boolean;
    iconName?: LineIconName | null;
    labelLines?: 1 | 2;
    labelText: string;
    onPress: () => void;
    tone?: "primary" | "danger" | "surface";
    width?: number;
  }) =>
    renderAdminPrimaryButton({
      density: "compact",
      disabled: input.disabled,
      iconName: input.iconName,
      labelLines: input.labelLines ?? (input.labelText.length > 10 ? 2 : 1),
      labelText: input.labelText,
      minWidth: input.width ?? adminTableActionButtonWidth,
      onPress: input.onPress,
      prefixText: "",
      tone: input.tone,
      width: input.width ?? adminTableActionButtonWidth,
    });

  const renderAdminBinaryToggle = (input: {
    checked: boolean;
    disabled?: boolean;
    labelText?: string;
    offLabelText?: string;
    onPress: () => void;
  }) => {
    const labelText =
      input.labelText ??
      (input.checked ? "Enabled" : input.offLabelText ?? "Disabled");

    return (
      <Pressable
        accessibilityRole="switch"
        accessibilityState={{
          checked: input.checked,
          disabled: input.disabled === true,
        }}
        disabled={input.disabled}
        onPress={input.onPress}
        style={(state) => {
          const stateRecord =
            state && typeof state === "object"
              ? (state as unknown as Record<string, unknown>)
              : {};
          const focused = stateRecord.focused === true;
          const hovered = stateRecord.hovered === true;
          const pressed = stateRecord.pressed === true;

          return [
            adminWebOutlineReset,
            {
              alignItems: "center",
              backgroundColor:
                focused || hovered ? theme.backgroundSubtle : theme.surface,
              borderColor:
                focused || hovered
                  ? theme.accent
                  : input.checked
                    ? theme.accentStrong
                    : theme.borderSubtle,
              borderRadius: adminOperationalLayout.controlRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: 8,
              justifyContent: "center",
              minHeight: adminOperationalLayout.controlHeight,
              minWidth: isAdminMobileLayout ? 108 : 118,
              opacity: input.disabled ? 0.55 : pressed ? 0.86 : 1,
              paddingHorizontal: 10,
              paddingVertical: 6,
            },
          ];
        }}
      >
        <View
          style={{
            backgroundColor: input.checked
              ? theme.accent
              : theme.borderStrong,
            borderRadius: 999,
            height: 18,
            justifyContent: "center",
            paddingHorizontal: 2,
            width: 32,
          }}
        >
          <View
            style={{
              alignSelf: input.checked ? "flex-end" : "flex-start",
              backgroundColor: theme.surface,
              borderRadius: 999,
              height: 14,
              width: 14,
            }}
          />
        </View>
        <Text
          numberOfLines={1}
          style={[
            styles.pillValueText,
            {
              color: input.checked
                ? theme.accentStrong
                : theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: 11,
              fontWeight: adminTypography.strongWeight,
              lineHeight: 14,
            },
          ]}
        >
          {labelText}
        </Text>
      </Pressable>
    );
  };

  const renderAdminModalHeader = (input: {
    actions?: ReactNode;
    eyebrowText: string;
    onClose?: () => void;
    subtitleText?: string;
    titleText: string;
  }) => {
    const isCloseDisabled =
      isAdminCommandSubmitting || isAdminOutboundCommandSubmitting;

    return (
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "stretch",
            backgroundColor: theme.surface,
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: 1,
            borderColor: "transparent",
            borderTopLeftRadius: adminOperationalLayout.panelRadius,
            borderTopRightRadius: adminOperationalLayout.panelRadius,
            borderWidth: 0,
            flexDirection: "column",
            gap: adminModalDensity.actionGap,
            marginHorizontal: -adminModalDensity.cardPadding,
            marginTop: -adminModalDensity.cardPadding,
            paddingHorizontal: adminModalDensity.cardPadding,
            paddingVertical: tokens.spacing.md,
            position: "relative",
          },
        ]}
      >
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: adminModalDensity.actionGap,
            minWidth: 0,
            paddingRight: input.onClose ? 48 : 0,
            width: "100%",
          }}
        >
          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.accentSubtle,
              borderRadius: adminOperationalLayout.panelRadius,
              flexShrink: 0,
              height: 38,
              justifyContent: "center",
              width: 38,
            }}
          >
            <LineIcon
              color={theme.accentStrong}
              name="case"
              size={18}
              strokeWidth={2.3}
            />
          </View>
          <View style={[styles.listGroup, { flex: 1, gap: 4, minWidth: 0 }]}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.accentStrong,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.eyebrowFontSize,
                  fontWeight: "800",
                  letterSpacing: 0,
                  textTransform: "uppercase",
                },
              ]}
            >
              {input.eyebrowText}
            </Text>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  flexShrink: 1,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.titleFontSize,
                  lineHeight: adminModalDensity.titleLineHeight,
                },
              ]}
            >
              {input.titleText}
            </Text>
            {input.subtitleText ? (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.subtitleFontSize,
                    lineHeight: adminModalDensity.subtitleLineHeight,
                  },
                ]}
              >
                {input.subtitleText}
              </Text>
            ) : null}
          </View>
        </View>
        {input.actions ? (
          <View
            style={[
              styles.patientRowFooter,
              {
                alignSelf: "stretch",
                flexShrink: 0,
                gap: adminModalDensity.actionGap,
                justifyContent: isMediumLayout ? "flex-end" : "flex-start",
                maxWidth: "100%",
                width: "100%",
              },
            ]}
          >
            {input.actions}
          </View>
        ) : null}
        {input.onClose ? (
          <Pressable
            accessibilityLabel="Close"
            accessibilityRole="button"
            accessibilityState={{ disabled: isCloseDisabled }}
            disabled={isCloseDisabled}
            onPress={isCloseDisabled ? undefined : input.onClose}
            style={(state) => {
              const stateRecord =
                state && typeof state === "object"
                  ? (state as unknown as Record<string, unknown>)
                  : {};
              const hovered = stateRecord.hovered === true;
              const pressed = stateRecord.pressed === true;

              return {
                alignItems: "center",
                backgroundColor:
                  hovered || pressed ? theme.backgroundSubtle : "transparent",
                borderRadius: adminOperationalLayout.controlRadius,
                height: 40,
                justifyContent: "center",
                opacity: isCloseDisabled ? 0.45 : 1,
                position: "absolute",
                right: adminModalDensity.cardPadding,
                top: tokens.spacing.md,
                width: 40,
              };
            }}
          >
            <LineIcon
              color={theme.textSecondary}
              name="x"
              size={16}
              strokeWidth={2.25}
            />
          </Pressable>
        ) : null}
      </View>
    );
  };

  const renderAdminModalSelectField = (input: {
    emptyLabel?: string;
    fieldKey?: string;
    fullWidth?: boolean;
    labelText: string;
    onChange: (value: string) => void;
    options: ReadonlyArray<{ labelText: string; value: string }>;
    value: string;
  }) => {
    if (Platform.OS !== "web") {
      return (
        <View
          key={input.fieldKey ?? input.labelText}
          style={[
            styles.listGroup,
            {
              gap: 4,
              minWidth: input.fullWidth ? undefined : 152,
              width: input.fullWidth ? "100%" : undefined,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: adminModalDensity.metaLabelFontSize,
                textTransform: "uppercase",
              },
            ]}
          >
            {input.labelText}
          </Text>
          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {input.options.map((option) => (
              <View key={option.value}>
                {renderWorkspaceFilterChip({
                  isSelected: input.value === option.value,
                  labelText: option.labelText,
                  onPress: () => input.onChange(option.value),
                  theme,
                  tokens,
                })}
              </View>
            ))}
          </View>
        </View>
      );
    }

    return (
      <View
        key={input.fieldKey ?? input.labelText}
        style={[
          styles.listGroup,
          {
            flex: isMediumLayout && !input.fullWidth ? 1 : undefined,
            gap: adminConfigurationDialog.fieldLabelGap,
            minWidth: isMediumLayout && !input.fullWidth ? 152 : undefined,
            width: isMediumLayout && !input.fullWidth ? undefined : "100%",
          },
        ]}
      >
        <View
          style={{
            alignItems: "center",
            flexDirection: "row",
            gap: 5,
            zIndex: 1,
          }}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: adminModalDensity.metaLabelFontSize,
                textTransform: "uppercase",
              },
            ]}
          >
            {input.labelText}
          </Text>
          {questionnaireFieldHelp[input.labelText]
            ? renderFieldHelpHint(questionnaireFieldHelp[input.labelText])
            : null}
        </View>
        {createElement(
          "select",
          {
            "aria-label": input.labelText,
            key: input.fieldKey ?? input.labelText,
            onChange: (event: SyntheticEvent) => {
              const target = event.target as { value?: string };

              input.onChange(target.value ?? "");
            },
            style: {
              background: theme.surface,
              border: `1px solid ${theme.borderSubtle}`,
              borderRadius: `${adminOperationalLayout.controlRadius}px`,
              boxSizing: "border-box",
              color: theme.textPrimary,
              colorScheme: "light",
              fontFamily: layout.bodyFontFamily,
              fontSize: `${adminModalDensity.fieldFontSize}px`,
              minHeight: `${adminConfigurationDialog.controlHeight}px`,
              outlineColor: theme.accentStrong,
              padding: "8px 12px",
              width: "100%",
            } satisfies CSSProperties,
            value: input.value,
          },
          ...[
            input.emptyLabel
              ? createElement(
                  "option",
                  {
                    key: "empty-option",
                    style: {
                      backgroundColor: theme.surface,
                      color: theme.textSecondary,
                    } satisfies CSSProperties,
                    value: "",
                  },
                  input.emptyLabel,
                )
              : null,
            ...input.options.map((option) =>
              createElement(
                "option",
                {
                  key: option.value,
                  style: {
                    backgroundColor: theme.surface,
                    color: theme.textPrimary,
                  } satisfies CSSProperties,
                  value: option.value,
                },
                option.labelText,
              ),
            ),
          ].filter(Boolean),
        )}
      </View>
    );
  };

  const renderAdminModalRail = (input: {
    avatarText: string;
    eyebrowText: string;
    footer?: ReactNode;
    glanceRows?: ReadonlyArray<{ labelText: string; valueText: string }>;
    overviewRows?: ReadonlyArray<{ labelText: string; valueText: string }>;
    pills?: ReadonlyArray<string>;
    subtitleText?: string;
    titleText: string;
  }) => (
    <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.md }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            gap: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.clinicianDetailModalAvatarCompact,
            {
              alignItems: "center",
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: 19,
              borderWidth: 1,
              justifyContent: "center",
            },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: 14,
                lineHeight: 18,
              },
            ]}
          >
            {input.avatarText.slice(0, 3).toUpperCase()}
          </Text>
        </View>
        <View style={[styles.listGroup, { flex: 1, gap: 2, minWidth: 0 }]}>
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.accentStrong,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                fontWeight: "700",
                textTransform: "uppercase",
              },
            ]}
          >
            {input.eyebrowText}
          </Text>
          <Text
            numberOfLines={2}
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: 16,
                lineHeight: 20,
              },
            ]}
          >
            {input.titleText}
          </Text>
          {input.subtitleText ? (
            <Text
              numberOfLines={2}
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  lineHeight: 15,
                },
              ]}
            >
              {input.subtitleText}
            </Text>
          ) : null}
        </View>
      </View>

      {input.pills?.length ? (
        <View style={[styles.patientRowFooter, { gap: 6 }]}>
          {input.pills.map((pillText) => (
            <View key={pillText}>
              {renderAdminStatusPill(pillText, { minWidth: 74 })}
            </View>
          ))}
        </View>
      ) : null}

      {input.overviewRows?.length ? (
        <View style={[styles.listGroup, { gap: tokens.spacing.xs }]}>
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                textTransform: "uppercase",
              },
            ]}
          >
            Overview
          </Text>
          {input.overviewRows.map((row) => (
            <View key={row.labelText} style={[styles.listGroup, { gap: 2 }]}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.accentStrong,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                  },
                ]}
              >
                {row.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 16,
                  },
                ]}
              >
                {row.valueText}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      {input.glanceRows?.length ? (
        <View
          style={[
            styles.listGroup,
            {
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              gap: tokens.spacing.xs,
              paddingTop: tokens.spacing.sm,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textPrimary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                textTransform: "uppercase",
              },
            ]}
          >
            At a glance
          </Text>
          {input.glanceRows.map((row) => (
            <View
              key={row.labelText}
              style={[
                styles.patientRowHeader,
                {
                  borderBottomColor: theme.borderSubtle,
                  borderBottomWidth: 1,
                  paddingBottom: 6,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                  },
                ]}
              >
                {row.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 16,
                    textAlign: "right",
                  },
                ]}
              >
                {row.valueText}
              </Text>
            </View>
          ))}
        </View>
      ) : null}

      <View style={{ flex: 1 }} />
      {input.footer}
    </View>
  );

  const renderAdminStatusDot = () => (
    <View
      style={[
        styles.activityDot,
        {
          alignSelf: "center",
          backgroundColor: theme.accentStrong,
          height: 12,
          width: 12,
        },
      ]}
    />
  );

  const renderAdminStatusPill = (
    statusValue: string,
    options?: { controlHeight?: boolean; minWidth?: number },
  ) => {
    const usesControlHeight = options?.controlHeight === true;
    const normalizedStatus = statusValue.trim().toLowerCase();
    const normalizedText = normalizedStatus.replaceAll("_", " ");
    const palette =
      normalizedStatus === "active" ||
      normalizedStatus === "approved" ||
      normalizedStatus === "complete" ||
      normalizedStatus === "completed" ||
      normalizedStatus === "healthy" ||
      normalizedStatus === "reviewed"
        ? {
            backgroundColor: adminFigmaPalette.successSoft,
            borderColor: theme.statusChip.success.border,
            textColor: theme.statusChip.success.text,
          }
        : normalizedStatus === "retired" ||
            normalizedStatus === "inactive" ||
            normalizedStatus === "failed" ||
            normalizedStatus === "blocked" ||
            normalizedStatus === "urgent"
          ? {
              backgroundColor: adminFigmaPalette.dangerSoft,
              borderColor: theme.statusChip.danger.border,
              textColor: theme.statusChip.danger.text,
            }
          : normalizedText.includes("review") ||
              normalizedText.includes("attention") ||
              normalizedText.includes("pending") ||
              normalizedText.includes("open") ||
              normalizedStatus === "draft"
            ? {
                backgroundColor: adminFigmaPalette.warningSoft,
                borderColor: theme.statusChip.warning.border,
                textColor: theme.statusChip.warning.text,
              }
          : {
              backgroundColor: adminFigmaPalette.infoSoft,
              borderColor: theme.statusChip.info.border,
              textColor: theme.statusChip.info.text,
            };

    return (
      <View
        style={[
          styles.inlinePill,
          {
            alignItems: "center",
            alignSelf: "flex-start",
            backgroundColor: palette.backgroundColor,
            borderColor: palette.borderColor,
            borderRadius: 999,
            justifyContent: "center",
            minHeight: usesControlHeight ? 32 : 24,
            minWidth: options?.minWidth ?? 74,
            paddingHorizontal: usesControlHeight ? 14 : 10,
            paddingVertical: usesControlHeight ? 7 : 3,
          },
        ]}
      >
        <Text
          style={[
            styles.pillValueText,
            {
              color: palette.textColor,
              fontFamily: layout.bodyFontFamily,
              fontSize: 10,
              fontWeight: adminTypography.strongWeight,
              lineHeight: 13,
              textAlign: "center",
            },
          ]}
        >
          {humanizeWorkspaceKey(statusValue)}
        </Text>
      </View>
    );
  };

  const renderAdminProtectedOwnerPill = (
    labelText: string | null = "Platform owner",
  ) => (
    <View
      style={[
        styles.inlinePill,
        {
          alignItems: "center",
          alignSelf: "flex-start",
          backgroundColor: adminFigmaPalette.warningSoft,
          borderColor: theme.statusChip.warning.border,
          borderRadius: 999,
          gap: 4,
          justifyContent: "center",
          minHeight: 24,
          paddingHorizontal: 9,
          paddingVertical: 3,
        },
      ]}
    >
      <LineIcon
        color={theme.statusChip.warning.text}
        name="star"
        size={13}
        strokeWidth={2.2}
      />
      <Text
        style={[
          styles.pillValueText,
          {
            color: theme.statusChip.warning.text,
            fontFamily: layout.bodyFontFamily,
            fontSize: 10,
            fontWeight: adminTypography.strongWeight,
            lineHeight: 13,
          },
        ]}
      >
        {labelText ?? "Platform owner"}
      </Text>
    </View>
  );

  const previewPatientRowsForStatusFilters: AdminPreviewPatientRow[] = patientRows
    .map((row) => ({
      ...row,
      careTypeLabel: resolveAdminPreviewPatientCareType(row),
    }));
  const previewPatientRows = previewPatientRowsForStatusFilters.filter((row) =>
    matchesAdminPatientQuickFilter(row, adminPatientQuickFilter),
  );
  const previewPatientAttentionCount = previewPatientRowsForStatusFilters.filter(
    (row) => !isAdminPatientArchived(row) && row.requiresAttention,
  ).length;
  const previewPatientActiveCount = previewPatientRowsForStatusFilters.filter(
    (row) => {
      const normalizedStatus = row.statusLabel.trim().toLowerCase();

      return (
        !isAdminPatientArchived(row) &&
        normalizedStatus !== "inactive" &&
        normalizedStatus !== "retired"
      );
    },
  ).length;
  const previewPatientInactiveCount = previewPatientRowsForStatusFilters.filter(
    (row) => {
      const normalizedStatus = row.statusLabel.trim().toLowerCase();

      return (
        !isAdminPatientArchived(row) &&
        (normalizedStatus === "inactive" || normalizedStatus === "retired")
      );
    },
  ).length;
  const previewPatientArchivedCount =
    previewPatientRowsForStatusFilters.filter(isAdminPatientArchived).length;
  const previewPatientPageRows =
    adminPatientOperationsPage != null
      ? previewPatientRows
      : paginateRows(previewPatientRows, adminPatientPageIndex, 10);
  const selectedAdminPatientRow =
    adminSelectedPatientRowId == null
      ? null
      : (previewPatientRows.find(
          (row) => row.id === adminSelectedPatientRowId,
        ) ?? null);
  const adminPatientOperationsFilteredTotalCount =
    adminPatientOperationsPage?.page.totalCount != null &&
    (adminPatientQuickFilter === "all" ||
      adminPatientQuickFilter === "archived")
      ? adminPatientOperationsPage.page.totalCount
      : previewPatientRows.length;
  const adminPatientOperationsPageCount = Math.max(
    1,
    Math.ceil(
      adminPatientOperationsFilteredTotalCount / adminPatientOperationsPageSize,
    ),
  );
  const adminPatientOperationsStatusText = hasAdminPatientOperationsError
    ? "Patient data could not refresh."
    : isAdminPatientOperationsLoading
      ? previewPatientRows.length > 0
        ? "Refreshing patient list..."
        : "Loading patient data..."
      : adminPatientOperationsReturnedEmptyPage
        ? "Showing summary rows while the patient table refreshes."
        : null;
  const adminPatientQuickFilterOptions: Array<{
    labelText: string;
    value: AdminPatientQuickFilter;
  }> = [
    { labelText: "All statuses", value: "all" },
    { labelText: "Needs attention", value: "attention" },
    { labelText: "Active", value: "active" },
    { labelText: "Inactive", value: "inactive" },
    { labelText: "Archived", value: "archived" },
  ];
  const previewClinicianRows = clinicianRows.map((row) => {
    const legacyDisplayName = formatAdminLegacyName(row.displayName);
    const teamName = row.teamName ?? resolveAdminPreviewTeamName(row);
    const teamPalette = resolveAdminTeamPalette(teamName);
    const normalizedClinicianId = row.clinicianUserId.trim().toLowerCase();
    const normalizedClinicianName = row.displayName.trim().toLowerCase();
    const assignedPatientCount =
      adminClinicianOperationsPage != null
        ? row.assignedPatientCount
        : rawPatientRows.filter((patient) => {
            const assignedId =
              patient.leadingClinicianUserId?.trim().toLowerCase() ?? "";
            const assignedLabel =
              patient.leadingClinicianLabel?.trim().toLowerCase() ?? "";

            return (
              (normalizedClinicianId.length > 0 &&
                (assignedId === normalizedClinicianId ||
                  assignedLabel === normalizedClinicianId ||
                  assignedLabel.includes(normalizedClinicianId))) ||
              (normalizedClinicianName.length > 0 &&
                assignedLabel === normalizedClinicianName)
            );
          }).length;

    return {
      ...row,
      assignedPatientCount,
      departmentName: row.programLabel ?? row.siteLabel,
      emailAddressText: row.emailAddress ?? "No email on record",
      legacyDisplayName,
      phoneText: row.phoneNumber ?? "No phone on record",
      statusKey: resolveAdminClinicianCrudStatus(row),
      teamName,
      teamPalette,
    };
  });
  const previewClinicianPageRows =
    adminClinicianOperationsPage != null
      ? previewClinicianRows
      : paginateRows(previewClinicianRows, adminClinicianPageIndex, 10);
  const adminClinicianOperationsFilteredTotalCount =
    adminClinicianOperationsPage?.page.totalCount ?? previewClinicianRows.length;
  const adminClinicianOperationsPageCount = Math.max(
    1,
    Math.ceil(
      adminClinicianOperationsFilteredTotalCount /
        adminClinicianOperationsPageSize,
    ),
  );
  const adminClinicianOperationsStatusText = hasAdminClinicianOperationsError
    ? "Clinician data could not refresh."
    : isAdminClinicianOperationsLoading
      ? "Loading clinician data..."
      : adminClinicianOperationsReturnedEmptyPage
        ? "Showing summary rows while the clinician table refreshes."
        : null;

  const previewDepartmentRows = (summary?.departments ?? [])
    .map((department) => ({
      clinicianCount: department.clinicianCount,
      departmentCode: department.departmentCode,
      name: department.displayName,
      siteCode: department.siteCode,
      status:
        adminOperationalStatusOptions.includes(
          department.status as AdminOperationalCrudStatus,
        )
          ? (department.status as AdminOperationalCrudStatus)
          : "active",
    }))
    .sort((left, right) => left.name.localeCompare(right.name));

  const previewTeamRows = (summary?.teams ?? [])
    .map((team) => ({
      clinicianCount: team.clinicianCount,
      departmentCode: team.departmentCode,
      name: team.displayName,
      palette: resolveAdminTeamPalette(team.displayName),
      status:
        adminOperationalStatusOptions.includes(
          team.status as AdminOperationalCrudStatus,
        )
          ? (team.status as AdminOperationalCrudStatus)
          : "active",
      teamKey: team.teamKey,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));

  const runUpdateDepartmentCommand = (row: {
    departmentCode?: string | null;
    name: string;
    siteCode?: string | null;
    status?: AdminOperationalCrudStatus | null;
  }) => {
    setAdminCrudForm({
      departmentCode:
        row.departmentCode ?? buildAdminCommandKey(row.name, "department"),
      displayName: row.name,
      entityKind: "department",
      mode: "update",
      siteCode: row.siteCode ?? "",
      status: row.status ?? "active",
    });
    setAdminOpsNotice(`Editing department ${row.name}.`);
  };
  const runRetireDepartmentCommand = (row: {
    departmentCode?: string | null;
    name: string;
  }) => {
    setAdminCrudForm({
      displayName: row.name,
      entityKind: "department",
      mode: "retire",
      targetKey:
        row.departmentCode ?? buildAdminCommandKey(row.name, "department"),
    });
    setAdminOpsNotice(`Review retire action for department ${row.name}.`);
  };
  const runUpdateTeamCommand = (row: {
    departmentCode?: string | null;
    name: string;
    status?: AdminOperationalCrudStatus | null;
    teamKey?: string | null;
  }) => {
    setAdminCrudForm({
      departmentCode: row.departmentCode ?? "",
      displayName: row.name,
      entityKind: "team",
      mode: "update",
      status: row.status ?? "active",
      teamKey: row.teamKey ?? buildAdminCommandKey(row.name, "team"),
    });
    setAdminOpsNotice(`Editing team ${row.name}.`);
  };
  const runRetireTeamCommand = (row: {
    name: string;
    teamKey?: string | null;
  }) => {
    setAdminCrudForm({
      displayName: row.name,
      entityKind: "team",
      mode: "retire",
      targetKey: row.teamKey ?? buildAdminCommandKey(row.name, "team"),
    });
    setAdminOpsNotice(`Review retire action for team ${row.name}.`);
  };

  const submitAdminCrudForm = () => {
    if (!adminCrudForm || isAdminCommandSubmitting) {
      return;
    }

    if (adminCrudForm.mode === "retire") {
      const targetKey = adminCrudForm.targetKey.trim();

      if (!targetKey) {
        setAdminOpsNotice("Retire action is missing a target key.");
        return;
      }

      const command =
        adminCrudForm.entityKind === "clinician"
          ? ({
              clinicianUserId: targetKey,
              kind: "retire-clinician",
            } satisfies TenantAdminCommandInputV1)
          : adminCrudForm.entityKind === "department"
            ? ({
                departmentCode: targetKey,
                kind: "retire-department",
              } satisfies TenantAdminCommandInputV1)
            : adminCrudForm.entityKind === "connector"
              ? ({
                  connectorId: targetKey,
                  kind: "retire-connector",
                } satisfies TenantAdminCommandInputV1)
              : ({
                  kind: "retire-team",
                  teamKey: targetKey,
                } satisfies TenantAdminCommandInputV1);

      void runAdminOpsCommand(command, {
        failureText: `${humanizeWorkspaceKey(adminCrudForm.entityKind)} ${adminCrudForm.displayName} could not be retired.`,
        pendingText: `Retiring ${humanizeWorkspaceKey(adminCrudForm.entityKind).toLowerCase()} ${adminCrudForm.displayName}...`,
        successText: `${humanizeWorkspaceKey(adminCrudForm.entityKind)} retired and admin summary refreshed.`,
      }).then((didSave) => {
        if (didSave) {
          setAdminCrudForm(null);
        }
      });
      return;
    }

    if (adminCrudForm.entityKind === "patient") {
      const structuredDisplayName = [
        adminCrudForm.givenName.trim(),
        adminCrudForm.familyName.trim(),
      ]
        .filter((part) => part.length > 0)
        .join(" ");
      const displayName =
        adminCrudForm.displayName.trim() || structuredDisplayName;
      const patientId =
        adminCrudForm.patientId.trim() ||
        buildGeneratedAdminPatientId(adminCrudForm);

      if (!displayName) {
        setAdminOpsNotice("Patient name is required.");
        return;
      }

      void runAdminOpsCommand(
        {
          addressCity: adminCrudForm.addressCity.trim() || null,
          addressState: adminCrudForm.addressState.trim() || null,
          addressStreet: adminCrudForm.addressStreet.trim() || null,
          addressZip: adminCrudForm.addressZip.trim() || null,
          alternateEmergencyContactPhone:
            adminCrudForm.alternateEmergencyContactPhone.trim() || null,
          contactWindowText: adminCrudForm.contactWindowText.trim() || null,
          dateOfBirth: adminCrudForm.dateOfBirth.trim() || null,
          displayName,
          emailAddress: adminCrudForm.emailAddress.trim() || null,
          emergencyContactPhone:
            adminCrudForm.emergencyContactPhone.trim() || null,
          ...(adminCrudForm.mode === "update"
            ? {
                expectedLifecycleVersion:
                  adminCrudForm.expectedLifecycleVersion ?? 1,
              }
            : {}),
          familyName: adminCrudForm.familyName.trim() || null,
          givenName: adminCrudForm.givenName.trim() || null,
          homePhoneNumber: adminCrudForm.homePhoneNumber.trim() || null,
          identifier: adminCrudForm.identifier.trim() || null,
          kind:
            adminCrudForm.mode === "create"
              ? "create-patient"
              : "update-patient",
          onboardingInitialFlowComplete:
            adminCrudForm.onboardingState === "complete",
          patientId,
          phoneNumber: adminCrudForm.phoneNumber.trim() || null,
          secondaryPhoneNumber:
            adminCrudForm.secondaryPhoneNumber.trim() || null,
          sex: adminCrudForm.sex || null,
          leadingClinicianLabel:
            clinicianUserIdOptions.find(
              (option) =>
                option.value === adminCrudForm.leadingClinicianUserId.trim(),
            )?.labelText ?? null,
          leadingClinicianUserId:
            adminCrudForm.leadingClinicianUserId.trim() || null,
          programLabel: adminCrudForm.programLabel.trim() || null,
          siteId: adminCrudForm.siteId.trim() || null,
          status: adminCrudForm.status,
          workPhoneNumber: adminCrudForm.workPhoneNumber.trim() || null,
        },
        {
          failureText: `Patient ${displayName} could not be saved.`,
          pendingText: `Saving patient ${displayName}...`,
          successText: "Patient saved and admin summary refreshed.",
        },
      ).then((didSave) => {
        if (didSave) {
          setAdminCrudForm(null);
        }
      });
      return;
    }

    if (adminCrudForm.entityKind === "clinician") {
      const displayName = adminCrudForm.displayName.trim();
      const clinicianUserId = resolveAdminClinicianUserId(adminCrudForm);
      const previewProfileKey = resolveAdminClinicianPreviewProfileKey(
        adminCrudForm,
        clinicianUserId,
      );
      const testLoginEmail =
        adminCrudForm.testLoginEmail.trim() ||
        adminCrudForm.emailAddress.trim() ||
        null;
      const testLoginPassword = adminCrudForm.testLoginPassword.trim() || null;

      if (!displayName || !clinicianUserId) {
        setAdminOpsNotice("Clinician name and user id are required.");
        return;
      }

      if (testLoginPassword && !testLoginEmail) {
        setAdminOpsNotice(
          "Clinician login email is required when a temporary password is set.",
        );
        return;
      }

      if (adminCrudForm.isProtectedOwner && adminCrudForm.status !== "active") {
        setAdminOpsNotice(
          "Adam Bartman is the protected platform owner and cannot be suspended, retired, or revoked.",
        );
        return;
      }

      void runAdminOpsCommand(
        {
          clinicianUserId,
          departmentCode: adminCrudForm.departmentCode.trim() || null,
          displayName,
          emailAddress: adminCrudForm.emailAddress.trim() || null,
          kind:
            adminCrudForm.mode === "create"
              ? "create-clinician"
              : "update-clinician",
          phoneNumber: adminCrudForm.phoneNumber.trim() || null,
          previewProfileKey,
          roleKey: "clinician",
          status: adminCrudForm.status,
          teamKey: adminCrudForm.teamKey.trim() || null,
          testLoginEmail,
          testLoginPassword,
        },
        {
          failureText: `Clinician ${displayName} could not be saved.`,
          pendingText: `Saving clinician ${displayName}...`,
          successText: "Clinician saved and admin summary refreshed.",
        },
      ).then((didSave) => {
        if (didSave) {
          setAdminCrudForm(null);
        }
      });
      return;
    }

    if (adminCrudForm.entityKind === "department") {
      const displayName = adminCrudForm.displayName.trim();
      const departmentCode =
        adminCrudForm.departmentCode.trim() ||
        buildAdminCommandKey(displayName, "department");

      if (!displayName || !departmentCode) {
        setAdminOpsNotice("Department name and code are required.");
        return;
      }

      void runAdminOpsCommand(
        {
          departmentCode,
          displayName,
          kind:
            adminCrudForm.mode === "create"
              ? "create-department"
              : "update-department",
          siteCode: adminCrudForm.siteCode.trim() || null,
          status: adminCrudForm.status,
        },
        {
          failureText: `Department ${displayName} could not be saved.`,
          pendingText: `Saving department ${displayName}...`,
          successText: "Department saved and admin summary refreshed.",
        },
      ).then((didSave) => {
        if (didSave) {
          setAdminCrudForm(null);
        }
      });
      return;
    }

    if (adminCrudForm.entityKind === "connector") {
      const displayName = adminCrudForm.displayName.trim();
      const sourceSystemKey = adminCrudForm.sourceSystemKey.trim();
      const metadataSummary = adminCrudForm.metadataSummary.trim();

      if (!displayName) {
        setAdminOpsNotice("Connector name is required.");
        return;
      }

      if (adminCrudForm.mode === "create" && !sourceSystemKey) {
        setAdminOpsNotice("Integration key is required.");
        return;
      }

      void runAdminOpsCommand(
        adminCrudForm.mode === "create"
          ? {
              connectorFamily: adminCrudForm.connectorFamily,
              departmentCode: adminCrudForm.departmentCode.trim() || null,
              displayName,
              kind: "create-connector",
              metadataSummary: metadataSummary || null,
              researchPackSlug:
                adminCrudForm.researchPackSlug.trim() ||
                adminCrudForm.connectorFamily.replaceAll("_", "-"),
              siteCode: adminCrudForm.siteCode.trim() || null,
              sourceAuthorityMode: adminCrudForm.sourceAuthorityMode,
              sourceSystemKey,
              status: adminCrudForm.status,
              teamKey: adminCrudForm.teamKey.trim() || null,
            }
          : {
              connectorId: adminCrudForm.connectorId,
              departmentCode: adminCrudForm.departmentCode.trim() || null,
              displayName,
              kind: "update-connector",
              metadataSummary: metadataSummary || null,
              researchPackSlug:
                adminCrudForm.researchPackSlug.trim() || undefined,
              siteCode: adminCrudForm.siteCode.trim() || null,
              sourceAuthorityMode: adminCrudForm.sourceAuthorityMode,
              status: adminCrudForm.status,
              teamKey: adminCrudForm.teamKey.trim() || null,
            },
        {
          failureText: `Connector ${displayName} could not be saved.`,
          pendingText: `Saving connector ${displayName}...`,
          successText: "Connector saved and admin summary refreshed.",
        },
      ).then((didSave) => {
        if (didSave) {
          setAdminCrudForm(null);
        }
      });
      return;
    }

    const displayName = adminCrudForm.displayName.trim();
    const teamKey =
      adminCrudForm.teamKey.trim() || buildAdminCommandKey(displayName, "team");

    if (!displayName || !teamKey) {
      setAdminOpsNotice("Team name and key are required.");
      return;
    }

    void runAdminOpsCommand(
      {
        departmentCode: adminCrudForm.departmentCode.trim() || null,
        displayName,
        kind: adminCrudForm.mode === "create" ? "create-team" : "update-team",
        status: adminCrudForm.status,
        teamKey,
      },
      {
        failureText: `Team ${displayName} could not be saved.`,
        pendingText: `Saving team ${displayName}...`,
        successText: "Team saved and admin summary refreshed.",
      },
    ).then((didSave) => {
      if (didSave) {
        setAdminCrudForm(null);
      }
    });
  };

  const submitAdminClinicianLifecycleCommand = (
    actionKind: "reactivate" | "retire" | "suspend",
  ) => {
    if (
      adminCrudForm == null ||
      adminCrudForm.entityKind !== "clinician" ||
      adminCrudForm.mode !== "update" ||
      isAdminCommandSubmitting
    ) {
      return;
    }

    const displayName = adminCrudForm.displayName.trim();
    const clinicianUserId = adminCrudForm.clinicianUserId.trim();
    const previewProfileKey = resolveAdminClinicianPreviewProfileKey(
      adminCrudForm,
      clinicianUserId,
    );
    const testLoginEmail =
      adminCrudForm.testLoginEmail.trim() ||
      adminCrudForm.emailAddress.trim() ||
      null;
    const testLoginPassword = adminCrudForm.testLoginPassword.trim() || null;

    if (!displayName || !clinicianUserId) {
      setAdminOpsNotice("Clinician name and user id are required.");
      return;
    }

    if (testLoginPassword && !testLoginEmail) {
      setAdminOpsNotice(
        "Clinician login email is required when a temporary password is set.",
      );
      return;
    }

    if (
      adminCrudForm.isProtectedOwner &&
      (actionKind === "retire" || actionKind === "suspend")
    ) {
      setAdminOpsNotice(
        "Adam Bartman is the protected platform owner and cannot be suspended, retired, or revoked.",
      );
      return;
    }

    if (actionKind === "retire") {
      void runAdminOpsCommand(
        {
          clinicianUserId,
          kind: "retire-clinician",
        },
        {
          failureText: `Clinician ${displayName} could not be retired.`,
          pendingText: `Retiring clinician ${displayName}...`,
          successText: "Clinician retired and admin summary refreshed.",
        },
      ).then((didSave) => {
        if (didSave) {
          setAdminCrudForm(null);
        }
      });
      return;
    }

    const nextStatus = actionKind === "suspend" ? "suspended" : "active";

    void runAdminOpsCommand(
      {
        clinicianUserId,
        departmentCode: adminCrudForm.departmentCode.trim() || null,
        displayName,
        emailAddress: adminCrudForm.emailAddress.trim() || null,
        kind: "update-clinician",
        phoneNumber: adminCrudForm.phoneNumber.trim() || null,
        previewProfileKey,
        roleKey: "clinician",
        status: nextStatus,
        teamKey: adminCrudForm.teamKey.trim() || null,
        testLoginEmail,
        testLoginPassword,
      },
      {
        failureText: `Clinician ${displayName} could not be ${
          actionKind === "suspend" ? "suspended" : "reactivated"
        }.`,
        pendingText: `${
          actionKind === "suspend" ? "Suspending" : "Reactivating"
        } clinician ${displayName}...`,
        successText: `Clinician ${
          actionKind === "suspend" ? "suspended" : "reactivated"
        } and admin summary refreshed.`,
      },
    ).then((didSave) => {
      if (didSave) {
        setAdminCrudForm(null);
      }
    });
  };

  const selectedAdminCrudDepartmentCode =
    adminCrudForm != null && "departmentCode" in adminCrudForm
      ? adminCrudForm.departmentCode.trim()
      : "";
  const adminCrudFieldLayoutStyle = {
    flexBasis: isMediumLayout ? 220 : undefined,
    flexGrow: 1,
    gap: adminConfigurationDialog.fieldLabelGap,
    minWidth: isMediumLayout ? 220 : undefined,
  };
  const siteCodeOptions = Array.from(
    new Set(
      [
        ...(summary?.workforceSubjects ?? []).map((subject) => subject.siteId),
        ...(summary?.connectors.registrations ?? []).map(
          (connector) => connector.siteCode,
        ),
        ...previewDepartmentRows.map((department) => department.siteCode),
        runtimeConfig.requestSiteId,
      ].filter((siteCode): siteCode is string => Boolean(siteCode?.trim())),
    ),
  )
    .sort((left, right) => left.localeCompare(right))
    .map((siteCode) => ({
      labelText: humanizeWorkspaceKey(siteCode),
      value: siteCode,
    }));
  const departmentCodeOptions = previewDepartmentRows
    .map((department) => ({
      labelText: department.name,
      value: department.departmentCode,
    }))
    .sort((left, right) => left.labelText.localeCompare(right.labelText));
  const teamKeyOptions = previewTeamRows
    .filter((team) =>
      selectedAdminCrudDepartmentCode.length > 0
        ? team.departmentCode === selectedAdminCrudDepartmentCode
        : true,
    )
    .map((team) => ({
      labelText: team.name,
      value: team.teamKey,
    }))
    .sort((left, right) => left.labelText.localeCompare(right.labelText));
  const connectorTeamKeyOptions = previewTeamRows
    .map((team) => ({
      labelText: team.name,
      value: team.teamKey,
    }))
    .sort((left, right) => left.labelText.localeCompare(right.labelText));
  const clinicianUserIdOptions = rawClinicianRows
    .map((clinician) => ({
      labelText: clinician.displayName,
      value: clinician.clinicianUserId,
    }))
    .filter(
      (option, index, options) =>
        option.value.trim().length > 0 &&
        options.findIndex((candidate) => candidate.value === option.value) ===
          index,
    )
    .sort((left, right) => left.labelText.localeCompare(right.labelText));
  const featureFlagScopeOptions = [
    { labelText: "All sites", value: "tenant" },
    { labelText: "Site", value: "site" },
    { labelText: "Department", value: "department" },
    { labelText: "Program", value: "program" },
  ];
  const featureFlagScopeKeyOptions =
    adminFeatureFlagDraft?.scope === "site"
      ? siteCodeOptions
      : adminFeatureFlagDraft?.scope === "department"
        ? departmentCodeOptions
        : adminFeatureFlagDraft?.scope === "program"
          ? connectorTeamKeyOptions
          : [];

  const renderAdminCrudTextField = (
    labelText: string,
    value: string,
    fieldName: string,
    disabled = false,
  ) => (
    <View key={fieldName} style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            textTransform: "uppercase",
          },
        ]}
      >
        {labelText}
      </Text>
      <TextInput
        editable={!disabled && !isAdminCommandSubmitting}
        onChangeText={(nextValue) =>
          updateAdminCrudFormField(fieldName, nextValue)
        }
        placeholder={labelText}
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: disabled ? theme.surfaceMuted : adminFigmaPalette.field,
            borderColor: theme.borderSubtle,
            borderRadius: adminOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: adminModalDensity.fieldFontSize,
            minHeight: adminConfigurationDialog.controlHeight,
            paddingHorizontal: 12,
            paddingVertical: 8,
          },
        ]}
        value={value}
      />
    </View>
  );

  const renderAdminCrudSelectField = (
    labelText: string,
    value: string,
    options: ReadonlyArray<{ labelText: string; value: string }>,
    fieldName: string,
    emptyLabel: string,
  ) => {
    if (options.length === 0) {
      return renderAdminCrudTextField(labelText, value, fieldName);
    }

    if (Platform.OS === "web") {
      return (
        <View key={fieldName} style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
                textTransform: "uppercase",
              },
            ]}
          >
            {labelText}
          </Text>
          {createElement(
            "select",
            {
              key: fieldName,
              disabled: isAdminCommandSubmitting,
              onChange: (event: SyntheticEvent) => {
                const target = event.target as { value?: string };

                updateAdminCrudFormField(fieldName, target.value ?? "");
              },
              style: {
                background: adminFigmaPalette.field,
                border: `1px solid ${theme.borderSubtle}`,
                borderRadius: `${adminOperationalLayout.controlRadius}px`,
                boxSizing: "border-box",
                color: theme.textPrimary,
                colorScheme: "light",
                fontFamily: layout.bodyFontFamily,
                fontSize: `${adminModalDensity.fieldFontSize}px`,
                minHeight: `${adminConfigurationDialog.controlHeight}px`,
                outlineColor: theme.accentStrong,
                padding: "8px 12px",
                width: "100%",
              } satisfies CSSProperties,
              value,
            },
            ...[
              createElement(
                "option",
                {
                  key: "empty-option",
                  style: {
                    backgroundColor: theme.surface,
                    color: theme.textSecondary,
                  } satisfies CSSProperties,
                  value: "",
                },
                emptyLabel,
              ),
              ...options.map((option) =>
                createElement(
                  "option",
                  {
                    key: option.value,
                    style: {
                      backgroundColor: theme.surface,
                      color: theme.textPrimary,
                    } satisfies CSSProperties,
                    value: option.value,
                  },
                  option.labelText,
                ),
              ),
            ],
          )}
        </View>
      );
    }

    return (
      <View key={fieldName} style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              textTransform: "uppercase",
            },
          ]}
        >
          {labelText}
        </Text>
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderWorkspaceFilterChip({
            isSelected: value.trim().length === 0,
            labelText: emptyLabel,
            onPress: () => updateAdminCrudFormField(fieldName, ""),
            theme,
            tokens,
          })}
          {options.map((option) =>
            renderWorkspaceFilterChip({
              isSelected: value === option.value,
              labelText: option.labelText,
              onPress: () => updateAdminCrudFormField(fieldName, option.value),
              theme,
              tokens,
            }),
          )}
        </View>
      </View>
    );
  };

  const renderAdminFeatureFlagTextField = (
    labelText: string,
    value: string,
    fieldName: keyof AdminFeatureFlagDraftState,
    disabled = false,
  ) => (
    <View style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            textTransform: "uppercase",
          },
        ]}
      >
        {labelText}
      </Text>
      <TextInput
        editable={!disabled && !isAdminCommandSubmitting}
        onChangeText={(nextValue) =>
          updateAdminFeatureFlagDraftField(fieldName, nextValue)
        }
        placeholder={labelText}
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: disabled ? theme.surfaceMuted : adminFigmaPalette.field,
            borderColor: theme.borderSubtle,
            borderRadius: adminOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: adminModalDensity.fieldFontSize,
            minHeight: adminConfigurationDialog.controlHeight,
            paddingHorizontal: 12,
            paddingVertical: 8,
          },
        ]}
        value={value}
      />
    </View>
  );

  const renderAdminFeatureFlagSelectField = (
    labelText: string,
    value: string,
    options: ReadonlyArray<{ labelText: string; value: string }>,
    fieldName: keyof AdminFeatureFlagDraftState,
    emptyLabel?: string,
    disabled = false,
  ) => {
    if (Platform.OS === "web") {
      return (
        <View key={fieldName} style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
                textTransform: "uppercase",
              },
            ]}
          >
            {labelText}
          </Text>
          {createElement(
            "select",
            {
              key: fieldName,
              disabled: disabled || isAdminCommandSubmitting,
              onChange: (event: SyntheticEvent) => {
                const target = event.target as { value?: string };

                updateAdminFeatureFlagDraftField(
                  fieldName,
                  target.value ?? "",
                );
              },
              style: {
                background: disabled ? theme.surfaceMuted : adminFigmaPalette.field,
                border: `1px solid ${theme.borderSubtle}`,
                borderRadius: `${adminOperationalLayout.controlRadius}px`,
                boxSizing: "border-box",
                color: theme.textPrimary,
                colorScheme: "light",
                fontFamily: layout.bodyFontFamily,
                fontSize: `${adminModalDensity.fieldFontSize}px`,
                minHeight: `${adminConfigurationDialog.controlHeight}px`,
                outlineColor: theme.accentStrong,
                padding: "8px 12px",
                width: "100%",
              } satisfies CSSProperties,
              value,
            },
            ...[
              emptyLabel
                ? createElement(
                    "option",
                    {
                      key: "empty-option",
                      style: {
                        backgroundColor: theme.surface,
                        color: theme.textSecondary,
                      } satisfies CSSProperties,
                      value: "",
                    },
                    emptyLabel,
                  )
                : null,
              ...options.map((option) =>
                createElement(
                  "option",
                  {
                    key: option.value,
                    style: {
                      backgroundColor: theme.surface,
                      color: theme.textPrimary,
                    } satisfies CSSProperties,
                    value: option.value,
                  },
                  option.labelText,
                ),
              ),
            ].filter(Boolean),
          )}
        </View>
      );
    }

    return (
      <View key={fieldName} style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              textTransform: "uppercase",
            },
          ]}
        >
          {labelText}
        </Text>
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {emptyLabel
            ? renderWorkspaceFilterChip({
                isSelected: value.trim().length === 0,
                labelText: emptyLabel,
                onPress: () => updateAdminFeatureFlagDraftField(fieldName, ""),
                theme,
                tokens,
              })
            : null}
          {options.map((option) =>
            renderWorkspaceFilterChip({
              isSelected: value === option.value,
              labelText: option.labelText,
              onPress: () =>
                updateAdminFeatureFlagDraftField(fieldName, option.value),
              theme,
              tokens,
            }),
          )}
        </View>
      </View>
    );
  };

  const renderAdminCrudStatusPicker = (
    labelText: string,
    value: string,
    options: readonly string[],
    fieldName = "status",
  ) => {
    if (Platform.OS === "web") {
      return renderAdminModalSelectField({
        fieldKey: fieldName,
        labelText,
        onChange: (nextValue) => updateAdminCrudFormField(fieldName, nextValue),
        options: options.map((option) => ({
          labelText: humanizeWorkspaceKey(option),
          value: option,
        })),
        value,
      });
    }

    return (
      <View style={[styles.listGroup, adminCrudFieldLayoutStyle]}>
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              textTransform: "uppercase",
            },
          ]}
        >
          {labelText}
        </Text>
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {options.map((statusValue) =>
            renderWorkspaceFilterChip({
              isSelected: value === statusValue,
              labelText: humanizeWorkspaceKey(statusValue),
              onPress: () => updateAdminCrudFormField(fieldName, statusValue),
              theme,
              tokens,
            }),
          )}
        </View>
      </View>
    );
  };

  const renderAdminClinicianLifecycleControls = (
    form: Extract<AdminCrudFormState, { entityKind: "clinician" }>,
  ) => {
    const status = form.status;
    const isCreate = form.mode === "create";
    const isProtectedOwner = form.isProtectedOwner;
    const canSuspend = !isCreate && status === "active" && !isProtectedOwner;
    const canRetire = !isCreate && status !== "inactive" && !isProtectedOwner;
    const canReactivate = !isCreate && status !== "active";

    return (
      <View
        style={[
          styles.listGroup,
          adminCrudFieldLayoutStyle,
          {
            flexBasis: isMediumLayout ? "100%" : undefined,
            minWidth: isMediumLayout ? "100%" : undefined,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
              textTransform: "uppercase",
            },
          ]}
        >
          Lifecycle
        </Text>
        <View
          style={[
            styles.patientRowFooter,
            {
              gap: tokens.spacing.xs,
            },
          ]}
        >
          {renderAdminTableActionButton({
            disabled: !canSuspend,
            labelText: "Suspend",
            onPress: () => submitAdminClinicianLifecycleCommand("suspend"),
            tone: "surface",
            width: adminToolbarActionButtonWidth,
          })}
          {renderAdminTableActionButton({
            disabled: !canRetire,
            labelText: "Retire",
            onPress: () => submitAdminClinicianLifecycleCommand("retire"),
            tone: "danger",
            width: adminToolbarActionButtonWidth,
          })}
          {renderAdminTableActionButton({
            disabled: !canReactivate,
            labelText: "Reactivate",
            onPress: () => submitAdminClinicianLifecycleCommand("reactivate"),
            tone: "primary",
            width: adminToolbarActionButtonWidth,
          })}
        </View>
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.xs,
            },
          ]}
        >
          {isCreate
            ? "New clinicians are created active."
            : isProtectedOwner
              ? "Protected platform owner: this account cannot be suspended, retired, or revoked."
            : `Current lifecycle state: ${
                status === "inactive" ? "Retired" : humanizeWorkspaceKey(status)
              }.`}
        </Text>
      </View>
    );
  };

  const renderAdminModalFrame = (input: {
    accessibilityLabel?: string;
    children: ReactNode;
    contentPadding?: number;
    maxWidth: number;
    onClose: () => void;
    presentation?: "dialog" | "drawer" | "workspace";
    rail?: ReactNode;
    visible: boolean;
  }) => {
    if (!input.visible) {
      return null;
    }

    const hasRail = Boolean(input.rail);
    const presentation =
      input.presentation ?? (hasRail ? "drawer" : "dialog");
    const isDrawerPresentation = presentation === "drawer";
    const isWorkspacePresentation = presentation === "workspace";
    const handleClose = () => {
      if (isAdminCommandSubmitting || isAdminOutboundCommandSubmitting) {
        return;
      }

      input.onClose();
    };
    const availableModalWidth = Math.max(
      280,
      viewportWidth - (isDrawerPresentation ? 0 : isAdminMobileLayout ? 24 : 44),
    );
    const modalWidth = isDrawerPresentation
      ? Math.min(
          input.maxWidth,
          Math.max(
            320,
            Math.min(
              adminOperationalLayout.detailDrawerWidth,
              availableModalWidth,
            ),
          ),
        )
      : Math.min(input.maxWidth, availableModalWidth);
    const isSplitModalLayout =
      hasRail &&
      isDrawerPresentation &&
      isMediumLayout &&
      modalWidth >= 680;
    const splitModalMaxHeight = Math.min(
      760,
      Math.max(360, viewportHeight - 90),
    );
    // Field-help tooltip rendered at the overlay layer (outside the scrolled,
    // clipped panel) and positioned from the "?" icon's window coordinates.
    const fieldHelpTooltip =
      activeFieldHelp != null
        ? (() => {
            const tooltipWidth = 260;
            const tooltipLeft = Math.max(
              12,
              Math.min(activeFieldHelp.left, viewportWidth - tooltipWidth - 12),
            );
            const placeBelow =
              activeFieldHelp.iconBottom + 80 <= viewportHeight;

            return (
              <View
                pointerEvents="none"
                style={[
                  {
                    backgroundColor: theme.textPrimary,
                    borderRadius: 8,
                    left: tooltipLeft,
                    maxWidth: tooltipWidth,
                    paddingHorizontal: 10,
                    paddingVertical: 8,
                    position: "absolute",
                    zIndex: 9999,
                  },
                  placeBelow
                    ? { top: activeFieldHelp.iconBottom + 6 }
                    : {
                        bottom: viewportHeight - activeFieldHelp.iconTop + 6,
                      },
                ]}
              >
                <Text
                  style={{
                    color: theme.surface,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    lineHeight: 15,
                  }}
                >
                  {activeFieldHelp.text}
                </Text>
              </View>
            );
          })()
        : null;

    return (
      <Modal
        accessibilityLabel={input.accessibilityLabel ?? "Admin dialog"}
        animationType="fade"
        accessibilityViewIsModal={true}
        onRequestClose={handleClose}
        transparent
        visible={input.visible}
      >
        <View
          style={[
            styles.patientSupportModalOverlay,
            {
              alignItems: isDrawerPresentation ? "flex-end" : "center",
              justifyContent: "center",
              padding: isDrawerPresentation ? 0 : isAdminMobileLayout ? 12 : 22,
            },
          ]}
        >
          <Pressable
            accessible={false}
            focusable={false}
            onPress={handleClose}
            style={styles.patientSupportModalBackdrop}
          />
          <View
            pointerEvents="box-none"
            style={[
              styles.patientSupportModalContent,
              {
                alignItems: isDrawerPresentation ? "flex-end" : "center",
                flex: 1,
                justifyContent: "center",
              },
            ]}
          >
            {hasRail ? (
              <View
                style={[
                  styles.clinicianDetailModalPanel,
                  {
                    backgroundColor: adminFigmaPalette.surface,
                    borderColor: theme.borderSubtle,
                    borderBottomLeftRadius:
                      isDrawerPresentation && !isAdminMobileLayout
                        ? adminOperationalLayout.panelRadius
                        : 0,
                    borderBottomRightRadius: 0,
                    borderRadius: isDrawerPresentation
                      ? 0
                      : adminOperationalLayout.panelRadius,
                    borderTopLeftRadius:
                      isDrawerPresentation && !isAdminMobileLayout
                        ? adminOperationalLayout.panelRadius
                        : 0,
                    borderTopRightRadius: 0,
                    height: isDrawerPresentation ? "100%" : undefined,
                    maxHeight: isDrawerPresentation
                      ? "100%"
                      : splitModalMaxHeight,
                    maxWidth: modalWidth,
                    shadowColor: theme.accentStrong,
                    shadowOffset: { width: -10, height: 0 },
                    shadowOpacity: 0.16,
                    shadowRadius: 28,
                    width: modalWidth,
                  },
                ]}
              >
                <View
                  style={[
                  styles.clinicianDetailModalSplitLayout,
                  {
                    flexDirection: isSplitModalLayout ? "row" : "column",
                    maxHeight: "100%",
                  },
                  ]}
                >
                  <View
                    style={[
                      styles.clinicianDetailModalRail,
                      {
                        backgroundColor: adminFigmaPalette.panelSubtle,
                        borderBottomColor: isSplitModalLayout
                          ? "transparent"
                          : theme.borderSubtle,
                        borderBottomWidth: isSplitModalLayout ? 0 : 1,
                        borderRightColor: isSplitModalLayout
                          ? theme.borderSubtle
                          : "transparent",
                        borderRightWidth: isSplitModalLayout ? 1 : 0,
                        gap: adminConfigurationDialog.sectionGap,
                        maxHeight: isSplitModalLayout ? "100%" : 260,
                        padding: adminModalDensity.cardPadding,
                        width: isSplitModalLayout ? 224 : "100%",
                      },
                    ]}
                  >
                    {input.rail}
                  </View>
                  <View
                    style={[
                      styles.clinicianDetailModalMain,
                      {
                        backgroundColor: adminFigmaPalette.surface,
                      },
                    ]}
                  >
                    <ScrollView
                      keyboardShouldPersistTaps="handled"
                      showsVerticalScrollIndicator={false}
                      style={[styles.patientSupportModalScroll, { flex: 1 }]}
                      contentContainerStyle={{
                        gap: adminModalDensity.actionGap,
                        padding:
                          input.contentPadding ??
                          adminModalDensity.cardPadding,
                      }}
                    >
                      {input.children}
                    </ScrollView>
                  </View>
                </View>
              </View>
            ) : (
              <View
                style={{
                  height: isWorkspacePresentation
                    ? Math.max(
                        240,
                        viewportHeight - (isAdminMobileLayout ? 24 : 44),
                      )
                    : undefined,
                  maxHeight: isWorkspacePresentation
                    ? Math.max(
                        240,
                        viewportHeight - (isAdminMobileLayout ? 24 : 44),
                      )
                    : "90%",
                  maxWidth: modalWidth,
                  shadowColor: theme.accentStrong,
                  shadowOffset: { width: 0, height: 18 },
                  shadowOpacity: 0.14,
                  shadowRadius: 28,
                  width: "100%",
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: adminOperationalLayout.panelRadius,
                  borderWidth: 1,
                  overflow: "hidden",
                }}
              >
                <ScrollView
                  keyboardShouldPersistTaps="handled"
                  showsVerticalScrollIndicator={false}
                  style={styles.patientSupportModalScroll}
                  contentContainerStyle={{
                    gap: adminModalDensity.actionGap,
                    padding:
                      input.contentPadding ?? adminModalDensity.cardPadding,
                  }}
                >
                  {input.children}
                </ScrollView>
              </View>
            )}
          </View>
          {fieldHelpTooltip}
        </View>
      </Modal>
    );
  };

  const resolveAdminCrudSurfaceLabel = (
    form: AdminCrudFormState,
  ): string => {
    if (form.mode === "retire") {
      return "Record retirement";
    }

    if (form.entityKind === "patient") {
      return form.mode === "create" ? "Patient setup" : "Patient profile";
    }

    if (form.entityKind === "clinician") {
      return "Clinician profile";
    }

    if (form.entityKind === "connector") {
      return "Integration";
    }

    if (form.entityKind === "department") {
      return "Department";
    }

    return "Care team";
  };

  const resolveAdminCrudActionLabel = (form: AdminCrudFormState): string => {
    const entityLabel = humanizeWorkspaceKey(form.entityKind).toLowerCase();

    if (form.mode === "retire") {
      return `Retire ${entityLabel}`;
    }

    return form.mode === "create"
      ? `Create ${entityLabel}`
      : `Update ${entityLabel}`;
  };

  const renderAdminCrudFormPanel = () => {
    if (!adminCrudForm) {
      return null;
    }

    const titleText = resolveAdminCrudActionLabel(adminCrudForm);

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminControlRadius,
            borderWidth: 0,
            gap: adminConfigurationDialog.sectionGap,
            padding: adminModalDensity.cardPadding,
          },
        ]}
      >
        {renderAdminModalHeader({
          eyebrowText: resolveAdminCrudSurfaceLabel(adminCrudForm),
          onClose: () => setAdminCrudForm(null),
          subtitleText:
            adminCrudForm.mode === "retire"
              ? "This will retire the selected record."
              : "Keep access, care-team routing, and operational status aligned before saving.",
          titleText: titleText,
        })}

        {adminCrudForm.mode === "retire" ? (
          <View
            style={[
              styles.workspaceGrid,
              { gap: adminConfigurationDialog.fieldGap },
            ]}
          >
            {renderAdminCrudTextField(
              "Record key",
              adminCrudForm.targetKey,
              "targetKey",
              true,
            )}
            {renderAdminCrudTextField(
              "Display name",
              adminCrudForm.displayName,
              "displayName",
              true,
            )}
          </View>
        ) : adminCrudForm.entityKind === "patient" ? (
          <View
            style={[
              styles.workspaceGrid,
              {
                flexDirection: isMediumLayout ? "row" : "column",
                flexWrap: "wrap",
                gap: adminConfigurationDialog.fieldGap,
              },
            ]}
          >
            {renderAdminCrudTextField(
              "Display name",
              adminCrudForm.displayName,
              "displayName",
            )}
            {renderAdminCrudTextField(
              "Given name",
              adminCrudForm.givenName,
              "givenName",
            )}
            {renderAdminCrudTextField(
              "Family name",
              adminCrudForm.familyName,
              "familyName",
            )}
            {adminCrudForm.mode === "update"
              ? renderAdminCrudTextField(
                  "System patient reference",
                  adminCrudForm.patientId,
                  "patientId",
                  true,
                )
              : null}
            {renderAdminCrudTextField(
              adminCrudForm.mode === "create"
                ? "MRN (optional)"
                : "MRN / identifier",
              adminCrudForm.identifier,
              "identifier",
            )}
            {renderAdminCrudTextField(
              "Date of birth",
              adminCrudForm.dateOfBirth,
              "dateOfBirth",
            )}
            {renderAdminCrudSelectField(
              "Sex",
              adminCrudForm.sex,
              [
                { labelText: "Female", value: "female" },
                { labelText: "Male", value: "male" },
              ],
              "sex",
              "Not recorded",
            )}
            {renderAdminCrudSelectField(
              "Leading clinician",
              adminCrudForm.leadingClinicianUserId,
              clinicianUserIdOptions,
              "leadingClinicianUserId",
              "Unassigned",
            )}
            {renderAdminCrudTextField(
              "Program",
              adminCrudForm.programLabel,
              "programLabel",
            )}
            {renderAdminCrudTextField(
              "Primary phone",
              adminCrudForm.phoneNumber,
              "phoneNumber",
            )}
            {renderAdminCrudTextField(
              "Secondary phone",
              adminCrudForm.secondaryPhoneNumber,
              "secondaryPhoneNumber",
            )}
            {renderAdminCrudTextField(
              "Home phone",
              adminCrudForm.homePhoneNumber,
              "homePhoneNumber",
            )}
            {renderAdminCrudTextField(
              "Work phone",
              adminCrudForm.workPhoneNumber,
              "workPhoneNumber",
            )}
            {renderAdminCrudTextField(
              "Emergency contact phone",
              adminCrudForm.emergencyContactPhone,
              "emergencyContactPhone",
            )}
            {renderAdminCrudTextField(
              "Alternate emergency phone",
              adminCrudForm.alternateEmergencyContactPhone,
              "alternateEmergencyContactPhone",
            )}
            {renderAdminCrudTextField(
              "Email address",
              adminCrudForm.emailAddress,
              "emailAddress",
            )}
            {renderAdminCrudTextField(
              "Contact window",
              adminCrudForm.contactWindowText,
              "contactWindowText",
            )}
            {renderAdminCrudTextField(
              "Street address",
              adminCrudForm.addressStreet,
              "addressStreet",
            )}
            {renderAdminCrudTextField(
              "City",
              adminCrudForm.addressCity,
              "addressCity",
            )}
            {renderAdminCrudTextField(
              "State",
              adminCrudForm.addressState,
              "addressState",
            )}
            {renderAdminCrudTextField(
              "ZIP / postal code",
              adminCrudForm.addressZip,
              "addressZip",
            )}
            {renderAdminCrudSelectField(
              "Site",
              adminCrudForm.siteId,
              siteCodeOptions,
              "siteId",
              "Unassigned",
            )}
            {renderAdminCrudStatusPicker(
              "Status",
              adminCrudForm.status,
              adminPatientStatusOptions,
            )}
            {renderAdminCrudStatusPicker(
              "Onboarding",
              adminCrudForm.onboardingState,
              ["open", "complete"],
              "onboardingState",
            )}
          </View>
        ) : adminCrudForm.entityKind === "clinician" ? (
          <View
            style={[
              styles.workspaceGrid,
              {
                flexDirection: isMediumLayout ? "row" : "column",
                flexWrap: "wrap",
                gap: adminConfigurationDialog.fieldGap,
              },
            ]}
          >
            {renderAdminCrudTextField(
              "Display name",
              adminCrudForm.displayName,
              "displayName",
            )}
            {adminCrudForm.mode === "update"
              ? renderAdminCrudTextField(
                  "Clinician ID",
                  adminCrudForm.clinicianUserId,
                  "clinicianUserId",
                  true,
                )
              : null}
            {renderAdminCrudSelectField(
              "Department",
              adminCrudForm.departmentCode,
              departmentCodeOptions,
              "departmentCode",
              "Unassigned",
            )}
            {renderAdminCrudSelectField(
              "Team",
              adminCrudForm.teamKey,
              teamKeyOptions,
              "teamKey",
              "Unassigned",
            )}
            {renderAdminCrudTextField(
              "Email address",
              adminCrudForm.emailAddress,
              "emailAddress",
            )}
            {renderAdminCrudTextField(
              "Phone number",
              adminCrudForm.phoneNumber,
              "phoneNumber",
            )}
            {adminCrudForm.mode === "update"
              ? renderAdminCrudTextField(
                  "Login subject",
                  adminCrudForm.previewProfileKey ||
                    resolveAdminClinicianPreviewProfileKey(adminCrudForm),
                  "previewProfileKey",
                  true,
                )
              : null}
            {renderAdminCrudTextField(
              "Login email",
              adminCrudForm.testLoginEmail,
              "testLoginEmail",
            )}
            {renderAdminCrudTextField(
              "Temporary password",
              adminCrudForm.testLoginPassword,
              "testLoginPassword",
            )}
            {renderAdminClinicianLifecycleControls(adminCrudForm)}
          </View>
        ) : adminCrudForm.entityKind === "department" ? (
          <View
            style={[
              styles.workspaceGrid,
              {
                flexDirection: isMediumLayout ? "row" : "column",
                flexWrap: "wrap",
                gap: adminConfigurationDialog.fieldGap,
              },
            ]}
          >
            {renderAdminCrudTextField(
              "Department name",
              adminCrudForm.displayName,
              "displayName",
            )}
            {renderAdminCrudTextField(
              "Department code",
              adminCrudForm.departmentCode,
              "departmentCode",
              adminCrudForm.mode === "update",
            )}
            {renderAdminCrudSelectField(
              "Site code",
              adminCrudForm.siteCode,
              siteCodeOptions,
              "siteCode",
              "All sites",
            )}
            {renderAdminCrudStatusPicker(
              "Status",
              adminCrudForm.status,
              adminOperationalStatusOptions,
            )}
          </View>
        ) : adminCrudForm.entityKind === "connector" ? (
          <View
            style={[
              styles.workspaceGrid,
              {
                flexDirection: isMediumLayout ? "row" : "column",
                flexWrap: "wrap",
                gap: adminConfigurationDialog.fieldGap,
              },
            ]}
          >
            {renderAdminCrudTextField(
              "Connector name",
              adminCrudForm.displayName,
              "displayName",
            )}
            {adminCrudForm.mode === "update"
              ? renderAdminCrudTextField(
                  "Connector id",
                  adminCrudForm.connectorId,
                  "connectorId",
                  true,
                )
              : null}
            {renderAdminCrudTextField(
              "Integration key",
              adminCrudForm.sourceSystemKey,
              "sourceSystemKey",
              adminCrudForm.mode === "update",
            )}
            {renderAdminCrudTextField(
              "Integration pack",
              adminCrudForm.researchPackSlug,
              "researchPackSlug",
            )}
            {renderAdminCrudTextField(
              "Record notes",
              adminCrudForm.metadataSummary,
              "metadataSummary",
            )}
            {renderAdminCrudSelectField(
              "Site code",
              adminCrudForm.siteCode,
              siteCodeOptions,
              "siteCode",
              "All sites",
            )}
            {renderAdminCrudSelectField(
              "Department",
              adminCrudForm.departmentCode,
              departmentCodeOptions,
              "departmentCode",
              "Unassigned",
            )}
            {renderAdminCrudSelectField(
              "Team",
              adminCrudForm.teamKey,
              connectorTeamKeyOptions,
              "teamKey",
              "Unassigned",
            )}
            {renderAdminCrudStatusPicker(
              "Family",
              adminCrudForm.connectorFamily,
              adminConnectorFamilyOptions,
              "connectorFamily",
            )}
            {renderAdminCrudStatusPicker(
              "Sync mode",
              adminCrudForm.sourceAuthorityMode,
              adminConnectorSourceAuthorityOptions,
              "sourceAuthorityMode",
            )}
            {renderAdminCrudStatusPicker(
              "Status",
              adminCrudForm.status,
              adminConnectorStatusOptions,
            )}
          </View>
        ) : (
          <View
            style={[
              styles.workspaceGrid,
              {
                flexDirection: isMediumLayout ? "row" : "column",
                flexWrap: "wrap",
                gap: adminConfigurationDialog.fieldGap,
              },
            ]}
          >
            {renderAdminCrudTextField(
              "Team name",
              adminCrudForm.displayName,
              "displayName",
            )}
            {renderAdminCrudTextField(
              "Team key",
              adminCrudForm.teamKey,
              "teamKey",
              adminCrudForm.mode === "update",
            )}
            {renderAdminCrudSelectField(
              "Department",
              adminCrudForm.departmentCode,
              departmentCodeOptions,
              "departmentCode",
              "Unassigned",
            )}
            {renderAdminCrudStatusPicker(
              "Status",
              adminCrudForm.status,
              adminOperationalStatusOptions,
            )}
          </View>
        )}

        <View
          style={[
            styles.patientRowFooter,
            {
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              gap: adminModalDensity.actionGap,
              justifyContent: "flex-end",
              marginTop: tokens.spacing.xs,
              paddingTop: tokens.spacing.sm,
            },
          ]}
        >
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminCommandSubmitting,
            labelText: "Cancel",
            onPress: () => {
              setAdminCrudForm(null);
              setAdminOpsNotice("Changes were not saved.");
            },
            prefixText: "",
            tone: "surface",
          })}
          {renderAdminPrimaryButton({
            labelText:
              isAdminCommandSubmitting
                ? "Saving"
                : resolveAdminCrudActionLabel(adminCrudForm),
            density: "compact",
            disabled: isAdminCommandSubmitting,
            onPress: submitAdminCrudForm,
            prefixText: "",
            tone: adminCrudForm.mode === "retire" ? "danger" : "primary",
          })}
        </View>
      </View>
    );
  };

  const renderAdminCrudFormRail = () => {
    if (!adminCrudForm) {
      return null;
    }

    const entityLabel = humanizeWorkspaceKey(adminCrudForm.entityKind);
    const targetLabel =
      adminCrudForm.displayName.trim() ||
      ("targetKey" in adminCrudForm ? adminCrudForm.targetKey.trim() : "") ||
      entityLabel;
    const statusText =
      "status" in adminCrudForm
        ? humanizeWorkspaceKey(adminCrudForm.status)
        : resolveAdminCrudActionLabel(adminCrudForm);
    const overviewRows = [
      { labelText: "Status", valueText: statusText },
      {
        labelText: "Action",
        valueText: resolveAdminCrudActionLabel(adminCrudForm),
      },
    ];
    const extraRows =
      adminCrudForm.mode === "retire"
        ? [
            {
              labelText: "Audit key",
              valueText: adminCrudForm.targetKey || "Missing target key",
            },
          ]
        : adminCrudForm.entityKind === "patient"
          ? [
              ...(adminCrudForm.mode === "update"
                ? [
                    {
                      labelText: "System reference",
                      valueText: adminCrudForm.patientId || "Not recorded",
                    },
                  ]
                : []),
              {
                labelText: "Date of birth",
                valueText: adminCrudForm.dateOfBirth || "Not recorded",
              },
              {
                labelText: "Leading clinician",
                valueText:
                  clinicianUserIdOptions.find(
                    (option) =>
                      option.value === adminCrudForm.leadingClinicianUserId,
                  )?.labelText ||
                  adminCrudForm.leadingClinicianUserId ||
                  "Unassigned",
              },
              {
                labelText: "Phone",
                valueText: adminCrudForm.phoneNumber || "No phone on record",
              },
            ]
          : adminCrudForm.entityKind === "clinician"
          ? [
              ...(adminCrudForm.mode === "update"
                ? [
                    {
                      labelText: "Clinician ID",
                      valueText:
                        adminCrudForm.clinicianUserId || "Not recorded",
                    },
                    {
                      labelText: "Login subject",
                      valueText:
                        adminCrudForm.previewProfileKey ||
                        resolveAdminClinicianPreviewProfileKey(adminCrudForm),
                    },
                  ]
                : []),
              {
                labelText: "Department",
                valueText: adminCrudForm.departmentCode || "Unassigned",
              },
              {
                labelText: "Team",
                valueText: adminCrudForm.teamKey || "Unassigned",
              },
            ]
          : adminCrudForm.entityKind === "connector"
            ? [
                {
                  labelText: "Source system",
                  valueText:
                    adminCrudForm.sourceSystemKey || "Choose integration source",
                },
                {
                  labelText: "Family",
                  valueText: humanizeWorkspaceKey(
                    adminCrudForm.connectorFamily,
                  ),
                },
                {
                  labelText: "Authority",
                  valueText: humanizeWorkspaceKey(
                    adminCrudForm.sourceAuthorityMode,
                  ),
                },
              ]
            : adminCrudForm.entityKind === "department"
              ? [
                  {
                    labelText: "Code",
                    valueText:
                      adminCrudForm.departmentCode ||
                      (adminCrudForm.mode === "create"
                        ? "Enter department code"
                        : "Not recorded"),
                  },
                  {
                    labelText: "Site",
                    valueText: adminCrudForm.siteCode || "All sites",
                  },
                ]
              : [
                  {
                    labelText: "Team key",
                    valueText:
                      adminCrudForm.teamKey ||
                      (adminCrudForm.mode === "create"
                        ? "Enter team key"
                        : "Not recorded"),
                  },
                  {
                    labelText: "Department",
                    valueText: adminCrudForm.departmentCode || "Unassigned",
                  },
                ];

    return renderAdminModalRail({
      avatarText: entityLabel.slice(0, 3),
      eyebrowText: resolveAdminCrudSurfaceLabel(adminCrudForm),
      glanceRows: [
        {
          labelText: "Next step",
          valueText:
            adminCrudForm.mode === "retire"
              ? "Confirm retire"
              : "Review and save",
        },
        {
          labelText: "Record details",
          valueText:
            adminCrudForm.mode === "create" ? "New record" : "Existing record",
        },
      ],
      overviewRows: [...overviewRows, ...extraRows],
      pills:
        adminCrudForm.mode === "retire"
          ? ["Retire"]
          : "status" in adminCrudForm
            ? [adminCrudForm.status]
            : [resolveAdminCrudActionLabel(adminCrudForm)],
      titleText: targetLabel,
    });
  };

  const renderAdminCrudFormModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: "Admin record editor",
      children: renderAdminCrudFormPanel(),
      contentPadding: 0,
      maxWidth:
        adminCrudForm == null
          ? 0
          : adminCrudForm.mode === "retire"
            ? 760
            : adminCrudForm.entityKind === "connector"
              ? 760
              : adminCrudForm.entityKind === "patient"
                ? 680
              : adminCrudForm.entityKind === "clinician"
                ? 680
                : 640,
      onClose: () => {
        if (isAdminCommandSubmitting) {
          return;
        }

        setAdminCrudForm(null);
        setAdminOpsNotice("Changes were not saved.");
      },
      rail: null,
      visible: adminCrudForm != null,
    });

  const renderAdminPatientLifecycleModal = () => {
    if (!adminPatientLifecycleDraft) {
      return renderAdminModalFrame({
        accessibilityLabel: "Patient lifecycle confirmation",
        children: null,
        maxWidth: 0,
        onClose: () => undefined,
        visible: false,
      });
    }

    const isArchive = adminPatientLifecycleDraft.action === "archive";
    const updateDraft = (
      patch: Partial<
        Pick<
          AdminPatientLifecycleDraft,
          "notes" | "reasonKey" | "restoreStatus"
        >
      >,
    ): void => {
      setAdminPatientLifecycleDraft((current) =>
        current == null ? current : { ...current, ...patch },
      );
    };
    const closeLifecycleDialog = (): void => {
      if (isAdminCommandSubmitting) {
        return;
      }

      setAdminPatientLifecycleDraft(null);
      setAdminOpsNotice("Patient lifecycle was not changed.");
    };

    return renderAdminModalFrame({
      accessibilityLabel: isArchive
        ? "Archive patient confirmation"
        : "Restore patient confirmation",
      children: (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: theme.borderStrong,
              borderRadius: adminControlRadius,
              borderWidth: 0,
              gap: adminConfigurationDialog.sectionGap,
              padding: adminModalDensity.cardPadding,
            },
          ]}
        >
          {renderAdminModalHeader({
            eyebrowText: "Patient lifecycle",
            onClose: closeLifecycleDialog,
            subtitleText: isArchive
              ? "Archiving removes the patient from active workflows without permanently deleting the record."
              : "Restoring returns the patient to the selected operational state.",
            titleText: `${
              isArchive ? "Archive" : "Restore"
            } ${adminPatientLifecycleDraft.displayName}`,
          })}

          {isArchive
            ? renderAdminModalSelectField({
                fieldKey: "patient-archive-reason",
                fullWidth: true,
                labelText: "Archive reason",
                onChange: (reasonKey) => updateDraft({ reasonKey }),
                options: adminPatientArchiveReasonOptions,
                value: adminPatientLifecycleDraft.reasonKey,
              })
            : renderAdminModalSelectField({
                fieldKey: "patient-restore-state",
                fullWidth: true,
                labelText: "Restore as",
                onChange: (restoreStatus) =>
                  updateDraft({
                    restoreStatus:
                      restoreStatus as AdminPatientLifecycleDraft["restoreStatus"],
                  }),
                options: [
                  { labelText: "Active", value: "active" },
                  { labelText: "Draft", value: "draft" },
                  { labelText: "Inactive", value: "inactive" },
                ],
                value: adminPatientLifecycleDraft.restoreStatus,
              })}

          <View style={[styles.listGroup, { gap: 5, width: "100%" }]}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.metaLabelFontSize,
                  textTransform: "uppercase",
                },
              ]}
            >
              Notes (optional)
            </Text>
            <TextInput
              editable={!isAdminCommandSubmitting}
              multiline
              numberOfLines={4}
              onChangeText={(notes) => updateDraft({ notes })}
              placeholder={
                isArchive
                  ? "Add context for the audit history"
                  : "Add context for this restoration"
              }
              placeholderTextColor={theme.textTertiary}
              style={[
                styles.searchInput,
                {
                  backgroundColor: adminFigmaPalette.field,
                  borderColor: theme.borderSubtle,
                  borderRadius: adminOperationalLayout.controlRadius,
                  borderWidth: 1,
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.fieldFontSize,
                  minHeight: 96,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  textAlignVertical: "top",
                },
              ]}
              value={adminPatientLifecycleDraft.notes}
            />
          </View>

          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {`This confirmation targets lifecycle version ${adminPatientLifecycleDraft.expectedLifecycleVersion}. If another admin changes the patient first, the request will stop safely and must be reviewed again.`}
          </Text>

          <View
            style={[
              styles.patientRowFooter,
              {
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                gap: adminModalDensity.actionGap,
                justifyContent: "flex-end",
                paddingTop: tokens.spacing.sm,
              },
            ]}
          >
            {renderAdminPrimaryButton({
              density: "compact",
              disabled: isAdminCommandSubmitting,
              labelText: "Cancel",
              onPress: closeLifecycleDialog,
              prefixText: "",
              tone: "surface",
            })}
            {renderAdminPrimaryButton({
              density: "compact",
              disabled:
                isAdminCommandSubmitting ||
                (isArchive &&
                  adminPatientLifecycleDraft.reasonKey.trim().length === 0),
              labelText: isAdminCommandSubmitting
                ? isArchive
                  ? "Archiving"
                  : "Restoring"
                : isArchive
                  ? "Archive patient"
                  : "Restore patient",
              onPress: submitAdminPatientLifecycleCommand,
              prefixText: "",
              tone: isArchive ? "danger" : "primary",
            })}
          </View>
        </View>
      ),
      contentPadding: 0,
      maxWidth: 620,
      onClose: closeLifecycleDialog,
      visible: true,
    });
  };

  const renderAdminFeatureFlagDraftPanel = () => {
    if (!adminFeatureFlagDraft) {
      return null;
    }

    const isSystemFlag = adminFeatureFlagDraft.flagKind === "system";

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminControlRadius,
            borderWidth: 0,
            gap: adminConfigurationDialog.sectionGap,
            padding: adminModalDensity.cardPadding,
          },
        ]}
      >
        {renderAdminModalHeader({
          eyebrowText: "Admin flag",
          onClose: () => setAdminFeatureFlagDraft(null),
          subtitleText:
            "Creates a live configuration row after confirmation.",
          titleText: "Create flag",
        })}

        <View
          style={[
            styles.workspaceGrid,
            {
              flexDirection: isMediumLayout ? "row" : "column",
              flexWrap: "wrap",
              gap: adminConfigurationDialog.fieldGap,
            },
          ]}
        >
          {renderAdminFeatureFlagTextField(
            "Flag key",
            adminFeatureFlagDraft.flagKey,
            "flagKey",
          )}
          {renderAdminFeatureFlagSelectField(
            "Flag type",
            adminFeatureFlagDraft.flagKind,
            [
              { labelText: "Feature flag", value: "feature" },
              { labelText: "System flag", value: "system" },
            ],
            "flagKind",
          )}
          {renderAdminFeatureFlagSelectField(
            "State",
            adminFeatureFlagDraft.enabledState,
            [
              { labelText: "Enabled", value: "enabled" },
              { labelText: "Disabled", value: "disabled" },
            ],
            "enabledState",
          )}
          {renderAdminFeatureFlagTextField(
            "Variant",
            isSystemFlag ? "system" : adminFeatureFlagDraft.flagVariant,
            "flagVariant",
            isSystemFlag,
          )}
          {renderAdminFeatureFlagSelectField(
            "Scope",
            isSystemFlag ? "tenant" : adminFeatureFlagDraft.scope,
            featureFlagScopeOptions,
            "scope",
            undefined,
            isSystemFlag,
          )}
          {adminFeatureFlagDraft.scope === "tenant" || isSystemFlag
            ? renderAdminFeatureFlagTextField(
                "Scope key",
                "All sites",
                "scopeKey",
                true,
              )
            : renderAdminFeatureFlagSelectField(
                "Scope key",
                adminFeatureFlagDraft.scopeKey,
                featureFlagScopeKeyOptions,
                "scopeKey",
                "Choose scope key",
              )}
          {renderAdminFeatureFlagTextField(
            "Rollout note",
            adminFeatureFlagDraft.rolloutNote,
            "rolloutNote",
          )}
        </View>

        {isAdminOutboundCallsLoading || hasAdminOutboundCallsError ? (
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: hasAdminOutboundCallsError
                  ? adminFigmaPalette.dangerSoft
                  : adminFigmaPalette.panelSubtle,
                borderColor: hasAdminOutboundCallsError
                  ? theme.statusChip.danger.border
                  : adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: hasAdminOutboundCallsError
                    ? theme.statusChip.danger.text
                    : adminFigmaPalette.inkMuted,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminCompact.bodyFontSize,
                  fontWeight: adminTypography.strongWeight,
                  lineHeight: adminCompact.bodyLineHeight,
                },
              ]}
            >
              {hasAdminOutboundCallsError
                ? "Outbound call data could not be loaded. Last known configuration is shown until the API is available."
                : "Refreshing outbound call configuration..."}
            </Text>
          </View>
        ) : null}

        <View
          style={[
            styles.patientRowFooter,
            {
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              gap: adminModalDensity.actionGap,
              justifyContent: "flex-end",
              marginTop: tokens.spacing.xs,
              paddingTop: tokens.spacing.sm,
            },
          ]}
        >
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminCommandSubmitting,
            labelText: "Cancel",
            onPress: () => {
              setAdminFeatureFlagDraft(null);
              setAdminOpsNotice("Flag was not saved.");
            },
            prefixText: "",
            tone: "surface",
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminCommandSubmitting,
            labelText: isAdminCommandSubmitting ? "Saving" : "Save flag",
            onPress: submitAdminFeatureFlagDraft,
            prefixText: "",
            tone: "primary",
          })}
        </View>
      </View>
    );
  };

  const renderAdminFeatureFlagDraftRail = () => {
    if (!adminFeatureFlagDraft) {
      return null;
    }

    const flagKey = adminFeatureFlagDraft.flagKey.trim() || "New flag";
    const flagKind = humanizeWorkspaceKey(adminFeatureFlagDraft.flagKind);
    const scopeLabel =
      adminFeatureFlagDraft.flagKind === "system"
        ? "All sites"
        : humanizeWorkspaceKey(adminFeatureFlagDraft.scope);

    return renderAdminModalRail({
      avatarText: "FLAG",
      eyebrowText: "Flags",
      glanceRows: [
        { labelText: "Action", valueText: "Set configuration" },
        { labelText: "Record details", valueText: "Saved after confirmation" },
      ],
      overviewRows: [
        { labelText: "Flag key", valueText: flagKey },
        { labelText: "Type", valueText: flagKind },
        { labelText: "Scope", valueText: scopeLabel },
        {
          labelText: "Scope key",
          valueText:
            adminFeatureFlagDraft.scopeKey.trim() ||
            (adminFeatureFlagDraft.scope === "tenant"
              ? "All sites"
              : "Choose a scope key"),
        },
      ],
      pills: [adminFeatureFlagDraft.enabledState, flagKind],
      titleText: flagKey,
    });
  };

  const renderAdminFeatureFlagDraftModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: "Admin flag editor",
      children: renderAdminFeatureFlagDraftPanel(),
      contentPadding: 0,
      maxWidth: 1040,
      onClose: () => {
        if (isAdminCommandSubmitting) {
          return;
        }

        setAdminFeatureFlagDraft(null);
        setAdminOpsNotice("Flag was not saved.");
      },
      rail: null,
      visible: adminFeatureFlagDraft != null,
    });

  const renderAdminAiModelEditorTextField = (input: {
    fieldName: string;
    labelText: string;
    multiline?: boolean;
    value: string;
  }) => (
    <View
      key={input.fieldName}
      style={[
        styles.listGroup,
        {
          flexBasis: input.multiline || !isMediumLayout ? "100%" : "48%",
          flexGrow: 1,
          gap: adminConfigurationDialog.fieldLabelGap,
          minWidth: input.multiline || !isMediumLayout ? "100%" : 220,
        },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            textTransform: "uppercase",
          },
        ]}
      >
        {input.labelText}
      </Text>
      <TextInput
        editable={!isAdminCommandSubmitting}
        multiline={input.multiline}
        numberOfLines={input.multiline ? 7 : 1}
        onChangeText={(nextValue) =>
          updateAdminAiModelArtifactEditorField(input.fieldName, nextValue)
        }
        placeholder={input.labelText}
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: adminFigmaPalette.field,
            borderColor: theme.borderSubtle,
            borderRadius: adminOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: adminModalDensity.fieldFontSize,
            lineHeight: input.multiline ? 20 : undefined,
            minHeight: input.multiline
              ? 148
              : adminConfigurationDialog.controlHeight,
            paddingHorizontal: 12,
            paddingVertical: input.multiline ? 10 : 8,
            textAlignVertical: input.multiline ? "top" : "center",
          },
        ]}
        value={input.value}
      />
    </View>
  );

  const renderAdminAiModelArtifactEditorPanel = () => {
    if (!adminAiModelArtifactEditor) {
      return null;
    }

    const isPrompt = adminAiModelArtifactEditor.kind === "prompt";
    const titleText = isPrompt
      ? adminAiModelArtifactEditor.mode === "create"
        ? "Add response guidance"
        : "Edit response guidance"
      : adminAiModelArtifactEditor.mode === "create"
        ? "Add knowledge source"
        : "Edit knowledge source";
    const surfaceText = isPrompt ? "Response guidance" : "Knowledge source";
    const contentText = isPrompt
      ? adminAiModelArtifactEditor.promptText
      : adminAiModelArtifactEditor.sourceText;
    const contentPreviewText = resolveAdminProductCopyText(
      contentText,
      isPrompt
        ? "Approved response guidance for care-team review."
        : "Approved clinical knowledge source.",
    );
    const contentPreview =
      contentPreviewText.length > 180
        ? `${contentPreviewText.slice(0, 180)}...`
        : contentPreviewText;

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminControlRadius,
            borderWidth: 0,
            gap: adminConfigurationDialog.sectionGap,
            padding: adminModalDensity.cardPadding,
          },
        ]}
      >
        {renderAdminModalHeader({
          eyebrowText: "AI model policy",
          onClose: () => setAdminAiModelArtifactEditor(null),
          subtitleText: isPrompt
            ? "Author approved guidance for clinician and patient-facing assistants."
            : "Register approved source material for care-team and patient-facing answers.",
          titleText,
        })}

        <View
          style={[
            styles.workspaceGrid,
            {
              flexDirection: isMediumLayout ? "row" : "column",
              flexWrap: "wrap",
              gap: adminConfigurationDialog.fieldGap,
            },
          ]}
        >
          {renderAdminModalSelectField({
            fieldKey: "modelKey",
            labelText: "Model",
            onChange: (nextValue) =>
              updateAdminAiModelArtifactEditorField("modelKey", nextValue),
            options: [
              { labelText: "Chat", value: "chat" },
              { labelText: "Voice", value: "voice" },
              { labelText: "Shared", value: "shared" },
            ],
            value: adminAiModelArtifactEditor.modelKey,
          })}
          {isPrompt ? (
            <>
              {renderAdminAiModelEditorTextField({
                fieldName: "labelText",
                labelText: "Guidance label",
                value: adminAiModelArtifactEditor.labelText,
              })}
              {renderAdminAiModelEditorTextField({
                fieldName: "purposeText",
                labelText: "Purpose",
                value: adminAiModelArtifactEditor.purposeText,
              })}
              {renderAdminAiModelEditorTextField({
                fieldName: "promptText",
                labelText: "Response guidance",
                multiline: true,
                value: adminAiModelArtifactEditor.promptText,
              })}
            </>
          ) : (
            <>
              {renderAdminAiModelEditorTextField({
                fieldName: "titleText",
                labelText: "Source title",
                value: adminAiModelArtifactEditor.titleText,
              })}
              {renderAdminAiModelEditorTextField({
                fieldName: "summaryText",
                labelText: "Summary",
                value: adminAiModelArtifactEditor.summaryText,
              })}
              {renderAdminAiModelEditorTextField({
                fieldName: "sourceText",
                labelText: "Knowledge text",
                multiline: true,
                value: adminAiModelArtifactEditor.sourceText,
              })}
            </>
          )}
        </View>

        <View
          style={[
            styles.patientRowFooter,
            {
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              gap: adminModalDensity.actionGap,
              justifyContent: "flex-end",
              marginTop: tokens.spacing.xs,
              paddingTop: tokens.spacing.sm,
            },
          ]}
        >
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminCommandSubmitting,
            labelText: "Cancel",
            onPress: () => {
              setAdminAiModelArtifactEditor(null);
              setAdminOpsNotice("AI model policy change was not saved.");
            },
            prefixText: "",
            tone: "surface",
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminCommandSubmitting,
            labelText: isAdminCommandSubmitting
              ? "Saving"
              : isPrompt
                ? "Save guidance"
                : "Save source",
            onPress: submitAdminAiModelArtifactEditor,
            prefixText: "",
            tone: "primary",
          })}
        </View>
      </View>
    );
  };

  const renderAdminAiModelArtifactEditorRail = () => {
    if (!adminAiModelArtifactEditor) {
      return null;
    }

      const isPrompt = adminAiModelArtifactEditor.kind === "prompt";
      const primaryText = isPrompt
      ? adminAiModelArtifactEditor.labelText.trim() || "Response guidance"
      : adminAiModelArtifactEditor.titleText.trim() || "Knowledge source";

    return renderAdminModalRail({
      avatarText: "AI",
      eyebrowText: "AI policy",
      glanceRows: [
        { labelText: "Model", valueText: humanizeWorkspaceKey(adminAiModelArtifactEditor.modelKey) },
        {
          labelText: "Status",
          valueText:
            adminAiModelArtifactEditor.mode === "create"
              ? "New record"
              : "Editing existing record",
        },
      ],
      overviewRows: [
        { labelText: "Surface", valueText: isPrompt ? "Response guidance" : "Knowledge source" },
        {
          labelText: "Save state",
          valueText:
            adminAiModelArtifactEditor.mode === "create"
              ? "Active after save"
              : "Updates current policy",
        },
      ],
      pills: [
        humanizeWorkspaceKey(adminAiModelArtifactEditor.modelKey),
        isPrompt ? "Guidance" : "Knowledge source",
      ],
      subtitleText: "Review approved content before saving it for the care team.",
      titleText: primaryText,
    });
  };

  const renderAdminAiModelArtifactEditorModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: "AI model policy editor",
      children: renderAdminAiModelArtifactEditorPanel(),
      contentPadding: 0,
      maxWidth: 1040,
      onClose: () => {
        if (isAdminCommandSubmitting) {
          return;
        }

        setAdminAiModelArtifactEditor(null);
        setAdminOpsNotice("AI model policy change was not saved.");
      },
      rail: null,
      visible: adminAiModelArtifactEditor != null,
    });

  // Single source of inline field-help copy (§6c). Keyed by field label so the
  // hint is wired in automatically wherever that label is rendered.
  const questionnaireFieldHelp: Record<string, string> = {
    "Question lead-in":
      'A short phrase the agent speaks before the question (e.g. "Now a few questions about your medications").',
    "Answer hint":
      'How the agent tells the patient to answer (e.g. "say yes or no").',
    "Answer hint behavior":
      "Controls when the agent includes the answer hint: default first prompts, always, hidden, or none.",
    "Clinician review label":
      "The reason tag a clinician sees in the review queue when this question is flagged.",
    "Review keywords":
      "If a spoken answer contains any of these words, it is flagged for clinician review.",
    "Plain-language definition":
      "A lay explanation the agent gives if the patient does not understand the question.",
    "Follow-up of":
      "Ask this question only after an earlier gate question. Follow-up questions appear indented under their gate question.",
    "Show when":
      "The gate answer that reveals this follow-up question during the call.",
    "Equals value":
      "The exact choice value that reveals this follow-up (matches the gate answer).",
  };
  const renderFieldHelpHint = (helpText: string) => {
    const hintRef = createRef<View>();
    const openHelp = () => {
      hintRef.current?.measureInWindow((x, y, _width, height) => {
        setActiveFieldHelp({
          iconBottom: y + height,
          iconTop: y,
          left: x,
          text: helpText,
        });
      });
    };

    return (
      <Pressable
        accessibilityHint={helpText}
        accessibilityLabel="Field help"
        accessibilityRole="button"
        onHoverIn={openHelp}
        onHoverOut={() => setActiveFieldHelp(null)}
        onPress={() => {
          if (activeFieldHelp?.text === helpText) {
            setActiveFieldHelp(null);
          } else {
            openHelp();
          }
        }}
        ref={hintRef}
      >
        {(state) => {
          const stateRecord =
            state && typeof state === "object"
              ? (state as unknown as Record<string, unknown>)
              : {};
          const active =
            stateRecord.hovered === true ||
            activeFieldHelp?.text === helpText;

          return (
            <View
              style={{
                alignItems: "center",
                borderColor: active ? theme.accentStrong : theme.borderStrong,
                borderRadius: 999,
                borderWidth: 1,
                height: 14,
                justifyContent: "center",
                width: 14,
              }}
            >
              <Text
                style={{
                  color: active ? theme.accentStrong : theme.textTertiary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 9,
                  fontWeight: adminTypography.strongWeight,
                  lineHeight: 11,
                }}
              >
                ?
              </Text>
            </View>
          );
        }}
      </Pressable>
    );
  };
  const renderAdminOutboundQuestionnaireRevisionTextField = (
    labelText: string,
    value: string,
    onChangeText: (value: string) => void,
    multiline = false,
  ) => (
    <View
      style={[
        styles.listGroup,
        multiline
          ? {
              gap: adminConfigurationDialog.fieldLabelGap,
              width: "100%",
            }
          : adminCrudFieldLayoutStyle,
      ]}
    >
      <View
        style={{
          alignItems: "center",
          flexDirection: "row",
          gap: 5,
          zIndex: 1,
        }}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: adminModalDensity.metaLabelFontSize,
              textTransform: "uppercase",
            },
          ]}
        >
          {labelText}
        </Text>
        {questionnaireFieldHelp[labelText]
          ? renderFieldHelpHint(questionnaireFieldHelp[labelText])
          : null}
      </View>
      <TextInput
        accessibilityLabel={labelText}
        editable={!isAdminOutboundCommandSubmitting}
        multiline={multiline}
        onChangeText={onChangeText}
        placeholder={labelText}
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: adminModalDensity.fieldFontSize,
            lineHeight: multiline ? 20 : undefined,
            maxHeight: multiline ? 176 : undefined,
            minHeight: multiline
              ? 66
              : adminConfigurationDialog.controlHeight,
            paddingHorizontal: 12,
            paddingVertical: 8,
            shadowColor: theme.accentStrong,
            shadowOffset: { height: 2, width: 0 },
            shadowOpacity: 0.04,
            shadowRadius: 8,
            textAlignVertical: multiline ? "top" : "center",
          },
        ]}
        value={value}
      />
    </View>
  );

  const renderAdminOutboundQuestionnaireRevisionToggle = (input: {
    checked: boolean;
    labelText: string;
    descriptionText?: string;
    onPress: () => void;
  }) => {
    const checked = input.checked;

    return (
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ checked }}
        disabled={isAdminOutboundCommandSubmitting}
        key={input.labelText}
        onPress={input.onPress}
        style={(state) => {
          const stateRecord =
            state && typeof state === "object"
              ? (state as unknown as Record<string, unknown>)
              : {};
          const hovered = stateRecord.hovered === true;
          const focused = stateRecord.focused === true;
          const pressed = stateRecord.pressed === true;

          return [
            adminWebOutlineReset,
            {
              alignItems: input.descriptionText ? "flex-start" : "center",
              backgroundColor: checked
                ? theme.accentSubtle
                : adminFigmaPalette.field,
              borderColor:
                focused || hovered
                  ? theme.accentStrong
                  : checked
                    ? theme.accent
                    : theme.borderSubtle,
              borderRadius: adminControlRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: 7,
              minHeight: 34,
              opacity:
                isAdminOutboundCommandSubmitting || pressed ? 0.72 : 1,
              paddingHorizontal: 10,
              paddingVertical: 6,
            },
          ];
        }}
      >
        <LineIcon
          color={checked ? theme.accentStrong : theme.textSecondary}
          name={checked ? "check" : "x"}
          size={14}
          strokeWidth={2.4}
        />
        {input.descriptionText ? (
          <View style={{ flex: 1, gap: 2 }}>
            <Text
              style={[
                styles.badgeText,
                {
                  color: checked ? theme.accentStrong : theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11.5,
                  fontWeight: adminTypography.strongWeight,
                  lineHeight: 15,
                },
              ]}
            >
              {input.labelText}
            </Text>
            <Text
              style={[
                styles.badgeText,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 10.5,
                  fontWeight: adminTypography.labelWeight,
                  lineHeight: 14,
                },
              ]}
            >
              {input.descriptionText}
            </Text>
          </View>
        ) : (
          <Text
            numberOfLines={1}
            style={[
              styles.badgeText,
              {
                color: checked ? theme.accentStrong : theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 10.5,
                fontWeight: adminTypography.labelWeight,
                lineHeight: 13,
              },
            ]}
          >
            {input.labelText}
          </Text>
        )}
      </Pressable>
    );
  };

  const renderAdminOutboundDialerTextField = (
    labelText: string,
    value: string,
    onChangeText: (value: string) => void,
  ) => (
    <View
      style={[
        styles.listGroup,
        { gap: adminConfigurationDialog.fieldLabelGap },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: adminModalDensity.metaLabelFontSize,
            textTransform: "uppercase",
          },
        ]}
      >
        {labelText}
      </Text>
      <TextInput
        editable={!isAdminOutboundCommandSubmitting}
        onChangeText={onChangeText}
        placeholder={labelText}
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: adminFigmaPalette.field,
            borderColor: theme.borderSubtle,
            borderRadius: adminOperationalLayout.controlRadius,
            borderWidth: 1,
            color: theme.textPrimary,
            fontFamily: layout.bodyFontFamily,
            fontSize: adminModalDensity.fieldFontSize,
            minHeight: adminConfigurationDialog.controlHeight,
            paddingHorizontal: 12,
            paddingVertical: 8,
            textAlignVertical: "center",
          },
        ]}
        value={value}
      />
    </View>
  );

  const openOutboundDialerConfigEditor = (
    campaign: TenantAdminOutboundCampaignV1,
  ) => {
    const dialer = campaign.dialerConfig;
    const managedTelephony =
      dialer?.metadata?.managedTelephony === false ? false : true;
    const managedSetupRequestedAt = readAdminOutboundMetadataText(
      dialer?.metadata,
      "managedSetupRequestedAt",
    );

    setAdminOutboundDialerDraft({
      campaignId: campaign.campaignId,
      campaignName: campaign.displayName,
      connectCampaignArn: dialer?.connectCampaignArn ?? "",
      connectCampaignId: dialer?.connectCampaignId ?? "",
      connectCampaignRegion: dialer?.connectCampaignRegion ?? "",
      connectContactFlowId: dialer?.connectContactFlowId ?? "",
      connectInstanceId: dialer?.connectInstanceId ?? "",
      connectSourcePhoneNumber: dialer?.connectSourcePhoneNumber ?? "",
      currentDialerStatus: dialer?.dialerStatus ?? "unconfigured",
      currentReadinessBlockers: dialer?.readiness?.blockerMessages ?? [],
      dialerMode: dialer?.dialerMode ?? "direct_connect",
      managedTelephony,
      managedSetupRequestedAt,
      showAdvancedProviderFields: managedTelephony === false,
    });
  };
  const updateAdminOutboundDialerField = <
    TKey extends keyof AdminOutboundDialerConfigDraft,
  >(
    field: TKey,
    value: AdminOutboundDialerConfigDraft[TKey],
  ) => {
    setAdminOutboundDialerDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            [field]: value,
          },
    );
  };
  const runUpdateOutboundDialerConfig = () => {
    const draft = adminOutboundDialerDraft;

    if (draft == null) {
      return;
    }

    const isManagedSetup =
      draft.managedTelephony && !draft.showAdvancedProviderFields;
    const updatedAt = new Date().toISOString();
    const requestedAt =
      draft.currentDialerStatus === "ready"
        ? draft.managedSetupRequestedAt
        : draft.managedSetupRequestedAt || updatedAt;
    const metadata: Record<string, unknown> = isManagedSetup
      ? {
          adminDialerSetupPreference: "managed",
          managedSetupLastSavedAt: updatedAt,
          ...(requestedAt.length > 0
            ? {
                managedSetupRequestedAt: requestedAt,
              }
            : {}),
        }
      : {
          adminDialerSetupPreference: "support_override",
          supportOverrideLastSavedAt: updatedAt,
        };

    void runAdminOutboundCommand(
      {
        campaignId: draft.campaignId,
        dialer:
          isManagedSetup
            ? {
                dialerMode: draft.dialerMode,
                managedTelephony: true,
                metadata,
              }
            : {
                connectCampaignArn: draft.connectCampaignArn.trim() || null,
                connectCampaignId: draft.connectCampaignId.trim() || null,
                connectCampaignRegion:
                  draft.connectCampaignRegion.trim() || null,
                connectContactFlowId: draft.connectContactFlowId.trim() || null,
                connectInstanceId: draft.connectInstanceId.trim() || null,
                connectSourcePhoneNumber:
                  draft.connectSourcePhoneNumber.trim() || null,
                dialerMode: draft.dialerMode,
                managedTelephony: draft.managedTelephony,
                metadata,
              },
        kind: "update-campaign-dialer-config",
        reasonText: isManagedSetup
          ? "Managed calling setup requested from admin console."
          : "Support calling override saved from admin console.",
      },
      isManagedSetup
        ? `Saving managed calling setup for ${draft.campaignName}...`
        : `Saving support calling override for ${draft.campaignName}...`,
      "Calling setup could not be saved.",
    ).then((succeeded) => {
      if (succeeded) {
        setAdminOutboundDialerDraft(null);
      }
    });
  };
  const renderAdminOutboundDialerConfigPanel = () => {
    const draft = adminOutboundDialerDraft;

    if (draft == null) {
      return null;
    }

    const isManagedSetup =
      draft.managedTelephony && !draft.showAdvancedProviderFields;
    const setupRequested = draft.managedSetupRequestedAt.length > 0;
    const setupReady = draft.currentDialerStatus === "ready";
    const callingSetupStatusText = setupReady
      ? "Ready"
      : setupRequested
        ? "Requested"
        : "Needs setup";
    const callingSetupDetailText = setupReady
      ? "This campaign can use the approved AmpleCare calling setup."
      : setupRequested
        ? `AmpleCare setup requested ${formatAdminSummaryTimestamp(
            draft.managedSetupRequestedAt,
          )}.`
        : "Save a managed setup request and AmpleCare will finish the calling-line mapping.";
    const callingModeDetailText =
      draft.dialerMode === "connect_campaign"
        ? "Queue due targets for scheduled campaign batches."
        : "Call one selected queued target now.";
    const firstReadinessBlocker = draft.currentReadinessBlockers[0] ?? null;
    const saveDialerSetupLabel = isAdminOutboundCommandSubmitting
      ? "Saving"
      : isManagedSetup
        ? setupReady
          ? "Save managed calling"
          : setupRequested
            ? "Refresh setup request"
            : "Request AmpleCare setup"
        : "Save support override";
    const renderCallingSetupSummaryCard = (input: {
      detailText: string;
      iconName: LineIconName;
      statusText: string;
      titleText: string;
    }) => (
      <View
        key={input.titleText}
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: theme.borderSubtle,
            borderRadius: adminOperationalLayout.panelRadius,
            flex: 1,
            gap: tokens.spacing.xs,
            minWidth: 180,
            padding: tokens.spacing.sm,
          },
        ]}
      >
        <View style={[styles.patientRowHeader, { gap: tokens.spacing.xs }]}>
          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.accentSubtle,
              borderRadius: adminOperationalLayout.controlRadius,
              height: 28,
              justifyContent: "center",
              width: 28,
            }}
          >
            <LineIcon
              color={theme.accentStrong}
              name={input.iconName}
              size={15}
              strokeWidth={2.45}
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              numberOfLines={1}
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 10,
                  fontWeight: adminTypography.labelWeight,
                  textTransform: "uppercase",
                },
              ]}
            >
              {input.titleText}
            </Text>
            <Text
              numberOfLines={1}
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12.5,
                  fontWeight: adminTypography.titleWeight,
                },
              ]}
            >
              {input.statusText}
            </Text>
          </View>
        </View>
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: 10.5,
              lineHeight: 14,
            },
          ]}
        >
          {input.detailText}
        </Text>
      </View>
    );

    return (
      <View
        style={[
          styles.listGroup,
          { gap: adminConfigurationDialog.sectionGap },
        ]}
      >
        {renderAdminModalHeader({
          eyebrowText: "Campaign calling",
          onClose: () => setAdminOutboundDialerDraft(null),
          subtitleText:
            "Choose how this campaign places questionnaire calls. Most admins only need AmpleCare-managed setup; support handles the technical mapping.",
          titleText: `Calling setup — ${draft.campaignName}`,
        })}
        <View
          style={[
            styles.patientRowFooter,
            {
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: adminConfigurationDialog.fieldGap,
            },
          ]}
        >
          {renderCallingSetupSummaryCard({
            detailText: callingSetupDetailText,
            iconName: setupReady ? "check" : setupRequested ? "clock" : "flag",
            statusText: callingSetupStatusText,
            titleText: "Setup status",
          })}
          {renderCallingSetupSummaryCard({
            detailText: callingModeDetailText,
            iconName: draft.dialerMode === "connect_campaign" ? "program" : "phone",
            statusText:
              draft.dialerMode === "connect_campaign"
                ? "Scheduled batches"
                : "One patient now",
            titleText: "Calling mode",
          })}
          {renderCallingSetupSummaryCard({
            detailText: isManagedSetup
              ? "Recommended for customer admins."
              : "Visible for AmpleCare support overrides only.",
            iconName: isManagedSetup ? "settings" : "plug",
            statusText: isManagedSetup ? "AmpleCare-managed" : "Support override",
            titleText: "Setup path",
          })}
        </View>
        {renderAdminModalSelectField({
          fieldKey: "dialer-mode",
          labelText: "Calling mode",
          onChange: (value) =>
            updateAdminOutboundDialerField(
              "dialerMode",
              value as TenantAdminOutboundDialerModeV1,
            ),
          options: [
            {
              labelText: "Scheduled batches",
              value: "connect_campaign",
            },
            { labelText: "One patient now", value: "direct_connect" },
          ],
          value: draft.dialerMode,
        })}
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: theme.borderSubtle,
              borderRadius: adminOperationalLayout.panelRadius,
              gap: tokens.spacing.sm,
              padding: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              { alignItems: "center", gap: tokens.spacing.sm },
            ]}
          >
            <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: adminCompact.rowTitleFontSize,
                    fontWeight: adminTypography.titleWeight,
                    lineHeight: adminCompact.rowTitleLineHeight,
                  },
                ]}
              >
                AmpleCare-managed calling
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminCompact.bodyFontSize,
                    lineHeight: adminCompact.bodyLineHeight,
                  },
                ]}
              >
                AmpleCare uses the approved tenant calling line, questionnaire
                flow, and dialing guardrails. Admins can request setup without
                knowing provider ids.
              </Text>
            </View>
            {renderAdminStatusPill(
              isManagedSetup ? "Managed" : "Support",
              { minWidth: 86 },
            )}
          </View>
          {!setupReady && firstReadinessBlocker != null ? (
            <View
              style={[
                styles.detailBodyCard,
                {
                  backgroundColor: theme.statusChip.warning.background,
                  borderColor: theme.statusChip.warning.border,
                  borderRadius: adminOperationalLayout.panelRadius,
                  gap: 3,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.statusChip.warning.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: adminTypography.titleWeight,
                  },
                ]}
              >
                What AmpleCare needs to finish
              </Text>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.statusChip.warning.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10.5,
                    lineHeight: 14,
                  },
                ]}
              >
                {resolveAdminOutboundCallingBlockerText(firstReadinessBlocker)}
              </Text>
            </View>
          ) : null}
          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {renderWorkspaceFilterChip({
              isSelected: isManagedSetup,
              labelText: "Use AmpleCare setup",
              onPress: () =>
                setAdminOutboundDialerDraft((current) =>
                  current == null
                    ? current
                    : {
                        ...current,
                        managedTelephony: true,
                        showAdvancedProviderFields: false,
                      },
                ),
              theme,
              tokens,
            })}
            {renderWorkspaceFilterChip({
              isSelected: draft.showAdvancedProviderFields,
              labelText: draft.showAdvancedProviderFields
                ? "Hide support fields"
                : "Support override",
              onPress: () =>
                setAdminOutboundDialerDraft((current) =>
                  current == null
                    ? current
                    : {
                        ...current,
                        managedTelephony:
                          current.showAdvancedProviderFields || false,
                        showAdvancedProviderFields:
                          !current.showAdvancedProviderFields,
                      },
                ),
              theme,
              tokens,
            })}
          </View>
        </View>
        {draft.showAdvancedProviderFields ? (
          <>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminCompact.bodyFontSize,
                  lineHeight: adminCompact.bodyLineHeight,
                },
              ]}
            >
              Support-only fields are for AmpleCare operations when the managed
              setup cannot be resolved automatically.
            </Text>
            <View
              style={[
                styles.patientRowFooter,
                {
                  alignItems: "stretch",
                  flexWrap: "wrap",
                  gap: adminConfigurationDialog.fieldGap,
                },
              ]}
            >
              <View style={{ flex: 1, minWidth: 260 }}>
                {renderAdminOutboundDialerTextField(
                  "Batch campaign id",
                  draft.connectCampaignId,
                  (value) =>
                    updateAdminOutboundDialerField("connectCampaignId", value),
                )}
              </View>
              <View style={{ flex: 1, minWidth: 260 }}>
                {renderAdminOutboundDialerTextField(
                  "Batch campaign ARN",
                  draft.connectCampaignArn,
                  (value) =>
                    updateAdminOutboundDialerField("connectCampaignArn", value),
                )}
              </View>
              <View style={{ flex: 1, minWidth: 260 }}>
                {renderAdminOutboundDialerTextField(
                  "Calling region",
                  draft.connectCampaignRegion,
                  (value) =>
                    updateAdminOutboundDialerField(
                      "connectCampaignRegion",
                      value,
                    ),
                )}
              </View>
              <View style={{ flex: 1, minWidth: 260 }}>
                {renderAdminOutboundDialerTextField(
                  "Telephony instance id",
                  draft.connectInstanceId,
                  (value) =>
                    updateAdminOutboundDialerField("connectInstanceId", value),
                )}
              </View>
              <View style={{ flex: 1, minWidth: 260 }}>
                {renderAdminOutboundDialerTextField(
                  "Questionnaire flow id",
                  draft.connectContactFlowId,
                  (value) =>
                    updateAdminOutboundDialerField(
                      "connectContactFlowId",
                      value,
                    ),
                )}
              </View>
              <View style={{ flex: 1, minWidth: 260 }}>
                {renderAdminOutboundDialerTextField(
                  "Calling line (E.164)",
                  draft.connectSourcePhoneNumber,
                  (value) =>
                    updateAdminOutboundDialerField(
                      "connectSourcePhoneNumber",
                      value,
                    ),
                )}
              </View>
            </View>
          </>
        ) : null}
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminOutboundCommandSubmitting,
            labelText: "Cancel",
            onPress: () => setAdminOutboundDialerDraft(null),
            prefixText: "",
            tone: "surface",
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminOutboundCommandSubmitting,
            labelText: isAdminOutboundCommandSubmitting
              ? "Saving"
              : saveDialerSetupLabel,
            onPress: runUpdateOutboundDialerConfig,
            prefixText: "",
            tone: "primary",
          })}
        </View>
      </View>
    );
  };
  const renderAdminOutboundDialerConfigModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: "Campaign calling configuration",
      children: renderAdminOutboundDialerConfigPanel(),
      maxWidth: 720,
      onClose: () => {
        if (isAdminOutboundCommandSubmitting) {
          return;
        }

        setAdminOutboundDialerDraft(null);
      },
      rail: null,
      visible: adminOutboundDialerDraft != null,
    });

  const openOutboundCreateCampaignEditor = () => {
    const firstVersionId =
      adminOutboundQuestionnaireTemplates.find(
        (template) => template.activeVersion != null,
      )?.activeVersion?.questionnaireVersionId ?? "";

    setAdminOutboundCreateDraft({
      displayName: "New outbound campaign",
      purposeText:
        "Outbound questionnaire campaign created from the admin console.",
      questionnaireTemplateVersionId: firstVersionId,
    });
  };
  const updateAdminOutboundCreateField = (
    field: keyof AdminOutboundCreateCampaignDraft,
    value: string,
  ) => {
    setAdminOutboundCreateDraft((current) =>
      current == null ? current : { ...current, [field]: value },
    );
  };
  const runCreateOutboundCampaignFromDraft = () => {
    const draft = adminOutboundCreateDraft;

    if (draft == null) {
      return;
    }

    const trimmedName = draft.displayName.trim();

    if (trimmedName.length === 0) {
      setAdminOpsNotice("Enter a campaign name before creating.");
      return;
    }

    if (draft.questionnaireTemplateVersionId.length === 0) {
      setAdminOpsNotice(
        "Publish a questionnaire version before creating a campaign.",
      );
      return;
    }

    const slug =
      trimmedName
        .toLowerCase()
        .replace(/[^a-z0-9]+/gu, "-")
        .replace(/(^-|-$)/gu, "")
        .slice(0, 40) || "campaign";

    void runAdminOutboundCommand(
      {
        campaignKey: `${slug}-${Date.now().toString(36)}`,
        campaignKind: "outbound_questionnaire",
        campaignStatus: "draft",
        displayName: trimmedName,
        eligibilityRules: {},
        kind: "create-campaign",
        policy: {
          allowedDays: ["monday", "tuesday", "wednesday", "thursday", "friday"],
          allowedEndLocalTime: "17:00",
          allowedStartLocalTime: "09:00",
          callCapMinutes: 8,
          maxAttemptsPerDay: 2,
          maxAttemptsTotal: 4,
          minRetryCooldownMinutes: 240,
          optOutPolicy: { suppressionRequired: true },
          policyKey: "primary",
          policyMetadata: {
            recentCallContext: {
              enabled: true,
              mode: "active_call_only",
            },
          },
          recordingPolicy: "disabled",
          resumePolicy: adminOutboundDefaultResumePolicy,
          timezoneMode: "patient_local",
          voicemailPolicy: "minimal_message",
        },
        purposeText: draft.purposeText.trim() || trimmedName,
        questionnaireTemplateVersionId: draft.questionnaireTemplateVersionId,
        reviewRoutingRules: { redFlags: "clinician_review_required" },
      },
      `Creating ${trimmedName}...`,
      "Outbound campaign could not be created.",
    ).then((succeeded) => {
      if (succeeded) {
        setAdminOutboundCreateDraft(null);
      }
    });
  };
  const renderAdminOutboundCreateCampaignPanel = () => {
    const draft = adminOutboundCreateDraft;

    if (draft == null) {
      return null;
    }

    const versionOptions = adminOutboundQuestionnaireTemplates.flatMap(
      (template) => {
        const version = template.activeVersion;

        if (version == null) {
          return [];
        }

        return [
          {
            labelText: `${template.displayName} · v${String(
              version.versionNumber,
            )}`,
            value: version.questionnaireVersionId,
          },
        ];
      },
    );

    return (
      <View
        style={[
          styles.listGroup,
          { gap: adminConfigurationDialog.sectionGap },
        ]}
      >
        {renderAdminModalHeader({
          eyebrowText: "New campaign",
          onClose: () => setAdminOutboundCreateDraft(null),
          subtitleText:
            "Name the campaign and pick the approved questionnaire version it will run.",
          titleText: "Create outbound campaign",
        })}
        {renderAdminOutboundDialerTextField(
          "Campaign name",
          draft.displayName,
          (value) => updateAdminOutboundCreateField("displayName", value),
        )}
        {renderAdminOutboundDialerTextField(
          "Purpose",
          draft.purposeText,
          (value) => updateAdminOutboundCreateField("purposeText", value),
        )}
        {versionOptions.length > 0 ? (
          renderAdminModalSelectField({
            fieldKey: "create-campaign-version",
            labelText: "Questionnaire version",
            onChange: (value) =>
              updateAdminOutboundCreateField(
                "questionnaireTemplateVersionId",
                value,
              ),
            options: versionOptions,
            value: draft.questionnaireTemplateVersionId,
          })
        ) : (
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.warning,
                fontFamily: layout.bodyFontFamily,
                fontSize: adminModalDensity.bodyFontSize,
                lineHeight: adminModalDensity.bodyLineHeight,
              },
            ]}
          >
            No published questionnaire versions yet — publish one in the
            Questionnaires tab first.
          </Text>
        )}
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminOutboundCommandSubmitting,
            labelText: "Cancel",
            onPress: () => setAdminOutboundCreateDraft(null),
            prefixText: "",
            tone: "surface",
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            disabled:
              isAdminOutboundCommandSubmitting || versionOptions.length === 0,
            labelText: isAdminOutboundCommandSubmitting
              ? "Creating"
              : "Create campaign",
            onPress: runCreateOutboundCampaignFromDraft,
            prefixText: "",
            tone: "primary",
          })}
        </View>
      </View>
    );
  };
  const renderAdminOutboundCreateCampaignModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: "Create outbound campaign",
      children: renderAdminOutboundCreateCampaignPanel(),
      maxWidth: 640,
      onClose: () => {
        if (isAdminOutboundCommandSubmitting) {
          return;
        }

        setAdminOutboundCreateDraft(null);
      },
      rail: null,
      visible: adminOutboundCreateDraft != null,
    });

  const buildAdminOutboundQuestionnaireVersionOptions = (
    campaign?: TenantAdminOutboundCampaignV1,
  ): Array<{
    labelText: string;
    value: string;
  }> => {
    const seenVersionIds = new Set<string>();
    const options: Array<{ labelText: string; value: string }> = [];
    const questionnaireTemplateId = campaign?.questionnaireTemplateId ?? null;

    for (const template of adminOutboundQuestionnaireTemplates) {
      if (
        questionnaireTemplateId != null &&
        template.questionnaireTemplateId !== questionnaireTemplateId
      ) {
        continue;
      }

      const versions: TenantAdminOutboundQuestionnaireVersionV1[] = [
        template.activeVersion,
        template.latestVersion,
      ].filter(
        (version): version is TenantAdminOutboundQuestionnaireVersionV1 =>
          version != null,
      );

      for (const version of versions) {
        if (
          version.versionStatus !== "active" ||
          version.clinicalApprovedAt == null ||
          version.questionCount < 1
        ) {
          continue;
        }

        if (seenVersionIds.has(version.questionnaireVersionId)) {
          continue;
        }

        seenVersionIds.add(version.questionnaireVersionId);
        options.push({
          labelText: `${template.displayName} · v${String(
            version.versionNumber,
          )}`,
          value: version.questionnaireVersionId,
        });
      }
    }

    return options;
  };

  const openOutboundCampaignQuestionnaireImpactReview = (
    campaign: TenantAdminOutboundCampaignV1,
  ) => {
    const options = buildAdminOutboundQuestionnaireVersionOptions(campaign);
    const targetVersionId =
      options.find(
        (option) => option.value !== campaign.questionnaireTemplateVersionId,
      )?.value ??
      campaign.questionnaireTemplateVersionId ??
      options[0]?.value ??
      "";

    if (targetVersionId.length === 0) {
      setAdminOpsNotice(
        "Publish a questionnaire version before reviewing campaign impact.",
      );
      return;
    }

    setAdminOutboundCampaignQuestionnaireImpactDraft({
      campaign,
      impactReview: null,
      migrateEligibleQueuedSessions: false,
      targetQuestionnaireTemplateVersionId: targetVersionId,
    });
  };

  const updateAdminOutboundCampaignQuestionnaireImpactDraft = (
    updates: Partial<
      Pick<
        AdminOutboundCampaignQuestionnaireImpactDraft,
        "migrateEligibleQueuedSessions" | "targetQuestionnaireTemplateVersionId"
      >
    >,
  ) => {
    setAdminOutboundCampaignQuestionnaireImpactDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            ...updates,
            impactReview: null,
          },
    );
  };

  const runOutboundCampaignQuestionnaireImpactReview = async (
    applyApproved = false,
  ) => {
    const draft = adminOutboundCampaignQuestionnaireImpactDraft;

    if (draft == null) {
      return;
    }

    if (applyApproved && draft.impactReview == null) {
      setAdminOpsNotice("Run the campaign impact review before applying.");
      return;
    }

    const result = await runAdminOutboundCommand(
      {
        campaignId: draft.campaign.campaignId,
        impactReviewApprovalToken: applyApproved
          ? draft.impactReview?.approvalToken
          : null,
        kind: "update-campaign-questionnaire-version",
        migrateEligibleQueuedSessions: draft.migrateEligibleQueuedSessions,
        propagationMode: applyApproved ? "apply_approved" : "impact_review",
        questionnaireTemplateVersionId:
          draft.targetQuestionnaireTemplateVersionId,
        reasonText: applyApproved
          ? "Applied approved campaign questionnaire impact review."
          : "Prepared campaign questionnaire impact review from Admin.",
      },
      applyApproved
        ? `Applying questionnaire impact review for ${draft.campaign.displayName}...`
        : `Reviewing questionnaire impact for ${draft.campaign.displayName}...`,
      applyApproved
        ? "Campaign questionnaire change could not be applied."
        : "Campaign questionnaire impact review could not be prepared.",
    );

    if (!result?.campaignQuestionnaireImpactReview) {
      return;
    }

    if (applyApproved) {
      setAdminOutboundCampaignQuestionnaireImpactDraft(null);
      return;
    }

    setAdminOutboundCampaignQuestionnaireImpactDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            impactReview: result.campaignQuestionnaireImpactReview ?? null,
          },
    );
  };

  const renderAdminOutboundCampaignQuestionnaireImpactModal = () => {
    const draft = adminOutboundCampaignQuestionnaireImpactDraft;

    if (draft == null) {
      return null;
    }

    const versionOptions = buildAdminOutboundQuestionnaireVersionOptions(
      draft.campaign,
    );
    const review = draft.impactReview;
    const renderImpactMetric = (labelText: string, valueText: string) => (
      <View
        key={labelText}
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: theme.borderSubtle,
            borderRadius: 8,
            flex: 1,
            gap: 2,
            minWidth: 132,
            padding: tokens.spacing.sm,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: 10,
              fontWeight: adminTypography.labelWeight,
              textTransform: "uppercase",
            },
          ]}
        >
          {labelText}
        </Text>
        <Text
          style={[
            styles.metricValue,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
              fontSize: 18,
              lineHeight: 22,
            },
          ]}
        >
          {valueText}
        </Text>
      </View>
    );

    return renderAdminModalFrame({
      accessibilityLabel: "Campaign questionnaire impact review",
      children: (
        <View style={[styles.listGroup, { gap: adminModalDensity.actionGap }]}>
          {renderAdminModalHeader({
            eyebrowText: "Campaign impact review",
            onClose: () =>
              setAdminOutboundCampaignQuestionnaireImpactDraft(null),
            subtitleText:
              "Review this selected campaign before applying a questionnaire version change. Started, paused, answered, completed, and review sessions remain pinned.",
            titleText: draft.campaign.displayName,
          })}
          {renderAdminModalSelectField({
            fieldKey: "questionnaire-impact-target-version",
            labelText: "Target questionnaire version",
            onChange: (value) =>
              updateAdminOutboundCampaignQuestionnaireImpactDraft({
                targetQuestionnaireTemplateVersionId: value,
              }),
            options: versionOptions,
            value: draft.targetQuestionnaireTemplateVersionId,
          })}
          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {renderWorkspaceFilterChip({
              isSelected: !draft.migrateEligibleQueuedSessions,
              labelText: "Leave queued sessions pinned",
              onPress: () =>
                updateAdminOutboundCampaignQuestionnaireImpactDraft({
                  migrateEligibleQueuedSessions: false,
                }),
              theme,
              tokens,
            })}
            {renderWorkspaceFilterChip({
              isSelected: draft.migrateEligibleQueuedSessions,
              labelText: "Migrate untouched queued sessions",
              onPress: () =>
                updateAdminOutboundCampaignQuestionnaireImpactDraft({
                  migrateEligibleQueuedSessions: true,
                }),
              theme,
              tokens,
            })}
          </View>
          {review == null ? (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminCompact.bodyFontSize,
                  lineHeight: adminCompact.bodyLineHeight,
                },
              ]}
            >
              Run review to see affected queued targets, eligible untouched
              sessions, immutable pinned sessions, and policy differences before
              applying.
            </Text>
          ) : (
            <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
              <View
                style={[
                  styles.patientRowFooter,
                  {
                    alignItems: "stretch",
                    flexWrap: "wrap",
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {renderImpactMetric(
                  "Queued targets",
                  String(review.queuedTargetCount),
                )}
                {renderImpactMetric(
                  "Eligible migration",
                  String(review.eligibleSessionCount),
                )}
                {renderImpactMetric(
                  "Remain pinned",
                  String(review.sessionsThatWillRemainPinnedCount),
                )}
                {renderImpactMetric(
                  "Already target",
                  String(review.sessionsAlreadyOnTargetCount),
                )}
              </View>
              <View
                style={[
                  styles.patientRowFooter,
                  {
                    alignItems: "stretch",
                    flexWrap: "wrap",
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                {renderImpactMetric(
                  "In progress",
                  String(review.inProgressSessionCount),
                )}
                {renderImpactMetric("Paused", String(review.pausedSessionCount))}
                {renderImpactMetric(
                  "Completed",
                  String(review.completedSessionCount),
                )}
                {renderImpactMetric("Failed", String(review.failedSessionCount))}
              </View>
              <View
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: adminFigmaPalette.panelSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: 8,
                    gap: tokens.spacing.xs,
                    padding: tokens.spacing.sm,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12.5,
                      fontWeight: adminTypography.titleWeight,
                    },
                  ]}
                >
                  {`v${String(
                    review.currentQuestionnaireVersionNumber ?? "unknown",
                  )} to v${String(
                    review.targetQuestionnaireVersionNumber ?? "unknown",
                  )}`}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10.5,
                      lineHeight: 14,
                    },
                  ]}
                >
                  {`Target has ${String(
                    review.targetQuestionCount,
                  )} question(s). Resume policy ${
                    review.policyDiff.resumePolicyChanged
                      ? "changes"
                      : "matches"
                  }; review policy ${
                    review.policyDiff.reviewPolicyChanged
                      ? "changes"
                      : "matches"
                  }. Approval ${review.approvalToken}.`}
                </Text>
              </View>
            </View>
          )}
          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {renderAdminPrimaryButton({
              density: "compact",
              disabled: isAdminOutboundCommandSubmitting,
              labelText: "Cancel",
              onPress: () =>
                setAdminOutboundCampaignQuestionnaireImpactDraft(null),
              prefixText: "",
              tone: "surface",
            })}
            {renderAdminPrimaryButton({
              density: "compact",
              disabled: isAdminOutboundCommandSubmitting,
              labelText: "Review impact",
              onPress: () => void runOutboundCampaignQuestionnaireImpactReview(),
              prefixText: "",
              tone: "surface",
            })}
            {renderAdminPrimaryButton({
              density: "compact",
              disabled: isAdminOutboundCommandSubmitting || review == null,
              labelText: "Apply approved",
              onPress: () =>
                void runOutboundCampaignQuestionnaireImpactReview(true),
              prefixText: "",
              tone: "primary",
            })}
          </View>
        </View>
      ),
      maxWidth: 760,
      onClose: () => {
        if (isAdminOutboundCommandSubmitting) {
          return;
        }

        setAdminOutboundCampaignQuestionnaireImpactDraft(null);
      },
      rail: null,
      visible: adminOutboundCampaignQuestionnaireImpactDraft != null,
    });
  };

  const renderAdminOutboundQuestionnaireRevisionPanel = () => {
    const draft = adminOutboundQuestionnaireRevisionDraft;

    if (!draft) {
      return null;
    }

    const draftResumePolicy = {
      ...adminOutboundDefaultResumePolicy,
      ...readAdminOutboundRecord(draft.resumePolicy),
    };
    const draftReviewPolicy = normalizeAdminOutboundQuestionnaireReviewPolicy(
      draft.reviewPolicy,
    );
    const draftVoicePolicy = normalizeAdminOutboundQuestionnaireVoicePolicy(
      draftReviewPolicy.voicePolicy,
    );
    const revisionScrollMaxHeight = Math.max(
      320,
      viewportHeight - (isAdminMobileLayout ? 250 : 220),
    );
    const revisionTabSteps: ReadonlyArray<{
      id: AdminOutboundQuestionnaireRevisionStep;
      labelText: string;
      stepText: string;
    }> = [
      { id: "basics", labelText: "Basics", stepText: "1" },
      { id: "policy", labelText: "Policy", stepText: "2" },
      { id: "questions", labelText: "Questions", stepText: "3" },
    ];

    return (
      <View
        style={[
          styles.listGroup,
          { gap: adminConfigurationDialog.sectionGap },
        ]}
      >
        {renderAdminModalHeader({
          eyebrowText: "Questionnaire revision",
          onClose: () => setAdminOutboundQuestionnaireRevisionDraft(null),
          subtitleText:
            "Publishing creates a new approved version and keeps older versions available for audit.",
          titleText: "Edit questionnaire",
        })}

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: "column",
              gap: adminModalDensity.actionGap,
            },
          ]}
        >
          <View
            style={[
              styles.patientRowFooter,
              {
                backgroundColor: adminFigmaPalette.panelSubtle,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                flexWrap: "wrap",
                gap: 6,
                padding: 6,
                width: "100%",
              },
            ]}
          >
            {revisionTabSteps.map((step) => {
              const isActiveStep = adminOutboundRevisionStep === step.id;

              return (
                <Pressable
                  accessibilityLabel={`${step.stepText}. ${step.labelText}`}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: isActiveStep }}
                  key={step.id}
                  onPress={() => setAdminOutboundRevisionStep(step.id)}
                  style={[
                    adminWebOutlineReset,
                    {
                      alignItems: "center",
                      backgroundColor: isActiveStep
                        ? theme.surface
                        : "transparent",
                      borderColor: isActiveStep
                        ? theme.borderStrong
                        : "transparent",
                      borderRadius: adminOperationalLayout.controlRadius,
                      borderWidth: 1,
                      flex: 1,
                      flexDirection: "row",
                      gap: 9,
                      justifyContent: "center",
                      minHeight: 40,
                      minWidth: isAdminMobileLayout ? 120 : 150,
                      paddingHorizontal: 11,
                      paddingVertical: 9,
                    },
                  ]}
                >
                  <View
                    style={{
                      alignItems: "center",
                      backgroundColor: isActiveStep
                        ? theme.accent
                        : theme.accentSubtle,
                      borderRadius: 999,
                      height: 20,
                      justifyContent: "center",
                      width: 20,
                    }}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: isActiveStep
                            ? theme.accentContrast
                            : theme.accentStrong,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          fontWeight: adminTypography.strongWeight,
                        },
                      ]}
                    >
                      {step.stepText}
                    </Text>
                  </View>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: isActiveStep
                          ? theme.accentStrong
                          : theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12.5,
                        fontWeight: isActiveStep
                          ? adminTypography.titleWeight
                          : adminTypography.labelWeight,
                      },
                    ]}
                  >
                    {step.labelText}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <ScrollView
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={true}
            style={{
              maxHeight: revisionScrollMaxHeight,
              width: "100%",
            }}
            contentContainerStyle={{
              gap: adminModalDensity.actionGap,
              paddingBottom: tokens.spacing.xs,
            }}
          >
            <View
              style={[
                styles.listGroup,
                { flex: 1, gap: adminModalDensity.actionGap, minWidth: 0 },
              ]}
            >
            {adminOutboundRevisionStep === "basics" ? (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.panelSubtle,
              borderColor: adminFigmaPalette.border,
              borderRadius: adminOperationalLayout.panelRadius,
              gap: adminModalDensity.actionGap,
              padding: adminModalDensity.cardPadding,
            },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "stretch",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: adminModalDensity.actionGap,
              },
            ]}
          >
            {renderAdminOutboundQuestionnaireRevisionTextField(
              "Display name",
              draft.displayName,
              (value) =>
                updateAdminOutboundQuestionnaireRevisionField(
                  "displayName",
                  value,
                ),
            )}
            {renderAdminOutboundQuestionnaireRevisionTextField(
              "Version label",
              draft.versionLabel,
              (value) =>
                updateAdminOutboundQuestionnaireRevisionField(
                  "versionLabel",
                  value,
                ),
            )}
            {renderAdminOutboundQuestionnaireRevisionTextField(
              "Estimated minutes",
              draft.defaultEstimatedMinutesText,
              (value) =>
                updateAdminOutboundQuestionnaireRevisionField(
                  "defaultEstimatedMinutesText",
                  value.replace(/[^0-9]/gu, ""),
                ),
            )}
          </View>
          {renderAdminOutboundQuestionnaireRevisionTextField(
            "Clinical purpose",
            draft.clinicalPurpose,
            (value) =>
              updateAdminOutboundQuestionnaireRevisionField(
                "clinicalPurpose",
                value,
              ),
            true,
          )}
        </View>
            ) : null}

            {adminOutboundRevisionStep === "policy" ? (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: adminFigmaPalette.border,
              borderRadius: adminOperationalLayout.panelRadius,
              gap: tokens.spacing.md,
              padding: adminModalDensity.cardPadding,
            },
          ]}
        >
          <View style={[styles.listGroup, { gap: 4 }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Policy
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              These settings publish into the next questionnaire version. Use
              the question-specific rules only when a prompt needs custom
              routing or voice behavior.
	            </Text>
	          </View>
	          <View
	            style={[
	              styles.listGroup,
	              {
	                backgroundColor: adminFigmaPalette.panelSubtle,
	                borderColor: adminFigmaPalette.border,
	                borderRadius: adminOperationalLayout.panelRadius,
	                borderWidth: 1,
	                gap: tokens.spacing.sm,
	                padding: tokens.spacing.md,
	              },
	            ]}
	          >
	            <View style={[styles.listGroup, { gap: 3 }]}>
	              <Text
	                style={[
	                  styles.metricLabel,
	                  {
	                    color: theme.textSecondary,
	                    fontFamily: layout.bodyFontFamily,
	                    fontSize: adminModalDensity.metaLabelFontSize,
	                    textTransform: "uppercase",
	                  },
	                ]}
	              >
		                What the caller says
	              </Text>
	              <Text
	                style={[
	                  styles.sectionSubtitle,
	                  {
	                    color: theme.textSecondary,
	                    fontFamily: layout.bodyFontFamily,
	                    fontSize: adminModalDensity.bodyFontSize,
	                    lineHeight: adminModalDensity.bodyLineHeight,
	                  },
	                ]}
	              >
	                These fields control first-time and returning-patient intro copy
	                for future sessions pinned to this published version.
	              </Text>
	            </View>
	            <View
	              style={[
	                styles.patientRowHeader,
	                {
	                  alignItems: "stretch",
	                  flexDirection: isMediumLayout ? "row" : "column",
	                  gap: adminModalDensity.actionGap,
	                },
	              ]}
	            >
	              {renderAdminModalSelectField({
	                fieldKey: "outbound-voice-tone-preset",
	                labelText: "Tone preset",
	                onChange: (value) =>
	                  updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                    "tonePreset",
	                    value,
	                  ),
	                options: [
	                  { labelText: "Warm clinical", value: "warm_clinical" },
	                  { labelText: "Gentle concise", value: "gentle_concise" },
	                  { labelText: "Formal concise", value: "formal_concise" },
	                  { labelText: "Custom", value: "custom" },
	                ],
	                value: readAdminOutboundText(draftVoicePolicy.tonePreset),
	              })}
	              {renderAdminModalSelectField({
	                fieldKey: "outbound-voice-intro-mode",
	                labelText: "Intro mode",
	                onChange: (value) =>
	                  updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                    "introMode",
	                    value,
	                  ),
	                options: [
	                  {
	                    labelText: "Provider first",
	                    value: "provider_first_powered_by_amplecare",
	                  },
	                  {
	                    labelText: "Provider only",
	                    value: "provider_only_white_label",
	                  },
	                  { labelText: "AmpleCare first", value: "amplecare_first" },
	                ],
	                value: readAdminOutboundText(draftVoicePolicy.introMode),
	              })}
	              {renderAdminOutboundQuestionnaireRevisionTextField(
	                "Disclosure version",
	                readAdminOutboundText(draftVoicePolicy.disclosureVersion),
	                (value) =>
	                  updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                    "disclosureVersion",
	                    value,
	                  ),
	              )}
	            </View>
	            {readAdminOutboundText(draftVoicePolicy.tonePreset) === "custom"
	              ? renderAdminOutboundQuestionnaireRevisionTextField(
	                  "Custom tone instructions",
	                  readAdminOutboundText(
	                    draftVoicePolicy.customToneInstructions,
	                  ),
	                  (value) =>
	                    updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                      "customToneInstructions",
	                      value,
	                    ),
	                  true,
	                )
	              : null}
	            {renderAdminOutboundQuestionnaireRevisionTextField(
	              "Full intro",
	              readAdminOutboundText(draftVoicePolicy.fullIntroText),
	              (value) =>
	                updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                  "fullIntroText",
	                  value,
	                ),
	              true,
	            )}
	            {renderAdminOutboundQuestionnaireRevisionTextField(
	              "Brief intro",
	              readAdminOutboundText(draftVoicePolicy.briefIntroText),
	              (value) =>
	                updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                  "briefIntroText",
	                  value,
	                ),
	              true,
	            )}
	            {renderAdminOutboundQuestionnaireRevisionTextField(
	              "Answer instructions",
	              readAdminOutboundText(draftVoicePolicy.answerInstructionText),
	              (value) =>
	                updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                  "answerInstructionText",
	                  value,
	                ),
	              true,
	            )}
	            {renderAdminOutboundQuestionnaireRevisionTextField(
	              "Privacy disclosure",
	              readAdminOutboundText(draftVoicePolicy.privacyDisclosureText),
	              (value) =>
	                updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                  "privacyDisclosureText",
	                  value,
	                ),
	              true,
	            )}
	            {renderAdminOutboundQuestionnaireRevisionTextField(
	              "Clinician-review disclosure",
	              readAdminOutboundText(
	                draftVoicePolicy.clinicianReviewDisclosureText,
	              ),
	              (value) =>
	                updateAdminOutboundQuestionnaireRevisionVoicePolicyValue(
	                  "clinicianReviewDisclosureText",
	                  value,
	                ),
	              true,
	            )}
	          </View>
	          <View
	            style={[
	              styles.listGroup,
	              {
	                backgroundColor: adminFigmaPalette.panelSubtle,
	                borderColor: adminFigmaPalette.border,
	                borderRadius: adminOperationalLayout.panelRadius,
	                borderWidth: 1,
	                gap: tokens.spacing.sm,
	                padding: tokens.spacing.md,
	              },
	            ]}
	          >
	            <View style={[styles.listGroup, { gap: 3 }]}>
	              <Text
	                style={[
	                  styles.metricLabel,
	                  {
	                    color: theme.textSecondary,
	                    fontFamily: layout.bodyFontFamily,
	                    fontSize: adminModalDensity.metaLabelFontSize,
	                    textTransform: "uppercase",
	                  },
	                ]}
	              >
		                When to route to clinician
	              </Text>
	              <Text
	                style={[
	                  styles.sectionSubtitle,
	                  {
	                    color: theme.textSecondary,
	                    fontFamily: layout.bodyFontFamily,
	                    fontSize: adminModalDensity.bodyFontSize,
	                    lineHeight: adminModalDensity.bodyLineHeight,
	                  },
	                ]}
	              >
	                The questionnaire version owns clinical red and yellow
	                routing. Campaign routing only chooses the operational queue
	                fallback.
	              </Text>
	            </View>
	            <View
	              style={[
	                styles.patientRowHeader,
	                {
	                  alignItems: "stretch",
	                  flexDirection: isMediumLayout ? "row" : "column",
	                  gap: adminModalDensity.actionGap,
	                },
	              ]}
	            >
	              {renderAdminModalSelectField({
	                fieldKey: "outbound-review-unsure-routing",
	                labelText: "Not sure answers",
	                onChange: (value) =>
	                  updateAdminOutboundQuestionnaireRevisionReviewPolicyValue(
	                    "unsureRouting",
	                    value,
	                  ),
	                options: [
	                  { labelText: "Yellow review", value: "yellow_flag" },
	                  { labelText: "Red review", value: "red_flag" },
	                  { labelText: "No review", value: "none" },
	                ],
	                value: readAdminOutboundText(draftReviewPolicy.unsureRouting),
	              })}
	              {renderAdminModalSelectField({
	                fieldKey: "outbound-review-yellow-routing",
	                labelText: "Yellow routing",
	                onChange: (value) =>
	                  updateAdminOutboundQuestionnaireRevisionReviewPolicyValue(
	                    "yellowFlagRouting",
	                    value,
	                  ),
	                options: [
	                  {
	                    labelText: "Clinician review",
	                    value: "clinician_review_required",
	                  },
	                  { labelText: "Queue review", value: "queue_review" },
	                  { labelText: "No review", value: "none" },
	                ],
	                value: readAdminOutboundText(
	                  draftReviewPolicy.yellowFlagRouting,
	                ),
	              })}
	              {renderAdminModalSelectField({
	                fieldKey: "outbound-review-red-routing",
	                labelText: "Red routing",
	                onChange: (value) =>
	                  updateAdminOutboundQuestionnaireRevisionReviewPolicyValue(
	                    "redFlagRouting",
	                    value,
	                  ),
	                options: [
	                  { labelText: "Stop and review", value: "stop_and_review" },
	                  {
	                    labelText: "Clinician review",
	                    value: "clinician_review_required",
	                  },
	                  { labelText: "No review", value: "none" },
	                ],
	                value: readAdminOutboundText(draftReviewPolicy.redFlagRouting),
	              })}
	            </View>
	            <View
	              style={[
	                styles.patientRowHeader,
	                {
	                  alignItems: "stretch",
	                  flexDirection: isMediumLayout ? "row" : "column",
	                  gap: adminModalDensity.actionGap,
	                },
	              ]}
	            >
	              {renderAdminOutboundQuestionnaireRevisionTextField(
	                "Review queue key",
	                readAdminOutboundText(draftReviewPolicy.reviewQueueKey),
	                (value) =>
	                  updateAdminOutboundQuestionnaireRevisionReviewPolicyValue(
	                    "reviewQueueKey",
	                    value,
	                  ),
	              )}
	              {renderAdminOutboundQuestionnaireRevisionToggle({
	                checked:
	                  draftReviewPolicy.clinicianReviewDisclosureRequired !== false,
	                labelText: "Disclose clinician review",
	                onPress: () =>
	                  updateAdminOutboundQuestionnaireRevisionReviewPolicyValue(
	                    "clinicianReviewDisclosureRequired",
	                    draftReviewPolicy.clinicianReviewDisclosureRequired === false,
	                  ),
	              })}
	            </View>
	          </View>
	          <View
	            style={[
	              styles.listGroup,
              {
                backgroundColor: adminFigmaPalette.panelSubtle,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                gap: tokens.spacing.sm,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <View style={[styles.listGroup, { gap: 3 }]}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
	                When to call back
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.bodyFontSize,
                    lineHeight: adminModalDensity.bodyLineHeight,
                  },
                ]}
              >
                Controls how the voice runtime pauses and resumes an unfinished
                questionnaire.
              </Text>
            </View>
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "stretch",
                  flexDirection: isMediumLayout ? "row" : "column",
                  gap: adminModalDensity.actionGap,
                },
              ]}
            >
              {renderAdminOutboundQuestionnaireRevisionTextField(
                "Resume delay minutes",
                String(
                  readAdminOutboundNumber(
                    draftResumePolicy.defaultResumeDelayMinutes,
                    adminOutboundDefaultResumePolicy.defaultResumeDelayMinutes,
                  ),
                ),
                (value) =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyNumber(
                    "defaultResumeDelayMinutes",
                    value,
                  ),
              )}
              {renderAdminOutboundQuestionnaireRevisionTextField(
                "Max paused hours",
                String(
                  readAdminOutboundNumber(
                    draftResumePolicy.maxPausedHours,
                    adminOutboundDefaultResumePolicy.maxPausedHours,
                  ),
                ),
                (value) =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyNumber(
                    "maxPausedHours",
                    value,
                  ),
              )}
              {renderAdminOutboundQuestionnaireRevisionTextField(
                "Max resume attempts",
                String(
                  readAdminOutboundNumber(
                    draftResumePolicy.maxResumeAttempts,
                    adminOutboundDefaultResumePolicy.maxResumeAttempts,
                  ),
                ),
                (value) =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyNumber(
                    "maxResumeAttempts",
                    value,
                  ),
              )}
            </View>
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "stretch",
                  flexDirection: isMediumLayout ? "row" : "column",
                  gap: adminModalDensity.actionGap,
                },
              ]}
            >
              {renderAdminOutboundQuestionnaireRevisionTextField(
                "Morning callback",
                readAdminOutboundText(draftResumePolicy.morningLocalTime) ||
                  adminOutboundDefaultResumePolicy.morningLocalTime,
                (value) =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyValue(
                    "morningLocalTime",
                    value,
                  ),
              )}
              {renderAdminOutboundQuestionnaireRevisionTextField(
                "Afternoon callback",
                readAdminOutboundText(draftResumePolicy.afternoonLocalTime) ||
                  adminOutboundDefaultResumePolicy.afternoonLocalTime,
                (value) =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyValue(
                    "afternoonLocalTime",
                    value,
                  ),
              )}
              {renderAdminOutboundQuestionnaireRevisionTextField(
                "Evening callback",
                readAdminOutboundText(draftResumePolicy.eveningLocalTime) ||
                  adminOutboundDefaultResumePolicy.eveningLocalTime,
                (value) =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyValue(
                    "eveningLocalTime",
                    value,
                  ),
              )}
            </View>
            <View
              style={[
                styles.patientRowFooter,
                {
                  gap: tokens.spacing.xs,
                  justifyContent: "flex-start",
                },
              ]}
            >
              {renderAdminOutboundQuestionnaireRevisionToggle({
                checked: readAdminOutboundBoolean(
                  draftResumePolicy.allowPatientRequestedTime,
                  adminOutboundDefaultResumePolicy.allowPatientRequestedTime,
                ),
                labelText: "Patient callback time",
                onPress: () =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyValue(
                    "allowPatientRequestedTime",
                    !readAdminOutboundBoolean(
                      draftResumePolicy.allowPatientRequestedTime,
                      adminOutboundDefaultResumePolicy.allowPatientRequestedTime,
                    ),
                  ),
              })}
              {renderAdminOutboundQuestionnaireRevisionToggle({
                checked: readAdminOutboundBoolean(
                  draftResumePolicy.allowOutsideCampaignWindow,
                  adminOutboundDefaultResumePolicy.allowOutsideCampaignWindow,
                ),
                labelText: "Resume outside window",
                onPress: () =>
                  updateAdminOutboundQuestionnaireRevisionResumePolicyValue(
                    "allowOutsideCampaignWindow",
                    !readAdminOutboundBoolean(
                      draftResumePolicy.allowOutsideCampaignWindow,
                      adminOutboundDefaultResumePolicy.allowOutsideCampaignWindow,
                    ),
                  ),
              })}
            </View>
          </View>
          <View style={[styles.listGroup, { gap: 3 }]}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.metaLabelFontSize,
                  textTransform: "uppercase",
                },
              ]}
            >
              Question-specific rules
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Expand a question only when its answer hint, review keywords, or
              red-flag behavior needs custom handling.
            </Text>
          </View>
          {draft.questions.map((question, index) => {
            const voicePhraseConfig = readAdminOutboundRecord(
              question.voicePhraseConfig,
            );
            const redFlagRules = readAdminOutboundRecord(question.redFlagRules);
            const answerNormalization = readAdminOutboundRecord(
              question.answerNormalization,
            );
            const questionMetadata = readAdminOutboundRecord(
              question.questionMetadata,
            );
            const leadInText =
              readAdminOutboundText(voicePhraseConfig.leadIn) ||
              readAdminOutboundText(voicePhraseConfig.promptLeadIn) ||
              readAdminOutboundText(voicePhraseConfig.voiceLeadIn);
            const answerHintText =
              readAdminOutboundText(voicePhraseConfig.answerHint) ||
              readAdminOutboundText(voicePhraseConfig.answerHintText) ||
              readAdminOutboundText(voicePhraseConfig.binaryAnswerHint);
            const keywordText =
              readAdminOutboundText(redFlagRules.keywordsInputText) ||
              formatAdminOutboundKeywordList(
                redFlagRules.keywords ?? redFlagRules.containsAny,
              );
            const answerHintBehavior =
              resolveAdminOutboundAnswerHintBehavior(voicePhraseConfig);
            const unsureRequiresReview =
              redFlagRules.unsureRequiresReview !== false;
            const alwaysRequiresReview = readAdminOutboundBoolean(
              redFlagRules.alwaysRequiresReview,
              false,
            );
            const yesRequiresReview = readAdminOutboundBoolean(
              redFlagRules.yesRequiresReview,
              false,
            );
            const noRequiresReview = readAdminOutboundBoolean(
              redFlagRules.noRequiresReview,
              false,
            );
            const stopRoutineAutomation = readAdminOutboundBoolean(
              redFlagRules.stopRoutineAutomation,
              false,
            );
            const treatAsRedFlag = readAdminOutboundBoolean(
              redFlagRules.treatAsRedFlag,
              false,
            );
            const medicationReconciliationMetadata = readAdminOutboundRecord(
              questionMetadata.medicationReconciliation,
            );
            const medicationReconciliation = readAdminOutboundBoolean(
              answerNormalization.medicationReconciliation,
              false,
            );
            const capturesPatientReportedMedication = readAdminOutboundBoolean(
              medicationReconciliationMetadata.capturesPatientReportedMedication,
              medicationReconciliation,
            );
            const plainLanguageDefinition = readAdminOutboundText(
              questionMetadata.plainLanguageDefinition,
            );
            const canConfigureMedicationReview =
              question.answerType === "short_text";
            const policyQuestionKey =
              question.questionKey || `question-${String(index)}`;
            const isPolicyQuestionExpanded =
              adminOutboundExpandedPolicyQuestionKeys.has(policyQuestionKey);
            const togglePolicyQuestion = () =>
              setAdminOutboundExpandedPolicyQuestionKeys((current) => {
                const next = new Set(current);

                if (next.has(policyQuestionKey)) {
                  next.delete(policyQuestionKey);
                } else {
                  next.add(policyQuestionKey);
                }

                return next;
              });

            // Collapsed-row summary chips: show WHAT is configured at a glance,
            // so you only expand a question when you intend to change it.
            const policyMarkerTone = {
              danger: theme.danger,
              warning: theme.warning,
              accent: theme.accentStrong,
              neutral: theme.textSecondary,
            } as const;
            const reviewLabelText = readAdminOutboundText(
              question.clinicianReviewLabel,
            );
            const questionSkipLogic = readAdminOutboundRecord(
              question.skipLogic,
            );
            const hasBranchSkipLogic =
              Array.isArray(questionSkipLogic.branches) &&
              questionSkipLogic.branches.length > 0;
            const hasSkipLogic =
              Object.keys(readAdminOutboundRecord(questionSkipLogic.onlyIf))
                .length > 0 || hasBranchSkipLogic;
            // Follow-up presentation mirrors the Questions step: a row gated
            // by an earlier question's answer indents under its gate with a
            // "↳ Follow-up of …" caption. Gate lookup only searches EARLIER
            // questions (same semantics as the runtime evaluator), so a
            // deleted or forward gate degrades to a warning caption instead
            // of crashing. List order stays untouched — sequence order
            // already places follow-ups right after their gates.
            const policyFollowUpTrigger = describeAdminOutboundFollowUpTrigger(
              question.skipLogic?.onlyIf,
            );
            const policyFollowUpGateQuestion =
              policyFollowUpTrigger?.kind === "follow-up"
                ? findAdminOutboundFollowUpGateQuestion(
                    draft.questions,
                    index,
                    policyFollowUpTrigger.gateQuestionKey,
                  )
                : null;
            const isPolicyFollowUpGateMissing =
              policyFollowUpTrigger?.kind === "follow-up" &&
              policyFollowUpGateQuestion == null;
            const policyFollowUpCaptionText =
              policyFollowUpGateQuestion == null
                ? policyFollowUpTrigger?.kind === "follow-up"
                  ? policyFollowUpTrigger.gateQuestionKey
                  : ""
                : truncateAdminOutboundFollowUpPromptText(
                    policyFollowUpGateQuestion.promptText,
                  ) || policyFollowUpGateQuestion.questionKey;
            const redFlagActive =
              yesRequiresReview ||
              noRequiresReview ||
              alwaysRequiresReview ||
              treatAsRedFlag ||
              stopRoutineAutomation;
            const redFlagMarkerLabel = treatAsRedFlag
              ? "Red flag"
              : stopRoutineAutomation
                ? "Stop & review"
                : alwaysRequiresReview
                  ? "Always review"
                  : yesRequiresReview
                    ? "Review on yes"
                    : noRequiresReview
                      ? "Review on no"
                      : "Review";
            const policyMarkers: Array<{
              key: string;
              label: string;
              tone: keyof typeof policyMarkerTone;
            }> = [];
            if (redFlagActive) {
              policyMarkers.push({
                key: "redflag",
                label: redFlagMarkerLabel,
                tone:
                  treatAsRedFlag || stopRoutineAutomation
                    ? "danger"
                    : "warning",
              });
            }
            if (keywordText.trim()) {
              policyMarkers.push({
                key: "keywords",
                label: "Keywords",
                tone: "warning",
              });
            }
            if (reviewLabelText.trim()) {
              policyMarkers.push({
                key: "label",
                label: reviewLabelText.trim(),
                tone: "accent",
              });
            }
            // Prefer a chip that says WHEN the question is asked over the
            // generic "Skip logic" one; fall back for missing gates, branch
            // jumps, or conditions too complex to summarize honestly.
            if (
              policyFollowUpTrigger?.kind === "follow-up" &&
              !isPolicyFollowUpGateMissing &&
              !hasBranchSkipLogic &&
              policyFollowUpTrigger.label != null
            ) {
              policyMarkers.push({
                key: "skip",
                label: policyFollowUpTrigger.label,
                tone: "neutral",
              });
            } else if (
              policyFollowUpTrigger?.kind === "demographic" &&
              !hasBranchSkipLogic
            ) {
              policyMarkers.push({
                key: "skip",
                label: "Asked by age/sex",
                tone: "neutral",
              });
            } else if (hasSkipLogic) {
              policyMarkers.push({
                key: "skip",
                label: "Skip logic",
                tone: "neutral",
              });
            }
            if (plainLanguageDefinition.trim()) {
              policyMarkers.push({
                key: "def",
                label: "Definition",
                tone: "neutral",
              });
            }
            if (medicationReconciliation || capturesPatientReportedMedication) {
              policyMarkers.push({
                key: "medrec",
                label: "Med-rec",
                tone: "warning",
              });
            }
            if (leadInText.trim() || answerHintText.trim()) {
              policyMarkers.push({
                key: "voice",
                label: "Voice",
                tone: "neutral",
              });
            }

            return (
              <View
                key={`${question.questionKey}-policy-${String(index)}`}
                style={[
                  styles.listGroup,
                  {
                    backgroundColor: adminFigmaPalette.panelSubtle,
                    borderColor: adminFigmaPalette.border,
                    borderRadius: adminOperationalLayout.panelRadius,
                    borderWidth: 1,
                    gap: tokens.spacing.sm,
                    marginLeft:
                      policyFollowUpTrigger?.kind === "follow-up"
                        ? tokens.spacing.xl
                        : 0,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      flexDirection: isMediumLayout ? "row" : "column",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View style={[styles.listGroup, { flex: 1, gap: 3 }]}>
                    {policyFollowUpTrigger?.kind === "follow-up" ? (
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: isPolicyFollowUpGateMissing
                              ? theme.warning
                              : theme.accentStrong,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: adminModalDensity.metaLabelFontSize,
                          },
                        ]}
                      >
                        {isPolicyFollowUpGateMissing
                          ? `↳ Follow-up of ${policyFollowUpCaptionText} (gate question missing — will always be asked)`
                          : `↳ Follow-up of ${policyFollowUpCaptionText}`}
                      </Text>
                    ) : null}
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: adminModalDensity.metaLabelFontSize,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      {`${String(index + 1)}. ${question.sectionLabel ?? question.sectionKey ?? "Default"}`}
                    </Text>
                    <Text
                      numberOfLines={2}
                      style={[
                        styles.sectionTitle,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: adminModalDensity.bodyFontSize,
                          fontWeight: adminTypography.titleWeight,
                          lineHeight: adminModalDensity.bodyLineHeight,
                        },
                      ]}
                    >
                      {question.promptText}
                    </Text>
                    {policyMarkers.length > 0 ? (
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 4,
                        }}
                      >
                        {policyMarkers.map((marker) => (
                          <View
                            key={marker.key}
                            style={{
                              alignSelf: "flex-start",
                              borderColor: policyMarkerTone[marker.tone],
                              borderRadius: 999,
                              borderWidth: 1,
                              paddingHorizontal: 8,
                              paddingVertical: 2,
                            }}
                          >
                            <Text
                              style={[
                                styles.pillValueText,
                                {
                                  color: policyMarkerTone[marker.tone],
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: 10,
                                  fontWeight: adminTypography.strongWeight,
                                },
                              ]}
                            >
                              {marker.label}
                            </Text>
                          </View>
                        ))}
                      </View>
                    ) : null}
                  </View>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityState={{ expanded: isPolicyQuestionExpanded }}
                    onPress={togglePolicyQuestion}
                    style={[
                      adminWebOutlineReset,
                      {
                        alignItems: "center",
                        backgroundColor: isPolicyQuestionExpanded
                          ? theme.accentSubtle
                          : theme.surface,
                        borderColor: isPolicyQuestionExpanded
                          ? theme.accent
                          : theme.borderStrong,
                        borderRadius: adminOperationalLayout.controlRadius,
                        borderWidth: 1,
                        flexDirection: "row",
                        gap: 7,
                        minHeight: 34,
                        paddingHorizontal: 12,
                        paddingVertical: 6,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: isPolicyQuestionExpanded
                            ? theme.accentStrong
                            : theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: adminTypography.strongWeight,
                        },
                      ]}
                    >
                      {isPolicyQuestionExpanded ? "Hide policy" : "Show policy"}
                    </Text>
                    <LineIcon
                      color={
                        isPolicyQuestionExpanded
                          ? theme.accentStrong
                          : theme.textSecondary
                      }
                      name="chevron-right"
                      size={15}
                      strokeWidth={2.2}
                    />
                  </Pressable>
                </View>
                {isPolicyQuestionExpanded ? (
                  <View
                    style={{
                      borderTopColor: theme.borderSubtle,
                      borderTopWidth: 1,
                      gap: tokens.spacing.lg,
                      marginTop: tokens.spacing.xs,
                      paddingTop: tokens.spacing.md,
                    }}
                  >
                    <View style={{ gap: tokens.spacing.sm }}>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: adminModalDensity.metaLabelFontSize,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        Voice
                      </Text>
                      {renderAdminModalSelectField({
                        fieldKey: `answer-hint-behavior-${question.questionKey}`,
                        labelText: "Answer hint behavior",
                        onChange: (value) =>
                          updateAdminOutboundQuestionnaireRevisionAnswerHintBehavior(
                            index,
                            value === "always" ||
                              value === "hide" ||
                              value === "none"
                              ? value
                              : "default",
                          ),
                        options: [
                          {
                            labelText: "Default first prompts",
                            value: "default",
                          },
                          { labelText: "Always include", value: "always" },
                          { labelText: "Hide hint", value: "hide" },
                          { labelText: "No answer hint", value: "none" },
                        ],
                        value: answerHintBehavior,
                      })}
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "stretch",
                            flexDirection: isMediumLayout ? "row" : "column",
                            gap: adminModalDensity.actionGap,
                          },
                        ]}
                      >
                        {renderAdminOutboundQuestionnaireRevisionTextField(
                          "Question lead-in",
                          leadInText,
                          (value) =>
                            updateAdminOutboundQuestionnaireRevisionLeadIn(
                              index,
                              value,
                            ),
                        )}
                        {renderAdminOutboundQuestionnaireRevisionTextField(
                          "Answer hint",
                          answerHintText,
                          (value) =>
                            updateAdminOutboundQuestionnaireRevisionAnswerHint(
                              index,
                              value,
                            ),
                        )}
                      </View>
                    </View>
                    <View style={{ gap: tokens.spacing.sm }}>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: adminModalDensity.metaLabelFontSize,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        {"Review & safety"}
                      </Text>
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "stretch",
                            flexDirection: isMediumLayout ? "row" : "column",
                            gap: adminModalDensity.actionGap,
                          },
                        ]}
                      >
                        {renderAdminOutboundQuestionnaireRevisionTextField(
                          "Clinician review label",
                          question.clinicianReviewLabel ?? "",
                          (value) =>
                            updateAdminOutboundQuestionnaireRevisionClinicianReviewLabel(
                              index,
                              value,
                            ),
                        )}
                        {renderAdminOutboundQuestionnaireRevisionTextField(
                          "Review keywords",
                          keywordText,
                          (value) =>
                            updateAdminOutboundQuestionnaireRevisionReviewKeywords(
                              index,
                              value,
                            ),
                        )}
                      </View>
                      <View style={{ gap: tokens.spacing.sm }}>
                        {renderAdminOutboundQuestionnaireRevisionToggle({
                          checked: unsureRequiresReview,
                          descriptionText:
                            "Flag for clinician review when the patient answers 'not sure'.",
                          labelText: "Not sure review",
                          onPress: () =>
                            updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                              index,
                              "redFlagRules",
                              "unsureRequiresReview",
                              unsureRequiresReview ? false : true,
                            ),
                        })}
                        {renderAdminOutboundQuestionnaireRevisionToggle({
                          checked: yesRequiresReview,
                          descriptionText:
                            "Flag for review when the patient answers yes.",
                          labelText: "Yes review",
                          onPress: () =>
                            updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                              index,
                              "redFlagRules",
                              "yesRequiresReview",
                              yesRequiresReview ? null : true,
                            ),
                        })}
                        {renderAdminOutboundQuestionnaireRevisionToggle({
                          checked: noRequiresReview,
                          descriptionText:
                            "Flag for review when the patient answers no.",
                          labelText: "No review",
                          onPress: () =>
                            updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                              index,
                              "redFlagRules",
                              "noRequiresReview",
                              noRequiresReview ? null : true,
                            ),
                        })}
                        {renderAdminOutboundQuestionnaireRevisionToggle({
                          checked: alwaysRequiresReview,
                          descriptionText:
                            "Always send this question's answer for clinician review.",
                          labelText: "Always review",
                          onPress: () =>
                            updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                              index,
                              "redFlagRules",
                              "alwaysRequiresReview",
                              alwaysRequiresReview ? null : true,
                            ),
                        })}
                        {renderAdminOutboundQuestionnaireRevisionToggle({
                          checked: stopRoutineAutomation,
                          descriptionText:
                            "Pause the automated call for review when this question is flagged.",
                          labelText: "Stop on red flag",
                          onPress: () =>
                            updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                              index,
                              "redFlagRules",
                              "stopRoutineAutomation",
                              stopRoutineAutomation ? null : true,
                            ),
                        })}
                        {renderAdminOutboundQuestionnaireRevisionToggle({
                          checked: treatAsRedFlag,
                          descriptionText:
                            "Escalate this question's flag to red - the highest severity.",
                          labelText: "Treat as red",
                          onPress: () =>
                            updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                              index,
                              "redFlagRules",
                              "treatAsRedFlag",
                              treatAsRedFlag ? null : true,
                            ),
                        })}
                        {canConfigureMedicationReview
                          ? renderAdminOutboundQuestionnaireRevisionToggle({
                              checked: capturesPatientReportedMedication,
                              descriptionText:
                                "Capture the answer as a medication list for reconciliation.",
                              labelText: "Medication review",
                              onPress: () =>
                                updateAdminOutboundQuestionnaireRevisionMedicationReview(
                                  index,
                                  !capturesPatientReportedMedication,
                                ),
                            })
                          : null}
                      </View>
                    </View>
                    <View style={{ gap: tokens.spacing.sm }}>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: adminModalDensity.metaLabelFontSize,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        Definition
                      </Text>
                      {renderAdminOutboundQuestionnaireRevisionTextField(
                        "Plain-language definition",
                        plainLanguageDefinition,
                        (value) =>
                          updateAdminOutboundQuestionnaireRevisionQuestionRecordValue(
                            index,
                            "questionMetadata",
                            "plainLanguageDefinition",
                            value,
                          ),
                      )}
                    </View>
                  </View>
                ) : null}
              </View>
            );
          })}
        </View>
            ) : null}

            {adminOutboundRevisionStep === "questions" ? (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: adminFigmaPalette.border,
              borderRadius: adminOperationalLayout.panelRadius,
              gap: tokens.spacing.md,
              padding: adminModalDensity.cardPadding,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: adminModalDensity.sectionTitleFontSize,
                lineHeight: adminModalDensity.sectionTitleLineHeight,
              },
            ]}
          >
            Questions
          </Text>
          <View
            style={[
              styles.listGroup,
              {
                backgroundColor: adminFigmaPalette.panelSubtle,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                gap: tokens.spacing.sm,
                padding: tokens.spacing.md,
              },
            ]}
          >
            {renderAdminOutboundQuestionnaireRevisionTextField(
              "Paste question list",
              draft.bulkQuestionText,
              (value) =>
                updateAdminOutboundQuestionnaireRevisionField(
                  "bulkQuestionText",
                  value,
                ),
              true,
            )}
            <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
              {renderAdminPrimaryButton({
                density: "compact",
                disabled:
                  isAdminOutboundCommandSubmitting ||
                  draft.bulkQuestionText.trim().length === 0,
                iconName: "plus",
                labelText: "Append pasted",
                onPress: appendAdminOutboundQuestionnaireRevisionPastedQuestions,
                prefixText: "",
                tone: "surface",
              })}
              {renderAdminPrimaryButton({
                density: "compact",
                disabled: isAdminOutboundCommandSubmitting,
                iconName: "plus",
                labelText: "Add question",
                onPress: () => appendAdminOutboundQuestionnaireRevisionQuestion(),
                prefixText: "",
                tone: "surface",
              })}
            </View>
          </View>
          {draft.questions.map((question, index) => {
            // Follow-up ("ask only if") authoring state. Gate candidates are
            // restricted to EARLIER questions, so cycles and forward
            // references cannot be authored; a stale gate (deleted or moved
            // below this question) renders a warning chip instead.
            const followUpSelection = resolveAdminOutboundFollowUpGateSelection(
              question.skipLogic,
            );
            const followUpGateOptions = buildAdminOutboundFollowUpGateOptions(
              draft.questions,
              index,
            );
            const followUpGateQuestion =
              followUpSelection == null
                ? null
                : findAdminOutboundFollowUpGateQuestion(
                    draft.questions,
                    index,
                    followUpSelection.gateQuestionKey,
                  );
            const isFollowUpGateMissing =
              followUpSelection != null && followUpGateQuestion == null;
            const followUpTriggerOptions =
              buildAdminOutboundFollowUpTriggerOptions(
                followUpGateQuestion?.answerType ?? null,
              );
            const followUpGateCaptionText =
              followUpGateQuestion == null
                ? (followUpSelection?.gateQuestionKey ?? "")
                : truncateAdminOutboundFollowUpPromptText(
                    followUpGateQuestion.promptText,
                  ) || followUpGateQuestion.questionKey;
            const followUpTriggerValue =
              followUpSelection != null &&
              followUpTriggerOptions.some(
                (option) => option.value === followUpSelection.trigger,
              )
                ? followUpSelection.trigger
                : "answered";

            return (
            <View
              key={`${question.questionKey}-${String(index)}`}
              style={[
                styles.listGroup,
                {
                  borderTopColor: index === 0 ? "transparent" : adminFigmaPalette.border,
                  borderTopWidth: index === 0 ? 0 : 1,
                  gap: tokens.spacing.xs,
                  marginLeft:
                    followUpSelection == null ? 0 : tokens.spacing.xl,
                  paddingTop: index === 0 ? 0 : tokens.spacing.md,
                },
              ]}
            >
              {followUpSelection == null ? null : (
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: isFollowUpGateMissing
                        ? theme.warning
                        : theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminModalDensity.metaLabelFontSize,
                    },
                  ]}
                >
                  {`↳ Follow-up of ${followUpGateCaptionText}`}
                </Text>
              )}
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {`${String(index + 1)}. ${question.sectionLabel ?? question.sectionKey ?? "Default"}`}
              </Text>
              <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
                {renderAdminPrimaryButton({
                  density: "compact",
                  disabled: isAdminOutboundCommandSubmitting || index === 0,
                  iconName: "chevron-right",
                  iconRotationDegrees: -90,
                  labelText: "Up",
                  minWidth: 70,
                  onPress: () =>
                    moveAdminOutboundQuestionnaireRevisionQuestion(index, -1),
                  prefixText: "",
                  tone: "surface",
                })}
                {renderAdminPrimaryButton({
                  density: "compact",
                  disabled:
                    isAdminOutboundCommandSubmitting ||
                    index === draft.questions.length - 1,
                  iconName: "chevron-right",
                  iconRotationDegrees: 90,
                  labelText: "Down",
                  minWidth: 82,
                  onPress: () =>
                    moveAdminOutboundQuestionnaireRevisionQuestion(index, 1),
                  prefixText: "",
                  tone: "surface",
                })}
                {renderAdminPrimaryButton({
                  density: "compact",
                  disabled: isAdminOutboundCommandSubmitting,
                  iconName: "edit",
                  labelText: formatAdminOutboundQuestionAnswerType(
                    question.answerType,
                  ),
                  minWidth: 118,
                  onPress: () =>
                    cycleAdminOutboundQuestionnaireRevisionAnswerType(index),
                  prefixText: "",
                  tone: "surface",
                })}
                {renderAdminPrimaryButton({
                  density: "compact",
                  disabled: isAdminOutboundCommandSubmitting,
                  iconName: question.required === false ? "x" : "check",
                  labelText: question.required === false ? "Optional" : "Required",
                  minWidth: 100,
                  onPress: () =>
                    toggleAdminOutboundQuestionnaireRevisionRequired(index),
                  prefixText: "",
                  tone: "surface",
                })}
                {renderAdminPrimaryButton({
                  density: "compact",
                  disabled:
                    isAdminOutboundCommandSubmitting ||
                    draft.questions.length < 2,
                  iconName: "x",
                  labelText: "Remove",
                  minWidth: 90,
                  onPress: () =>
                    removeAdminOutboundQuestionnaireRevisionQuestion(index),
                  prefixText: "",
                  tone: "danger",
                })}
              </View>
              <TextInput
                editable={!isAdminOutboundCommandSubmitting}
                multiline={true}
                onChangeText={(value) =>
                  updateAdminOutboundQuestionnaireRevisionPrompt(index, value)
                }
                placeholder="Question prompt"
                placeholderTextColor={theme.textTertiary}
                style={[
                  styles.searchInput,
                  {
                    backgroundColor: theme.surface,
                    borderColor: theme.borderStrong,
                    borderRadius: adminOperationalLayout.controlRadius,
                    borderWidth: 1,
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.fieldFontSize,
                    lineHeight: 20,
                    maxHeight: 176,
                    minHeight: 66,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    shadowColor: theme.accentStrong,
                    shadowOffset: { height: 2, width: 0 },
                    shadowOpacity: 0.04,
                    shadowRadius: 8,
                    textAlignVertical: "top",
                  },
                ]}
                value={question.promptText}
              />
              <Text
                numberOfLines={1}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textTertiary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {`Question key: ${question.questionKey}`}
              </Text>
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "stretch",
                    flexDirection: isMediumLayout ? "row" : "column",
                    gap: adminModalDensity.actionGap,
                  },
                ]}
              >
                {renderAdminModalSelectField({
                  fieldKey: `follow-up-gate-${question.questionKey}-${String(index)}`,
                  labelText: "Follow-up of",
                  onChange: (value) =>
                    updateAdminOutboundQuestionnaireRevisionFollowUpGate(
                      index,
                      value,
                    ),
                  options: [
                    { labelText: "Always asked", value: "" },
                    ...followUpGateOptions,
                  ],
                  value: followUpSelection?.gateQuestionKey ?? "",
                })}
                {followUpSelection == null || followUpGateQuestion == null
                  ? null
                  : renderAdminModalSelectField({
                      fieldKey: `follow-up-trigger-${question.questionKey}-${String(index)}`,
                      labelText: "Show when",
                      onChange: (value) =>
                        updateAdminOutboundQuestionnaireRevisionFollowUpTrigger(
                          index,
                          value === "equals_yes" ||
                            value === "equals_no" ||
                            value === "equals_value"
                            ? value
                            : "answered",
                          followUpSelection.equalsText,
                        ),
                      options: followUpTriggerOptions,
                      value: followUpTriggerValue,
                    })}
                {followUpSelection != null &&
                followUpGateQuestion?.answerType === "single_choice" &&
                followUpSelection.trigger === "equals_value"
                  ? renderAdminOutboundQuestionnaireRevisionTextField(
                      "Equals value",
                      followUpSelection.equalsText,
                      (value) =>
                        updateAdminOutboundQuestionnaireRevisionFollowUpTrigger(
                          index,
                          "equals_value",
                          value,
                        ),
                    )
                  : null}
              </View>
              {isFollowUpGateMissing ? (
                <View
                  style={{
                    alignSelf: "flex-start",
                    borderColor: theme.warning,
                    borderRadius: 999,
                    borderWidth: 1,
                    paddingHorizontal: 8,
                    paddingVertical: 2,
                  }}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.warning,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: adminTypography.strongWeight,
                      },
                    ]}
                  >
                    Gate question missing — will always be asked
                  </Text>
                </View>
              ) : null}
            </View>
            );
          })}
        </View>
            ) : null}
          </View>
          </ScrollView>
        </View>

        <View
          style={[
            styles.patientRowFooter,
            {
              alignItems: "center",
              backgroundColor: theme.surface,
              borderColor: theme.borderStrong,
              borderRadius: adminOperationalLayout.panelRadius,
              borderWidth: 1,
              gap: tokens.spacing.xs,
              justifyContent: "flex-end",
              padding: tokens.spacing.sm,
              shadowColor: theme.accentStrong,
              shadowOffset: { height: -3, width: 0 },
              shadowOpacity: 0.05,
              shadowRadius: 14,
            },
          ]}
        >
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminOutboundCommandSubmitting,
            labelText: "Cancel",
            onPress: () => setAdminOutboundQuestionnaireRevisionDraft(null),
            prefixText: "",
            tone: "surface",
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            disabled: isAdminOutboundCommandSubmitting,
            labelText: isAdminOutboundCommandSubmitting
              ? "Publishing"
              : "Publish revision",
            onPress: () => {
              void submitAdminOutboundQuestionnaireRevision();
            },
            prefixText: "",
            tone: "primary",
          })}
        </View>
      </View>
    );
  };

  const renderAdminOutboundQuestionnaireRevisionModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: "Outbound questionnaire revision editor",
      children: renderAdminOutboundQuestionnaireRevisionPanel(),
      maxWidth: 1040,
      onClose: () => {
        if (isAdminOutboundCommandSubmitting) {
          return;
        }

        setActiveFieldHelp(null);
        setAdminOutboundQuestionnaireRevisionDraft(null);
      },
      presentation: "workspace",
      rail: null,
      visible: adminOutboundQuestionnaireRevisionDraft != null,
    });

  const analyticsAnchorDate = summary?.generatedAt
    ? new Date(summary.generatedAt)
    : new Date();
  const summaryAnalytics =
    (summary as
      | (TenantAdminSummaryResponseV1["summary"] & {
          analytics?: AdminAnalyticsSnapshot;
        })
      | null)?.analytics ?? null;
  const adminAnalyticsDepartmentOptions = [
    {
      labelText: "All departments",
      value: "all",
    },
    ...(summary?.departments ?? []).map((department) => ({
      labelText:
        department.displayName.trim().length > 0
          ? department.displayName
          : department.departmentCode,
      value: department.departmentCode,
    })),
  ];
  const adminAnalyticsSelectedDepartmentCode =
    adminAnalyticsDepartmentOptions.some(
      (option) => option.value === adminAnalyticsRangeSelection.departmentCode,
    )
      ? (adminAnalyticsRangeSelection.departmentCode ?? "all")
      : "all";
  const adminAnalyticsSelectedDepartmentLabel =
    adminAnalyticsDepartmentOptions.find(
      (option) => option.value === adminAnalyticsSelectedDepartmentCode,
    )?.labelText ?? "All departments";
  const parsedAnalyticsStartDate =
    parseAdminDateInputValue(adminAnalyticsRangeSelection.startDate) ??
    analyticsAnchorDate;
  const parsedAnalyticsEndDate =
    parseAdminDateInputValue(adminAnalyticsRangeSelection.endDate) ??
    parsedAnalyticsStartDate;
  const analyticsStartDate =
    parsedAnalyticsStartDate.getTime() <= parsedAnalyticsEndDate.getTime()
      ? parsedAnalyticsStartDate
      : parsedAnalyticsEndDate;
  const analyticsEndDate =
    parsedAnalyticsStartDate.getTime() <= parsedAnalyticsEndDate.getTime()
      ? parsedAnalyticsEndDate
      : parsedAnalyticsStartDate;
  const analyticsDateRangeLabel =
    formatAdminDateInputValue(analyticsStartDate) ===
    formatAdminDateInputValue(analyticsEndDate)
      ? formatAdminLegacyDateLabel(analyticsEndDate)
      : `${formatAdminLegacyDateLabel(analyticsStartDate)} - ${formatAdminLegacyDateLabel(analyticsEndDate)}`;

  const aiSignalTotal =
    (summary?.workflow.pendingMessageReviewCount ?? 0) +
    (summary?.audit.fallbackResolutionCount ?? 0) +
    (summary?.audit.unmappedResolutionCount ?? 0);
  const aiEditedPercent =
    aiSignalTotal > 0
      ? Math.round(
          ((summary?.workflow.pendingMessageReviewCount ?? 0) / aiSignalTotal) *
            100,
        )
      : 0;
  const aiRejectedPercent =
    aiSignalTotal > 0
      ? Math.round(
          (((summary?.audit.fallbackResolutionCount ?? 0) +
            (summary?.audit.unmappedResolutionCount ?? 0)) /
            aiSignalTotal) *
            100,
        )
      : 0;
  const aiApprovedPercent =
    aiSignalTotal > 0
      ? Math.max(0, 100 - aiEditedPercent - aiRejectedPercent)
      : 0;
  const organizationPatientCount =
    summaryAnalytics?.activePatientCount ?? adminPatientOperationsTotalCount;
  const analyticsInvitationCount =
    summaryAnalytics?.receivedInvitationCount ?? 0;
  const analyticsLoggedInCount =
    summaryAnalytics?.loggedInToAppCount ?? 0;
  const analyticsEngagedCount =
    summaryAnalytics?.engagedAgentCount ?? 0;
  const resolveAnalyticsInvitationPercent = (count: number) =>
    analyticsInvitationCount > 0 ? (count / analyticsInvitationCount) * 100 : 0;
  const formatAnalyticsInvitationRate = (count: number) =>
    resolveAnalyticsInvitationPercent(count).toFixed(1);
  const analyticsLoggedInPercent = resolveAnalyticsInvitationPercent(
    analyticsLoggedInCount,
  );
  const analyticsEngagedPercent = resolveAnalyticsInvitationPercent(
    analyticsEngagedCount,
  );
  const analyticsMessagesPerPatient =
    summaryAnalytics?.messagesPerPatient ?? null;
  const analyticsAiConfidencePercent =
    summaryAnalytics?.aiConfidencePercent ?? null;
  const analyticsLanguageModel = summaryAnalytics?.languageModel ?? {
    aiRating: null,
    aiRatingDelta: null,
    autonomousResponseDeltaPercent: null,
    autonomousResponsePercent: 0,
    clinicianApprovedDeltaPercent: null,
    clinicianApprovedPercent: 0,
    manualEntryDeltaPercent: null,
    manualEntryPercent: 0,
  };
  const analyticsSeries = summaryAnalytics?.accuracySeries ?? [];
  const analyticsChartSeries = sampleAdminAnalyticsSeries(analyticsSeries, 31);
  const analyticsAxisSeries = sampleAdminAnalyticsSeries(analyticsChartSeries, 7);
  const formatAdminAnalyticsNumber = (
    value: number | null | undefined,
    precision = 0,
  ) =>
    typeof value === "number" && Number.isFinite(value)
      ? value.toFixed(precision)
      : "N/A";
  const analyticsSummaryCards = [
    {
      id: "active-patients",
      delta: summaryAnalytics?.activePatientDeltaPercent ?? null,
      labelText: "Active patients",
      precision: 0,
      valueText: String(organizationPatientCount),
    },
    {
      id: "ai-responses",
      delta: summaryAnalytics?.aiResponseDeltaPercent ?? null,
      labelText: "AI Responses",
      precision: 0,
      valueText: String(
        summaryAnalytics?.aiResponseCount ??
          summary?.audit.derivedStateEvaluationCount ??
          0,
      ),
    },
    {
      id: "avg-response-time",
      delta: summaryAnalytics?.averageResponseTimeDeltaSeconds ?? null,
      labelText: "Avg response time (s)",
      precision: 2,
      valueText: formatAdminAnalyticsNumber(
        summaryAnalytics?.averageResponseTimeSeconds,
        2,
      ),
    },
    {
      id: "patient-messages",
      delta: summaryAnalytics?.patientMessageDeltaPercent ?? null,
      labelText: "Patient messages",
      precision: 0,
      valueText: String(
        summaryAnalytics?.patientMessageCount ??
          summary?.workflow.openConversationCount ??
          0,
      ),
    },
    {
      id: "messages-per-patient",
      delta: summaryAnalytics?.messagesPerPatientDeltaPercent ?? null,
      labelText: "Messages per patient",
      precision: 2,
      valueText: formatAdminAnalyticsNumber(analyticsMessagesPerPatient, 2),
    },
  ];
  const voiceAnalyticsSummaryCards = [
    {
      id: "voice-calls",
      delta: summaryAnalytics?.voiceCallDeltaPercent ?? null,
      labelText: "Voice calls",
      valueText: String(summaryAnalytics?.voiceCallCount ?? 0),
    },
    {
      id: "voice-minutes",
      delta: summaryAnalytics?.voiceMinutesDeltaPercent ?? null,
      labelText: "Est. voice minutes",
      valueText: String(summaryAnalytics?.voiceMinutes ?? 0),
    },
    {
      id: "voice-responses",
      delta: summaryAnalytics?.voiceResponseDeltaPercent ?? null,
      labelText: "Voice responses",
      valueText: String(summaryAnalytics?.voiceResponseCount ?? 0),
    },
    {
      id: "voice-time-saved",
      delta:
        summaryAnalytics?.estimatedClinicianMinutesSavedDeltaPercent ?? null,
      labelText: "Est. clinician minutes saved",
      valueText: String(
        summaryAnalytics?.estimatedClinicianMinutesSaved ?? 0,
      ),
    },
  ];
  const organizationAnalyticsSummaryCards = [
    ...analyticsSummaryCards,
    ...voiceAnalyticsSummaryCards,
  ];
  const analyticsDefinitionItems = [
    {
      id: "ai-approvals",
      labelText: "AI approvals",
      valueText:
        "Drafts approved after care-team rules pass, without manual rewrite.",
    },
    {
      id: "draft-quality",
      labelText: "Draft quality",
      valueText:
        "Average confidence across generated and reviewed response drafts.",
    },
    {
      id: "accuracy",
      labelText: "Accuracy",
      valueText:
        "Directional 5-point quality signal from autonomous, approved, and manual outcomes.",
    },
    {
      id: "minutes-saved",
      labelText: "Est. minutes saved",
      valueText:
        "Directional time estimate from handled voice responses and draft actions.",
    },
  ];

  const adminTopMetricCards: Array<{
    id: string;
    iconName: LineIconName;
    labelText: string;
    supportText: string;
    supportTone: "attention" | "ok" | "neutral";
    valueText: string;
  }> = [
    {
      id: "patients",
      iconName: "user",
      labelText: "Patients",
      supportText: `${String(patientAttentionCount)} need attention`,
      supportTone: patientAttentionCount > 0 ? "attention" : "ok",
      valueText: String(adminPatientOperationsTotalCount),
    },
    {
      id: "clinicians",
      iconName: "clinician",
      labelText: "Clinicians",
      supportText: `${String(clinicianSiteCount)} site scopes`,
      supportTone: "ok",
      valueText: String(
        summary?.memberships.clinicianCount ?? adminClinicianOperationsTotalCount,
      ),
    },
    {
      id: "reviews",
      iconName: "check",
      labelText: "Open reviews",
      supportText: `${String(highPriorityReviewCount)} high priority`,
      supportTone: highPriorityReviewCount > 0 ? "attention" : "ok",
      valueText: String(rawReviewRows.reduce((sum, row) => sum + row.count, 0)),
    },
    {
      id: "ai-review",
      iconName: "document",
      labelText: "AI approvals",
      supportText: `${String(aiEditedPercent)}% edited by staff`,
      supportTone: "neutral",
      valueText: `${String(aiApprovedPercent)}%`,
    },
  ];
  const outboundEmptyTargetCounts = {
    calling: 0,
    cancelled: 0,
    completed: 0,
    eligible: 0,
    expired: 0,
    paused: 0,
    review_required: 0,
    scheduled: 0,
    suppressed: 0,
  };
  const outboundDefaultGeneratedAt =
    summary?.generatedAt ?? "2026-05-26T00:00:00.000Z";
  const outboundDefaultAnalyticsStartAt = new Date(
    new Date(outboundDefaultGeneratedAt).getTime() - 13 * 24 * 60 * 60 * 1000,
  ).toISOString();
  const defaultAdminOutboundCallsSummary: TenantAdminOutboundCallsSummaryResponseV1 = {
    analytics: {
      answerRatePercent: 0,
      attemptedCallCount: 0,
      averageAnsweredQuestionCount: 0,
      busyAttemptCount: 0,
      completedSessionCount: 0,
      completionRatePercent: 0,
      estimatedMinutesSaved: 0,
      failedAttemptCount: 0,
      liveAnswerCount: 0,
      noAnswerAttemptCount: 0,
      optOutEventCount: 0,
      redFlagEventCount: 0,
      reviewRequiredRatePercent: 0,
      reviewRequiredSessionCount: 0,
      series: [],
      voicemailAttemptCount: 0,
      windowEndAt: outboundDefaultGeneratedAt,
      windowStartAt: outboundDefaultAnalyticsStartAt,
    },
    audit: {
      actorKindCounts: [],
      eventKindCounts: [],
      latestEventAt: null,
      recentEvents: [],
      totalEventCount: 0,
      windowEndAt: outboundDefaultGeneratedAt,
      windowStartAt: outboundDefaultAnalyticsStartAt,
    },
    campaigns: [
      {
        campaignId: "planned-pre-surgery-questionnaire",
        campaignKey: "pre-surgery-questionnaire",
        campaignKind: "pre_surgery_questionnaire",
        campaignStatus: "draft",
        createdAt: outboundDefaultGeneratedAt,
        createdByDisplay: "Admin setup",
        displayName: "Pre-surgery questionnaire",
        eligibilityRules: {
          relativeProcedureWindow: "7 to 2 days before procedure",
        },
        endsAt: null,
        policy: {
          callCapMinutes: 8,
          campaignId: "planned-pre-surgery-questionnaire",
          maxAttemptsPerDay: 2,
          maxAttemptsTotal: 4,
          minRetryCooldownMinutes: 240,
          optOutPolicy: {
            suppressionRequired: true,
          },
          policyId: "planned-primary-policy",
          policyKey: "primary",
          policyMetadata: {},
          recordingPolicy: "disabled",
          resumePolicy: {
            mode: "continue_from_last_unanswered_question",
          },
          updatedAt: outboundDefaultGeneratedAt,
          voicemailPolicy: "minimal_message",
          window: {
            allowedDays: [
              "monday",
              "tuesday",
              "wednesday",
              "thursday",
              "friday",
            ],
            allowedEndLocalTime: "17:00",
            allowedStartLocalTime: "09:00",
            blackoutDates: [],
            defaultTimezone: null,
            quietHours: {},
            timezoneMode: "patient_local",
          },
        },
        purposeText:
          "Ask approved pre-surgery readiness questions before the procedure window closes.",
        questionnaireDisplayName: "Pre-surgery readiness",
        questionnaireTemplateId: "planned-pre-surgery-readiness",
        questionnaireTemplateVersionId: null,
        questionnaireVersionNumber: null,
        reviewRoutingRules: {
          redFlags: "clinician_review_required",
        },
        startsAt: null,
        targetCounts: outboundEmptyTargetCounts,
        updatedAt: outboundDefaultGeneratedAt,
      },
    ],
    gate: {
      blockerMessages: [
        "AmpleCare outbound calling is disabled in the dev instance.",
        "Dedicated AmpleCare questionnaire call flow is not configured yet.",
      ],
      campaignApiReachable: true,
      liveDialingEnabled: false,
      outboundConnectEnabled: false,
      outboundQuestionnaireFlowConfigured: false,
    },
    generatedAt: outboundDefaultGeneratedAt,
    metrics: {
      activeCampaignCount: 0,
      completedSessionCount: 0,
      dueTodayCount: 0,
      failedAttemptCount: 0,
      inProgressSessionCount: 0,
      medicationCaptureReviewNeededCount: 0,
      pausedSessionCount: 0,
      repeatedNoInputPauseSessionCount: 0,
      reviewRequiredSessionCount: 0,
      scheduledStaleReconciliationCount: 0,
      scheduledTargetCount: 0,
    },
    recentSessions: [],
    recentTargets: [],
    schemaVersion: "tenant-admin-outbound-calls-summary-v1",
    tenantId: adminSummary?.tenantId ?? "tenant-unconfigured",
  };
  const defaultAdminOutboundQuestionnaireTemplates: TenantAdminOutboundQuestionnaireTemplateV1[] =
    [
      {
        activeVersion: null,
        clinicalPurpose:
          "Confirm pre-surgery readiness, identify red flags, and route nurse callbacks before the procedure window closes.",
        createdAt: summary?.generatedAt ?? "2026-05-26T00:00:00.000Z",
        defaultEstimatedMinutes: 7,
        defaultResumePolicy: {
          mode: "continue_from_last_unanswered_question",
        },
        defaultReviewPolicy: adminOutboundDefaultReviewPolicy,
        displayName: "Pre-surgery readiness",
        latestVersion: {
          authoredByDisplay: "Admin setup",
          clinicalApprovedAt: null,
          clinicalApprovedByDisplay: null,
          estimatedMinutes: 7,
          publishedAt: null,
          questionnaireTemplateId: "planned-pre-surgery-readiness",
          questionnaireVersionId: "planned-pre-surgery-readiness-draft-v1",
          questionCount: 8,
          questions: [
            {
              answerNormalization: {},
              answerType: "confirmation",
              clinicianReviewLabel: null,
              dtmfConfig: {},
              promptText:
                "Are you in a good place to answer a few quick pre-surgery questions now?",
              questionMetadata: {},
              questionId: "planned-question-availability",
              questionKey: "availability_confirmation",
              redFlagRules: {},
              required: true,
              sectionKey: "identity-availability",
              sectionLabel: "Identity and availability",
              sequenceNumber: 1,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "confirmation",
              clinicianReviewLabel: null,
              dtmfConfig: {},
              promptText:
                "Do you know the date or arrival window for your procedure?",
              questionMetadata: {},
              questionId: "planned-question-procedure-window",
              questionKey: "procedure_window_confirmation",
              redFlagRules: {},
              required: true,
              sectionKey: "identity-availability",
              sectionLabel: "Identity and availability",
              sequenceNumber: 2,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "yes_no",
              clinicianReviewLabel: null,
              dtmfConfig: {},
              promptText:
                "Have you read your preparation instructions, and do they make sense?",
              questionMetadata: {},
              questionId: "planned-question-prep-instructions",
              questionKey: "prep_instructions_understood",
              redFlagRules: {},
              required: true,
              sectionKey: "preparation",
              sectionLabel: "Preparation",
              sequenceNumber: 3,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "yes_no",
              clinicianReviewLabel: "Medication change review",
              dtmfConfig: {},
              promptText:
                "Since your last review, have you started, stopped, or changed any medicines?",
              questionMetadata: {},
              questionId: "planned-question-medication-change",
              questionKey: "medication_change_since_review",
              redFlagRules: {
                yesRequiresReview: true,
              },
              required: true,
              sectionKey: "medication",
              sectionLabel: "Medication changes",
              sequenceNumber: 4,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "yes_no",
              clinicianReviewLabel: null,
              dtmfConfig: {},
              promptText:
                "Do you have a ride home and someone who can help you after the procedure?",
              questionMetadata: {},
              questionId: "planned-question-support",
              questionKey: "transport_and_support",
              redFlagRules: {},
              required: true,
              sectionKey: "support",
              sectionLabel: "Transport and support",
              sequenceNumber: 5,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "yes_no",
              clinicianReviewLabel: "New symptom review",
              dtmfConfig: {},
              promptText:
                "Do you have any new fever, chest pain, trouble breathing, or anything else that feels urgent?",
              questionMetadata: {},
              questionId: "planned-question-red-flags",
              questionKey: "new_red_flag_symptoms",
              redFlagRules: {
                stopRoutineAutomation: true,
                yesRequiresReview: true,
              },
              required: true,
              sectionKey: "red-flags",
              sectionLabel: "Symptoms and red flags",
              sequenceNumber: 6,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "short_text",
              clinicianReviewLabel: "Callback request",
              dtmfConfig: {},
              promptText:
                "Is there anything you would like the care team to call you back about?",
              questionMetadata: {},
              questionId: "planned-question-callback",
              questionKey: "patient_callback_question",
              redFlagRules: {},
              required: false,
              sectionKey: "support",
              sectionLabel: "Transport and support",
              sequenceNumber: 7,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
            {
              answerNormalization: {},
              answerType: "confirmation",
              clinicianReviewLabel: null,
              dtmfConfig: {},
              promptText:
                "Before I finish, do you feel clear on what happens next?",
              questionMetadata: {},
              questionId: "planned-question-next-step",
              questionKey: "next_step_confirmation",
              redFlagRules: {},
              required: true,
              sectionKey: "closing",
              sectionLabel: "Closing",
              sequenceNumber: 8,
              skipLogic: {},
              validationRules: {},
              voicePhraseConfig: {},
            },
          ],
          resumePolicy: {
            mode: "continue_from_last_unanswered_question",
          },
          retiredAt: null,
          reviewPolicy: adminOutboundDefaultReviewPolicy,
          updatedAt: summary?.generatedAt ?? "2026-05-26T00:00:00.000Z",
          versionLabel: "Draft v1",
          versionNumber: 1,
          versionStatus: "draft",
        },
        locale: "en-AU",
        ownerDepartmentCode: "pre-op",
        questionnaireTemplateId: "planned-pre-surgery-readiness",
        questionnaireTemplateKey: "pre-surgery-readiness",
        status: "draft",
        updatedAt: summary?.generatedAt ?? "2026-05-26T00:00:00.000Z",
      },
    ];
  const adminOutboundCallsSummary =
    adminOutboundCallsPage ?? defaultAdminOutboundCallsSummary;
  const adminOutboundQuestionnaireTemplates =
    adminOutboundQuestionnairesPage ??
    defaultAdminOutboundQuestionnaireTemplates;
  const adminOutboundTargets =
    adminOutboundTargetsPage?.items ?? adminOutboundCallsSummary.recentTargets;
  const adminOutboundCampaignList = adminOutboundCallsSummary.campaigns;
  const adminResolvedSelectedCampaign =
    adminOutboundCampaignList.find(
      (campaign) => campaign.campaignId === adminOutboundSelectedCampaignId,
    ) ??
    adminOutboundCampaignList[0] ??
    null;

  const adminNavigationTabs: Array<{
    countText: string;
    iconName: LineIconName;
    id: AdminDashboardTabId;
    labelText: string;
    supportText: string;
  }> = [
    {
      countText: String(adminPatientOperationsTotalCount),
      iconName: "user",
      id: "patients",
      labelText: "Patients",
      supportText: `${String(patientAttentionCount)} need attention`,
    },
    {
      countText: String(
        summary?.memberships.clinicianCount ?? adminClinicianOperationsTotalCount,
      ),
      iconName: "clinician",
      id: "clinicians",
      labelText: "Clinicians",
      supportText: `${String(clinicianSiteCount)} site scopes`,
    },
    {
      countText: String(rawReviewRows.reduce((sum, row) => sum + row.count, 0)),
      iconName: "check",
      id: "reviews",
      labelText: "Reviews",
      supportText: `${String(highPriorityReviewCount)} high priority`,
    },
    {
      countText: String(adminOutboundCallsSummary.gate.blockerMessages.length),
      iconName: "phone",
      id: "outbound-calls",
      labelText: "Outbound calls",
      supportText: "Setup gate",
    },
    {
      countText: String(aiModelContextFlags.length),
      iconName: "bot",
      id: "ai-models",
      labelText: "AI models",
      supportText: `${String(
        activeAiModelPromptArtifactCount + activeAiModelSourceDocumentCount,
      )} guidance items`,
    },
    {
      countText: String(connectorRows.length),
      iconName: "plug",
      id: "connectors",
      labelText: "Connectors",
      supportText: `${String(activeConnectorCount)} active`,
    },
    {
      countText: `${String(adminPatientPortalEnabledCount)}/${String(
        patientPortalFeatureDefinitionsV1.length,
      )}`,
      iconName: "settings",
      id: "patient-portal",
      labelText: "Patient portal",
      supportText: adminPatientPortalHasUnsavedChanges
        ? "Unsaved modules"
        : "Module visibility",
    },
    {
      countText: String(featureFlagRows.length),
      iconName: "flag",
      id: "flags",
      labelText: "Feature flags",
      supportText: `${String(enabledFeatureFlagCount)} enabled`,
    },
    {
      countText: String(provisioningRuns.length),
      iconName: "case",
      id: "provisioning",
      labelText: "Provisioning",
      supportText: `${String(
        failedProvisioningRunCount +
          failedDatabaseAllocationCount +
          failedDatabaseClusterCount,
      )} need review`,
    },
    {
      countText: String(previewDepartmentRows.length),
      iconName: "hospital",
      id: "departments",
      labelText: "Departments",
      supportText: `${String(previewTeamRows.length)} teams`,
    },
    {
      countText: "Live",
      iconName: "chart",
      id: "analytics",
      labelText: "Analytics",
      supportText: adminAnalyticsSelectedDepartmentLabel,
    },
  ];
  const adminFigmaPalette = {
    accent: theme.accent,
    accentDark: theme.headerBand,
    accentDeep: theme.headerBand,
    accentDeepMuted: theme.headerBand,
    border: theme.borderStrong,
    danger: theme.statusChip.danger.text,
    dangerBright: theme.statusChip.danger.text,
    dangerSoft: theme.statusChip.danger.background,
    field: theme.backgroundSubtle,
    infoSoft: theme.statusChip.info.background,
    ink: theme.textPrimary,
    inkMuted: theme.textSecondary,
    panel: theme.surface,
    panelSubtle: theme.backgroundSubtle,
    rowHover: theme.backgroundSubtle,
    rowSelected: theme.accentSubtle,
    success: theme.statusChip.success.text,
    successBright: theme.statusChip.success.text,
    successSoft: theme.statusChip.success.background,
    surface: theme.surface,
    tab: theme.backgroundSubtle,
    tableHeader: theme.backgroundSubtle,
    warning: theme.statusChip.warning.text,
    warningBright: theme.statusChip.warning.text,
    warningSoft: theme.statusChip.warning.background,
  };
  const adminCompact = {
    bodyFontSize: adminOperationalTypography.body.fontSize,
    bodyLineHeight: adminOperationalTypography.body.lineHeight,
    headingFontSize: adminOperationalTypography.pageTitle.fontSize,
    headingLineHeight: adminOperationalTypography.pageTitle.lineHeight,
    metaCardValueFontSize: 14,
    rowPaddingHorizontal: isAdminMobileLayout ? 8 : 12,
    rowPaddingVertical: isAdminMobileLayout ? 10 : 12,
    rowTitleFontSize: adminOperationalTypography.body.fontSize,
    rowTitleLineHeight: adminOperationalTypography.body.lineHeight,
    searchPaddingHorizontal: isAdminMobileLayout ? 10 : 12,
    searchPaddingVertical: 8,
    sectionGap: 16,
    tableGap: 10,
  };
  const adminTypography = {
    bodyWeight: "500" as const,
    buttonWeight: "700" as const,
    labelWeight: "600" as const,
    sectionLabelWeight: "700" as const,
    selectedWeight: "700" as const,
    strongWeight: "700" as const,
    titleWeight: "800" as const,
  };
  const adminModalDensity = {
    actionGap: adminConfigurationDialog.actionGap,
    bodyFontSize: adminOperationalTypography.body.fontSize,
    bodyLineHeight: adminOperationalTypography.body.lineHeight,
    cardPadding: isAdminMobileLayout
      ? adminConfigurationDialog.paddingCompact
      : adminConfigurationDialog.paddingWide,
    eyebrowFontSize: 10,
    fieldFontSize: adminOperationalTypography.body.fontSize,
    metaLabelFontSize: adminOperationalTypography.metadata.fontSize,
    metaValueFontSize: adminOperationalTypography.table.fontSize,
    sectionTitleFontSize: adminOperationalTypography.sectionTitle.fontSize,
    sectionTitleLineHeight: adminOperationalTypography.sectionTitle.lineHeight,
    subtitleFontSize: adminOperationalTypography.table.fontSize,
    subtitleLineHeight: adminOperationalTypography.table.lineHeight,
    titleFontSize: 21,
    titleLineHeight: 27,
  };
  const adminClinicianTableMinWidth = isMediumLayout
    ? 760
    : isAdminMobileLayout
      ? 640
      : 720;
  const adminConnectorTableMinWidth = isMediumLayout
    ? 940
    : isAdminMobileLayout
      ? 680
      : 840;
  const adminPatientTableMinWidth = isMediumLayout
    ? 900
    : isAdminMobileLayout
      ? 680
      : 840;
  const adminReviewTableMinWidth = isMediumLayout
    ? 860
    : isAdminMobileLayout
      ? 680
      : 800;
  const adminCompactTitleStyle = {
    color: theme.textPrimary,
    fontFamily: layout.displayFontFamily,
    fontSize: adminCompact.headingFontSize,
    fontWeight: adminTypography.titleWeight,
    lineHeight: adminCompact.headingLineHeight,
    marginTop: 0,
  };
  const adminCompactTableHeaderPadding = {
    paddingHorizontal: adminCompact.rowPaddingHorizontal,
    paddingVertical: 8,
  };
  const adminCompactTableRowPadding = {
    paddingHorizontal: adminCompact.rowPaddingHorizontal,
    paddingVertical: adminCompact.rowPaddingVertical,
  };
  const adminTableShellStyle = {
    backgroundColor: adminFigmaPalette.surface,
    borderColor: theme.borderSubtle,
    borderRadius: 0,
    shadowColor: theme.accentStrong,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  };
  const adminTableHeaderStyle = {
    backgroundColor: adminFigmaPalette.tableHeader,
    borderBottomColor: theme.borderSubtle,
    borderBottomWidth: 1,
    gap: tokens.spacing.sm,
    ...adminCompactTableHeaderPadding,
  };
  const adminTableHeaderTextStyle = {
    color: theme.textSecondary,
    fontFamily: layout.bodyFontFamily,
    fontSize: adminOperationalTypography.metadata.fontSize,
    fontWeight: adminTypography.sectionLabelWeight,
    letterSpacing: 0,
    lineHeight: adminOperationalTypography.metadata.lineHeight,
    textTransform: "uppercase" as const,
  };
  const adminTableEmptyStateStyle = {
    backgroundColor: adminFigmaPalette.surface,
    borderColor: theme.borderSubtle,
    borderLeftColor: theme.accent,
    borderLeftWidth: 4,
    borderRadius: 0,
    padding: tokens.spacing.md,
  };
  const buildAdminTableRowStateStyle = (
    _index: number,
    options: {
      hovered?: boolean;
      pressed?: boolean;
      selected?: boolean;
    } = {},
  ) => {
    return {
      backgroundColor: options.pressed
        ? adminFigmaPalette.rowSelected
        : options.hovered
          ? adminFigmaPalette.rowHover
          : options.selected
            ? adminFigmaPalette.rowSelected
            : theme.surface,
      borderBottomColor: options.selected ? theme.borderStrong : theme.borderSubtle,
      borderLeftColor: options.selected
        ? theme.accentStrong
        : options.hovered
          ? theme.accent
          : "transparent",
      borderLeftWidth: 3,
      gap: tokens.spacing.sm,
      minHeight: adminOperationalLayout.rowMinHeight,
      shadowColor: theme.accentStrong,
      shadowOffset: { height: 0, width: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      ...adminCompactTableRowPadding,
    };
  };
  const buildAdminInteractiveTableRowStyle = (
    index: number,
    selected: boolean,
    state: unknown,
  ) => {
    const record =
      state && typeof state === "object"
        ? (state as Record<string, unknown>)
        : {};

    return [
      styles.patientDocumentTableRow,
      adminWebOutlineReset,
      buildAdminTableRowStateStyle(index, {
        hovered: record.hovered === true || record.focused === true,
        pressed: record.pressed === true,
        selected,
      }),
    ];
  };
  const adminCompactSearchPadding = {
    paddingHorizontal: adminCompact.searchPaddingHorizontal,
    paddingVertical: adminCompact.searchPaddingVertical,
  };
  const adminSearchSurfaceStyle = {
    backgroundColor: adminFigmaPalette.surface,
    borderColor: adminFigmaPalette.border,
    borderRadius: adminOperationalLayout.controlRadius,
    minHeight: adminOperationalLayout.controlHeight,
  };
  const adminSearchInputTextStyle = {
    color: theme.textPrimary,
    flex: 1,
    fontFamily: layout.bodyFontFamily,
    fontSize: adminOperationalTypography.table.fontSize,
    fontWeight: adminTypography.bodyWeight,
  };
  const adminMobileRecordCardStyle = {
    backgroundColor: adminFigmaPalette.surface,
    borderColor: theme.borderSubtle,
    borderRadius: adminOperationalLayout.mobileCardRadius,
    borderWidth: 1,
    gap: tokens.spacing.sm,
    padding: tokens.spacing.md,
    shadowColor: theme.accentStrong,
    shadowOffset: { height: 0, width: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  };
  const renderAdminMobileMetaRow = (
    labelText: string,
    valueText: string,
    options?: { valueColor?: string },
  ) => (
    <View
      key={labelText}
      style={[
        styles.patientRowHeader,
        {
          alignItems: "flex-start",
          borderTopColor: theme.borderSubtle,
          borderTopWidth: 1,
          gap: tokens.spacing.sm,
          justifyContent: "space-between",
          paddingTop: tokens.spacing.xs,
        },
      ]}
    >
      <Text
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            flex: 1,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
            textTransform: "uppercase",
          },
        ]}
      >
        {labelText}
      </Text>
      <Text
        numberOfLines={2}
        style={[
          styles.metricLabel,
          {
            color: options?.valueColor ?? theme.textPrimary,
            flex: 1.4,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.sm,
            lineHeight: tokens.typography.lineHeight.sm,
            textAlign: "right",
          },
        ]}
      >
        {valueText}
      </Text>
    </View>
  );
  const renderAdminMobileRecordCard = (input: {
    actions?: ReactNode;
    badge?: ReactNode;
    cardKey?: string;
    metaRows: ReactNode[];
    subtitleText?: string;
    titleText: string;
  }) => (
    <View key={input.cardKey} style={adminMobileRecordCardStyle}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "flex-start",
            gap: tokens.spacing.sm,
            justifyContent: "space-between",
          },
        ]}
      >
        <View style={[styles.listGroup, { flex: 1, gap: 3, minWidth: 0 }]}>
          <Text
            numberOfLines={2}
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.md,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            {input.titleText}
          </Text>
          {input.subtitleText ? (
            <Text
              numberOfLines={2}
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  lineHeight: tokens.typography.lineHeight.sm,
                },
              ]}
            >
              {input.subtitleText}
            </Text>
          ) : null}
        </View>
        {input.badge}
      </View>
      <View style={[styles.listGroup, { gap: tokens.spacing.xs }]}>
        {input.metaRows}
      </View>
      {input.actions ? (
        <View
          style={[
            styles.patientRowFooter,
            {
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              gap: tokens.spacing.xs,
              justifyContent: "flex-end",
              paddingTop: tokens.spacing.sm,
            },
          ]}
        >
          {input.actions}
        </View>
      ) : null}
    </View>
  );
  const renderAdminDeltaPill = (
    value: number | null | undefined,
    options?: { suffix?: string },
  ) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return null;
    }

    const isPositive = value >= 0;
    const suffix = options?.suffix ?? "%";
    const displayValue = `${isPositive ? "+" : ""}${value.toFixed(
      Math.abs(value) < 10 ? 1 : 0,
    )}${suffix}`;

    return (
      <View
        style={[
          styles.inlinePill,
          {
            backgroundColor: isPositive
              ? adminFigmaPalette.success
              : adminFigmaPalette.danger,
            borderColor: isPositive
              ? adminFigmaPalette.success
              : adminFigmaPalette.danger,
            borderRadius: 4,
            paddingHorizontal: 4,
            paddingVertical: 1,
          },
        ]}
      >
        <Text
          style={[
            styles.pillValueText,
            {
              color: adminFigmaPalette.surface,
              fontFamily: layout.bodyFontFamily,
              fontSize: 10,
              fontWeight: "700",
              lineHeight: 12,
            },
          ]}
        >
          {displayValue}
        </Text>
      </View>
    );
  };
  const renderAdminAnalyticsRing = (input: {
    background: string;
    children: ReactNode;
    diameter: number;
    innerColor?: string;
    ringWidth: number;
  }) => {
    const innerSize = input.diameter - input.ringWidth * 2;

    if (Platform.OS === "web") {
      return createElement(
        "div",
        {
          style: {
            alignItems: "center",
            background: input.background,
            borderRadius: "50%",
            display: "flex",
            height: input.diameter,
            justifyContent: "center",
            width: input.diameter,
          } satisfies CSSProperties,
        },
        createElement(
          "div",
          {
            style: {
              alignItems: "center",
              background: input.innerColor ?? adminFigmaPalette.surface,
              borderRadius: "50%",
              display: "flex",
              height: innerSize,
              justifyContent: "center",
              width: innerSize,
            } satisfies CSSProperties,
          },
          input.children,
        ),
      );
    }

    return (
      <View
        style={{
          alignItems: "center",
          borderColor: adminFigmaPalette.successBright,
          borderRadius: input.diameter / 2,
          borderWidth: input.ringWidth,
          height: input.diameter,
          justifyContent: "center",
          width: input.diameter,
        }}
      >
        {input.children}
      </View>
    );
  };
  const resolveAdminPercentText = (
    value: number | null | undefined,
    precision = 1,
  ) =>
    typeof value === "number" && Number.isFinite(value)
      ? value.toFixed(precision)
      : "0.0";
  const renderAdminAnalyticsBars = () => {
    const funnelEngagedPercent =
      adminAnalyticsFunnelMode === "overall"
        ? analyticsEngagedPercent
        : analyticsLoggedInCount > 0
          ? (analyticsEngagedCount / analyticsLoggedInCount) * 100
          : 0;
    const funnelLabels = [
      "100%",
      `${resolveAdminPercentText(analyticsLoggedInPercent)}%`,
      `${resolveAdminPercentText(funnelEngagedPercent)}%`,
    ];

    return (
      <View
        style={{
          backgroundColor: adminFigmaPalette.accent,
          borderRadius: 0,
          height: 62,
          overflow: "hidden",
          position: "relative",
          width: "100%",
        }}
      >
        {[1, 2].map((dividerIndex) => (
          <View
            key={`funnel-divider-${dividerIndex}`}
            style={{
              backgroundColor: adminFigmaPalette.tab,
              bottom: 0,
              left: `${(dividerIndex / 3) * 100}%` as `${number}%`,
              position: "absolute",
              top: 0,
              width: 1,
            }}
          />
        ))}
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              height: "100%",
              justifyContent: "space-around",
              paddingHorizontal: 12,
              width: "100%",
            },
          ]}
        >
          {funnelLabels.map((labelText, index) => (
            <View
              key={`${labelText}-${index}`}
              style={{
                alignItems: "center",
                flex: 1,
                justifyContent: "center",
                minWidth: 0,
              }}
            >
              <Text
                style={[
                  styles.title,
                  {
                    color: adminFigmaPalette.surface,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 17,
                    lineHeight: 21,
                    marginTop: 0,
                    textAlign: "center",
                  },
                ]}
              >
                {labelText}
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  };
  const renderAdminAccuracyTrend = () => {
    const maxPoint = 100;

    if (analyticsChartSeries.length === 0) {
      return (
        <View
          style={[
            styles.metricGroupCard,
            {
              alignItems: "center",
              backgroundColor: adminFigmaPalette.surface,
              borderColor: adminFigmaPalette.border,
              borderRadius: 8,
              justifyContent: "center",
              minHeight: 154,
              paddingHorizontal: 18,
              paddingVertical: 12,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: adminFigmaPalette.inkMuted,
                fontFamily: layout.bodyFontFamily,
                fontSize: adminCompact.bodyFontSize,
                lineHeight: adminCompact.bodyLineHeight,
                textAlign: "center",
              },
            ]}
          >
            No analytics trend data is available for this range yet.
          </Text>
        </View>
      );
    }

    return (
      <View
        style={[
          styles.metricGroupCard,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: "transparent",
            borderRadius: 8,
            minHeight: 154,
            paddingHorizontal: 18,
            paddingVertical: 12,
          },
        ]}
      >
        <View
          style={{
            height: 98,
            overflow: "hidden",
            position: "relative",
            width: "100%",
          }}
        >
          {[0, 25, 50, 75, 100].map((tick) => (
            <View
              key={tick}
              style={{
                alignItems: "center",
                borderTopColor: adminFigmaPalette.border,
                borderTopWidth: 1,
                flexDirection: "row",
                left: 0,
                position: "absolute",
                right: 0,
                top: `${100 - tick}%` as `${number}%`,
              }}
            >
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    left: -36,
                    position: "absolute",
                    top: -8,
                  },
                ]}
              >
                {tick}%
              </Text>
            </View>
          ))}
          <View
            style={{
              backgroundColor: adminFigmaPalette.successBright,
              bottom: 0,
              height: `${Math.min(
                maxPoint,
                analyticsLanguageModel.autonomousResponsePercent,
              )}%` as `${number}%`,
              left: 0,
              position: "absolute",
              right: 0,
            }}
          />
          <View
            style={{
              backgroundColor: adminFigmaPalette.warningBright,
              bottom: `${Math.min(
                maxPoint,
                analyticsLanguageModel.autonomousResponsePercent,
              )}%` as `${number}%`,
              height: `${Math.min(
                maxPoint,
                analyticsLanguageModel.clinicianApprovedPercent,
              )}%` as `${number}%`,
              left: 0,
              position: "absolute",
              right: 0,
            }}
          />
          <View
            style={{
              backgroundColor: adminFigmaPalette.dangerBright,
              bottom: `${Math.min(
                maxPoint,
                analyticsLanguageModel.autonomousResponsePercent +
                  analyticsLanguageModel.clinicianApprovedPercent,
              )}%` as `${number}%`,
              height: `${Math.min(
                maxPoint,
                analyticsLanguageModel.manualEntryPercent,
              )}%` as `${number}%`,
              left: 0,
              position: "absolute",
              right: 0,
            }}
          />
          {analyticsChartSeries.map((point, index) => {
            const denominator = Math.max(1, analyticsChartSeries.length - 1);
            const xPosition = (index / denominator) * 100;
            const yPosition =
              100 -
              Math.min(
                100,
                point.autonomousResponsePercent +
                  point.clinicianApprovedPercent * 0.5,
              );

            return (
              <View
                key={`${point.label}-${index}`}
                style={{
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.accent,
                  borderRadius: 999,
                  borderWidth: 2,
                  height: 10,
                  left: `${xPosition}%` as `${number}%`,
                  position: "absolute",
                  top: `${yPosition}%` as `${number}%`,
                  width: 10,
                }}
              />
            );
          })}
        </View>
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              gap: 8,
              justifyContent: "space-between",
              marginLeft: -4,
              marginTop: 6,
              width: "100%",
            },
          ]}
        >
          {analyticsAxisSeries.map((point, index) => (
            <Text
              key={`${point.label}-axis-${index}`}
              style={[
                styles.pillValueText,
                {
                  color: adminFigmaPalette.inkMuted,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 10,
                  textAlign: index === 0 ? "left" : "center",
                },
              ]}
            >
              {point.label}
            </Text>
          ))}
        </View>
        <View
          style={[
            styles.patientRowHeader,
            {
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "flex-start",
              marginTop: 10,
            },
          ]}
        >
          {[
            {
              color: adminFigmaPalette.success,
              labelText: "Autonomous",
              value: analyticsLanguageModel.autonomousResponsePercent,
            },
            {
              color: adminFigmaPalette.warning,
              labelText: "Approved",
              value: analyticsLanguageModel.clinicianApprovedPercent,
            },
            {
              color: adminFigmaPalette.dangerBright,
              labelText: "Manual",
              value: analyticsLanguageModel.manualEntryPercent,
            },
          ].map((item) => (
            <View
              key={item.labelText}
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  backgroundColor: adminFigmaPalette.panelSubtle,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: 8,
                  flexDirection: "row",
                  gap: 6,
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                },
              ]}
            >
              <View
                style={{
                  backgroundColor: item.color,
                  borderRadius: 999,
                  height: 8,
                  width: 8,
                }}
              />
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                  },
                ]}
              >
                {`${item.labelText} ${resolveAdminPercentText(item.value, 0)}%`}
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const adminIdentityText =
    activeWorkforceSession?.displayName?.trim() ||
    resolveWorkforceSessionRoleLabel(activeWorkforceSession?.roleKeys ?? []);
  const adminAvatarText = resolvePatientInitials(adminIdentityText);

  const renderAdminHeaderBar = () => (
    <View
      style={[
        styles.headerCard,
        {
          backgroundColor: theme.headerBand,
          borderColor: "transparent",
          borderRadius: 0,
          height: isAdminMobileLayout
            ? undefined
            : adminOperationalLayout.headerHeight,
          justifyContent: "center",
          minHeight: isAdminMobileLayout
            ? 0
            : adminOperationalLayout.headerHeight,
          overflow: isAdminMobileLayout ? "visible" : "hidden",
          paddingHorizontal: isAdminMobileLayout ? 12 : 20,
          paddingVertical: isAdminMobileLayout ? 10 : 0,
          shadowColor: theme.accentStrong,
          shadowOffset: { height: 0, width: 0 },
          shadowOpacity: 0,
          shadowRadius: 0,
          width: "100%",
        },
      ]}
    >
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            flexDirection: "row",
            gap: isAdminMobileLayout ? 8 : 16,
          },
        ]}
      >
        <View
          style={[
            styles.brandLockupRow,
            {
              gap: isAdminMobileLayout ? 8 : 14,
              minWidth: 0,
              flexBasis: 0,
              flexGrow: 1,
              flexShrink: 1,
              width: undefined,
            },
          ]}
        >
          <Text
            style={[
              styles.brand,
              {
                color: theme.headerText,
                fontFamily: layout.bodyFontFamily,
                fontSize: isAdminMobileLayout ? 13 : 14,
                fontWeight: adminTypography.titleWeight,
                lineHeight: isAdminMobileLayout ? 17 : 18,
                marginTop: 0,
              },
            ]}
            numberOfLines={1}
          >
            {"AmpleCare "}
            <Text style={{ color: theme.onGradientAvatarText }}>Admin</Text>
          </Text>
          {isAdminMobileLayout ? null : (
            <View
              style={{
                backgroundColor: theme.onGradientPillBackground,
                borderColor: theme.onGradientPillBorder,
                borderRadius: 6,
                borderWidth: 1,
                paddingHorizontal: 9,
                paddingVertical: 3,
              }}
            >
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: theme.onGradientAvatarText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 9,
                    fontWeight: "800",
                    letterSpacing: 0,
                    lineHeight: 12,
                  },
                ]}
              >
                COMMAND CENTER
              </Text>
            </View>
          )}
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              flex: isAdminMobileLayout ? 0 : 1,
              flexWrap: "nowrap",
              gap: isAdminMobileLayout ? tokens.spacing.xs : tokens.spacing.sm,
              justifyContent: "flex-end",
              minWidth: 0,
              width: undefined,
            },
          ]}
        >
          {isAdminMobileLayout ? null : (
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "center",
                  backgroundColor: theme.onGradientPillBackground,
                  borderColor: theme.onGradientPillBorder,
                  borderRadius: adminOperationalLayout.controlRadius,
                  borderWidth: 1,
                  flexShrink: 1,
                  gap: 8,
                  height: adminHeaderControlHeight,
                  minWidth: 0,
                  paddingHorizontal: 10,
                },
              ]}
            >
              <View
                style={[
                  styles.activityDot,
                  {
                    backgroundColor: theme.statusChip.success.text,
                    height: 9,
                    width: 9,
                  },
                ]}
              />
              <Text
                numberOfLines={1}
                style={[
                  styles.navigationText,
                  {
                    color: theme.headerText,
                    flexShrink: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: adminTypography.strongWeight,
                    lineHeight: 15,
                    minWidth: 0,
                  },
                ]}
              >
                {adminIdentityText}
              </Text>
            </View>
          )}

          <Pressable
            accessibilityLabel="Activity and review queues"
            accessibilityRole="button"
            onPress={() =>
              setAdminActivityPopoverOpen(!adminActivityPopoverOpen)
            }
            style={(state) => {
              const stateRecord =
                state && typeof state === "object"
                  ? (state as unknown as Record<string, unknown>)
                  : {};
              const focused = stateRecord.focused === true;
              const pressed = stateRecord.pressed === true;

              return [
                adminWebOutlineReset,
                {
                  alignItems: "center",
                  backgroundColor: pressed
                    ? theme.onGradientAvatarBackground
                    : theme.onGradientPillBackground,
                  borderColor: focused
                    ? theme.headerText
                    : theme.onGradientPillBorder,
                  borderRadius: adminOperationalLayout.controlRadius,
                  borderWidth: 1,
                  flexShrink: 0,
                  height: adminHeaderControlHeight,
                  justifyContent: "center",
                  minWidth: adminHeaderControlHeight,
                  position: "relative",
                  width: adminHeaderControlHeight,
                },
              ];
            }}
          >
            <LineIcon
              color={theme.headerText}
              name="pulse"
              size={16}
              strokeWidth={2.25}
            />
            {highPriorityReviewCount > 0 ? (
              <View
                style={{
                  backgroundColor: theme.warning,
                  borderRadius: 999,
                  minWidth: 15,
                  paddingHorizontal: 4,
                  paddingVertical: 1,
                  position: "absolute",
                  right: -3,
                  top: -3,
                }}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.accentContrast,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 9,
                      fontWeight: "800",
                      lineHeight: 12,
                      textAlign: "center",
                    },
                  ]}
                >
                  {String(highPriorityReviewCount)}
                </Text>
              </View>
            ) : null}
          </Pressable>

          <View
            style={[
              styles.patientAvatar,
              {
                backgroundColor: theme.onGradientAvatarBackground,
                borderColor: theme.onGradientAvatarBorder,
                borderRadius: 18,
                height: adminHeaderControlHeight,
                width: adminHeaderControlHeight,
              },
            ]}
          >
            <Text
              style={[
                styles.patientAvatarText,
                {
                  color: theme.headerText,
                  fontFamily: layout.bodyFontFamily,
                },
              ]}
            >
              {adminAvatarText}
            </Text>
          </View>

          <Pressable
            accessibilityLabel="Sign out"
            accessibilityRole="button"
            onPress={onSignOut}
            style={[
              {
                alignItems: "center",
                alignSelf: "center",
                backgroundColor: theme.onGradientPillBackground,
                borderColor: theme.onGradientPillBorder,
                borderRadius: adminOperationalLayout.controlRadius,
                borderWidth: 1,
                flexShrink: 0,
                height: adminHeaderControlHeight,
                justifyContent: "center",
                minWidth: isAdminMobileLayout ? 64 : undefined,
                paddingHorizontal: isAdminMobileLayout ? 8 : 12,
              },
            ]}
          >
            <Text
              numberOfLines={1}
              style={[
                styles.badgeText,
                {
                  color: theme.headerText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  fontWeight: adminTypography.strongWeight,
                },
              ]}
            >
              {isAdminMobileLayout ? "Exit" : "Sign out"}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );

  const renderAdminMetricCard = (
    metric: (typeof adminTopMetricCards)[number],
    metricIndex: number,
  ) => (
    <View
      key={metric.id}
      style={{
        alignItems: "center",
        backgroundColor: "transparent",
        borderColor: "transparent",
        borderLeftColor:
          metricIndex === 0 ? "transparent" : theme.borderSubtle,
        borderLeftWidth: metricIndex === 0 ? 0 : 1,
        borderRadius: 0,
        flexDirection: "row",
        flexGrow: usesScrollableAdminMetrics ? 0 : 1,
        gap: 8,
        minHeight: 54,
        minWidth: usesScrollableAdminMetrics
          ? isAdminMobileLayout
            ? 126
            : 170
          : 180,
        paddingHorizontal: usesScrollableAdminMetrics
          ? 12
          : metricIndex === 0
            ? 0
            : 18,
        paddingVertical: 8,
        width: usesScrollableAdminMetrics
          ? isAdminMobileLayout
            ? 126
            : 170
          : undefined,
      }}
    >
      <Text
        style={[
          styles.metricValue,
          {
            color: theme.textPrimary,
            fontFamily: layout.displayFontFamily,
            fontSize: 16,
            fontWeight: adminTypography.titleWeight,
            lineHeight: 20,
          },
        ]}
      >
        {metric.valueText}
      </Text>
      <Text
        numberOfLines={1}
        style={[
          styles.metricLabel,
          {
            color: theme.textSecondary,
            flexShrink: 1,
            fontFamily: layout.bodyFontFamily,
            fontSize: 10.5,
            fontWeight: adminTypography.labelWeight,
            lineHeight: 13,
          },
        ]}
      >
        {metric.labelText}
      </Text>
      {!usesScrollableAdminMetrics && metric.supportTone !== "neutral" ? (
        <Text
          numberOfLines={1}
          style={[
            styles.metricLabel,
            {
              color:
                metric.supportTone === "attention"
                  ? theme.danger
                  : theme.success,
              fontFamily: layout.bodyFontFamily,
              fontSize: 9.5,
              fontWeight: adminTypography.strongWeight,
              lineHeight: 12,
            },
          ]}
        >
          {metric.supportText}
        </Text>
      ) : null}
    </View>
  );

  const renderAdminMetricGroups = () => {
    if (usesScrollableAdminMetrics) {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            backgroundColor: theme.backgroundSubtle,
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: 1,
          }}
          contentContainerStyle={{
            alignItems: "stretch",
            flexDirection: "row",
            minWidth: "100%",
          }}
        >
          {adminTopMetricCards.map(renderAdminMetricCard)}
        </ScrollView>
      );
    }

    return (
      <View
        style={[
          styles.metricGroupRow,
          {
            backgroundColor: theme.backgroundSubtle,
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: 1,
            borderColor: "transparent",
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: 0,
            paddingHorizontal: isMediumLayout ? 20 : 14,
            paddingVertical: 0,
          },
        ]}
      >
        {adminTopMetricCards.map(renderAdminMetricCard)}
      </View>
    );
  };

  const renderAdminTabRail = () => {
    if (isAdminMobileLayout) {
      return (
        <ScrollView
          accessibilityLabel="Admin command center sections"
          horizontal
          role="tablist"
          showsHorizontalScrollIndicator={false}
          style={{
            backgroundColor: adminFigmaPalette.surface,
            borderBottomColor: adminFigmaPalette.border,
            borderBottomWidth: 1,
          }}
          contentContainerStyle={{
            alignItems: "center",
            flexDirection: "row",
            gap: 4,
            minWidth: "100%",
            padding: 6,
          }}
        >
          {adminNavigationTabs.map((tab) => {
            const isSelected = adminDashboardTabId === tab.id;

            return (
              <Pressable
                accessibilityLabel={`${tab.labelText}, ${tab.countText}`}
                accessibilityRole="tab"
                accessibilityState={{ selected: isSelected }}
                key={tab.id}
                onPress={() => setAdminDashboardTabId(tab.id)}
                style={(state) => {
                  const stateRecord =
                    state && typeof state === "object"
                      ? (state as unknown as Record<string, unknown>)
                      : {};
                  const focused = stateRecord.focused === true;
                  const pressed = stateRecord.pressed === true;

                  return {
                    alignItems: "center",
                    backgroundColor: isSelected
                      ? theme.accentSubtle
                      : pressed
                        ? adminFigmaPalette.rowHover
                        : "transparent",
                    borderColor:
                      isSelected || focused ? theme.accent : "transparent",
                    borderRadius: adminOperationalLayout.controlRadius,
                    borderWidth: 1,
                    height: 44,
                    justifyContent: "center",
                    opacity: pressed ? 0.88 : 1,
                    position: "relative",
                    width: 44,
                  };
                }}
              >
                <LineIcon
                  color={
                    isSelected ? theme.accentStrong : theme.textSecondary
                  }
                  name={tab.iconName}
                  size={17}
                  strokeWidth={2.2}
                />
                {tab.countText !== "0" ? (
                  <View
                    style={{
                      alignItems: "center",
                      backgroundColor:
                        tab.countText === "Live"
                          ? theme.success
                          : theme.accent,
                      borderColor: adminFigmaPalette.surface,
                      borderRadius: 999,
                      borderWidth: 1,
                      height: 7,
                      justifyContent: "center",
                      position: "absolute",
                      right: 4,
                      top: 4,
                      width: 7,
                    }}
                  />
                ) : null}
              </Pressable>
            );
          })}
        </ScrollView>
      );
    }

    if (!isMediumLayout) {
      return (
        <ScrollView
          accessibilityLabel="Admin command center sections"
          horizontal
          role="tablist"
          showsHorizontalScrollIndicator={false}
          style={{
            backgroundColor: adminFigmaPalette.surface,
            borderBottomColor: adminFigmaPalette.border,
            borderBottomWidth: 1,
          }}
          contentContainerStyle={{
            alignItems: "center",
            flexDirection: "row",
            gap: 6,
            padding: 6,
          }}
        >
          {adminNavigationTabs.map((tab) => {
            const isSelected = adminDashboardTabId === tab.id;

            return (
              <Pressable
                accessibilityLabel={`${tab.labelText}, ${tab.countText}`}
                accessibilityRole="tab"
                accessibilityState={{ selected: isSelected }}
                key={tab.id}
                onPress={() => setAdminDashboardTabId(tab.id)}
                style={(state) => {
                  const stateRecord =
                    state && typeof state === "object"
                      ? (state as unknown as Record<string, unknown>)
                      : {};
                  const focused = stateRecord.focused === true;
                  const pressed = stateRecord.pressed === true;

                  return {
                    alignItems: "center",
                    backgroundColor: isSelected
                      ? theme.accentSubtle
                      : pressed || focused
                        ? adminFigmaPalette.rowHover
                        : "transparent",
                    borderColor:
                      isSelected || focused ? theme.accent : theme.borderSubtle,
                    borderRadius: adminOperationalLayout.controlRadius,
                    borderWidth: 1,
                    flexDirection: "row",
                    gap: 7,
                    justifyContent: "center",
                    minHeight: 42,
                    minWidth: 118,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                  };
                }}
              >
                <LineIcon
                  color={
                    isSelected ? theme.accentStrong : theme.textSecondary
                  }
                  name={tab.iconName}
                  size={15}
                  strokeWidth={2.1}
                />
                <Text
                  numberOfLines={1}
                  style={[
                    styles.navigationText,
                    {
                      color: isSelected
                        ? theme.accentStrong
                        : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: isSelected
                        ? adminTypography.selectedWeight
                        : adminTypography.labelWeight,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {tab.labelText}
                </Text>
                {tab.countText !== "0" ? (
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: isSelected
                          ? theme.accentStrong
                          : theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: adminTypography.strongWeight,
                        lineHeight: 13,
                      },
                    ]}
                  >
                    {tab.countText}
                  </Text>
                ) : null}
              </Pressable>
            );
          })}
        </ScrollView>
      );
    }

    return (
      <View
        style={[
          styles.metricGroupRow,
          {
            backgroundColor: adminFigmaPalette.tab,
            borderColor: adminFigmaPalette.border,
            borderRadius: adminOperationalLayout.panelRadius,
            borderWidth: 1,
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: 0,
            overflow: "hidden",
            padding: 4,
          },
        ]}
      >
        {adminNavigationTabs.map((tab) => {
        const isSelected = adminDashboardTabId === tab.id;

        return (
          <Pressable
            accessibilityRole="tab"
            accessibilityState={{ selected: isSelected }}
            key={tab.id}
            onPress={() => setAdminDashboardTabId(tab.id)}
            style={(state) => {
              const stateRecord =
                state && typeof state === "object"
                  ? (state as unknown as Record<string, unknown>)
                  : {};
              const hovered = stateRecord.hovered === true;
              const focused = stateRecord.focused === true;
              const pressed = stateRecord.pressed === true;

              return [
              styles.metricGroupCard,
              {
                backgroundColor:
                  pressed || (!isSelected && (hovered || focused))
                    ? adminFigmaPalette.rowHover
                    : isSelected
                      ? adminFigmaPalette.surface
                      : "transparent",
                borderColor: isSelected
                  ? theme.accent
                  : "transparent",
                borderRadius: adminOperationalLayout.controlRadius,
                flex: 1,
                justifyContent: "center",
                minHeight: 42,
                opacity: pressed ? 0.9 : 1,
                paddingHorizontal: 12,
                paddingVertical: 8,
                shadowOpacity: 0,
                width: "auto",
              },
            ];
            }}
          >
            <Text
              numberOfLines={1}
              style={[
                styles.sectionTitle,
                {
                  color: isSelected ? theme.textPrimary : theme.textSecondary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 12,
                  fontWeight: isSelected
                    ? adminTypography.selectedWeight
                    : adminTypography.labelWeight,
                  lineHeight: 14,
                  textAlign: "center",
                },
              ]}
            >
              {tab.labelText}
            </Text>
            <Text
              numberOfLines={1}
              style={[
                styles.metricLabel,
                {
                  color: isSelected ? theme.accentStrong : theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 10,
                  fontWeight: adminTypography.strongWeight,
                  lineHeight: 12,
                  textAlign: "center",
                },
              ]}
            >
              {tab.countText}
            </Text>
          </Pressable>
        );
        })}
      </View>
    );
  };

  const renderAdminSideNav = () => (
    <View
      accessibilityLabel="Admin command center sections"
      role="navigation"
      style={[
        styles.listGroup,
        {
          alignSelf: "stretch",
          backgroundColor: adminFigmaPalette.panelSubtle,
          borderColor: "transparent",
          borderRadius: 0,
          borderRightColor: adminFigmaPalette.border,
          borderRightWidth: 1,
          gap: 10,
          paddingBottom: 14,
          paddingHorizontal: 0,
          paddingTop: 14,
          shadowColor: theme.accentStrong,
          shadowOffset: { height: 0, width: 0 },
          shadowOpacity: 0,
          shadowRadius: 0,
          width: adminOperationalLayout.sidebarWidth,
        },
      ]}
    >
      <View style={[styles.listGroup, { gap: 5 }]}>
        {adminNavigationTabs.map((tab) => {
          const isSelected = adminDashboardTabId === tab.id;
          const groupLabelText =
            tab.id === "patients"
              ? "Care"
              : tab.id === "outbound-calls"
                ? "Automation"
                : tab.id === "connectors"
                  ? "Platform"
                  : null;

          return (
            <View key={tab.id} style={[styles.listGroup, { gap: 5 }]}>
            {groupLabelText ? (
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textTertiary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 9,
                    fontWeight: adminTypography.sectionLabelWeight,
                    letterSpacing: 0,
                    marginTop: 6,
                    paddingHorizontal: 16,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {groupLabelText}
              </Text>
            ) : null}
            <Pressable
              accessibilityLabel={tab.labelText}
              accessibilityRole="tab"
              accessibilityState={{ selected: isSelected }}
              onPress={() => {
                setAdminDashboardTabId(tab.id);
                setAdminActivityPopoverOpen(false);
              }}
              style={(state) => {
                const stateRecord =
                  state && typeof state === "object"
                    ? (state as unknown as Record<string, unknown>)
                    : {};
                const hovered = stateRecord.hovered === true;
                const focused = stateRecord.focused === true;
                const pressed = stateRecord.pressed === true;

                return [
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    backgroundColor:
                      pressed || (!isSelected && (hovered || focused))
                        ? adminFigmaPalette.rowHover
                        : isSelected
                          ? theme.accentSubtle
                          : "transparent",
                    borderColor: "transparent",
                    borderRadius: 8,
                    gap: 9,
                    marginHorizontal: 8,
                    minHeight: 42,
                    paddingHorizontal: 8,
                    paddingVertical: 7,
                  },
                ];
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  borderRadius: 7,
                  height: 18,
                  justifyContent: "center",
                  width: 18,
                }}
              >
                <LineIcon
                  color={
                    isSelected ? theme.accentStrong : theme.textSecondary
                  }
                  name={tab.iconName}
                  size={16}
                  strokeWidth={2.25}
                />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.navigationText,
                    {
                      color: isSelected
                        ? theme.accentStrong
                        : adminFigmaPalette.ink,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12.5,
                      fontWeight: isSelected
                        ? adminTypography.titleWeight
                        : adminTypography.labelWeight,
                      letterSpacing: 0,
                      lineHeight: 16,
                    },
                  ]}
                >
                  {tab.labelText}
                </Text>
              </View>
              <View
                style={{
                  alignItems: "center",
                  backgroundColor:
                    tab.countText === "Live"
                      ? theme.statusChip.success.background
                      : tab.id === "reviews" && tab.countText !== "0"
                        ? theme.statusChip.warning.background
                        : isSelected
                          ? theme.accent
                          : theme.borderSubtle,
                  borderRadius: 999,
                  justifyContent: "center",
                  minWidth: 20,
                  paddingHorizontal: 7,
                  paddingVertical: 2,
                }}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    styles.pillValueText,
                    {
                      color:
                        tab.countText === "Live"
                          ? theme.statusChip.success.text
                          : tab.id === "reviews" && tab.countText !== "0"
                            ? theme.statusChip.warning.text
                            : isSelected
                              ? theme.accentContrast
                              : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      fontWeight: adminTypography.strongWeight,
                      lineHeight: 12,
                      textAlign: "center",
                    },
                  ]}
                >
                  {tab.countText}
                </Text>
              </View>
            </Pressable>
            </View>
          );
        })}
      </View>

    </View>
  );

  const renderAdminCommandCenterSection = (input: {
    actionLabel?: string;
    children: ReactNode;
    onActionPress?: () => void;
    titleText: string;
  }) => (
    <View
      style={[
        styles.detailBodyCard,
        {
          backgroundColor: "transparent",
          borderColor: "transparent",
          borderRadius: 0,
          gap: 8,
          padding: 0,
          shadowOpacity: 0,
          shadowRadius: 0,
        },
      ]}
    >
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            gap: 8,
            justifyContent: "space-between",
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: theme.textTertiary,
              fontFamily: layout.bodyFontFamily,
              fontSize: 10,
              fontWeight: adminTypography.sectionLabelWeight,
              letterSpacing: 0,
              lineHeight: 14,
              textTransform: "uppercase",
            },
          ]}
        >
          {input.titleText}
        </Text>
        {input.actionLabel && input.onActionPress ? (
          <Pressable
            accessibilityRole="button"
            onPress={input.onActionPress}
            style={(state) => {
              const stateRecord =
                state && typeof state === "object"
                  ? (state as unknown as Record<string, unknown>)
                  : {};
              const hovered = stateRecord.hovered === true;
              const pressed = stateRecord.pressed === true;

              return [
                styles.inlinePill,
                {
                  backgroundColor: pressed
                    ? adminFigmaPalette.rowSelected
                    : hovered
                      ? adminFigmaPalette.rowHover
                      : adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: adminOperationalLayout.controlRadius,
                  minHeight: 28,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                },
              ];
            }}
          >
            <Text
              style={[
                styles.pillValueText,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  fontWeight: adminTypography.strongWeight,
                },
              ]}
            >
              {input.actionLabel}
            </Text>
          </Pressable>
        ) : null}
      </View>
      {input.children}
    </View>
  );

  const renderAdminCommandCenterRow = (input: {
    iconName: LineIconName;
    labelText: string;
    onPress?: () => void;
    statusText?: string;
    valueText: string;
  }) => {
    const rowContent = (
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            gap: 10,
            justifyContent: "space-between",
          },
        ]}
      >
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              flex: 1,
              gap: 9,
              minWidth: 0,
            },
          ]}
        >
          <View
            style={{
              alignItems: "center",
              backgroundColor: adminFigmaPalette.infoSoft,
              borderColor: adminFigmaPalette.border,
              borderRadius: adminOperationalLayout.panelRadius,
              borderWidth: 1,
              height: 34,
              justifyContent: "center",
              width: 34,
            }}
          >
            <LineIcon
              color={adminFigmaPalette.accent}
              name={input.iconName}
              size={19}
              strokeWidth={2.2}
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text
              numberOfLines={1}
              style={[
                styles.navigationText,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  fontWeight: adminTypography.strongWeight,
                  lineHeight: 15,
                },
              ]}
            >
              {input.labelText}
            </Text>
            <Text
              numberOfLines={1}
              style={[
                styles.metricLabel,
                {
                  color: adminFigmaPalette.inkMuted,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  fontWeight: adminTypography.labelWeight,
                  lineHeight: 14,
                },
              ]}
            >
              {input.valueText}
            </Text>
          </View>
        </View>
        {input.statusText ? (
          <Text
            numberOfLines={1}
            style={[
              styles.pillValueText,
              {
                color: adminFigmaPalette.accent,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                fontWeight: adminTypography.strongWeight,
              },
            ]}
          >
            {input.statusText}
          </Text>
        ) : null}
      </View>
    );

    if (!input.onPress) {
      return (
        <View
          key={`${input.labelText}-${input.valueText}`}
          style={{
            borderBottomColor: adminFigmaPalette.border,
            borderBottomWidth: 1,
            paddingBottom: 10,
            paddingTop: 2,
          }}
        >
          {rowContent}
        </View>
      );
    }

    return (
      <Pressable
        accessibilityLabel={input.labelText}
        accessibilityRole="button"
        key={`${input.labelText}-${input.valueText}`}
        onPress={input.onPress}
        style={(state) => {
          const stateRecord =
            state && typeof state === "object"
              ? (state as unknown as Record<string, unknown>)
              : {};
          const hovered = stateRecord.hovered === true;
          const pressed = stateRecord.pressed === true;

          return {
            backgroundColor: pressed
              ? adminFigmaPalette.rowSelected
              : hovered
                ? adminFigmaPalette.rowHover
                : "transparent",
            borderBottomColor: adminFigmaPalette.border,
            borderBottomWidth: 1,
            borderRadius: 8,
            marginHorizontal: -4,
            paddingBottom: 10,
            paddingHorizontal: 4,
            paddingTop: 6,
          };
        }}
      >
        {rowContent}
      </Pressable>
    );
  };

  const renderAdminOptionStatusCard = (input: {
    accentColor: string;
    countText: string;
    isSelected: boolean;
    labelText: string;
    onPress: () => void;
  }) => (
    <Pressable
      accessibilityLabel={input.labelText}
      accessibilityRole="button"
      accessibilityState={{ selected: input.isSelected }}
      onPress={input.onPress}
      style={(state) => {
        const stateRecord =
          state && typeof state === "object"
            ? (state as unknown as Record<string, unknown>)
            : {};
        const hovered = stateRecord.hovered === true;
        const focused = stateRecord.focused === true;
        const pressed = stateRecord.pressed === true;

        return [
          styles.patientRowHeader,
          {
            alignItems: "center",
            backgroundColor:
              input.isSelected
                ? adminFigmaPalette.accent
                : pressed || hovered || focused
                  ? adminFigmaPalette.rowHover
                : adminFigmaPalette.surface,
            borderColor: input.isSelected
              ? adminFigmaPalette.accent
              : hovered || focused
                ? adminFigmaPalette.accent
                : adminFigmaPalette.border,
            borderRadius: adminOperationalLayout.controlRadius,
            borderWidth: 1,
            gap: 7,
            minHeight: 34,
            paddingHorizontal: 12,
            paddingVertical: 6,
            shadowColor: theme.accentStrong,
            shadowOffset: { height: 0, width: 0 },
            shadowOpacity: 0,
            shadowRadius: 0,
          },
        ];
      }}
    >
      <View
        style={{
          backgroundColor: input.accentColor,
          borderRadius: 999,
          height: 5,
          width: 5,
        }}
      />
      <Text
        style={[
          styles.navigationText,
          {
            color: input.isSelected ? theme.accentContrast : adminFigmaPalette.ink,
            fontFamily: layout.bodyFontFamily,
            fontSize: 11,
            fontWeight: input.isSelected
              ? adminTypography.strongWeight
              : adminTypography.labelWeight,
            lineHeight: 14,
          },
        ]}
      >
        {input.labelText}
      </Text>
      <Text
        style={[
          styles.metricLabel,
          {
            color: input.isSelected ? theme.accentContrast : input.accentColor,
            fontFamily: layout.bodyFontFamily,
            fontSize: 10,
            fontWeight: adminTypography.strongWeight,
            lineHeight: 13,
          },
        ]}
      >
        {input.countText}
      </Text>
    </Pressable>
  );

  const renderAdminOptionSelectControl = (input: {
    accessibilityLabel: string;
    minWidth?: number;
    onChange: (value: string) => void;
    options: Array<{
      labelText: string;
      value: string;
    }>;
    value: string;
  }) => {
    if (Platform.OS === "web") {
      return createElement(
        "select",
        {
          "aria-label": input.accessibilityLabel,
          onChange: (event: SyntheticEvent<HTMLSelectElement>) =>
            input.onChange(event.currentTarget.value),
          style: {
            backgroundColor: adminFigmaPalette.field,
            border: `1px solid ${adminFigmaPalette.border}`,
            borderRadius: adminOperationalLayout.controlRadius,
            color: adminFigmaPalette.ink,
            colorScheme: "light",
            cursor: "pointer",
            fontFamily: layout.bodyFontFamily,
            fontSize: 11,
            fontWeight: 600,
            minHeight: 34,
            minWidth: input.minWidth ?? 132,
            outlineColor: adminFigmaPalette.accent,
            padding: "0 32px 0 14px",
          } satisfies CSSProperties,
          value: input.value,
        },
        ...input.options.map((option) =>
          createElement(
            "option",
            {
              key: option.value,
              style: {
                backgroundColor: adminFigmaPalette.surface,
                color: adminFigmaPalette.ink,
              } satisfies CSSProperties,
              value: option.value,
            },
            option.labelText,
          ),
        ),
      );
    }

    return (
      <View
        style={[
          styles.flagRow,
          {
            gap: tokens.spacing.xs,
          },
        ]}
      >
        {input.options.map((option) =>
          renderWorkspaceFilterChip({
            isSelected: input.value === option.value,
            labelText: option.labelText,
            onPress: () => input.onChange(option.value),
            theme,
            tokens,
          }),
        )}
      </View>
    );
  };

  const renderAdminOptionIconButton = (input: {
    iconName: LineIconName;
    labelText: string;
    onPress: () => void;
  }) => (
    <Pressable
      accessibilityLabel={input.labelText}
      accessibilityRole="button"
      onPress={input.onPress}
      style={(state) => {
        const stateRecord =
          state && typeof state === "object"
            ? (state as unknown as Record<string, unknown>)
            : {};
        const hovered = stateRecord.hovered === true;
        const pressed = stateRecord.pressed === true;

        return {
          alignItems: "center",
          backgroundColor: pressed
            ? adminFigmaPalette.rowSelected
            : hovered
              ? adminFigmaPalette.rowHover
              : adminFigmaPalette.surface,
          borderColor: hovered ? adminFigmaPalette.accent : adminFigmaPalette.border,
          borderRadius: adminOperationalLayout.controlRadius,
          borderWidth: 1,
          height: 34,
          justifyContent: "center",
          width: 34,
        };
      }}
    >
      <LineIcon
        color={adminFigmaPalette.accent}
        name={input.iconName}
        size={18}
        strokeWidth={2.2}
      />
    </Pressable>
  );

  const renderAdminOptionPagination = (input: {
    currentPageIndex: number;
    itemLabel: string;
    onNext: () => void;
    onPrevious: () => void;
    pageCount: number;
    totalCount: number;
    visibleCount: number;
  }) => {
    const firstItemIndex =
      input.totalCount === 0 ? 0 : input.currentPageIndex * adminPatientOperationsPageSize + 1;
    const lastItemIndex =
      input.totalCount === 0
        ? 0
        : Math.min(
            input.totalCount,
            input.currentPageIndex * adminPatientOperationsPageSize +
              input.visibleCount,
          );

    return (
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            gap: tokens.spacing.sm,
            justifyContent: "space-between",
            paddingTop: tokens.spacing.sm,
          },
        ]}
      >
        <Text
          style={[
            styles.metricLabel,
            {
              color: adminFigmaPalette.inkMuted,
              fontFamily: layout.bodyFontFamily,
              fontSize: 11,
              lineHeight: 14,
            },
          ]}
        >
          {`Showing ${firstItemIndex} to ${lastItemIndex} of ${input.totalCount} ${input.itemLabel}`}
        </Text>
        <View style={[styles.patientRowHeader, { alignItems: "center", gap: 6 }]}>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "<",
            onPress: input.onPrevious,
            theme,
            tokens,
          })}
          <Text
            style={[
              styles.metricLabel,
              {
                color: adminFigmaPalette.ink,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                fontWeight: adminTypography.strongWeight,
              },
            ]}
          >
            {`${input.currentPageIndex + 1} / ${input.pageCount}`}
          </Text>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: ">",
            onPress: input.onNext,
            theme,
            tokens,
          })}
        </View>
      </View>
    );
  };

  const renderAdminCommandCenterRail = () => {
    const recentRows: Array<{
      iconName: LineIconName;
      labelText: string;
      onPress: () => void;
      valueText: string;
    }> = [];

    if (summary?.activity.latestMessageAt) {
      recentRows.push({
        iconName: "phone",
        labelText: "Latest message",
        onPress: () => setAdminDashboardTabId("reviews"),
        valueText: formatAdminSummaryTimestamp(summary.activity.latestMessageAt),
      });
    }

    if (summary?.activity.latestDocumentAt) {
      recentRows.push({
        iconName: "document",
        labelText: "Latest document",
        onPress: () => setAdminDashboardTabId("reviews"),
        valueText: formatAdminSummaryTimestamp(summary.activity.latestDocumentAt),
      });
    }

    return (
      <View style={{ gap: 12, width: 310 }}>
        {renderAdminCommandCenterSection({
          children:
            recentRows.length > 0 ? (
              <View style={{ gap: 2 }}>
                {recentRows.map((row) => (
                  <View key={row.labelText}>
                    {renderAdminCommandCenterRow(row)}
                  </View>
                ))}
              </View>
            ) : (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 17,
                  },
                ]}
              >
                No recent activity is available from the admin summary yet.
              </Text>
            ),
          titleText: "Recent activity",
        })}
        {renderAdminCommandCenterSection({
          children:
            adminPriorityRows.length > 0 ? (
              <View style={{ gap: 2 }}>
                {adminPriorityRows.map((row) => (
                  <View key={row.id}>
                    {renderAdminCommandCenterRow({
                      iconName:
                        row.priorityLabel === "High" ? "flag" : "document",
                      labelText: row.queueLabel,
                      onPress: () => {
                        setAdminDashboardTabId("reviews");
                        openAdminReviewQueueItems(row);
                      },
                      statusText: row.priorityLabel,
                      valueText: `${String(row.count)} item${row.count === 1 ? "" : "s"}`,
                    })}
                  </View>
                ))}
              </View>
            ) : (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 17,
                  },
                ]}
              >
                No open review queues are waiting on the live summary.
              </Text>
            ),
          titleText: "Open review queues",
        })}
      </View>
    );
  };

  const renderAdminConnectorDetailPanel = (
    registration: TenantAdminSummaryConnectorRegistrationV1,
  ) => {
    const lastSyncText = formatAdminSummaryTimestamp(registration.lastSyncAt);
    const lastSyncRequestedText = registration.lastSyncRequestedAt
      ? formatAdminSummaryTimestamp(registration.lastSyncRequestedAt)
      : "No sync queued";
    const authReviewedText = registration.authReviewedAt
      ? formatAdminSummaryTimestamp(registration.authReviewedAt)
      : "Review pending";
    const connectorFamilyText = humanizeWorkspaceKey(
      registration.connectorFamily,
    );
    const connectorStatusText = humanizeWorkspaceKey(registration.status);
    const syncStatusText = registration.syncStatus
      ? humanizeWorkspaceKey(registration.syncStatus)
      : "Not queued";
    const syncInvocationText = registration.syncInvocationStatus
      ? humanizeWorkspaceKey(registration.syncInvocationStatus)
      : "No sync activity yet";
    const authStatusText = registration.authReviewStatus
      ? humanizeWorkspaceKey(registration.authReviewStatus)
      : "Review pending";
    const authStatusSentenceText =
      authStatusText.toLowerCase() === "review pending"
        ? "pending"
        : authStatusText.toLowerCase();
    const connectorSummaryCards = [
      {
        helperText: `${connectorFamilyText} integration scoped to ${registration.scopeLabel}.`,
        labelText: "Connector health",
        valueText: connectorStatusText,
      },
      {
        helperText: registration.syncInvocationStatus
          ? `Latest request ${syncInvocationText.toLowerCase()}.`
          : "No sync request has reported yet.",
        labelText: "Sync state",
        valueText: syncStatusText,
      },
      {
        helperText: registration.authReviewNotes?.trim().length
          ? registration.authReviewNotes
          : "Needs a clear access review note before routine monitoring.",
        labelText: "Access review",
        valueText: authStatusText,
      },
      {
        helperText: registration.lastSyncRequestedAt
          ? `Queued ${lastSyncRequestedText}.`
          : "No queued refresh request.",
        labelText: "Last successful sync",
        valueText: lastSyncText,
      },
    ];
    const scopeRows = [
      { labelText: "Family", valueText: connectorFamilyText },
      { labelText: "Scope", valueText: registration.scopeLabel },
      {
        labelText: "Site",
        valueText: registration.siteCode ?? "Tenant wide",
      },
      {
        labelText: "Department",
        valueText: registration.departmentCode ?? "All departments",
      },
      {
        labelText: "Team",
        valueText: registration.teamKey ?? "All teams",
      },
    ];
    const syncRows = [
      { labelText: "Latest result", valueText: syncInvocationText },
      { labelText: "Last successful sync", valueText: lastSyncText },
      { labelText: "Queued refresh", valueText: lastSyncRequestedText },
    ];
    const authRows = [
      { labelText: "Current decision", valueText: authStatusText },
      { labelText: "Reviewed at", valueText: authReviewedText },
      { labelText: "Connector status", valueText: connectorStatusText },
    ];
    const renderConnectorFactRows = (
      rows: ReadonlyArray<{ labelText: string; valueText: string }>,
    ) => (
      <View style={[styles.listGroup, { gap: 6 }]}>
        {rows.map((row) => (
          <View
            key={row.labelText}
            style={[
              styles.patientRowHeader,
              {
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                gap: tokens.spacing.sm,
                paddingTop: 6,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.metaLabelFontSize,
                  textTransform: "uppercase",
                },
              ]}
            >
              {row.labelText}
            </Text>
            <Text
              numberOfLines={2}
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  flexShrink: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.metaValueFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                  textAlign: "right",
                },
              ]}
            >
              {row.valueText}
            </Text>
          </View>
        ))}
      </View>
    );

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminOperationalLayout.panelRadius,
            borderWidth: 0,
            gap: adminCompact.sectionGap,
            padding: adminModalDensity.cardPadding,
          },
        ]}
      >
        {renderAdminModalHeader({
          actions: (
            <>
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Sync now",
                onPress: () => runAdminConnectorSyncCommand(registration),
              })}
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Edit",
                onPress: () => runUpdateConnectorCommand(registration),
                prefixText: "",
                tone: "surface",
              })}
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Close",
                onPress: () => setAdminSelectedConnectorId(null),
                prefixText: "",
                tone: "surface",
              })}
            </>
          ),
          eyebrowText: "Connector workspace",
          onClose: () => setAdminSelectedConnectorId(null),
          subtitleText: `${connectorFamilyText} connector for ${registration.scopeLabel}. Sync ${syncStatusText.toLowerCase()}; access review ${authStatusSentenceText}.`,
          titleText: registration.displayName,
        })}

        <View style={[styles.patientMetaGrid, { gap: tokens.spacing.sm }]}>
          {connectorSummaryCards.map((item) => (
            <View
              key={item.labelText}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.surfaceElevated,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  flexBasis: isWideLayout ? 180 : 156,
                  gap: tokens.spacing.xs,
                  padding: adminModalDensity.cardPadding,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaValueFontSize,
                    lineHeight: adminModalDensity.bodyLineHeight,
                  },
                ]}
              >
                {item.valueText}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    lineHeight: 15,
                  },
                ]}
              >
                {item.helperText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Scope and ownership
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Confirm the integration owner and scope before changing refresh
              or access posture.
            </Text>
            {renderConnectorFactRows(scopeRows)}
          </View>

          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Sync operations
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Queue a refresh when the care team needs the latest operational
              data.
            </Text>
            {renderConnectorFactRows(syncRows)}
            <View
              style={[
                styles.patientRowFooter,
                { gap: adminModalDensity.actionGap, justifyContent: "flex-end" },
              ]}
            >
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Queue sync",
                onPress: () => runAdminConnectorSyncCommand(registration),
                prefixText: "",
                tone: "primary",
              })}
            </View>
          </View>
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Access posture
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              {registration.authReviewNotes?.trim().length
                ? registration.authReviewNotes
                : "No access review notes have been recorded yet."}
            </Text>
            {renderConnectorFactRows(authRows)}
            <View
              style={[
                styles.patientRowFooter,
                { gap: adminModalDensity.actionGap, justifyContent: "flex-end" },
              ]}
            >
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Mark active",
                onPress: () =>
                  runAdminConnectorAuthReviewCommand(
                    registration,
                    "reviewed",
                    "active",
                    "Connector auth reviewed",
                  ),
                prefixText: "",
                tone: "surface",
              })}
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Needs attention",
                onPress: () =>
                  runAdminConnectorAuthReviewCommand(
                    registration,
                    "needs_attention",
                    "paused",
                    "Connector auth needs attention",
                  ),
                prefixText: "",
                tone: "surface",
              })}
            </View>
          </View>

          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Lifecycle controls
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Use these controls only when the connector should stop feeding
              operational data.
            </Text>
            <View
              style={[
                styles.patientRowFooter,
                { gap: adminModalDensity.actionGap, justifyContent: "flex-end" },
              ]}
            >
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Block connector",
                onPress: () =>
                  runAdminConnectorAuthReviewCommand(
                    registration,
                    "blocked",
                    "paused",
                    "Connector auth blocked",
                  ),
                prefixText: "",
                tone: "danger",
              })}
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: "Retire",
                onPress: () => runRetireConnectorCommand(registration),
                prefixText: "",
                tone: "danger",
              })}
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderAdminConnectorDetailModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: selectedAdminConnector
        ? `${selectedAdminConnector.displayName} connector workspace`
        : "Connector workspace",
      children: selectedAdminConnector
        ? renderAdminConnectorDetailPanel(selectedAdminConnector)
        : null,
      contentPadding: 0,
      maxWidth: isWideLayout ? 1080 : 960,
      onClose: () => setAdminSelectedConnectorId(null),
      rail: selectedAdminConnector
          ? renderAdminModalRail({
            avatarText: "CON",
            eyebrowText: "Connector",
            footer: (
              <View style={[styles.listGroup, { gap: 6 }]}>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminModalDensity.metaLabelFontSize,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  Audit details
                </Text>
                {[
                  {
                    labelText: "Integration key",
                    valueText: selectedAdminConnector.sourceSystemKey,
                  },
                  {
                    labelText: "Review mode",
                    valueText: humanizeWorkspaceKey(
                      selectedAdminConnector.sourceAuthorityMode,
                    ),
                  },
                  {
                    labelText: "Connector ID",
                    valueText: selectedAdminConnector.connectorId,
                  },
                  {
                    labelText: "Request reference",
                    valueText:
                      selectedAdminConnector.syncInvocationRequestId ??
                      "Not available",
                  },
                  {
                    labelText: "Sync handler",
                    valueText:
                      selectedAdminConnector.syncInvocationFunctionName ??
                      "Not available",
                  },
                  {
                    labelText: "Command reference",
                    valueText:
                      selectedAdminConnector.lastSyncCommandId ?? "Not available",
                  },
                ].map((row) => (
                  <View key={row.labelText} style={[styles.listGroup, { gap: 1 }]}>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: adminModalDensity.metaLabelFontSize,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      {row.labelText}
                    </Text>
                    <Text
                      numberOfLines={2}
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: adminModalDensity.metaValueFontSize,
                          lineHeight: adminModalDensity.bodyLineHeight,
                        },
                      ]}
                    >
                      {row.valueText}
                    </Text>
                  </View>
                ))}
              </View>
            ),
            glanceRows: [
              {
                labelText: "Last sync",
                valueText: formatAdminSummaryTimestamp(
                  selectedAdminConnector.lastSyncAt,
                ),
              },
              {
                labelText: "Sync requested",
                valueText: selectedAdminConnector.lastSyncRequestedAt
                  ? formatAdminSummaryTimestamp(
                      selectedAdminConnector.lastSyncRequestedAt,
                    )
                  : "Not queued",
              },
              {
                labelText: "Auth review",
                valueText: selectedAdminConnector.authReviewStatus
                  ? humanizeWorkspaceKey(
                      selectedAdminConnector.authReviewStatus,
                    )
                  : "Review pending",
              },
            ],
            overviewRows: [
              {
                labelText: "Scope",
                valueText: selectedAdminConnector.scopeLabel,
              },
              {
                labelText: "Family",
                valueText: humanizeWorkspaceKey(
                  selectedAdminConnector.connectorFamily,
                ),
              },
              {
                labelText: "Status",
                valueText: humanizeWorkspaceKey(selectedAdminConnector.status),
              },
            ],
            pills: [
              humanizeWorkspaceKey(selectedAdminConnector.status),
              selectedAdminConnector.syncStatus
                ? humanizeWorkspaceKey(selectedAdminConnector.syncStatus)
                : "Sync not queued",
            ],
            subtitleText: "Integration health and sync controls.",
            titleText: selectedAdminConnector.displayName,
          })
        : null,
      visible: selectedAdminConnector != null,
    });

  const renderAdminConnectorsMobileList = () =>
    connectorPageRows.length > 0 ? (
      <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
        {connectorPageRows.map((registration) =>
          renderAdminMobileRecordCard({
            actions: (
              <>
                {renderAdminTableActionButton({
                  labelText: "Edit",
                  onPress: () => runUpdateConnectorCommand(registration),
                  tone: "primary",
                  width: 94,
                })}
                {renderAdminTableActionButton({
                  labelText:
                    registration.status === "active" ? "Sync" : "Review",
                  onPress: () =>
                    registration.status === "active"
                      ? runAdminConnectorSyncCommand(registration)
                      : runAdminConnectorAuthReviewCommand(
                          registration,
                          "reviewed",
                          "active",
                          "Connector auth review",
                        ),
                  tone: "surface",
                  width: 94,
                })}
                {renderAdminTableActionButton({
                  labelText: "Retire",
                  onPress: () => runRetireConnectorCommand(registration),
                  tone: "danger",
                  width: 94,
                })}
              </>
            ),
            badge: renderAdminStatusPill(
              humanizeWorkspaceKey(registration.status),
            ),
            cardKey: registration.connectorId,
            metaRows: [
              renderAdminMobileMetaRow(
                "Family",
                humanizeWorkspaceKey(registration.connectorFamily),
              ),
              renderAdminMobileMetaRow("Scope", registration.scopeLabel),
              renderAdminMobileMetaRow(
                "Sync",
                registration.syncStatus
                  ? humanizeWorkspaceKey(registration.syncStatus)
                  : "Not queued",
              ),
              renderAdminMobileMetaRow(
                "Last sync",
                formatAdminSummaryTimestamp(registration.lastSyncAt),
              ),
            ],
            subtitleText: [
              registration.syncStatus
                ? `${humanizeWorkspaceKey(registration.syncStatus)} sync`
                : "Sync not queued",
              registration.authReviewStatus
                ? `${humanizeWorkspaceKey(registration.authReviewStatus)} auth`
                : "Auth review pending",
            ].join(" | "),
            titleText: registration.displayName,
          }),
        )}
      </View>
    ) : (
      <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          No connectors match the current admin filters.
        </Text>
      </View>
    );

  const renderAdminConnectorsSurface = () => (
    <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: adminCompact.sectionGap,
          },
        ]}
      >
        <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.xs }]}>
          <Text
            style={[
              styles.title,
              {
                ...adminCompactTitleStyle,
              },
            ]}
          >
            Connectors
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            Manage active integrations and sync status.
          </Text>
        </View>

        <View
          style={[
            styles.patientRowFooter,
            {
              alignItems: isAdminMobileLayout ? "stretch" : "center",
              flexDirection: isAdminMobileLayout ? "column" : "row",
              gap: tokens.spacing.xs,
              width: isAdminMobileLayout ? "100%" : undefined,
            },
          ]}
        >
          {renderAdminPrimaryButton({
            labelText: "Create connector",
            onPress: runCreateConnectorCommand,
            stretch: isAdminMobileLayout,
            width: adminToolbarActionButtonWidth,
          })}
          {renderAdminPrimaryButton({
            labelText: "Sync active",
            onPress: () => {
              const connector =
                connectorRows.find((row) => row.status === "active") ??
                connectorRows[0];

              if (!connector) {
                setAdminOpsNotice("No connector is available to sync.");
                return;
              }

              runAdminConnectorSyncCommand(connector);
            },
            prefixText: "",
            stretch: isAdminMobileLayout,
            tone: "surface",
            width: adminToolbarActionButtonWidth,
          })}
        </View>
      </View>

      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.searchSurface,
            {
              ...adminSearchSurfaceStyle,
              alignItems: "center",
              flex: 1,
              flexDirection: "row",
              gap: tokens.spacing.xs,
              ...adminCompactSearchPadding,
              width: "100%",
            },
          ]}
        >
          <TextInput
            onChangeText={setAdminSearchQuery}
            placeholder="Search connectors..."
            placeholderTextColor={theme.textTertiary}
            style={[
              styles.searchInput,
              adminSearchInputTextStyle,
            ]}
            value={adminSearchQuery}
          />
          <LineIcon
            color={theme.textSecondary}
            name="search"
            size={15}
            strokeWidth={2.2}
          />
        </View>

        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {[
            "all",
            ...(summary?.connectors.statuses ?? []).map(
              (status) => status.status,
            ),
          ].map((statusValue) =>
            renderWorkspaceFilterChip({
              isSelected: adminConnectorStatusFilter === statusValue,
              labelText:
                statusValue === "all"
                  ? "All statuses"
                  : humanizeWorkspaceKey(statusValue),
              onPress: () => setAdminConnectorStatusFilter(statusValue),
              theme,
              tokens,
            }),
          )}
        </View>
      </View>

      <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
        {[
          ["status", "Sort status"],
          ["name", "Sort name"],
          ["sync", "Sort last sync"],
        ].map(([value, labelText]) =>
          renderWorkspaceFilterChip({
            isSelected: adminConnectorSortKey === value,
            labelText,
            onPress: () =>
              setAdminConnectorSortKey(value as "name" | "status" | "sync"),
            theme,
            tokens,
          }),
        )}
      </View>

      <View
        style={[
          styles.patientMetaGrid,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {[
          { labelText: "Active", valueText: String(activeConnectorCount) },
          {
            labelText: "Needs attention",
            valueText: String(inactiveConnectorCount),
          },
          { labelText: "Scopes", valueText: String(connectorScopeCount) },
          { labelText: "Latest sync", valueText: latestConnectorSyncText },
        ].map((item) => (
          <View
            key={item.labelText}
            style={[
              styles.patientMetaCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.xs,
                padding: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              {item.labelText}
            </Text>
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                },
              ]}
            >
              {item.valueText}
            </Text>
          </View>
        ))}
      </View>

      {isAdminMobileLayout ? (
        renderAdminConnectorsMobileList()
      ) : (
      <ScrollView
        horizontal={true}
        showsHorizontalScrollIndicator={true}
        style={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}
        contentContainerStyle={{
          minWidth: adminConnectorTableMinWidth,
          width: "100%",
        }}
      >
        <View
          style={[
            styles.patientDocumentTable,
            adminTableShellStyle,
            {
              minWidth: adminConnectorTableMinWidth,
              width: "100%",
            },
          ]}
        >
          <View
            style={[
              styles.patientDocumentTableHeader,
              adminTableHeaderStyle,
            ]}
          >
          {[
            { labelText: "Connector", flexValue: 1.9 },
            { labelText: "Family", flexValue: 1 },
            { labelText: "Scope", flexValue: 1 },
            { labelText: "Status", flexValue: 0.9 },
            { labelText: "Last sync", flexValue: 1 },
            { labelText: "Actions", flexValue: 2.8 },
          ].map((item) => (
            <Text
              key={item.labelText}
              style={[
                styles.metricLabel,
                {
                  flex: item.flexValue,
                  ...adminTableHeaderTextStyle,
                },
              ]}
            >
              {item.labelText}
            </Text>
          ))}
          </View>

        {connectorPageRows.length > 0 ? (
          connectorPageRows.map((registration, index) => {
            const isSelected =
              selectedAdminConnector?.connectorId === registration.connectorId;

            return (
              <Pressable
                key={registration.connectorId}
                onPress={() => {
                  setAdminSelectedConnectorId(registration.connectorId);
                  setAdminOpsNotice(
                    `Connector workspace opened for ${registration.displayName}.`,
                  );
                }}
                style={(state) =>
                  buildAdminInteractiveTableRowStyle(index, isSelected, state)
                }
              >
                <View
                  style={[
                    styles.patientDocumentTableCell,
                    { flex: 1.9, gap: 2 },
                  ]}
                >
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: tokens.typography.fontSize.md,
                        lineHeight: tokens.typography.lineHeight.md,
                      },
                    ]}
                  >
                    {registration.displayName}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                      },
                    ]}
                  >
                    {`Sync ${
                      registration.syncStatus
                        ? humanizeWorkspaceKey(
                            registration.syncStatus,
                          ).toLowerCase()
                        : "not queued"
                    }; access review ${
                      registration.authReviewStatus
                        ? humanizeWorkspaceKey(
                            registration.authReviewStatus,
                          ).toLowerCase() === "review pending"
                          ? "pending"
                          : humanizeWorkspaceKey(
                              registration.authReviewStatus,
                            ).toLowerCase()
                        : "pending"
                    }.`}
                  </Text>
                </View>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {humanizeWorkspaceKey(registration.connectorFamily)}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {registration.scopeLabel}
                </Text>
                <View style={[styles.patientDocumentTableCell, { flex: 0.9 }]}>
                  <View
                    style={[
                      styles.inlinePill,
                      {
                        backgroundColor:
                          registration.status === "active"
                            ? theme.backgroundSubtle
                            : theme.surfaceMuted,
                        borderColor:
                          registration.status === "active"
                            ? theme.info
                            : theme.warning,
                        borderRadius: layout.controlRadius,
                        paddingHorizontal: tokens.spacing.sm,
                        paddingVertical: tokens.spacing.xs,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color:
                            registration.status === "active"
                              ? theme.info
                              : theme.warning,
                          fontFamily: layout.bodyFontFamily,
                        },
                      ]}
                    >
                      {humanizeWorkspaceKey(registration.status)}
                    </Text>
                  </View>
                </View>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {formatAdminSummaryTimestamp(registration.lastSyncAt)}
                </Text>
                <View
                  style={[
                    styles.patientRowFooter,
                    {
                      flex: 2.8,
                      flexWrap: "wrap",
                      gap: tokens.spacing.xs,
                      justifyContent: "flex-start",
                      minWidth: 0,
                    },
                  ]}
                >
                  {renderAdminTableActionButton({
                    labelText: "Edit",
                    onPress: () => runUpdateConnectorCommand(registration),
                    tone: "primary",
                  })}
                  {renderAdminTableActionButton({
                    labelText: "Retire",
                    onPress: () => runRetireConnectorCommand(registration),
                    tone: "danger",
                  })}
                  {renderAdminTableActionButton({
                    labelText:
                      registration.status === "active" ? "Sync" : "Review auth",
                    labelLines: 2,
                    onPress: () =>
                      registration.status === "active"
                        ? runAdminConnectorSyncCommand(registration)
                        : runAdminConnectorAuthReviewCommand(
                            registration,
                            "reviewed",
                            "active",
                            "Connector auth review",
                          ),
                    tone: "surface",
                  })}
                </View>
              </Pressable>
            );
          })
        ) : (
          <View
            style={[
              styles.detailBodyCard,
              adminTableEmptyStateStyle,
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              No connectors match the current admin filters.
            </Text>
          </View>
        )}
      </View>
      </ScrollView>
      )}

      {connectorRows.length > 5 ? (
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Prev",
            onPress: () =>
              setAdminConnectorPageIndex((current) => Math.max(0, current - 1)),
            theme,
            tokens,
          })}
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
              },
            ]}
          >
            {`Page ${adminConnectorPageIndex + 1} of ${Math.max(1, Math.ceil(connectorRows.length / 5))}`}
          </Text>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Next",
            onPress: () =>
              setAdminConnectorPageIndex((current) =>
                Math.min(
                  Math.max(0, Math.ceil(connectorRows.length / 5) - 1),
                  current + 1,
                ),
              ),
            theme,
            tokens,
          })}
        </View>
      ) : null}

    </View>
  );

  const renderAdminReviewQueueDetailModal = () => {
    const queue = selectedAdminReviewQueue;

    if (!queue) {
      return null;
    }

    const loadedItems = selectedAdminReviewQueueItems?.items ?? [];
    const totalCount =
      selectedAdminReviewQueueItems?.page.totalCount ?? queue.count;
    const pendingCount = loadedItems.filter((item) => {
      const statusText = item.statusLabel.toLowerCase();

      return (
        statusText.includes("pending") ||
        statusText.includes("open") ||
        statusText.includes("review")
      );
    }).length;
    const highPriorityCount = loadedItems.filter(
      (item) => item.priorityLabel.toLowerCase() === "high",
    ).length;
    const completedCount = loadedItems.filter((item) => {
      const statusText = item.statusLabel.toLowerCase();

      return (
        statusText.includes("complete") ||
        statusText.includes("closed") ||
        statusText.includes("done")
      );
    }).length;
    const reviewQueueFilterOptions: ReadonlyArray<{
      filterId: AdminReviewQueueDetailFilter;
      labelText: string;
    }> = [
      { filterId: "all", labelText: `All (${loadedItems.length})` },
      { filterId: "pending", labelText: `Pending (${pendingCount})` },
      { filterId: "high", labelText: `High priority (${highPriorityCount})` },
      { filterId: "completed", labelText: `Completed (${completedCount})` },
    ];
    const filteredReviewItems = loadedItems.filter((item) => {
      const statusText = item.statusLabel.toLowerCase();
      const priorityText = item.priorityLabel.toLowerCase();

      if (adminReviewQueueDetailFilter === "pending") {
        return (
          statusText.includes("pending") ||
          statusText.includes("open") ||
          statusText.includes("review")
        );
      }

      if (adminReviewQueueDetailFilter === "high") {
        return priorityText === "high";
      }

      if (adminReviewQueueDetailFilter === "completed") {
        return (
          statusText.includes("complete") ||
          statusText.includes("closed") ||
          statusText.includes("done")
        );
      }

      return true;
    });
    const selectedReviewFilterLabel =
      reviewQueueFilterOptions.find(
        (option) => option.filterId === adminReviewQueueDetailFilter,
      )?.labelText ?? "Selected";
    const closeModal = () => setAdminSelectedReviewQueueId(null);
    const queueLeadText =
      queue.sourceArea
        .split(/\s+/u)
        .map((part) => part[0])
        .join("")
        .slice(0, 3)
        .toUpperCase() || "Q";
    const railRows = [
      { labelText: "Area", valueText: queue.sourceArea },
      { labelText: "Owner", valueText: queue.ownerLabel },
      { labelText: "Priority", valueText: queue.priorityLabel },
      { labelText: "Last signal", valueText: queue.latestSignalText },
    ];
    const glanceRows = [
      { labelText: "Open items", valueText: String(totalCount) },
      {
        labelText: "Visible now",
        valueText: selectedAdminReviewQueueItems
          ? String(filteredReviewItems.length)
          : "Loading",
      },
      {
        labelText: "Last signal",
        valueText: queue.latestSignalText,
      },
    ];

    return renderAdminModalFrame({
      accessibilityLabel: `${queue.queueLabel} review queue`,
      contentPadding: 0,
      maxWidth: 1080,
      onClose: closeModal,
      rail: renderAdminModalRail({
        avatarText: queueLeadText,
        eyebrowText: "Review queue",
        footer: renderAdminPrimaryButton({
          density: "compact",
          labelText: "Close",
          onPress: closeModal,
          prefixText: "",
          tone: "surface",
          width: 112,
        }),
        glanceRows,
        overviewRows: railRows,
        pills: [
          queue.priorityLabel,
          selectedAdminReviewQueueItems ? "Live data" : "Loading",
        ],
        subtitleText: queue.sourceArea,
        titleText: queue.queueLabel,
      }),
      visible: true,
      children: (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: theme.borderStrong,
              borderRadius: adminControlRadius,
              borderWidth: 0,
              gap: adminModalDensity.actionGap,
              padding: adminModalDensity.cardPadding,
            },
          ]}
        >
          {renderAdminModalHeader({
            actions: renderAdminPrimaryButton({
              density: "compact",
              labelText: "Close",
              onPress: closeModal,
              prefixText: "",
              tone: "surface",
              width: 112,
            }),
            eyebrowText: queue.ownerLabel,
            onClose: closeModal,
            subtitleText: `${queue.priorityLabel} priority queue for ${queue.sourceArea.toLowerCase()}. ${queue.supportingText}`,
            titleText: queue.queueLabel,
          })}

          <View style={[styles.patientMetaGrid, { gap: tokens.spacing.sm }]}>
            {[
              {
                helperText: queue.supportingText,
                labelText: "Open items",
                valueText: String(totalCount),
              },
              {
                helperText: selectedAdminReviewQueueItems
                  ? "Currently visible in this workspace."
                  : "Loading current items.",
                labelText: "Visible now",
                valueText: selectedAdminReviewQueueItems
                  ? String(filteredReviewItems.length)
                  : "Loading",
              },
              {
                helperText: queue.latestSignalText,
                labelText: "Priority",
                valueText: queue.priorityLabel,
              },
              {
                helperText: queue.sourceArea,
                labelText: "Owner",
                valueText: queue.ownerLabel,
              },
            ].map((item) => (
              <View
                key={item.labelText}
                style={[
                  styles.patientMetaCard,
                  {
                    backgroundColor: theme.surfaceElevated,
                    borderColor: theme.borderSubtle,
                    borderRadius: layout.controlRadius,
                    flexBasis: isWideLayout ? 180 : 156,
                    gap: tokens.spacing.xs,
                    padding: adminModalDensity.cardPadding,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminModalDensity.metaLabelFontSize,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminModalDensity.metaValueFontSize,
                      lineHeight: adminModalDensity.bodyLineHeight,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminModalDensity.metaLabelFontSize,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {item.helperText}
                </Text>
              </View>
            ))}
          </View>

          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {reviewQueueFilterOptions.map((option) => (
              <View key={option.filterId}>
                {renderWorkspaceFilterChip({
                  chipKey: option.filterId,
                  isSelected: adminReviewQueueDetailFilter === option.filterId,
                  labelText: option.labelText,
                  onPress: () =>
                    setAdminReviewQueueDetailFilter(option.filterId),
                  theme,
                  tokens,
                })}
              </View>
            ))}
          </View>

          <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
            {isSelectedAdminReviewQueueLoading ? (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                Loading live queue items...
              </Text>
            ) : selectedAdminReviewQueueItemsError ? (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.danger,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {selectedAdminReviewQueueItemsError}
              </Text>
            ) : filteredReviewItems.length > 0 ? (
              filteredReviewItems.map((item, index) => (
                <View
                  key={item.itemId}
                  style={[
                    styles.detailBodyCard,
                    {
                      backgroundColor:
                        index % 2 === 0
                          ? adminFigmaPalette.field
                          : adminFigmaPalette.surface,
                      borderColor: theme.borderSubtle,
                      borderRadius: adminOperationalLayout.panelRadius,
                      gap: tokens.spacing.sm,
                      padding: adminModalDensity.cardPadding,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "flex-start",
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.clinicianDetailModalLeadColumnCompact,
                        {
                          alignItems: "center",
                          gap: 4,
                          width: 88,
                        },
                      ]}
                    >
                      {renderAdminStatusPill(item.priorityLabel, {
                        minWidth: 78,
                      })}
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            lineHeight: 14,
                            textAlign: "center",
                          },
                        ]}
                      >
                        {formatAdminSummaryTimestamp(
                          item.occurredAt ?? item.updatedAt,
                        )}
                      </Text>
                    </View>
                    <View style={[styles.listGroup, { flex: 1, gap: 8 }]}>
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "flex-start",
                            gap: tokens.spacing.sm,
                          },
                        ]}
                      >
                        {renderAdminStatusPill(item.sourceArea, {
                          minWidth: 74,
                        })}
                        {renderAdminStatusPill(item.statusLabel, {
                          minWidth: 74,
                        })}
                        <View style={[styles.listGroup, { flex: 1, gap: 3 }]}>
                          <Text
                            style={[
                              styles.sectionTitle,
                              {
                                color: theme.textPrimary,
                                fontFamily: layout.displayFontFamily,
                                fontSize: 16,
                                lineHeight: 20,
                              },
                            ]}
                          >
                            {item.title}
                          </Text>
                          <Text
                            style={[
                              styles.sectionSubtitle,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 13,
                                lineHeight: 18,
                              },
                            ]}
                          >
                            {resolveAdminProductCopyText(
                              item.previewText ?? item.routeLabel,
                              "Review the supporting record from this queue.",
                            )}
                          </Text>
                        </View>
                      </View>
                      <View
                        style={[
                          styles.patientRowHeader,
                          {
                            borderTopColor: theme.borderSubtle,
                            borderTopWidth: 1,
                            gap: tokens.spacing.md,
                            justifyContent: "flex-start",
                            paddingTop: tokens.spacing.xs,
                          },
                        ]}
                      >
                        {[
                          {
                            labelText: "Patient",
                            valueText:
                              item.patientDisplayName ??
                              item.patientIdentifier ??
                              "No patient",
                          },
                          {
                            labelText: "Owner",
                            valueText: resolveAdminProductCopyText(
                              item.ownerLabel,
                              queue.ownerLabel,
                            ),
                          },
                          {
                            labelText: "Updated",
                            valueText: formatAdminSummaryTimestamp(
                              item.updatedAt ?? item.occurredAt,
                            ),
                          },
                        ].map((row) => (
                          <View
                            key={`${item.itemId}-${row.labelText}`}
                            style={{
                              borderRightColor: theme.borderSubtle,
                              borderRightWidth: 1,
                              flex: 1,
                              gap: 2,
                              paddingRight: tokens.spacing.sm,
                            }}
                          >
                            <Text
                              style={[
                                styles.metricLabel,
                                {
                                  color: theme.accentStrong,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: 11,
                                },
                              ]}
                            >
                              {row.labelText}
                            </Text>
                            <Text
                              numberOfLines={1}
                              style={[
                                styles.navigationText,
                                {
                                  color: theme.textPrimary,
                                  fontFamily: layout.bodyFontFamily,
                                  fontSize: 12,
                                  lineHeight: 16,
                                },
                              ]}
                            >
                              {row.valueText}
                            </Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  </View>
                </View>
              ))
            ) : selectedAdminReviewQueueItems ? (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {loadedItems.length > 0
                  ? `No ${selectedReviewFilterLabel.toLowerCase()} items are visible for this queue.`
                  : "No live queue items are available for this queue."}
              </Text>
            ) : (
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                Loading live queue items...
              </Text>
            )}
          </View>
        </View>
      ),
    });
  };

  const renderAdminReviewsMobileList = () =>
    reviewPageRows.length > 0 ? (
      <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
        {reviewPageRows.map((row) => {
          const isReviewQueueLoading = adminReviewQueueItemsLoadingId === row.id;

          return renderAdminMobileRecordCard({
            actions: renderAdminTableActionButton({
              disabled: row.count < 1 || isReviewQueueLoading,
              labelText: isReviewQueueLoading ? "Loading..." : "View items",
              onPress: () => openAdminReviewQueueItems(row),
              tone: row.priorityLabel === "High" ? "primary" : "surface",
              width: 116,
            }),
            badge: renderAdminStatusPill(row.priorityLabel, { minWidth: 76 }),
            cardKey: row.id,
            metaRows: [
              renderAdminMobileMetaRow("Open items", String(row.count)),
              renderAdminMobileMetaRow("Owner", row.ownerLabel),
              renderAdminMobileMetaRow("Last signal", row.latestSignalText),
            ],
            subtitleText: `${row.sourceArea} - ${row.supportingText}`,
            titleText: row.queueLabel,
          });
        })}
      </View>
    ) : (
      <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          No review backlog rows match the current admin filters.
        </Text>
      </View>
    );

  const updateAdminOutboundQuestionnaireRevisionField = (
    fieldName: keyof Pick<
      AdminOutboundQuestionnaireRevisionDraft,
      | "bulkQuestionText"
      | "clinicalPurpose"
      | "defaultEstimatedMinutesText"
      | "displayName"
      | "versionLabel"
    >,
    value: string,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            [fieldName]: value,
          },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionResumePolicyValue = (
    key: string,
    value: unknown,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            defaultResumePolicy: setAdminOutboundRecordValue(
              current.defaultResumePolicy,
              key,
              value,
            ),
            resumePolicy: setAdminOutboundRecordValue(
              current.resumePolicy,
              key,
              value,
            ),
          },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionResumePolicyNumber = (
    key: string,
    value: string,
  ): void => {
    const numericValue = Number.parseInt(value.replace(/[^0-9]/gu, ""), 10);

    updateAdminOutboundQuestionnaireRevisionResumePolicyValue(
      key,
      Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null,
    );
  };

  const updateAdminOutboundQuestionnaireRevisionVoicePolicyValue = (
    key: string,
    value: unknown,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) => {
      if (current == null) {
        return current;
      }

      const reviewPolicy = normalizeAdminOutboundQuestionnaireReviewPolicy(
        current.reviewPolicy,
      );
      const voicePolicy = normalizeAdminOutboundQuestionnaireVoicePolicy(
        setAdminOutboundRecordValue(
          normalizeAdminOutboundQuestionnaireVoicePolicy(reviewPolicy.voicePolicy),
          key,
          value,
        ),
      );

      return {
        ...current,
        defaultReviewPolicy: {
          ...reviewPolicy,
          voicePolicy,
        },
        reviewPolicy: {
          ...reviewPolicy,
          voicePolicy,
        },
      };
    });
  };

  const updateAdminOutboundQuestionnaireRevisionReviewPolicyValue = (
    key: string,
    value: unknown,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) => {
      if (current == null) {
        return current;
      }

      let nextReviewPolicyRecord = setAdminOutboundRecordValue(
        current.reviewPolicy,
        key,
        value,
      );

      if (key === "redFlagRouting") {
        nextReviewPolicyRecord = setAdminOutboundRecordValue(
          nextReviewPolicyRecord,
          "redFlagStopAutomationDefault",
          value === "stop_and_review",
        );
      }

      const reviewPolicy = normalizeAdminOutboundQuestionnaireReviewPolicy(
        nextReviewPolicyRecord,
      );

      return {
        ...current,
        defaultReviewPolicy: reviewPolicy,
        reviewPolicy,
      };
    });
  };

  const updateAdminOutboundQuestionnaireRevisionQuestion = (
    questionIndex: number,
    updater: (
      question: TenantAdminOutboundQuestionnaireQuestionInputV1,
    ) => TenantAdminOutboundQuestionnaireQuestionInputV1,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            questions: current.questions.map((question, index) =>
              index === questionIndex ? updater(question) : question,
            ),
          },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionQuestionRecord = (
    questionIndex: number,
    recordName:
      | "answerNormalization"
      | "questionMetadata"
      | "redFlagRules"
      | "voicePhraseConfig",
    updater: (record: Record<string, unknown>) => Record<string, unknown>,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => ({
      ...question,
      [recordName]: updater(readAdminOutboundRecord(question[recordName])),
    }));
  };

  const updateAdminOutboundQuestionnaireRevisionQuestionRecordValue = (
    questionIndex: number,
    recordName:
      | "answerNormalization"
      | "questionMetadata"
      | "redFlagRules"
      | "voicePhraseConfig",
    key: string,
    value: unknown,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestionRecord(
      questionIndex,
      recordName,
      (record) => setAdminOutboundRecordValue(record, key, value),
    );
  };

  const updateAdminOutboundQuestionnaireRevisionAnswerHintBehavior = (
    questionIndex: number,
    behavior: "default" | "always" | "hide" | "none",
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestionRecord(
      questionIndex,
      "voicePhraseConfig",
      (record) => {
        let nextRecord = { ...record };

        [
          "answerHintBehavior",
          "includeAnswerHint",
          "showAnswerHint",
          "repeatAnswerHint",
          "forceAnswerHint",
          "suppressAnswerHint",
          "hideAnswerHint",
          "omitAnswerHint",
        ].forEach((key) => {
          delete nextRecord[key];
        });

        if (behavior === "always") {
          nextRecord.answerHintBehavior = "always";
        }

        if (behavior === "hide") {
          nextRecord.answerHintBehavior = "hide";
        }

        if (behavior === "none") {
          nextRecord.answerHintBehavior = "none";
        }

        return nextRecord;
      },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionLeadIn = (
    questionIndex: number,
    value: string,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestionRecord(
      questionIndex,
      "voicePhraseConfig",
      (record) => {
        let nextRecord = setAdminOutboundRecordValue(record, "leadIn", value);

        nextRecord = setAdminOutboundRecordValue(
          nextRecord,
          "promptLeadIn",
          null,
        );
        nextRecord = setAdminOutboundRecordValue(
          nextRecord,
          "voiceLeadIn",
          null,
        );

        return nextRecord;
      },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionAnswerHint = (
    questionIndex: number,
    value: string,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestionRecord(
      questionIndex,
      "voicePhraseConfig",
      (record) => {
        let nextRecord = setAdminOutboundRecordValue(record, "answerHint", value);

        nextRecord = setAdminOutboundRecordValue(
          nextRecord,
          "answerHintText",
          null,
        );
        nextRecord = setAdminOutboundRecordValue(
          nextRecord,
          "binaryAnswerHint",
          null,
        );

        return nextRecord;
      },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionReviewKeywords = (
    questionIndex: number,
    value: string,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestionRecord(
      questionIndex,
      "redFlagRules",
      (record) => {
        const keywords = parseAdminOutboundKeywordList(value);
        let nextRecord = setAdminOutboundRecordValue(
          record,
          "keywordsInputText",
          value,
        );

        nextRecord = setAdminOutboundRecordValue(
          nextRecord,
          "keywords",
          keywords,
        );

        nextRecord = setAdminOutboundRecordValue(
          nextRecord,
          "containsAny",
          null,
        );

        return nextRecord;
      },
    );
  };

  const updateAdminOutboundQuestionnaireRevisionMedicationReview = (
    questionIndex: number,
    enabled: boolean,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => {
      const nextAnswerNormalization = setAdminOutboundRecordValue(
        question.answerNormalization,
        "medicationReconciliation",
        enabled ? true : null,
      );
      const questionMetadata = readAdminOutboundRecord(question.questionMetadata);
      const currentMedicationMetadata = readAdminOutboundRecord(
        questionMetadata.medicationReconciliation,
      );
      const nextMedicationMetadata = enabled
        ? {
            ...currentMedicationMetadata,
            capturesPatientReportedMedication: true,
            matchingEnabled: false,
            matchingStatus: "external_medication_matching_not_configured",
            requiresClinicianReview:
              currentMedicationMetadata.requiresClinicianReview === false
                ? false
                : true,
          }
        : {
            ...currentMedicationMetadata,
            capturesPatientReportedMedication: false,
            matchingEnabled: false,
            matchingStatus: "external_medication_matching_not_configured",
          };
      const nextQuestionMetadata = setAdminOutboundRecordValue(
        questionMetadata,
        "medicationReconciliation",
        nextMedicationMetadata,
      );

      return {
        ...question,
        answerNormalization: nextAnswerNormalization,
        questionMetadata: nextQuestionMetadata,
      };
    });
  };

  const updateAdminOutboundQuestionnaireRevisionClinicianReviewLabel = (
    questionIndex: number,
    value: string,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => ({
      ...question,
      clinicianReviewLabel: value.trim().length > 0 ? value : null,
    }));
  };

  const updateAdminOutboundQuestionnaireRevisionPrompt = (
    questionIndex: number,
    value: string,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => ({
      ...question,
      promptText: value,
    }));
  };

  const cycleAdminOutboundQuestionnaireRevisionAnswerType = (
    questionIndex: number,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => ({
      ...question,
      answerType: nextAdminOutboundQuestionAnswerType(question.answerType),
    }));
  };

  const toggleAdminOutboundQuestionnaireRevisionRequired = (
    questionIndex: number,
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => ({
      ...question,
      required: question.required === false,
    }));
  };

  const updateAdminOutboundQuestionnaireRevisionFollowUpGate = (
    questionIndex: number,
    gateQuestionKey: string,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) => {
      if (current == null) {
        return current;
      }

      const trimmedGateKey = gateQuestionKey.trim();
      // Only an EARLIER question can gate a follow-up; anything else
      // (cleared selector, stale key) falls back to "Always asked".
      const gateQuestion =
        trimmedGateKey.length === 0
          ? null
          : findAdminOutboundFollowUpGateQuestion(
              current.questions,
              questionIndex,
              trimmedGateKey,
            );

      return {
        ...current,
        questions: current.questions.map((question, index) =>
          index === questionIndex
            ? {
                ...question,
                skipLogic: buildAdminOutboundFollowUpSkipLogic(
                  question.skipLogic,
                  gateQuestion == null
                    ? { gateQuestionKey: null }
                    : {
                        gateQuestionKey: trimmedGateKey,
                        trigger:
                          gateQuestion.answerType === "yes_no" ||
                          gateQuestion.answerType === "confirmation"
                            ? "equals_yes"
                            : "answered",
                      },
                ),
              }
            : question,
        ),
      };
    });
  };

  const updateAdminOutboundQuestionnaireRevisionFollowUpTrigger = (
    questionIndex: number,
    trigger: AdminOutboundFollowUpTriggerKind,
    equalsValue = "",
  ): void => {
    updateAdminOutboundQuestionnaireRevisionQuestion(questionIndex, (question) => {
      const selection = resolveAdminOutboundFollowUpGateSelection(
        question.skipLogic,
      );

      if (selection == null) {
        return question;
      }

      return {
        ...question,
        skipLogic: buildAdminOutboundFollowUpSkipLogic(question.skipLogic, {
          equalsValue,
          gateQuestionKey: selection.gateQuestionKey,
          trigger,
        }),
      };
    });
  };

  const removeAdminOutboundQuestionnaireRevisionQuestion = (
    questionIndex: number,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) =>
      current == null
        ? current
        : {
            ...current,
            questions: resequenceAdminOutboundQuestions(
              current.questions.filter((_, index) => index !== questionIndex),
            ),
          },
    );
  };

  const moveAdminOutboundQuestionnaireRevisionQuestion = (
    questionIndex: number,
    direction: -1 | 1,
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) => {
      if (!current) {
        return current;
      }

      const nextIndex = questionIndex + direction;

      if (nextIndex < 0 || nextIndex >= current.questions.length) {
        return current;
      }

      const questions = [...current.questions];
      const [question] = questions.splice(questionIndex, 1);

      if (!question) {
        return current;
      }

      questions.splice(nextIndex, 0, question);

      return {
        ...current,
        questions: resequenceAdminOutboundQuestions(questions),
      };
    });
  };

  const appendAdminOutboundQuestionnaireRevisionQuestion = (
    promptText = "",
  ): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) => {
      if (!current) {
        return current;
      }

      const existingKeys = new Set(
        current.questions.map((question) => question.questionKey),
      );
      const question = buildAdminOutboundCustomQuestion(
        promptText,
        existingKeys,
        current.questions.length + 1,
      );

      return {
        ...current,
        questions: [...current.questions, question],
      };
    });
  };

  const appendAdminOutboundQuestionnaireRevisionPastedQuestions = (): void => {
    setAdminOutboundQuestionnaireRevisionDraft((current) => {
      if (!current) {
        return current;
      }

      const prompts = parseAdminOutboundQuestionPasteLines(
        current.bulkQuestionText,
      );

      if (prompts.length === 0) {
        setAdminOpsNotice("Paste at least one question before appending.");
        return current;
      }

      const existingKeys = new Set(
        current.questions.map((question) => question.questionKey),
      );
      const customQuestions = prompts.map((promptText, index) =>
        buildAdminOutboundCustomQuestion(
          promptText,
          existingKeys,
          current.questions.length + index + 1,
        ),
      );

      return {
        ...current,
        bulkQuestionText: "",
        questions: [...current.questions, ...customQuestions],
      };
    });
  };

  const normalizeAdminOutboundQuestionnaireRevisionQuestions = (
    questions: TenantAdminOutboundQuestionnaireQuestionInputV1[],
  ): TenantAdminOutboundQuestionnaireQuestionInputV1[] => {
    const existingKeys = new Set<string>();

    return questions.map((question, index) => {
      const trimmedPrompt = question.promptText.trim();
      const questionKey =
        question.questionKey.trim().length > 0 &&
        !existingKeys.has(question.questionKey)
          ? question.questionKey
          : createUniqueAdminOutboundQuestionKey(
              trimmedPrompt || `question ${String(index + 1)}`,
              existingKeys,
            );

      existingKeys.add(questionKey);
      const redFlagRules = { ...readAdminOutboundRecord(question.redFlagRules) };

      delete redFlagRules.keywordsInputText;

      return {
        ...question,
        promptText: trimmedPrompt,
        questionKey,
        redFlagRules: normalizeAdminOutboundRedFlagRules(redFlagRules),
        voicePhraseConfig: normalizeAdminOutboundVoicePhraseConfig(
          question.voicePhraseConfig,
        ),
        sequenceNumber: index + 1,
      };
    });
  };

  const submitAdminOutboundQuestionnaireRevision = async (): Promise<void> => {
    const draft = adminOutboundQuestionnaireRevisionDraft;

    if (!draft) {
      return;
    }

    const estimatedMinutes = Number.parseInt(
      draft.defaultEstimatedMinutesText.trim(),
      10,
    );
    const normalizedReviewPolicy =
      normalizeAdminOutboundQuestionnaireReviewPolicy(draft.reviewPolicy);
    const questions = normalizeAdminOutboundQuestionnaireRevisionQuestions(
      draft.questions,
    );

    if (!Number.isInteger(estimatedMinutes) || estimatedMinutes < 1) {
      setAdminOpsNotice("Estimated call time must be at least 1 minute.");
      return;
    }

    if (questions.some((question) => question.promptText.length === 0)) {
      setAdminOpsNotice("Every questionnaire prompt needs text before publishing.");
      return;
    }

    if (questions.length < 1) {
      setAdminOpsNotice("Add at least one questionnaire prompt before publishing.");
      return;
    }

    const updatedTemplate = await runAdminOutboundCommand(
      {
        clinicalPurpose: draft.clinicalPurpose,
        defaultEstimatedMinutes: estimatedMinutes,
        defaultResumePolicy: draft.defaultResumePolicy,
        defaultReviewPolicy: normalizedReviewPolicy,
        displayName: draft.displayName,
        kind: "update-questionnaire-template",
        locale: draft.locale,
        ownerDepartmentCode: draft.ownerDepartmentCode || null,
        questionnaireTemplateId: draft.questionnaireTemplateId,
        questionnaireTemplateKey: draft.questionnaireTemplateKey,
        status: "active",
        templateMetadata: {
          source: "admin-outbound-questionnaire-revision",
        },
      },
      "Updating outbound questionnaire...",
      "Outbound questionnaire could not be updated.",
    );

    if (!updatedTemplate) {
      return;
    }

    const publishedVersion = await runAdminOutboundCommand(
      {
        clinicalApproval: {
          approvedAt: new Date().toISOString(),
          approvedByDisplay:
            activeWorkforceSession?.displayName ?? "Tenant admin",
          approvedByReference:
            activeWorkforceSession?.subjectKey ?? runtimeConfig.requestTenantId,
          approvalNotes:
            "Published from the Admin questionnaire revision editor.",
        },
        estimatedMinutes,
        kind: "publish-questionnaire-version",
        questionnaireTemplateId: draft.questionnaireTemplateId,
        questions,
        resumePolicy: draft.resumePolicy,
        reviewPolicy: normalizedReviewPolicy,
        versionLabel:
          draft.versionLabel.trim().length > 0
            ? draft.versionLabel.trim()
            : null,
      },
      "Publishing outbound questionnaire revision...",
      "Outbound questionnaire revision could not be published.",
    );

    if (publishedVersion) {
      const versionLabel =
        publishedVersion.affectedQuestionnaireVersionId == null
          ? "Published questionnaire revision."
          : `Published questionnaire revision ${publishedVersion.affectedQuestionnaireVersionId}.`;

      setAdminOpsNotice(
        `${versionLabel} Campaigns remain pinned to their current questionnaire version until an explicit campaign impact review is approved.`,
      );
      setAdminOutboundQuestionnaireRevisionDraft(null);
    }
  };

  const renderAdminOutboundCallsSurface = () => {
    const outboundAnalyticsSource =
      adminOutboundAnalyticsScope === "campaign" &&
      adminOutboundCampaignAnalytics != null
        ? adminOutboundCampaignAnalytics
        : adminOutboundCallsSummary;
    const outboundAnalytics = outboundAnalyticsSource.analytics;
    const outboundAudit = outboundAnalyticsSource.audit;
    const outboundNoAnswerOrVoicemailCount =
      outboundAnalytics.noAnswerAttemptCount +
      outboundAnalytics.voicemailAttemptCount;
    const outboundAnalyticsSeriesRows = outboundAnalytics.series.slice(-7);
    const outboundRecentAuditRows = outboundAudit.recentEvents.slice(0, 4);
    const formatOutboundPercent = (value: number) =>
      `${formatAdminAnalyticsNumber(value, 1)}%`;
    const formatOutboundAuditLabel = (value: string) =>
      value
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    const outboundMetricCards = [
      {
        labelText: "Answer rate",
        supportText: `${String(outboundAnalytics.liveAnswerCount)} of ${String(
          outboundAnalytics.attemptedCallCount,
        )} attempts reached`,
        valueText: formatOutboundPercent(outboundAnalytics.answerRatePercent),
      },
      {
        labelText: "Completion rate",
        supportText: `${String(outboundAnalytics.completedSessionCount)} completed calls`,
        valueText: formatOutboundPercent(outboundAnalytics.completionRatePercent),
      },
      {
        labelText: "No answer or VM",
        supportText: `${String(outboundAnalytics.busyAttemptCount)} busy, ${String(
          outboundAnalytics.failedAttemptCount,
        )} failed`,
        valueText: String(outboundNoAnswerOrVoicemailCount),
      },
      {
        labelText: "Opt-outs",
        supportText: `${String(outboundAnalytics.redFlagEventCount)} red flags`,
        valueText: String(outboundAnalytics.optOutEventCount),
      },
    ];
    const outboundReliabilityMetrics = adminOutboundCallsSummary.metrics;
    const outboundAuditRollupRows = [
      {
        labelText: "Audit events",
        supportText: outboundAudit.latestEventAt
          ? formatAdminSummaryTimestamp(outboundAudit.latestEventAt)
          : "No recent events",
        valueText: String(outboundAudit.totalEventCount),
      },
      {
        labelText: "Time saved",
        supportText: `${formatAdminAnalyticsNumber(
          outboundAnalytics.averageAnsweredQuestionCount,
          1,
        )} avg answers`,
        valueText: `${String(outboundAnalytics.estimatedMinutesSaved)} min`,
      },
      {
        labelText: "Review rate",
        supportText: `${String(outboundAnalytics.reviewRequiredSessionCount)} sessions routed`,
        valueText: formatOutboundPercent(
          outboundAnalytics.reviewRequiredRatePercent,
        ),
      },
      {
        labelText: "No-input pauses",
        supportText: "Paused after no usable response; re-ask before retry.",
        valueText: String(
          outboundReliabilityMetrics.repeatedNoInputPauseSessionCount ?? 0,
        ),
      },
      {
        labelText: "Medication re-asks",
        supportText: "Patient reported meds without structured entries.",
        valueText: String(
          outboundReliabilityMetrics.medicationCaptureReviewNeededCount ?? 0,
        ),
      },
      {
        labelText: "Stale reconciled",
        supportText: "Scheduler moved disconnected calls back to resume.",
        valueText: String(
          outboundReliabilityMetrics.scheduledStaleReconciliationCount ?? 0,
        ),
      },
    ];
    const selectedOutboundCampaign = adminResolvedSelectedCampaign;
    const selectedCampaignPolicy = selectedOutboundCampaign?.policy ?? null;
    const selectedResumePolicy = {
      ...adminOutboundDefaultResumePolicy,
      ...readAdminOutboundRecord(selectedCampaignPolicy?.resumePolicy),
    };
    const selectedPolicyMetadata = readAdminOutboundRecord(
      selectedCampaignPolicy?.policyMetadata,
    );
    const selectedOutboundConsentPolicy = readAdminOutboundRecord(
      selectedPolicyMetadata.outboundConsent,
    );
    const selectedOutboundAdvanceNoticePolicySource = readAdminOutboundText(
      selectedOutboundConsentPolicy.policyVersion,
    ).trim();
    const selectedOutboundAdvanceNoticePolicyApprovedAt =
      readAdminOutboundText(selectedOutboundConsentPolicy.approvedAt).trim();
    const selectedOutboundAdvanceNoticePolicyApprovedAtMs = Date.parse(
      selectedOutboundAdvanceNoticePolicyApprovedAt,
    );
    const selectedOutboundAdvanceNoticePolicyReady =
      selectedOutboundConsentPolicy.basis === "advance_notice" &&
      selectedOutboundConsentPolicy.targetConsentState === "not_required" &&
      selectedOutboundConsentPolicy.noticeProvidedBeforeEnrollment === true &&
      selectedOutboundConsentPolicy.patientAffirmativeConsentClaimed ===
        false &&
      selectedOutboundConsentPolicy.channel === "voice" &&
      selectedOutboundConsentPolicy.purpose === "outbound_questionnaire" &&
      selectedOutboundAdvanceNoticePolicySource.length > 0 &&
      readAdminOutboundText(
        selectedOutboundConsentPolicy.approvedByReference,
      ).trim().length > 0 &&
      Number.isFinite(selectedOutboundAdvanceNoticePolicyApprovedAtMs) &&
      selectedOutboundAdvanceNoticePolicyApprovedAtMs <= Date.now() &&
      readAdminOutboundText(
        selectedOutboundConsentPolicy.evidenceReference,
      ).trim().length > 0;
    const selectedRecentContextPolicy = readAdminOutboundRecord(
      selectedPolicyMetadata.recentCallContext,
    );
    const selectedAllowedDays =
      selectedCampaignPolicy?.window.allowedDays.length === 5
        ? "Weekdays"
        : selectedCampaignPolicy?.window.allowedDays
            .map((day) => day.charAt(0).toUpperCase() + day.slice(1, 3))
            .join(", ") || "No days";
    const selectedResumeDefaultDelayMinutes = readAdminOutboundNumber(
      selectedResumePolicy.defaultResumeDelayMinutes,
      adminOutboundDefaultResumePolicy.defaultResumeDelayMinutes,
    );
    const selectedResumeMaxPausedHours = readAdminOutboundNumber(
      selectedResumePolicy.maxPausedHours,
      adminOutboundDefaultResumePolicy.maxPausedHours,
    );
    const selectedResumeMaxAttempts = readAdminOutboundNumber(
      selectedResumePolicy.maxResumeAttempts,
      adminOutboundDefaultResumePolicy.maxResumeAttempts,
    );
    const policyRows = [
      {
        iconName: "clock" as const,
        labelText: "Call window",
        valueText: selectedCampaignPolicy
          ? `${selectedAllowedDays}, ${selectedCampaignPolicy.window.allowedStartLocalTime}-${selectedCampaignPolicy.window.allowedEndLocalTime} ${selectedCampaignPolicy.window.timezoneMode.replace(/_/gu, " ")}`
          : "No saved call window",
      },
      {
        iconName: "sync" as const,
        labelText: "Retry policy",
        valueText: selectedCampaignPolicy
          ? `${String(selectedCampaignPolicy.maxAttemptsPerDay)} per day, ${String(selectedCampaignPolicy.maxAttemptsTotal)} total, ${String(selectedCampaignPolicy.minRetryCooldownMinutes)} minute cooldown`
          : "No retry policy saved",
      },
      {
        iconName: "archive" as const,
        labelText: "Resume behavior",
        valueText: `${String(selectedResumeDefaultDelayMinutes)} minute default, ${String(selectedResumeMaxPausedHours)} hour max pause`,
      },
      {
        iconName: "phone" as const,
        labelText: "Patient callback times",
        valueText: readAdminOutboundBoolean(
          selectedResumePolicy.allowPatientRequestedTime,
          adminOutboundDefaultResumePolicy.allowPatientRequestedTime,
        )
          ? `Morning ${String(selectedResumePolicy.morningLocalTime)}, afternoon ${String(selectedResumePolicy.afternoonLocalTime)}, ${String(selectedResumeMaxAttempts)} resume attempts`
          : "Patient-requested callbacks off",
      },
      {
        iconName: "document" as const,
        labelText: "Recent call context",
        valueText: readAdminOutboundBoolean(
          selectedRecentContextPolicy.enabled,
          true,
        )
          ? `Enabled, ${String(selectedRecentContextPolicy.mode ?? "active_call_only").replace(/_/gu, " ")}`
          : "Disabled",
      },
      {
        iconName: "volume-off" as const,
        labelText: "Recording",
        valueText: selectedCampaignPolicy
          ? `${selectedCampaignPolicy.recordingPolicy.replace(/_/gu, " ")}, voicemail ${selectedCampaignPolicy.voicemailPolicy.replace(/_/gu, " ")}`
          : "No recording policy saved",
      },
    ];
    const outboundActiveTargetStatuses = new Set([
      "eligible",
      "scheduled",
      "calling",
      "paused",
      "review_required",
    ]);
    const outboundTargetCandidateRows = previewPatientRowsForStatusFilters
      .map((row) => {
        const patientId = row.patientId ?? row.subjectKey;
        const defaultTargetReasonKey = "admin_selected_questionnaire";
        const patientTargets = adminOutboundTargets.filter(
          (target) => target.patientId === patientId,
        );
        const patientSuppressionTarget = patientTargets.find(
          (target) =>
            target.targetStatus === "suppressed" ||
            target.suppressionReasonKey != null ||
            target.consentState === "declined",
        );
        const activeCampaignTargets = patientTargets.filter(
          (target) =>
            target.campaignId ===
              (selectedOutboundCampaign?.campaignId ?? "") &&
            outboundActiveTargetStatuses.has(target.targetStatus),
        );
        const exactCampaignTarget = activeCampaignTargets.find(
          (target) => target.targetReasonKey === defaultTargetReasonKey,
        );
        const existingCampaignTarget =
          exactCampaignTarget ??
          (activeCampaignTargets.length === 1
            ? activeCampaignTargets[0]
            : undefined);
        const hasCampaignTargetConflict = activeCampaignTargets.length > 1;
        const targetReasonKey =
          existingCampaignTarget?.targetReasonKey ?? defaultTargetReasonKey;
        const normalizedStatus = row.statusLabel.trim().toLowerCase();
        const isInactive =
          normalizedStatus === "inactive" || normalizedStatus === "retired";
        const hasPhone = row.phoneNumber?.trim().length ? true : false;
        const hasContactWindow = row.contactWindowText?.trim().length
          ? true
          : false;
        const isSuppressed = isInactive || patientSuppressionTarget != null;
        const readinessText = isSuppressed
          ? "Suppressed"
          : hasCampaignTargetConflict
            ? "Target conflict"
          : hasPhone
            ? "Ready to review"
            : "Needs phone";
        const eligibilityText = isSuppressed
          ? patientSuppressionTarget?.suppressionReasonKey
            ? `Outbound contact blocked: ${humanizeWorkspaceKey(
                patientSuppressionTarget.suppressionReasonKey,
              )}.`
            : "Inactive or opted-out patients stay out of outbound queues."
          : hasCampaignTargetConflict
            ? "Multiple active targets require admin reconciliation."
          : row.careTypeLabel === "Procedure"
            ? "Procedure follow-up candidate."
            : row.requiresAttention
              ? "Care-team attention candidate."
              : "Requires admin eligibility review.";

        return {
          consentSource: existingCampaignTarget?.consentSource ?? null,
          consentState: existingCampaignTarget?.consentState ?? null,
          consentText: existingCampaignTarget
            ? `${
                existingCampaignTarget.consentState === "not_required"
                  ? "Covered by advance-notice policy"
                  : humanizeWorkspaceKey(existingCampaignTarget.consentState)
              }${
                existingCampaignTarget.consentSource
                  ? ` · ${existingCampaignTarget.consentSource}`
                  : ""
              }`
            : "Not recorded - attest policy before adding",
          eligibilityText,
          hasContactWindow,
          hasPhone,
          hasCampaignTargetConflict,
          patientSuppressionReasonKey:
            patientSuppressionTarget?.suppressionReasonKey ??
            (patientSuppressionTarget?.consentState === "declined"
              ? "patient_opt_out"
              : null),
          patientRow: row,
          phoneText: row.phoneNumber ?? "No phone on record",
          readinessText,
          scheduleText:
            row.contactWindowText ?? "Default weekday 09:00-17:00 window",
          targetReasonKey,
        };
      });
    const outboundTargetGuardrailRows = [
      {
        iconName: "phone" as const,
        labelText: "Contactability",
        valueText: "Phone and local window required.",
      },
      {
        iconName: "check" as const,
        labelText: "Contact authorization",
        valueText:
          "Admin attests advance notice before assignment; separate consent is not required.",
      },
      {
        iconName: "stop" as const,
        labelText: "Suppression",
        valueText: "Opt-outs, inactive, missing phone stay blocked.",
      },
      {
        iconName: "sync" as const,
        labelText: "Bulk targeting",
        valueText: "Creates target records only; scheduler and dialing stay off.",
      },
    ];
    const persistedOutboundTemplate =
      adminOutboundQuestionnairesPage?.[0] ?? null;
    const selectedOutboundTemplate =
      persistedOutboundTemplate ??
      adminOutboundQuestionnaireTemplates[0] ??
      defaultAdminOutboundQuestionnaireTemplates[0];
    const shouldOpenScaffoldOutboundEditor =
      runtimeConfig.clinicianWorkbenchTransportMode === "scaffold";
    const selectedOutboundVersion =
      selectedOutboundTemplate?.activeVersion ??
      (selectedOutboundTemplate?.latestVersion?.versionStatus === "active"
        ? selectedOutboundTemplate.latestVersion
        : null);
    const readyOutboundTargetCandidates = outboundTargetCandidateRows.filter(
      (candidate) =>
        candidate.hasPhone &&
        candidate.readinessText === "Ready to review" &&
        candidate.targetReasonKey === "admin_selected_questionnaire",
    );
    const outboundSelectedIdSet = new Set(adminOutboundSelectedTargetIds);
    const selectedReadyOutboundTargetCandidates =
      readyOutboundTargetCandidates.filter((candidate) =>
        outboundSelectedIdSet.has(candidate.patientRow.id),
      );
    const buildOutboundAdvanceNoticeAttestationKey = (
      scope: "bulk-all-ready" | "bulk-selected" | "single",
      candidates: ReadonlyArray<(typeof outboundTargetCandidateRows)[number]>,
    ) => {
      const patientIds = candidates
        .map(
          (candidate) =>
            candidate.patientRow.patientId ?? candidate.patientRow.subjectKey,
        )
        .sort();

      return [
        "advance-notice",
        selectedOutboundCampaign?.campaignId ?? "no-campaign",
        selectedOutboundAdvanceNoticePolicySource || "no-policy",
        scope,
        ...patientIds,
      ].join("|");
    };
    const activeBulkOutboundScope =
      adminOutboundSelectedTargetIds.length > 0
        ? ("selected" as const)
        : ("all-ready" as const);
    const activeBulkOutboundTargetCandidates =
      activeBulkOutboundScope === "selected"
        ? selectedReadyOutboundTargetCandidates
        : readyOutboundTargetCandidates;
    const activeBulkOutboundAttestationKey =
      buildOutboundAdvanceNoticeAttestationKey(
        activeBulkOutboundScope === "selected"
          ? "bulk-selected"
          : "bulk-all-ready",
        activeBulkOutboundTargetCandidates,
      );
    const isOutboundAdvanceNoticeAttested = (attestationKey: string) =>
      adminOutboundAdvanceNoticePolicyAttestationKey === attestationKey;
    const schedulableOutboundTargets = adminOutboundTargets.filter(
      (target) =>
        selectedOutboundCampaign != null &&
        target.campaignId === selectedOutboundCampaign.campaignId &&
        ["eligible", "scheduled"].includes(target.targetStatus) &&
        target.phoneNumberE164.trim().length > 0,
    );
    const selectedOutboundDialerReadiness =
      selectedOutboundCampaign?.dialerConfig?.readiness ?? null;
    const liveOutboundDialingReady =
      selectedOutboundCampaign?.dialerConfig?.dialerStatus === "ready" &&
      selectedOutboundDialerReadiness?.directCallReady === true;
    const campaignScaleOutboundDialingReady =
      selectedOutboundCampaign?.dialerConfig?.dialerStatus === "ready" &&
      selectedOutboundDialerReadiness?.campaignBatchReady === true;
    const directCallableOutboundTargets = adminOutboundTargets.filter(
      (target) =>
        selectedOutboundCampaign != null &&
        target.campaignId === selectedOutboundCampaign.campaignId &&
        target.targetStatus === "scheduled" &&
        (target.queuedDirectCallAttemptId ?? "").trim().length > 0 &&
        target.phoneNumberE164.trim().length > 0,
    );
    const firstDirectCallableOutboundTarget =
      directCallableOutboundTargets[0] ?? null;
    const resumeScheduledOutboundTargets = schedulableOutboundTargets.filter(
      (target) => {
        const eligibleAt = Date.parse(target.nextEligibleAttemptAt ?? "");

        return Number.isFinite(eligibleAt) && eligibleAt > Date.now();
      },
    );
    const firstResumeScheduledOutboundTarget =
      resumeScheduledOutboundTargets[0] ?? null;
    const liveOutboundCallIdleStatusText = liveOutboundDialingReady
      ? schedulableOutboundTargets.length > 0
          ? "Run dry run first"
          : "No queued calls"
      : "Live calls locked";
    const selectedOutboundCampaignIsActive =
      selectedOutboundCampaign?.campaignStatus === "active";
    const runCreateDefaultOutboundQuestionnaire = () => {
      if (!selectedOutboundTemplate) {
        return;
      }

      void runAdminOutboundCommand(
        {
          clinicalPurpose: selectedOutboundTemplate.clinicalPurpose,
          defaultEstimatedMinutes:
            selectedOutboundTemplate.defaultEstimatedMinutes ?? 7,
          defaultResumePolicy: selectedOutboundTemplate.defaultResumePolicy,
          defaultReviewPolicy: selectedOutboundTemplate.defaultReviewPolicy,
          displayName: selectedOutboundTemplate.displayName,
          kind: "create-questionnaire-template",
          locale: selectedOutboundTemplate.locale,
          ownerDepartmentCode: selectedOutboundTemplate.ownerDepartmentCode,
          questionnaireTemplateKey:
            selectedOutboundTemplate.questionnaireTemplateKey,
          status: "draft",
          templateMetadata: {
            source: "admin-outbound-default-authoring",
          },
        },
        "Creating outbound questionnaire template...",
        "Outbound questionnaire template could not be created.",
      );
    };
    const runPublishOutboundQuestionnaireVersion = (
      template: TenantAdminOutboundQuestionnaireTemplateV1,
    ) => {
      const sourceQuestions =
        template.latestVersion?.questions ??
        defaultAdminOutboundQuestionnaireTemplates[0]?.latestVersion?.questions ??
        [];

      void runAdminOutboundCommand(
        {
          clinicalApproval: {
            approvedAt: new Date().toISOString(),
            approvedByDisplay:
              activeWorkforceSession?.displayName ?? "Tenant admin",
            approvedByReference:
              activeWorkforceSession?.subjectKey ?? runtimeConfig.requestTenantId,
            approvalNotes:
              "Published from the Admin outbound questionnaire MVP.",
          },
          estimatedMinutes:
            template.latestVersion?.estimatedMinutes ??
            template.defaultEstimatedMinutes ??
            7,
          kind: "publish-questionnaire-version",
          questionnaireTemplateId: template.questionnaireTemplateId,
          questions: sourceQuestions.map((question) => ({
            answerNormalization: question.answerNormalization,
            answerType: question.answerType,
            clinicianReviewLabel: question.clinicianReviewLabel,
            dtmfConfig: question.dtmfConfig,
            promptText: question.promptText,
            questionMetadata: question.questionMetadata,
            questionKey: question.questionKey,
            redFlagRules: question.redFlagRules,
            required: question.required,
            sectionKey: question.sectionKey,
            sectionLabel: question.sectionLabel,
            sequenceNumber: question.sequenceNumber,
            skipLogic: question.skipLogic,
            validationRules: question.validationRules,
            voicePhraseConfig: question.voicePhraseConfig,
          })),
          resumePolicy:
            template.latestVersion?.resumePolicy ?? template.defaultResumePolicy,
          reviewPolicy:
            template.latestVersion?.reviewPolicy ?? template.defaultReviewPolicy,
          versionLabel: "Admin approved v1",
        },
        "Publishing outbound questionnaire version...",
        "Outbound questionnaire version could not be published.",
      );
    };
    const openOutboundQuestionnaireRevisionEditor = (
      template: TenantAdminOutboundQuestionnaireTemplateV1,
    ) => {
      const sourceVersion = template.activeVersion ?? template.latestVersion;
      const sourceQuestions =
        sourceVersion?.questions ??
        defaultAdminOutboundQuestionnaireTemplates[0]?.latestVersion?.questions ??
        [];
      const nextVersionNumber = (template.latestVersion?.versionNumber ?? 0) + 1;

      setAdminOutboundRevisionStep("policy");
      setAdminOutboundQuestionnaireRevisionDraft({
        bulkQuestionText: "",
        clinicalPurpose: template.clinicalPurpose,
        defaultEstimatedMinutesText: String(
          template.defaultEstimatedMinutes ?? sourceVersion?.estimatedMinutes ?? 7,
        ),
        defaultResumePolicy: template.defaultResumePolicy,
        defaultReviewPolicy: template.defaultReviewPolicy,
        displayName: template.displayName,
        locale: template.locale,
        ownerDepartmentCode: template.ownerDepartmentCode ?? "",
        questionnaireTemplateId: template.questionnaireTemplateId,
        questionnaireTemplateKey: template.questionnaireTemplateKey,
        questions: sourceQuestions.map((question) => ({
          answerNormalization: question.answerNormalization,
          answerType: question.answerType,
          clinicianReviewLabel: question.clinicianReviewLabel,
          dtmfConfig: question.dtmfConfig,
          promptText: question.promptText,
          questionMetadata: question.questionMetadata,
          questionKey: question.questionKey,
          redFlagRules: question.redFlagRules,
          required: question.required,
          sectionKey: question.sectionKey,
          sectionLabel: question.sectionLabel,
          sequenceNumber: question.sequenceNumber,
          skipLogic: question.skipLogic,
          validationRules: question.validationRules,
          voicePhraseConfig: question.voicePhraseConfig,
        })),
        resumePolicy: sourceVersion?.resumePolicy ?? template.defaultResumePolicy,
        reviewPolicy: sourceVersion?.reviewPolicy ?? template.defaultReviewPolicy,
        sourceQuestionnaireVersionId:
          sourceVersion?.questionnaireVersionId ?? null,
        versionLabel: `Admin revised v${String(nextVersionNumber)}`,
      });
    };
    const runCreateDefaultOutboundCampaign = () => {
      if (!selectedOutboundVersion) {
        setAdminOpsNotice(
          "Publish an outbound questionnaire version before creating a campaign.",
        );
        return;
      }

      const baseCampaignKey = "pre-surgery-questionnaire";
      const existingDefaultCampaignCount = adminOutboundCampaignList.filter(
        (campaign) =>
          campaign.campaignKey === baseCampaignKey ||
          campaign.campaignKey.startsWith(`${baseCampaignKey}-`),
      ).length;
      const campaignKey =
        existingDefaultCampaignCount === 0
          ? baseCampaignKey
          : `${baseCampaignKey}-${Date.now().toString(36)}`;
      const displayName =
        existingDefaultCampaignCount === 0
          ? "Pre-surgery questionnaire"
          : `Pre-surgery questionnaire ${String(existingDefaultCampaignCount + 1)}`;

      void runAdminOutboundCommand(
        {
          campaignKey,
          campaignKind: "pre_surgery_questionnaire",
          campaignStatus: "draft",
          displayName,
          eligibilityRules: {
            relativeProcedureWindow: "7 to 2 days before procedure",
          },
          kind: "create-campaign",
          policy: {
            allowedDays: [
              "monday",
              "tuesday",
              "wednesday",
              "thursday",
              "friday",
            ],
            allowedEndLocalTime: "17:00",
            allowedStartLocalTime: "09:00",
            callCapMinutes: 8,
            maxAttemptsPerDay: 2,
            maxAttemptsTotal: 4,
            minRetryCooldownMinutes: 240,
            optOutPolicy: {
              suppressionRequired: true,
            },
            policyKey: "primary",
            policyMetadata: {
              recentCallContext: {
                enabled: true,
                mode: "active_call_only",
              },
            },
            recordingPolicy: "disabled",
            resumePolicy: adminOutboundDefaultResumePolicy,
            timezoneMode: "patient_local",
            voicemailPolicy: "minimal_message",
          },
          purposeText:
            "Ask approved pre-surgery readiness questions before the procedure window closes.",
          questionnaireTemplateVersionId:
            selectedOutboundVersion.questionnaireVersionId,
          reviewRoutingRules: {
            redFlags: "clinician_review_required",
          },
        },
        "Creating outbound campaign...",
        "Outbound campaign could not be created.",
      );
    };
    const runDuplicateOutboundCampaign = (
      campaign: TenantAdminOutboundCampaignV1,
    ) => {
      void runAdminOutboundCommand(
        {
          kind: "duplicate-campaign",
          reasonText: "Duplicated from admin console.",
          sourceCampaignId: campaign.campaignId,
        },
        `Duplicating ${campaign.displayName}...`,
        "Campaign could not be duplicated.",
      );
    };
    const runOutboundCampaignLifecycle = (
      campaign: TenantAdminOutboundCampaignV1,
      action: "pause-campaign" | "resume-campaign" | "archive-campaign",
    ) => {
      const verb =
        action === "pause-campaign"
          ? "Pausing"
          : action === "resume-campaign"
            ? "Resuming"
            : "Archiving";

      void runAdminOutboundCommand(
        {
          campaignId: campaign.campaignId,
          kind: action,
          reasonText: "Updated from admin console.",
        },
        `${verb} ${campaign.displayName}...`,
        `Campaign could not be updated (${action}).`,
      );
    };
    const runValidateOutboundCampaignReadiness = (
      campaign: TenantAdminOutboundCampaignV1,
    ) => {
      void runAdminOutboundCommand(
        {
          campaignId: campaign.campaignId,
          kind: "validate-campaign-readiness",
          reasonText: "Readiness re-check from admin console.",
        },
        `Checking readiness for ${campaign.displayName}...`,
        "Campaign readiness could not be validated.",
      );
    };
    const runDuplicateOutboundQuestionnaire = (
      template: TenantAdminOutboundQuestionnaireTemplateV1,
    ) => {
      void runAdminOutboundCommand(
        {
          kind: "duplicate-questionnaire-template",
          reasonText: "Duplicated from admin console.",
          sourceQuestionnaireTemplateId: template.questionnaireTemplateId,
        },
        `Duplicating ${template.displayName}...`,
        "Questionnaire could not be duplicated.",
      );
    };
    const runSetOutboundCampaignScheduling = (nextState: "active" | "paused") => {
      if (!selectedOutboundCampaign) {
        setAdminOpsNotice("Create an outbound campaign before changing scheduling.");
        return;
      }

      void runAdminOutboundCommand(
        {
          campaignId: selectedOutboundCampaign.campaignId,
          kind: nextState === "active" ? "resume-campaign" : "pause-campaign",
          reasonText:
            nextState === "active"
              ? "Enabled for scheduler dry-run evaluation from Admin."
              : "Paused from Admin outbound call controls.",
        },
        nextState === "active"
          ? "Enabling outbound scheduling..."
          : "Pausing outbound scheduling...",
        nextState === "active"
          ? "Outbound scheduling could not be enabled."
          : "Outbound scheduling could not be paused.",
      );
    };
    const runOutboundSchedulerDryRun = () => {
      if (!selectedOutboundCampaign) {
        setAdminOpsNotice("Create an outbound campaign before running the scheduler.");
        return;
      }

      if (!selectedOutboundCampaignIsActive) {
        setAdminOpsNotice(
          "Enable scheduling for the campaign before running the dry-run scheduler.",
        );
        return;
      }

      if (schedulableOutboundTargets.length === 0) {
        setAdminOpsNotice(
          "Flag at least one patient target before running the dry-run scheduler.",
        );
        return;
      }

      void runAdminOutboundCommand(
        {
          campaignId: selectedOutboundCampaign.campaignId,
          kind: "run-outbound-scheduler-dry-run",
          limit: 5,
          reasonText: "Admin-triggered dry run. Live AmpleCare dialing remains blocked.",
        },
        "Running outbound scheduler dry run...",
        "Outbound scheduler dry run could not be completed.",
      );
    };
    const runQueueOutboundResumeNow = () => {
      if (!firstResumeScheduledOutboundTarget) {
        setAdminOpsNotice("No scheduled resume target is available to queue.");
        return;
      }

      void runAdminOutboundCommand(
        {
          kind: "manually-schedule-outbound-call-attempt",
          reasonText: "Admin queued scheduled questionnaire resume for immediate direct call.",
          scheduledFor: new Date().toISOString(),
          targetId: firstResumeScheduledOutboundTarget.targetId,
        },
        `Queueing resume for ${firstResumeScheduledOutboundTarget.patientDisplayName}...`,
        "Scheduled resume could not be queued.",
      );
    };
    const runStartOutboundDirectCall = () => {
      if (!liveOutboundDialingReady) {
        const blockers =
          selectedOutboundCampaign?.dialerConfig?.readiness?.blockerMessages ??
          adminOutboundCallsSummary.gate.blockerMessages;

        setAdminOpsNotice(
          `Live AmpleCare calls are locked: ${
            blockers.map(resolveAdminOutboundCallingBlockerText).join(" ") ||
            "complete outbound call readiness before starting a call."
          }`,
        );
        return;
      }

      if (!firstDirectCallableOutboundTarget) {
        setAdminOpsNotice(
          "Run the dry-run scheduler first so there is a queued direct-call attempt.",
        );
        return;
      }
      const queuedAttemptId =
        firstDirectCallableOutboundTarget.queuedDirectCallAttemptId?.trim() ?? "";

      if (!queuedAttemptId) {
        setAdminOpsNotice(
          "Run the dry-run scheduler first so there is a queued direct-call attempt.",
        );
        return;
      }

      void runAdminOutboundCommand(
        {
          attemptId: queuedAttemptId,
          kind: "start-outbound-direct-call",
          reasonText: "Admin-triggered direct-call MVP.",
        },
        `Starting AmpleCare call for ${firstDirectCallableOutboundTarget.patientDisplayName}...`,
        "AmpleCare direct call could not be started.",
      );
    };
    const runSubmitOutboundCampaignDialBatch = () => {
      if (!selectedOutboundCampaign) {
        setAdminOpsNotice(
          "Create an outbound campaign before submitting a campaign dial batch.",
        );
        return;
      }

      if (!selectedOutboundCampaignIsActive) {
        setAdminOpsNotice(
          "Enable scheduling for the campaign before submitting a campaign dial batch.",
        );
        return;
      }

      if (!campaignScaleOutboundDialingReady) {
        const blockers =
          selectedOutboundCampaign.dialerConfig?.readiness?.blockerMessages ??
          adminOutboundCallsSummary.gate.blockerMessages;

        setAdminOpsNotice(
          `Campaign-scale AmpleCare dialing is locked: ${
            blockers.map(resolveAdminOutboundCallingBlockerText).join(" ") ||
            "complete outbound campaign readiness before submitting a batch."
          }`,
        );
        return;
      }

      if (schedulableOutboundTargets.length === 0) {
        setAdminOpsNotice(
          "Flag at least one due patient target before submitting a campaign dial batch.",
        );
        return;
      }

      void runAdminOutboundCommand(
        {
          campaignId: selectedOutboundCampaign.campaignId,
          kind: "submit-outbound-campaign-dial-batch",
          limit: 5,
          reasonText: "Admin-triggered managed campaign dial batch.",
        },
        "Submitting outbound campaign dial batch...",
        "Outbound campaign dial batch could not be submitted.",
      );
    };
    const runFlagOutboundCandidate = (
      candidate: (typeof outboundTargetCandidateRows)[number],
    ) => {
      if (!selectedOutboundCampaign) {
        setAdminOpsNotice("Create an outbound campaign before flagging patients.");
        return;
      }

      if (
        !candidate.hasPhone ||
        candidate.readinessText !== "Ready to review"
      ) {
        setAdminOpsNotice(
          candidate.hasCampaignTargetConflict
            ? "Reconcile this patient's duplicate active targets before changing outbound authorization."
            : "Resolve this patient's phone or suppression blocker before adding them.",
        );
        return;
      }

      if (!selectedOutboundAdvanceNoticePolicyReady) {
        setAdminOpsNotice(
          "Configure an approved advance-notice policy for this campaign before adding patients.",
        );
        return;
      }

      const attestationKey = buildOutboundAdvanceNoticeAttestationKey(
        "single",
        [candidate],
      );

      if (!isOutboundAdvanceNoticeAttested(attestationKey)) {
        setAdminOpsNotice(
          "Confirm the approved advance-notice policy for this patient before adding them.",
        );
        return;
      }

      void runAdminOutboundCommand(
        {
          campaignId: selectedOutboundCampaign.campaignId,
          consentSource: selectedOutboundAdvanceNoticePolicySource,
          consentState: "not_required",
          kind: "flag-patient-for-outbound-call",
          patientId:
            candidate.patientRow.patientId ?? candidate.patientRow.subjectKey,
          patientTimezone: null,
          phoneNumberE164: candidate.patientRow.phoneNumber ?? "",
          preferredCallWindow: candidate.patientRow.contactWindowText
            ? { contactWindowText: candidate.patientRow.contactWindowText }
            : {},
          targetReasonKey: candidate.targetReasonKey,
        },
        `Flagging ${candidate.patientRow.displayName} for outbound calling...`,
        "Patient could not be flagged for outbound calling.",
      ).then((createdTarget) => {
        if (createdTarget) {
          setAdminOutboundAdvanceNoticePolicyAttestationKey(null);
        }
      });
    };
    const runBulkCreateOutboundTargets = (
      scope: "all-ready" | "selected" = "all-ready",
    ) => {
      const targetCandidates =
        scope === "selected"
          ? selectedReadyOutboundTargetCandidates
          : readyOutboundTargetCandidates;

      if (!selectedOutboundCampaign || targetCandidates.length === 0) {
        setAdminOpsNotice(
          scope === "selected"
            ? "Select at least one unsuppressed patient with a valid phone number before creating targets."
            : "Create an outbound campaign and select patients with valid phone numbers before bulk targeting.",
        );
        return;
      }

      if (!selectedOutboundAdvanceNoticePolicyReady) {
        setAdminOpsNotice(
          "Configure an approved advance-notice policy for this campaign before creating targets.",
        );
        return;
      }

      const attestationKey = buildOutboundAdvanceNoticeAttestationKey(
        scope === "selected" ? "bulk-selected" : "bulk-all-ready",
        targetCandidates,
      );

      if (!isOutboundAdvanceNoticeAttested(attestationKey)) {
        setAdminOpsNotice(
          "Confirm the approved advance-notice policy for this exact patient selection before creating targets.",
        );
        return;
      }

      setAdminOutboundTargetToast(null);
      void runAdminOutboundCommand(
        {
          campaignId: selectedOutboundCampaign.campaignId,
          consentSource: selectedOutboundAdvanceNoticePolicySource,
          consentState: "not_required",
          kind: "bulk-create-outbound-call-targets",
          notes:
            scope === "selected"
              ? "Created from selected rows in the Admin outbound targeting queue."
              : "Created from the Admin outbound targeting queue.",
          patientIds: targetCandidates.map(
            (candidate) =>
              candidate.patientRow.patientId ?? candidate.patientRow.subjectKey,
          ),
          targetReasonKey: "admin_selected_questionnaire",
        },
        scope === "selected"
          ? "Creating selected outbound call targets..."
          : "Creating outbound call targets...",
        "Outbound call targets could not be created.",
      ).then((createdTargets) => {
        if (!createdTargets) {
          return;
        }

        setAdminOutboundSelectedTargetIds([]);
        setAdminOutboundAdvanceNoticePolicyAttestationKey(null);
        setAdminOutboundTargetToast(createdTargets.summaryText);
      });
    };

    const adminOutboundTabs: ReadonlyArray<{
      badgeText?: string;
      id: AdminOutboundTabId;
      labelText: string;
    }> = [
      { id: "overview", labelText: "Overview" },
      { id: "questionnaire", labelText: "Questionnaire" },
      {
        badgeText: String(outboundTargetCandidateRows.length),
        id: "patients",
        labelText: "Patients",
      },
      { id: "campaign", labelText: "Campaign" },
      { id: "activity", labelText: "Activity" },
    ];
    const resolveCampaignReadinessColor = (
      campaign: TenantAdminOutboundCampaignV1,
    ): string => {
      if (
        campaign.campaignStatus === "archived" ||
        campaign.campaignStatus === "completed"
      ) {
        return theme.textTertiary;
      }

      const dialerStatus = campaign.dialerConfig?.dialerStatus;

      if (dialerStatus === "blocked" || dialerStatus === "unconfigured") {
        return theme.danger;
      }

      if (
        campaign.campaignStatus === "paused" ||
        campaign.campaignStatus === "draft"
      ) {
        return theme.warning;
      }

      return theme.success;
    };
    const renderAdminOutboundCampaignSelector = () => {
      const selected = selectedOutboundCampaign;

      return (
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              flexWrap: "wrap",
              gap: tokens.spacing.sm,
              position: "relative",
              zIndex: 40,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 10,
                fontWeight: adminTypography.sectionLabelWeight,
                letterSpacing: 0,
                textTransform: "uppercase",
              },
            ]}
          >
            Active campaign
          </Text>
          <View style={{ position: "relative" }}>
            <Pressable
              accessibilityLabel="Switch active campaign"
              disabled={adminOutboundCampaignList.length === 0}
              onPress={() =>
                setAdminOutboundCampaignPickerOpen(
                  !adminOutboundCampaignPickerOpen,
                )
              }
              style={[
                adminWebOutlineReset,
                {
                  alignItems: "center",
                  backgroundColor: theme.surface,
                  borderColor: theme.borderStrong,
                  borderRadius: adminOperationalLayout.controlRadius,
                  borderWidth: 1.5,
                  flexDirection: "row",
                  gap: 9,
                  paddingHorizontal: 13,
                  paddingVertical: 8,
                },
              ]}
            >
              <View
                style={{
                  backgroundColor: selected
                    ? resolveCampaignReadinessColor(selected)
                    : theme.textTertiary,
                  borderRadius: 999,
                  height: 9,
                  width: 9,
                }}
              />
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 14,
                    fontWeight: adminTypography.titleWeight,
                  },
                ]}
              >
                {selected?.displayName ?? "No campaigns yet"}
              </Text>
              {selected?.questionnaireVersionNumber != null ? (
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textTertiary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                    },
                  ]}
                >
                  {`· v${String(selected.questionnaireVersionNumber)}`}
                </Text>
              ) : null}
              <View style={{ transform: [{ rotate: "90deg" }] }}>
                <LineIcon
                  color={theme.accentStrong}
                  name="chevron-right"
                  size={14}
                  strokeWidth={2.4}
                />
              </View>
            </Pressable>
            {adminOutboundCampaignPickerOpen ? (
              <View
                style={{
                  backgroundColor: theme.surface,
                  borderColor: theme.borderStrong,
                  borderRadius: adminOperationalLayout.panelRadius,
                  borderWidth: 1,
                  marginTop: 6,
                  maxWidth: 360,
                  minWidth: 280,
                  padding: 6,
                  position: "absolute",
                  shadowColor: theme.textPrimary,
                  shadowOffset: { height: 12, width: 0 },
                  shadowOpacity: 0.16,
                  shadowRadius: 28,
                  top: "100%",
                  zIndex: 30,
                }}
              >
                {adminOutboundCampaignList.map((campaign) => {
                  const isActive =
                    campaign.campaignId === selected?.campaignId;

                  return (
                    <Pressable
                      key={campaign.campaignId}
                      onPress={() => {
                        setAdminOutboundSelectedCampaignId(campaign.campaignId);
                        setAdminOutboundCampaignPickerOpen(false);
                        setAdminOutboundPatientPageIndex(0);
                      }}
                      style={[
                        adminWebOutlineReset,
                        {
                          alignItems: "center",
                          backgroundColor: isActive
                            ? theme.accentSubtle
                            : "transparent",
                          borderRadius: adminOperationalLayout.controlRadius,
                          flexDirection: "row",
                          gap: 10,
                          paddingHorizontal: 11,
                          paddingVertical: 9,
                        },
                      ]}
                    >
                      <View
                        style={{
                          backgroundColor:
                            resolveCampaignReadinessColor(campaign),
                          borderRadius: 999,
                          height: 9,
                          width: 9,
                        }}
                      />
                      <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.navigationText,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 13,
                              fontWeight: adminTypography.titleWeight,
                            },
                          ]}
                        >
                          {campaign.displayName}
                        </Text>
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.metricLabel,
                            {
                              color: theme.textTertiary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10.5,
                            },
                          ]}
                        >
                          {`${humanizeWorkspaceKey(campaign.campaignStatus)}${
                            campaign.questionnaireDisplayName
                              ? ` · ${campaign.questionnaireDisplayName}`
                              : ""
                          }`}
                        </Text>
                      </View>
                      {isActive ? (
                        <LineIcon
                          color={theme.accentStrong}
                          name="check"
                          size={14}
                          strokeWidth={2.6}
                        />
                      ) : null}
                    </Pressable>
                  );
                })}
              </View>
            ) : null}
          </View>
        </View>
      );
    };
    const renderOutboundCampaignActionButton = (input: {
      labelText: string;
      onPress: () => void;
      tone?: "primary" | "surface" | "danger";
    }) =>
      renderAdminPrimaryButton({
        density: "compact",
        disabled: isAdminOutboundCommandSubmitting,
        iconName: null,
        labelText: input.labelText,
        minWidth: 0,
        onPress: input.onPress,
        prefixText: "",
        tone: input.tone ?? "surface",
      });
    const renderAdminOutboundCampaignLibrary = () => {
      const libraryColumns: ReadonlyArray<{
        flexValue: number;
        labelText: string;
      }> = [
        { flexValue: 2.3, labelText: "Campaign" },
        { flexValue: 1.9, labelText: "Questionnaire" },
        { flexValue: 0.7, labelText: "Targets" },
        { flexValue: 1.1, labelText: "Calling" },
        { flexValue: 1, labelText: "Readiness" },
        { flexValue: 3, labelText: "Actions" },
      ];

      return (
        <View style={[styles.listGroup, { gap: tokens.spacing.sm, width: "100%" }]}>
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: isMediumLayout ? "center" : "flex-start",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.displayFontFamily,
                    fontSize: adminCompact.rowTitleFontSize,
                    fontWeight: adminTypography.titleWeight,
                    lineHeight: adminCompact.rowTitleLineHeight,
                  },
                ]}
              >
                Campaign library
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminCompact.bodyFontSize,
                    lineHeight: adminCompact.bodyLineHeight,
                  },
                ]}
              >
                Select a campaign to scope the workspace, or duplicate, pause and
                archive existing ones.
              </Text>
            </View>
            {renderAdminPrimaryButton({
              disabled: isAdminOutboundCommandSubmitting,
              iconName: "plus",
              labelText: "New campaign",
              minWidth: 140,
              onPress: openOutboundCreateCampaignEditor,
              prefixText: "",
              tone: "primary",
            })}
          </View>

          <View
            style={[
              styles.patientDocumentTable,
              adminTableShellStyle,
              {
                borderRadius: adminOperationalLayout.panelRadius,
                width: "100%",
              },
            ]}
          >
            <View
              style={[styles.patientDocumentTableHeader, adminTableHeaderStyle]}
            >
              {libraryColumns.map((column) => (
                <Text
                  key={column.labelText}
                  style={[
                    styles.metricLabel,
                    { flex: column.flexValue, ...adminTableHeaderTextStyle },
                  ]}
                >
                  {column.labelText}
                </Text>
              ))}
            </View>

            {adminOutboundCampaignList.length > 0 ? (
              adminOutboundCampaignList.map((campaign, index) => {
                const isActive =
                  campaign.campaignId === selectedOutboundCampaign?.campaignId;
                const dialerStatus =
                  campaign.dialerConfig?.dialerStatus ?? "unconfigured";
                const managedSetupRequested =
                  readAdminOutboundMetadataText(
                    campaign.dialerConfig?.metadata,
                    "managedSetupRequestedAt",
                  ).length > 0;
                const targetTotal = Object.values(campaign.targetCounts).reduce(
                  (sum, value) => sum + value,
                  0,
                );
                const isPaused = campaign.campaignStatus === "paused";
                const isArchived = campaign.campaignStatus === "archived";

                return (
                  <View
                    key={campaign.campaignId}
                    style={[
                      styles.patientDocumentTableRow,
                      buildAdminTableRowStateStyle(index, { selected: isActive }),
                    ]}
                  >
                    <View
                      style={[
                        styles.patientDocumentTableCell,
                        { flex: 2.3, gap: 2 },
                      ]}
                    >
                      <View
                        style={{
                          alignItems: "center",
                          flexDirection: "row",
                          gap: 8,
                        }}
                      >
                        <View
                          style={{
                            backgroundColor:
                              resolveCampaignReadinessColor(campaign),
                            borderRadius: 999,
                            height: 8,
                            width: 8,
                          }}
                        />
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.sectionTitle,
                            {
                              color: theme.textPrimary,
                              fontFamily: layout.displayFontFamily,
                              fontSize: 13.5,
                              fontWeight: adminTypography.titleWeight,
                              lineHeight: 17,
                            },
                          ]}
                        >
                          {campaign.displayName}
                        </Text>
                      </View>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textTertiary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10.5,
                          },
                        ]}
                      >
                        {humanizeWorkspaceKey(campaign.campaignStatus)}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.patientDocumentTableCell,
                        { flex: 1.9, gap: 2 },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 12,
                            fontWeight: adminTypography.strongWeight,
                          },
                        ]}
                      >
                        {campaign.questionnaireDisplayName ?? "Not linked"}
                      </Text>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textTertiary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10.5,
                          },
                        ]}
                      >
                        {campaign.questionnaireVersionNumber != null
                          ? `v${String(campaign.questionnaireVersionNumber)}`
                          : "No version"}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.metricValue,
                        {
                          color: theme.textPrimary,
                          flex: 0.7,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 14,
                        },
                      ]}
                    >
                      {String(targetTotal)}
                    </Text>
                    <View
                      style={[styles.patientDocumentTableCell, { flex: 1.1 }]}
                    >
                      {renderAdminStatusPill(
                        dialerStatus === "ready"
                          ? "Ready"
                          : dialerStatus === "disabled"
                            ? "Disabled"
                            : managedSetupRequested
                              ? "Requested"
                              : "Setup needed",
                        { minWidth: 84 },
                      )}
                    </View>
                    <View style={[styles.patientDocumentTableCell, { flex: 1 }]}>
                      {renderAdminStatusPill(
                        isArchived
                          ? "Archived"
                          : isPaused
                            ? "Paused"
                            : dialerStatus === "ready"
                              ? "Ready"
                              : "Blocked",
                        { minWidth: 80 },
                      )}
                    </View>
                    <View
                      style={[
                        styles.patientRowFooter,
                        { flex: 3, gap: tokens.spacing.xs },
                      ]}
                    >
                      {isActive ? (
                        renderAdminStatusPill("Selected", {
                          controlHeight: true,
                          minWidth: 84,
                        })
                      ) : (
                        renderOutboundCampaignActionButton({
                          labelText: "Select",
                          onPress: () => {
                            setAdminOutboundSelectedCampaignId(
                              campaign.campaignId,
                            );
                            setAdminOutboundPatientPageIndex(0);
                          },
                          tone: "primary",
                        })
                      )}
                      {renderOutboundCampaignActionButton({
                        labelText: "Duplicate",
                        onPress: () => runDuplicateOutboundCampaign(campaign),
                      })}
                      {renderOutboundCampaignActionButton({
                        labelText: "Recheck",
                        onPress: () =>
                          runValidateOutboundCampaignReadiness(campaign),
                      })}
                      {renderOutboundCampaignActionButton({
                        labelText: "Impact",
                        onPress: () =>
                          openOutboundCampaignQuestionnaireImpactReview(
                            campaign,
                          ),
                        tone: "primary",
                      })}
                      {renderOutboundCampaignActionButton({
                        labelText:
                          dialerStatus === "ready"
                            ? "Calling"
                            : managedSetupRequested
                              ? "Requested"
                              : "Setup",
                        onPress: () => openOutboundDialerConfigEditor(campaign),
                      })}
                      {!isArchived
                        ? renderOutboundCampaignActionButton({
                            labelText: isPaused ? "Resume" : "Pause",
                            onPress: () =>
                              runOutboundCampaignLifecycle(
                                campaign,
                                isPaused ? "resume-campaign" : "pause-campaign",
                              ),
                          })
                        : null}
                      {!isArchived
                        ? renderOutboundCampaignActionButton({
                            labelText: "Archive",
                            onPress: () =>
                              runOutboundCampaignLifecycle(
                                campaign,
                                "archive-campaign",
                              ),
                            tone: "danger",
                          })
                        : null}
                    </View>
                  </View>
                );
              })
            ) : (
              <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  No campaigns yet. Create one to begin outbound targeting.
                </Text>
              </View>
            )}
          </View>
        </View>
      );
    };
    const renderAdminOutboundCampaignReadiness = () => {
      const campaign = selectedOutboundCampaign;

      if (campaign == null) {
        return null;
      }

      const dialer = campaign.dialerConfig;
      const managedSetupRequestedAt = readAdminOutboundMetadataText(
        dialer?.metadata,
        "managedSetupRequestedAt",
      );
      const targetTotal = Object.values(campaign.targetCounts).reduce(
        (sum, value) => sum + value,
        0,
      );
      const dialerReady = dialer?.dialerStatus === "ready";
      const dialingReady =
        dialer?.dialerMode === "connect_campaign"
          ? dialer?.readiness?.campaignBatchReady === true
          : dialer?.readiness?.directCallReady === true;
      const checks: ReadonlyArray<{
        actionLabel?: string;
        detailText: string;
        ok: boolean;
        onAction?: () => void;
        titleText: string;
      }> = [
        {
          detailText:
            campaign.questionnaireDisplayName != null
              ? `${campaign.questionnaireDisplayName}${
                  campaign.questionnaireVersionNumber != null
                    ? ` · v${String(campaign.questionnaireVersionNumber)}`
                    : ""
                }`
              : "No questionnaire version linked",
          ok: campaign.questionnaireTemplateVersionId != null,
          onAction: () => setAdminOutboundTab("questionnaire"),
          actionLabel: "Open questionnaire",
          titleText: "Questionnaire version published",
        },
        {
          detailText:
            campaign.policy != null
              ? "Call window, retries and resume policy set"
              : "No call policy configured",
          ok: campaign.policy != null,
          titleText: "Call policy valid",
        },
        {
          detailText:
            dialerReady && dialer?.metadata?.managedTelephony !== false
              ? "AmpleCare-managed calling is active"
              : dialerReady
                ? "Support override saved"
                : managedSetupRequestedAt.length > 0
                  ? `AmpleCare setup requested ${formatAdminSummaryTimestamp(
                      managedSetupRequestedAt,
                    )}`
                  : "AmpleCare-managed calling needs setup",
          ok: dialerReady,
          onAction: () => openOutboundDialerConfigEditor(campaign),
          actionLabel: dialerReady ? "Edit calling setup" : "Set up calling",
          titleText: "Calling setup ready",
        },
        {
          detailText: `${String(targetTotal)} target${
            targetTotal === 1 ? "" : "s"
          } staged`,
          ok: targetTotal > 0,
          onAction: () => setAdminOutboundTab("patients"),
          actionLabel: targetTotal > 0 ? "Edit audience" : "Add patients",
          titleText: "Targets available",
        },
        {
          detailText:
            dialingReady
              ? dialer?.dialerMode === "connect_campaign"
                ? "Queued batch dialing ready"
                : "Live calling ready"
              : dialer?.readiness?.blockerMessages?.[0] != null
                ? resolveAdminOutboundCallingBlockerText(
                    dialer.readiness.blockerMessages[0],
                  )
                : "Calling setup is still locked",
          ok: dialingReady,
          titleText: "Dialing ready",
        },
      ];
      const setupReady = checks.slice(0, 4).every((check) => check.ok);

      return (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: adminFigmaPalette.border,
              borderRadius: adminOperationalLayout.panelRadius,
              gap: 2,
              padding: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              { alignItems: "center", gap: tokens.spacing.sm },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: adminFigmaPalette.inkMuted,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 10,
                  fontWeight: adminTypography.sectionLabelWeight,
                  letterSpacing: 0,
                  textTransform: "uppercase",
                },
              ]}
            >
              {`Campaign readiness — ${campaign.displayName}`}
            </Text>
            <View
              style={[
                styles.inlinePill,
                {
                  backgroundColor: setupReady
                    ? theme.statusChip.success.background
                    : theme.statusChip.warning.background,
                  borderColor: setupReady
                    ? theme.statusChip.success.border
                    : theme.statusChip.warning.border,
                  borderRadius: 999,
                  paddingHorizontal: 11,
                  paddingVertical: 4,
                },
              ]}
            >
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: setupReady
                      ? theme.statusChip.success.text
                      : theme.statusChip.warning.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: adminTypography.strongWeight,
                  },
                ]}
              >
                {setupReady ? "Ready to schedule" : "Setup incomplete"}
              </Text>
            </View>
          </View>
          {checks.map((check) => (
            <View
              key={check.titleText}
              style={{
                alignItems: "flex-start",
                borderBottomColor: theme.borderSubtle,
                borderBottomWidth: 1,
                flexDirection: "row",
                gap: 11,
                paddingVertical: 9,
              }}
            >
              <View
                style={{
                  alignItems: "center",
                  backgroundColor: check.ok
                    ? theme.success
                    : theme.statusChip.warning.background,
                  borderRadius: 999,
                  height: 22,
                  justifyContent: "center",
                  marginTop: 1,
                  width: 22,
                }}
              >
                <LineIcon
                  color={check.ok ? theme.accentContrast : theme.warning}
                  name={check.ok ? "check" : "flag"}
                  size={12}
                  strokeWidth={2.6}
                />
              </View>
              <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12.5,
                      fontWeight: adminTypography.titleWeight,
                    },
                  ]}
                >
                  {check.titleText}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {check.detailText}
                </Text>
              </View>
              {check.onAction ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={check.onAction}
                  style={[adminWebOutlineReset, { alignSelf: "center" }]}
                >
                  <Text
                    style={[
                      styles.badgeText,
                      {
                        color: theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "800",
                        lineHeight: 14,
                      },
                    ]}
                  >
                    {`${check.actionLabel ?? "Open"} ›`}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          ))}
          <View
            style={[
              styles.patientRowFooter,
              {
                alignItems: "center",
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                gap: tokens.spacing.sm,
                justifyContent: "flex-end",
                marginTop: 6,
                paddingTop: 10,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  lineHeight: 15,
                },
              ]}
            >
              {setupReady
                ? "All set - dry-run, then enable scheduling."
                : `Next: ${
                    checks.find((check) => !check.ok)?.titleText ??
                    "finish setup"
                  }`}
            </Text>
            {renderAdminPrimaryButton({
              density: "compact",
              disabled: isAdminOutboundCommandSubmitting,
              iconName: "sync",
              labelText: "Run dry-run",
              minWidth: 118,
              onPress: runOutboundSchedulerDryRun,
              prefixText: "",
              tone: "surface",
            })}
            {setupReady
              ? renderAdminPrimaryButton({
                  density: "compact",
                  disabled: isAdminOutboundCommandSubmitting,
                  iconName: selectedOutboundCampaignIsActive ? "stop" : "check",
                  labelText: selectedOutboundCampaignIsActive
                    ? "Pause scheduling"
                    : "Enable scheduling",
                  minWidth: 150,
                  onPress: () =>
                    runSetOutboundCampaignScheduling(
                      selectedOutboundCampaignIsActive ? "paused" : "active",
                    ),
                  prefixText: "",
                  tone: selectedOutboundCampaignIsActive
                    ? "surface"
                    : "primary",
                })
              : renderAdminPrimaryButton({
                  density: "compact",
                  disabled: isAdminOutboundCommandSubmitting,
                  labelText: `Continue setup`,
                  minWidth: 140,
                  onPress:
                    checks.find((check) => !check.ok)?.onAction ??
                    (() => setAdminOutboundTab("campaign")),
                  prefixText: "",
                  tone: "primary",
                })}
          </View>
        </View>
      );
    };
    const renderAdminOutboundTabBar = () => (
      <View
        accessibilityLabel="Outbound campaign sections"
        role="tablist"
        style={[
          styles.patientRowHeader,
          {
            borderBottomColor: theme.borderSubtle,
            borderBottomWidth: 1.5,
            flexWrap: "wrap",
            gap: 2,
          },
        ]}
      >
        {adminOutboundTabs.map((tab) => {
          const isActive = adminOutboundTab === tab.id;

          return (
            <Pressable
              accessibilityLabel={tab.labelText}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              key={tab.id}
              onPress={() => setAdminOutboundTab(tab.id)}
              style={(state) => {
                const stateRecord =
                  state && typeof state === "object"
                    ? (state as unknown as Record<string, unknown>)
                    : {};
                const focused = stateRecord.focused === true;
                const pressed = stateRecord.pressed === true;

                return [
                  adminWebOutlineReset,
                  {
                    alignItems: "center",
                    backgroundColor:
                      focused || pressed
                        ? theme.accentSubtle
                        : "transparent",
                    borderBottomColor: isActive
                      ? theme.accent
                      : "transparent",
                    borderBottomWidth: 2.5,
                    borderTopLeftRadius: adminOperationalLayout.controlRadius,
                    borderTopRightRadius: adminOperationalLayout.controlRadius,
                    flexDirection: "row",
                    gap: 6,
                    marginBottom: -1.5,
                    paddingHorizontal: 14,
                    paddingVertical: 9,
                  },
                ];
              }}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: isActive ? theme.accentStrong : theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 13,
                    fontWeight: isActive ? "800" : "700",
                  },
                ]}
              >
                {tab.labelText}
              </Text>
              {tab.badgeText ? (
                <View
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: theme.accentSubtle,
                      borderColor: "transparent",
                      borderRadius: 999,
                      paddingHorizontal: 7,
                      paddingVertical: 1,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: "800",
                        lineHeight: 13,
                      },
                    ]}
                  >
                    {tab.badgeText}
                  </Text>
                </View>
              ) : null}
            </Pressable>
          );
        })}
      </View>
    );

    const resolveOutboundCandidateCrossCampaign = (
      candidate: (typeof outboundTargetCandidateRows)[number],
    ) => {
      const patientId =
        candidate.patientRow.patientId ?? candidate.patientRow.subjectKey;
      const patientTargets = adminOutboundTargets.filter(
        (target) => target.patientId === patientId,
      );
      const activeOtherCampaign = patientTargets.find(
        (target) =>
          target.campaignId !==
            (selectedOutboundCampaign?.campaignId ?? "") &&
          (target.targetStatus === "scheduled" ||
            target.targetStatus === "calling" ||
            target.targetStatus === "eligible" ||
            target.targetStatus === "paused" ||
            target.targetStatus === "review_required"),
      );

      return {
        activeOtherCampaignName:
          activeOtherCampaign?.campaignDisplayName ?? null,
        suppressed: candidate.readinessText === "Suppressed",
        suppressionReasonKey: candidate.patientSuppressionReasonKey,
      };
    };
    const outboundFilterMatchers: Record<
      AdminOutboundPatientFilter,
      (candidate: (typeof outboundTargetCandidateRows)[number]) => boolean
    > = {
      all: () => true,
      ready: (candidate) => candidate.readinessText === "Ready to review",
      "no-phone": (candidate) => !candidate.hasPhone,
      suppressed: (candidate) => candidate.readinessText === "Suppressed",
      targeted: (candidate) =>
        adminOutboundTargets.some(
          (target) =>
            target.patientId ===
            (candidate.patientRow.patientId ?? candidate.patientRow.subjectKey),
        ),
    };
    const outboundFilterChips: ReadonlyArray<{
      id: AdminOutboundPatientFilter;
      labelText: string;
    }> = [
      { id: "all", labelText: "All" },
      { id: "ready", labelText: "Ready to review" },
      { id: "targeted", labelText: "Targeted" },
      { id: "no-phone", labelText: "No phone" },
      { id: "suppressed", labelText: "Suppressed" },
    ];
    const outboundSearchQuery = adminOutboundPatientSearch.trim().toLowerCase();
    const outboundFilteredCandidates = outboundTargetCandidateRows.filter(
      (candidate) => {
        if (!outboundFilterMatchers[adminOutboundPatientFilter](candidate)) {
          return false;
        }

        if (outboundSearchQuery.length === 0) {
          return true;
        }

        return (
          candidate.patientRow.displayName
            .toLowerCase()
            .includes(outboundSearchQuery) ||
          candidate.phoneText.toLowerCase().includes(outboundSearchQuery)
        );
      },
    );
    const outboundPageSize = 12;
    const outboundPageCount = Math.max(
      1,
      Math.ceil(outboundFilteredCandidates.length / outboundPageSize),
    );
    const outboundSafePageIndex = Math.min(
      Math.max(adminOutboundPatientPageIndex, 0),
      outboundPageCount - 1,
    );
    const outboundPageStart = outboundSafePageIndex * outboundPageSize;
    const outboundPageCandidates = outboundFilteredCandidates.slice(
      outboundPageStart,
      outboundPageStart + outboundPageSize,
    );
    const toggleOutboundSelection = (candidateId: string) => {
      setAdminOutboundSelectedTargetIds((previous) =>
        previous.includes(candidateId)
          ? previous.filter((value) => value !== candidateId)
          : [...previous, candidateId],
      );
    };
    const outboundSelectablePageCandidates = outboundPageCandidates.filter(
      (candidate) => candidate.readinessText === "Ready to review",
    );
    const outboundPageAllSelected =
      outboundSelectablePageCandidates.length > 0 &&
      outboundSelectablePageCandidates.every((candidate) =>
        outboundSelectedIdSet.has(candidate.patientRow.id),
      );
    const toggleOutboundSelectAllOnPage = () => {
      const pageIds = outboundSelectablePageCandidates.map(
        (candidate) => candidate.patientRow.id,
      );

      setAdminOutboundSelectedTargetIds((previous) => {
        if (pageIds.every((id) => previous.includes(id))) {
          return previous.filter((id) => !pageIds.includes(id));
        }

        const merged = new Set(previous);

        pageIds.forEach((id) => merged.add(id));

        return Array.from(merged);
      });
    };
    const renderOutboundCheckbox = (checked: boolean) => (
      <View
        style={{
          alignItems: "center",
          backgroundColor: checked ? theme.accent : theme.surface,
          borderColor: checked ? theme.accent : theme.borderStrong,
          borderRadius: 5,
          borderWidth: 1.5,
          height: 18,
          justifyContent: "center",
          width: 18,
        }}
      >
        {checked ? (
          <LineIcon
            color={theme.accentContrast}
            name="check"
            size={12}
            strokeWidth={3}
          />
        ) : null}
      </View>
    );
    const renderOutboundConsentChip = (
      consentState: TenantAdminOutboundConsentStateV1 | null,
    ) => {
      const tone =
        consentState === "confirmed" || consentState === "not_required"
          ? theme.statusChip.success
          : consentState === "declined"
            ? theme.statusChip.danger
            : theme.statusChip.warning;
      const labelText =
        consentState === "confirmed"
          ? "Confirmed"
          : consentState === "not_required"
            ? "Policy covered"
            : consentState === "declined"
              ? "Declined"
              : "Not recorded";

      return (
        <View
          style={[
            styles.inlinePill,
            {
              alignSelf: "flex-start",
              backgroundColor: tone.background,
              borderColor: tone.border,
              borderRadius: adminOperationalLayout.controlRadius,
              paddingHorizontal: 9,
              paddingVertical: 2,
            },
          ]}
        >
          <Text
            style={[
              styles.pillValueText,
              {
                color: tone.text,
                fontFamily: layout.bodyFontFamily,
                fontSize: 10,
                fontWeight: adminTypography.strongWeight,
                lineHeight: 13,
              },
            ]}
          >
            {labelText}
          </Text>
        </View>
      );
    };
    const renderOutboundAdvanceNoticePolicyAttestation = (input: {
      attestationKey: string;
      patientCount: number;
    }) => {
      const checked = isOutboundAdvanceNoticeAttested(input.attestationKey);
      const patientLabel =
        input.patientCount === 1
          ? "this patient was"
          : `these ${String(input.patientCount)} patients were`;

      return (
        <Pressable
          accessibilityLabel="Confirm approved advance-notice policy"
          accessibilityRole="checkbox"
          accessibilityState={{
            checked,
            disabled:
              !selectedOutboundAdvanceNoticePolicyReady ||
              input.patientCount === 0,
          }}
          disabled={
            !selectedOutboundAdvanceNoticePolicyReady ||
            input.patientCount === 0
          }
          onPress={() =>
            setAdminOutboundAdvanceNoticePolicyAttestationKey(
              checked ? null : input.attestationKey,
            )
          }
          style={[
            adminWebOutlineReset,
            {
              alignItems: "flex-start",
              backgroundColor: checked
                ? theme.statusChip.success.background
                : theme.backgroundSubtle,
              borderColor: checked
                ? theme.statusChip.success.border
                : theme.borderStrong,
              borderRadius: adminOperationalLayout.controlRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: tokens.spacing.sm,
              opacity: input.patientCount === 0 ? 0.62 : 1,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: tokens.spacing.sm,
            },
          ]}
        >
          <View style={{ paddingTop: 1 }}>
            {renderOutboundCheckbox(checked)}
          </View>
          <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12,
                  fontWeight: adminTypography.strongWeight,
                  lineHeight: 16,
                },
              ]}
            >
              {selectedOutboundAdvanceNoticePolicyReady
                ? checked
                  ? "Advance-notice policy confirmed"
                  : "Confirm advance-notice policy"
                : "Advance-notice policy not configured"}
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                  lineHeight: 15,
                },
              ]}
            >
              {selectedOutboundAdvanceNoticePolicyReady
                ? input.patientCount === 0
                  ? "No eligible patients are in this action."
                  : `I confirm ${patientLabel} informed in advance under policy ${selectedOutboundAdvanceNoticePolicySource}. Separate consent is not required.`
                : "Configure an approved campaign policy before assigning patients."}
            </Text>
          </View>
        </Pressable>
      );
    };
    const outboundDrawerCandidate =
      adminOutboundDrawerCandidateId == null
        ? null
        : (outboundTargetCandidateRows.find(
            (candidate) =>
              candidate.patientRow.id === adminOutboundDrawerCandidateId,
          ) ?? null);
    const outboundTableColumns: ReadonlyArray<{
      flexValue: number;
      labelText: string;
    }> = [
      { flexValue: 2.2, labelText: "Patient" },
      { flexValue: 1.4, labelText: "Phone" },
      { flexValue: 0.9, labelText: "Consent" },
      { flexValue: 1.5, labelText: "Cross-campaign" },
      { flexValue: 1.3, labelText: "Readiness" },
    ];
    const renderAdminOutboundPatientDrawer = () => {
      if (outboundDrawerCandidate == null) {
        return null;
      }

      const candidate = outboundDrawerCandidate;
      const drawerAttestationKey =
        buildOutboundAdvanceNoticeAttestationKey("single", [candidate]);
      const reasonText = humanizeWorkspaceKey(candidate.targetReasonKey);
      const drawerRows: ReadonlyArray<{ labelText: string; valueText: string }> =
        [
          { labelText: "Phone", valueText: candidate.phoneText },
          { labelText: "Call window", valueText: candidate.scheduleText },
          { labelText: "Consent", valueText: candidate.consentText },
          {
            labelText: "Has phone",
            valueText: candidate.hasPhone ? "Yes" : "No phone on record",
          },
          {
            labelText: "Suppressed",
            valueText:
              candidate.readinessText === "Suppressed" ? "Yes" : "No",
          },
          { labelText: "Reason", valueText: reasonText },
        ];

      return (
        <Modal
          animationType="fade"
          onRequestClose={() => setAdminOutboundDrawerCandidateId(null)}
          transparent
          visible
        >
          <View
            style={{
              backgroundColor: "rgba(15,42,71,0.32)",
              flex: 1,
              flexDirection: "row",
              justifyContent: "flex-end",
            }}
          >
            <Pressable
              accessibilityLabel="Close patient detail"
              onPress={() => setAdminOutboundDrawerCandidateId(null)}
              style={[adminWebOutlineReset, { flex: 1 }]}
            />
            <View
              style={{
                backgroundColor: theme.surface,
                borderLeftColor: theme.borderSubtle,
                borderLeftWidth: 1,
                borderTopLeftRadius: isAdminMobileLayout
                  ? 0
                  : adminOperationalLayout.panelRadius,
                borderBottomLeftRadius: isAdminMobileLayout
                  ? 0
                  : adminOperationalLayout.panelRadius,
                maxWidth: "100%",
                overflow: "hidden",
                shadowColor: theme.textPrimary,
                shadowOffset: { height: 0, width: -10 },
                shadowOpacity: 0.16,
                shadowRadius: 28,
                width: 420,
              }}
            >
              <View
                style={[
                  {
                    alignItems: "center",
                    backgroundColor: theme.surface,
                    borderBottomColor: theme.borderSubtle,
                    borderBottomWidth: 1,
                    flexDirection: "row",
                    gap: tokens.spacing.sm,
                    paddingHorizontal: adminModalDensity.cardPadding,
                    paddingVertical: tokens.spacing.md,
                  },
                ]}
              >
                <View
                  style={{
                  alignItems: "center",
                    backgroundColor: theme.accentSubtle,
                    borderColor: theme.borderSubtle,
                    borderRadius: adminOperationalLayout.panelRadius,
                    borderWidth: 1,
                    height: 40,
                    justifyContent: "center",
                    width: 40,
                  }}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.accentStrong,
                        fontFamily: layout.displayFontFamily,
                        fontSize: 13,
                        fontWeight: adminTypography.titleWeight,
                      },
                    ]}
                  >
                    {resolvePatientInitials(candidate.patientRow.displayName)}
                  </Text>
                </View>
                <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.accentStrong,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        letterSpacing: 0,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    Targeting review
                  </Text>
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: 18,
                        fontWeight: adminTypography.titleWeight,
                        lineHeight: 22,
                      },
                    ]}
                  >
                    {candidate.patientRow.displayName}
                  </Text>
                </View>
                <Pressable
                  accessibilityLabel="Close"
                  onPress={() => setAdminOutboundDrawerCandidateId(null)}
                  style={[
                    adminWebOutlineReset,
                    {
                      alignItems: "center",
                      borderRadius: adminOperationalLayout.controlRadius,
                      height: 30,
                      justifyContent: "center",
                      width: 30,
                    },
                  ]}
                >
                  <LineIcon
                    color={theme.textSecondary}
                    name="x"
                    size={18}
                    strokeWidth={2.4}
                  />
                </Pressable>
              </View>

              <ScrollView
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{
                  gap: tokens.spacing.sm,
                  padding: adminModalDensity.cardPadding,
                }}
              >
                <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
                  {renderAdminStatusPill(candidate.readinessText, {
                    minWidth: 104,
                  })}
                  {renderAdminStatusPill(candidate.patientRow.careTypeLabel, {
                    minWidth: 84,
                  })}
                </View>
                {drawerRows.map((row) => (
                  <View
                    key={row.labelText}
                    style={{
                      borderBottomColor: theme.borderSubtle,
                      borderBottomWidth: 1,
                      flexDirection: "row",
                      gap: tokens.spacing.sm,
                      justifyContent: "space-between",
                      paddingVertical: 7,
                    }}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                        },
                      ]}
                    >
                      {row.labelText}
                    </Text>
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: theme.textPrimary,
                          flex: 1,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12.5,
                          fontWeight: adminTypography.strongWeight,
                          textAlign: "right",
                        },
                      ]}
                    >
                      {row.valueText}
                    </Text>
                  </View>
                ))}
                {renderOutboundAdvanceNoticePolicyAttestation({
                  attestationKey: drawerAttestationKey,
                  patientCount: 1,
                })}
                <View
                  style={[
                    styles.patientRowFooter,
                    { gap: tokens.spacing.xs, marginTop: tokens.spacing.xs },
                  ]}
                >
                  {renderAdminPrimaryButton({
                    density: "compact",
                    iconName: "eye",
                    labelText: "Full patient",
                    minWidth: 120,
                    onPress: () => {
                      setAdminSelectedPatientRowId(candidate.patientRow.id);
                      setAdminOutboundDrawerCandidateId(null);
                      setAdminOpsNotice(
                        `Patient detail workspace opened for ${candidate.patientRow.displayName}.`,
                      );
                    },
                    prefixText: "",
                    tone: "surface",
                  })}
                  {renderAdminPrimaryButton({
                    density: "compact",
                    disabled:
                      isAdminOutboundCommandSubmitting ||
                      !selectedOutboundAdvanceNoticePolicyReady ||
                      !isOutboundAdvanceNoticeAttested(
                        drawerAttestationKey,
                      ) ||
                      !candidate.hasPhone ||
                      candidate.readinessText !== "Ready to review",
                    iconName: "flag",
                    labelText: "Flag for call",
                    minWidth: 120,
                    onPress: () => {
                      runFlagOutboundCandidate(candidate);
                      setAdminOutboundDrawerCandidateId(null);
                    },
                    prefixText: "",
                    tone: "primary",
                  })}
                </View>
              </ScrollView>
            </View>
          </View>
        </Modal>
      );
    };
    const renderAdminOutboundPatientsPanel = () => (
      <View style={[styles.listGroup, { gap: tokens.spacing.sm, width: "100%" }]}>
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: isMediumLayout ? "center" : "flex-start",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: tokens.spacing.sm,
            },
          ]}
        >
          <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminCompact.rowTitleFontSize,
                  fontWeight: adminTypography.titleWeight,
                  lineHeight: adminCompact.rowTitleLineHeight,
                },
              ]}
            >
              Patient targeting
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: adminFigmaPalette.inkMuted,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminCompact.bodyFontSize,
                  lineHeight: adminCompact.bodyLineHeight,
                },
              ]}
            >
              Review against phone, consent, call-window and suppression rules
              before creating any target.
            </Text>
          </View>
          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {renderAdminStatusPill("No live calls", {
              controlHeight: true,
              minWidth: 100,
            })}
            {renderAdminPrimaryButton({
              disabled:
                isAdminOutboundCommandSubmitting ||
                !selectedOutboundAdvanceNoticePolicyReady ||
                !isOutboundAdvanceNoticeAttested(
                  activeBulkOutboundAttestationKey,
                ) ||
                activeBulkOutboundTargetCandidates.length === 0,
              iconName: "plus",
              labelText:
                activeBulkOutboundScope === "selected"
                  ? "Create selected targets"
                  : "Bulk create targets",
              minWidth: 154,
              onPress: () =>
                runBulkCreateOutboundTargets(activeBulkOutboundScope),
              prefixText: "",
              tone: "surface",
            })}
          </View>
        </View>

        {renderOutboundAdvanceNoticePolicyAttestation({
          attestationKey: activeBulkOutboundAttestationKey,
          patientCount: activeBulkOutboundTargetCandidates.length,
        })}

        {adminOutboundTargetToast ? (
          <View
            style={{
              alignItems: "flex-start",
              backgroundColor: theme.statusChip.success.background,
              borderColor: theme.statusChip.success.border,
              borderRadius: adminOperationalLayout.panelRadius,
              borderWidth: 1,
              flexDirection: "row",
              gap: tokens.spacing.sm,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: tokens.spacing.sm,
            }}
          >
            <View
              style={{
                alignItems: "center",
                backgroundColor: theme.surface,
                borderColor: theme.statusChip.success.border,
                borderRadius: 999,
                borderWidth: 1,
                height: 24,
                justifyContent: "center",
                width: 24,
              }}
            >
              <LineIcon
                color={theme.statusChip.success.text}
                name="check"
                size={13}
                strokeWidth={2.4}
              />
            </View>
            <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.statusChip.success.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12.5,
                    fontWeight: adminTypography.strongWeight,
                    lineHeight: 16,
                  },
                ]}
              >
                Targets created
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.statusChip.success.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    lineHeight: 16,
                  },
                ]}
              >
                {adminOutboundTargetToast}
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Dismiss target creation confirmation"
              accessibilityRole="button"
              onPress={() => setAdminOutboundTargetToast(null)}
              style={[adminWebOutlineReset, { padding: 4 }]}
            >
              <LineIcon
                color={theme.statusChip.success.text}
                name="x"
                size={15}
                strokeWidth={2.2}
              />
            </Pressable>
          </View>
        ) : null}

        <View
          style={[
            styles.searchSurface,
            adminSearchSurfaceStyle,
            {
              alignItems: "center",
              flexDirection: "row",
              gap: tokens.spacing.xs,
              ...adminCompactSearchPadding,
            },
          ]}
        >
          <LineIcon
            color={theme.textSecondary}
            name="search"
            size={15}
            strokeWidth={2.25}
          />
          <TextInput
            onChangeText={(value) => {
              setAdminOutboundPatientSearch(value);
              setAdminOutboundPatientPageIndex(0);
            }}
            placeholder="Search by name or phone..."
            placeholderTextColor={theme.textTertiary}
            style={[adminSearchInputTextStyle, { lineHeight: 16 }]}
            value={adminOutboundPatientSearch}
          />
        </View>

        <View
          style={[
            styles.patientRowHeader,
            { alignItems: "center", flexWrap: "wrap", gap: 6 },
          ]}
        >
          {outboundFilterChips.map((chip) => {
            const isActive = adminOutboundPatientFilter === chip.id;
            const chipCount = outboundTargetCandidateRows.filter(
              outboundFilterMatchers[chip.id],
            ).length;

            return (
              <Pressable
                key={chip.id}
                onPress={() => {
                  setAdminOutboundPatientFilter(chip.id);
                  setAdminOutboundPatientPageIndex(0);
                }}
                style={[
                  adminWebOutlineReset,
                  styles.inlinePill,
                  {
                    alignItems: "center",
                    backgroundColor: isActive ? theme.accent : theme.surface,
                    borderColor: isActive ? theme.accent : theme.borderStrong,
                    borderRadius: 999,
                    flexDirection: "row",
                    gap: 6,
                    paddingHorizontal: 11,
                    paddingVertical: 5,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: isActive ? theme.accentContrast : theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11.5,
                      fontWeight: adminTypography.labelWeight,
                    },
                  ]}
                >
                  {chip.labelText}
                </Text>
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: isActive
                        ? theme.accentContrast
                        : theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      fontWeight: adminTypography.strongWeight,
                    },
                  ]}
                >
                  {String(chipCount)}
                </Text>
              </Pressable>
            );
          })}
        </View>

        {adminOutboundSelectedTargetIds.length > 0 ? (
          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.accentStrong,
              borderRadius: adminOperationalLayout.controlRadius,
              flexDirection: "row",
              flexWrap: "wrap",
              gap: tokens.spacing.sm,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: tokens.spacing.sm,
            }}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.headerText,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 12.5,
                  fontWeight: adminTypography.titleWeight,
                },
              ]}
            >
              {`${adminOutboundSelectedTargetIds.length} selected`}
            </Text>
            <Pressable
              disabled={
                isAdminOutboundCommandSubmitting ||
                !selectedOutboundAdvanceNoticePolicyReady ||
                !isOutboundAdvanceNoticeAttested(
                  activeBulkOutboundAttestationKey,
                ) ||
                selectedReadyOutboundTargetCandidates.length === 0
              }
              onPress={() => runBulkCreateOutboundTargets("selected")}
              style={[
                adminWebOutlineReset,
                {
                  backgroundColor: "rgba(255,255,255,0.16)",
                  borderColor: "rgba(255,255,255,0.28)",
                  borderRadius: 8,
                  borderWidth: 1,
                  flexDirection: "row",
                  gap: 5,
                  paddingHorizontal: 11,
                  paddingVertical: 5,
                },
              ]}
            >
              <LineIcon
                color={theme.headerText}
                name="plus"
                size={13}
                strokeWidth={2.4}
              />
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.headerText,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: adminTypography.buttonWeight,
                  },
                ]}
              >
                Create targets
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setAdminOutboundSelectedTargetIds([])}
              style={[adminWebOutlineReset, { marginLeft: "auto" }]}
            >
              <Text
                style={[
                  styles.badgeText,
                  {
                    color: theme.onGradientAvatarBorder ?? "#BAE6FD",
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: adminTypography.buttonWeight,
                  },
                ]}
              >
                Clear selection
              </Text>
            </Pressable>
          </View>
        ) : null}

        <View
          style={[
            styles.patientDocumentTable,
            adminTableShellStyle,
            {
              borderRadius: adminOperationalLayout.panelRadius,
              width: "100%",
            },
          ]}
        >
          <View style={[styles.patientDocumentTableHeader, adminTableHeaderStyle]}>
            <Pressable
              accessibilityLabel="Select all on page"
              disabled={outboundSelectablePageCandidates.length === 0}
              onPress={toggleOutboundSelectAllOnPage}
              style={[
                adminWebOutlineReset,
                {
                  alignItems: "center",
                  opacity:
                    outboundSelectablePageCandidates.length === 0 ? 0.45 : 1,
                  paddingRight: tokens.spacing.sm,
                  width: 34,
                },
              ]}
            >
              {renderOutboundCheckbox(outboundPageAllSelected)}
            </Pressable>
            {outboundTableColumns.map((column) => (
              <Text
                key={column.labelText}
                style={[
                  styles.metricLabel,
                  { flex: column.flexValue, ...adminTableHeaderTextStyle },
                ]}
              >
                {column.labelText}
              </Text>
            ))}
          </View>

          {outboundPageCandidates.length > 0 ? (
            outboundPageCandidates.map((candidate, index) => {
              const isRowSelected = outboundSelectedIdSet.has(
                candidate.patientRow.id,
              );
              const reasonText =
                candidate.patientRow.careTypeLabel === "Procedure"
                  ? "Pre-surgery questionnaire"
                  : "Admin review required";
              const crossCampaign =
                resolveOutboundCandidateCrossCampaign(candidate);

              return (
                <Pressable
                  key={candidate.patientRow.id}
                  onPress={() =>
                    setAdminOutboundDrawerCandidateId(candidate.patientRow.id)
                  }
                  style={(state) => {
                    const record = state as unknown as Record<string, unknown>;

                    return [
                      styles.patientDocumentTableRow,
                      adminWebOutlineReset,
                      buildAdminTableRowStateStyle(index, {
                        hovered:
                          record.hovered === true || record.focused === true,
                        selected: isRowSelected,
                      }),
                    ];
                  }}
                >
                  <Pressable
                    accessibilityLabel={`Select ${candidate.patientRow.displayName}`}
                    disabled={candidate.readinessText !== "Ready to review"}
                    onPress={() =>
                      toggleOutboundSelection(candidate.patientRow.id)
                    }
                    style={[
                      adminWebOutlineReset,
                      {
                        alignItems: "center",
                        opacity:
                          candidate.readinessText === "Ready to review"
                            ? 1
                            : 0.45,
                        paddingRight: tokens.spacing.sm,
                        width: 34,
                      },
                    ]}
                  >
                    {renderOutboundCheckbox(isRowSelected)}
                  </Pressable>
                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      {
                        alignItems: "center",
                        flex: 2.2,
                        flexDirection: "row",
                        gap: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: theme.accentSubtle,
                        borderRadius: 9,
                        height: 30,
                        justifyContent: "center",
                        width: 30,
                      }}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.accentStrong,
                            fontFamily: layout.displayFontFamily,
                            fontSize: 11,
                            fontWeight: adminTypography.titleWeight,
                          },
                        ]}
                      >
                        {resolvePatientInitials(candidate.patientRow.displayName)}
                      </Text>
                    </View>
                    <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.navigationText,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 13,
                            fontWeight: adminTypography.titleWeight,
                          },
                        ]}
                      >
                        {candidate.patientRow.displayName}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10.5,
                          },
                        ]}
                      >
                        {candidate.eligibilityText}
                      </Text>
                    </View>
                  </View>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: candidate.hasPhone
                          ? theme.textPrimary
                          : theme.textTertiary,
                        flex: 1.5,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                      },
                    ]}
                  >
                    {candidate.phoneText}
                  </Text>
                  <View
                    style={[styles.patientDocumentTableCell, { flex: 1 }]}
                  >
                    {renderOutboundConsentChip(candidate.consentState)}
                  </View>
                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      { flex: 1.5, gap: 2 },
                    ]}
                  >
                    {crossCampaign.suppressed ? (
                      <View
                        style={[
                          styles.inlinePill,
                          {
                            alignSelf: "flex-start",
                            backgroundColor:
                              theme.statusChip.danger.background,
                            borderColor: theme.statusChip.danger.border,
                            borderRadius: 999,
                            paddingHorizontal: 9,
                            paddingVertical: 2,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.pillValueText,
                            {
                              color: theme.statusChip.danger.text,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              fontWeight: adminTypography.strongWeight,
                            },
                          ]}
                        >
                          Global suppression
                        </Text>
                      </View>
                    ) : crossCampaign.activeOtherCampaignName != null ? (
                      <View
                        style={[
                          styles.inlinePill,
                          {
                            alignSelf: "flex-start",
                            backgroundColor:
                              theme.statusChip.warning.background,
                            borderColor: theme.statusChip.warning.border,
                            borderRadius: 999,
                            maxWidth: "100%",
                            paddingHorizontal: 9,
                            paddingVertical: 2,
                          },
                        ]}
                      >
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.pillValueText,
                            {
                              color: theme.statusChip.warning.text,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              fontWeight: adminTypography.strongWeight,
                            },
                          ]}
                        >
                          {`Active · ${crossCampaign.activeOtherCampaignName}`}
                        </Text>
                      </View>
                    ) : (
                      <View
                        style={[
                          styles.inlinePill,
                          {
                            alignSelf: "flex-start",
                            backgroundColor: theme.surface,
                            borderColor: theme.borderStrong,
                            borderRadius: 999,
                            paddingHorizontal: 9,
                            paddingVertical: 2,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.pillValueText,
                            {
                              color: theme.textTertiary,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10,
                              fontWeight: adminTypography.labelWeight,
                            },
                          ]}
                        >
                          Clear
                        </Text>
                      </View>
                    )}
                  </View>
                  <View
                    style={[styles.patientDocumentTableCell, { flex: 1.3 }]}
                  >
                    {renderAdminStatusPill(candidate.readinessText, {
                      minWidth: 96,
                    })}
                  </View>
                </Pressable>
              );
            })
          ) : (
            <View
              style={[styles.detailBodyCard, adminTableEmptyStateStyle]}
            >
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                No candidates match this search or filter.
              </Text>
            </View>
          )}

          <View
            style={{
              alignItems: "center",
              backgroundColor: theme.backgroundSubtle,
              borderTopColor: theme.borderSubtle,
              borderTopWidth: 1,
              flexDirection: "row",
              gap: tokens.spacing.sm,
              justifyContent: "space-between",
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: tokens.spacing.sm,
            }}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11.5,
                },
              ]}
            >
              {outboundFilteredCandidates.length === 0
                ? "No candidates"
                : `Showing ${String(outboundPageStart + 1)}-${String(
                    Math.min(
                      outboundPageStart + outboundPageSize,
                      outboundFilteredCandidates.length,
                    ),
                  )} of ${String(outboundFilteredCandidates.length)} candidates`}
            </Text>
            <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
              <Pressable
                disabled={outboundSafePageIndex <= 0}
                onPress={() =>
                  setAdminOutboundPatientPageIndex(
                    Math.max(outboundSafePageIndex - 1, 0),
                  )
                }
                style={[
                  adminWebOutlineReset,
                  styles.inlinePill,
                  {
                    backgroundColor: theme.surface,
                    borderColor: theme.borderStrong,
                    borderRadius: 8,
                    opacity: outboundSafePageIndex <= 0 ? 0.5 : 1,
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: adminTypography.strongWeight,
                    },
                  ]}
                >
                  Prev
                </Text>
              </Pressable>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11.5,
                  },
                ]}
              >
                {`Page ${String(outboundSafePageIndex + 1)} / ${String(
                  outboundPageCount,
                )}`}
              </Text>
              <Pressable
                disabled={outboundSafePageIndex >= outboundPageCount - 1}
                onPress={() =>
                  setAdminOutboundPatientPageIndex(
                    Math.min(outboundSafePageIndex + 1, outboundPageCount - 1),
                  )
                }
                style={[
                  adminWebOutlineReset,
                  styles.inlinePill,
                  {
                    backgroundColor: theme.surface,
                    borderColor: theme.borderStrong,
                    borderRadius: 8,
                    opacity:
                      outboundSafePageIndex >= outboundPageCount - 1 ? 0.5 : 1,
                    paddingHorizontal: 10,
                    paddingVertical: 5,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: adminTypography.strongWeight,
                    },
                  ]}
                >
                  Next
                </Text>
              </Pressable>
            </View>
          </View>
        </View>

        <View
          style={[
            styles.patientRowHeader,
            { alignItems: "center", flexWrap: "wrap", gap: 6 },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 10,
                fontWeight: adminTypography.sectionLabelWeight,
                letterSpacing: 0,
                textTransform: "uppercase",
              },
            ]}
          >
            Guardrails
          </Text>
          {outboundTargetGuardrailRows.map((guardrail) => (
            <View
              key={guardrail.labelText}
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  backgroundColor: theme.surface,
                  borderColor: theme.borderStrong,
                  borderRadius: 999,
                  flexDirection: "row",
                  gap: 6,
                  paddingHorizontal: 11,
                  paddingVertical: 5,
                },
              ]}
            >
              <LineIcon
                color={theme.accentStrong}
                name={guardrail.iconName}
                size={12}
                strokeWidth={2.4}
              />
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: adminTypography.labelWeight,
                  },
                ]}
              >
                {guardrail.labelText}
              </Text>
            </View>
          ))}
        </View>

        {renderAdminOutboundPatientDrawer()}
      </View>
    );

    const renderAdminOutboundSchedulingControls = () => (
      <View
        style={[
          styles.patientRowFooter,
          { flexWrap: "wrap", gap: tokens.spacing.xs },
        ]}
      >
        {selectedOutboundCampaign != null
          ? renderAdminPrimaryButton({
              disabled: isAdminOutboundCommandSubmitting,
              iconName: selectedOutboundCampaignIsActive ? "stop" : "check",
              labelText: selectedOutboundCampaignIsActive
                ? "Pause scheduling"
                : "Enable scheduling",
              minWidth: 150,
              onPress: () =>
                runSetOutboundCampaignScheduling(
                  selectedOutboundCampaignIsActive ? "paused" : "active",
                ),
              prefixText: "",
              tone: selectedOutboundCampaignIsActive ? "surface" : "primary",
            })
          : null}
        {selectedOutboundCampaign != null
          ? renderAdminPrimaryButton({
              disabled: isAdminOutboundCommandSubmitting,
              iconName: "sync",
              labelText: "Run dry run",
              minWidth: 122,
              onPress: runOutboundSchedulerDryRun,
              prefixText: "",
              tone: "surface",
            })
          : null}
        {liveOutboundDialingReady && firstDirectCallableOutboundTarget
          ? renderAdminPrimaryButton({
              disabled: isAdminOutboundCommandSubmitting,
              iconName: "phone",
              labelText: "Start live call",
              minWidth: 136,
              onPress: runStartOutboundDirectCall,
              prefixText: "",
              tone: "primary",
            })
          : liveOutboundDialingReady && firstResumeScheduledOutboundTarget
            ? renderAdminPrimaryButton({
                disabled: isAdminOutboundCommandSubmitting,
                iconName: "sync",
                labelText: "Queue resume now",
                minWidth: 164,
                onPress: runQueueOutboundResumeNow,
                prefixText: "",
                tone: "surface",
              })
          : renderAdminStatusPill(
              liveOutboundCallIdleStatusText,
              { controlHeight: true, minWidth: 128 },
            )}
        {selectedOutboundCampaign != null
          ? campaignScaleOutboundDialingReady &&
            schedulableOutboundTargets.length > 0
            ? renderAdminPrimaryButton({
                disabled: isAdminOutboundCommandSubmitting,
                iconName: "program",
                labelText: "Submit batch",
                minWidth: 132,
                onPress: runSubmitOutboundCampaignDialBatch,
                prefixText: "",
                tone: "primary",
              })
            : renderAdminStatusPill(
                campaignScaleOutboundDialingReady
                  ? "No due batch"
                  : "Campaign batch locked",
                { controlHeight: true, minWidth: 154 },
              )
          : null}
      </View>
    );

    return (
      <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: isAdminMobileLayout ? "stretch" : "center",
              flexDirection: isAdminMobileLayout ? "column" : "row",
              gap: tokens.spacing.md,
              width: "100%",
            },
          ]}
        >
          <View
            style={[
              styles.listGroup,
              { flex: isAdminMobileLayout ? undefined : 1, gap: tokens.spacing.xs, minWidth: 0 },
            ]}
          >
            <Text
              style={[
                styles.title,
                {
                  ...adminCompactTitleStyle,
                },
              ]}
            >
              Outbound calls
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              {"Questionnaire campaigns \u00B7 AmpleCare-managed dialing"}
            </Text>
          </View>

          <View
            style={[
              styles.patientRowFooter,
              {
                alignItems: "center",
                gap: tokens.spacing.xs,
                justifyContent: "flex-start",
              },
            ]}
          >
            <View
              style={{
                alignItems: "center",
                backgroundColor: liveOutboundDialingReady
                  ? adminFigmaPalette.successSoft
                  : adminFigmaPalette.warningSoft,
                borderColor: liveOutboundDialingReady
                  ? theme.statusChip.success.border
                  : theme.statusChip.warning.border,
                borderRadius: 999,
                borderWidth: 1,
                flexDirection: "row",
                gap: 6,
                paddingHorizontal: 10,
                paddingVertical: 4,
              }}
            >
              <View
                style={{
                  backgroundColor: liveOutboundDialingReady
                    ? theme.statusChip.success.text
                    : theme.statusChip.warning.text,
                  borderRadius: 999,
                  height: 7,
                  width: 7,
                }}
              />
              <Text
                style={[
                  styles.pillValueText,
                  {
                    color: liveOutboundDialingReady
                      ? theme.statusChip.success.text
                      : theme.statusChip.warning.text,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: adminTypography.strongWeight,
                    lineHeight: 14,
                  },
                ]}
              >
                {liveOutboundDialingReady
                  ? "Environment ready"
                  : "Environment blocked"}
              </Text>
            </View>
            {renderAdminPrimaryButton({
              disabled: isAdminOutboundCommandSubmitting,
              iconName: "plus",
              labelText: "New campaign",
              onPress: openOutboundCreateCampaignEditor,
              prefixText: "",
              tone: "primary",
            })}
          </View>
        </View>

        {adminOutboundTab === "overview"
          ? null
          : renderAdminOutboundCampaignSelector()}

        {renderAdminOutboundTabBar()}

        {adminOutboundTab === "overview" ? (
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "flex-start",
                flexDirection: isAdminMobileLayout ? "column" : "row",
                gap: adminCompact.sectionGap,
                width: "100%",
              },
            ]}
          >
            <View
              style={[
                styles.listGroup,
                {
                  flexShrink: 0,
                  gap: 10,
                  width: isAdminMobileLayout ? "100%" : 300,
                },
              ]}
            >
              {adminOutboundCampaignList.map((campaign) => {
                const isActiveCampaign =
                  campaign.campaignId === selectedOutboundCampaign?.campaignId;
                const campaignTargetTotal = Object.values(
                  campaign.targetCounts,
                ).reduce((sum, count) => sum + count, 0);
                const campaignScheduledCount =
                  campaign.targetCounts.scheduled ?? 0;
                const campaignDoneCount = [
                  campaign.questionnaireTemplateVersionId != null,
                  campaign.policy != null,
                  campaignTargetTotal > 0,
                  campaign.dialerConfig?.dialerStatus === "ready",
                ].filter(Boolean).length;
                const campaignIsLive =
                  campaignDoneCount === 4 &&
                  campaign.campaignStatus === "active";

                return (
                  <Pressable
                    accessibilityLabel={`Select campaign ${campaign.displayName}`}
                    key={campaign.campaignId}
                    onPress={() => {
                      setAdminOutboundSelectedCampaignId(campaign.campaignId);
                      setAdminOutboundPatientPageIndex(0);
                    }}
                    style={[
                      adminWebOutlineReset,
                      {
                        backgroundColor: theme.surface,
                        borderColor: isActiveCampaign
                          ? theme.accent
                          : adminFigmaPalette.border,
                        borderRadius: adminOperationalLayout.panelRadius,
                        borderWidth: isActiveCampaign ? 1.5 : 1,
                        gap: 7,
                        padding: 12,
                        width: "100%",
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        { alignItems: "center", gap: 7 },
                      ]}
                    >
                      <View
                        style={{
                          backgroundColor:
                            resolveCampaignReadinessColor(campaign),
                          borderRadius: 999,
                          height: 8,
                          width: 8,
                        }}
                      />
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.navigationText,
                          {
                            color: adminFigmaPalette.ink,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 13,
                            fontWeight: adminTypography.titleWeight,
                            lineHeight: 17,
                          },
                        ]}
                      >
                        {campaign.displayName}
                      </Text>
                    </View>
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          lineHeight: 14,
                        },
                      ]}
                    >
                      {`${
                        campaign.questionnaireDisplayName ??
                        campaign.purposeText
                      }${
                        campaign.questionnaireVersionNumber != null
                          ? ` · v${String(campaign.questionnaireVersionNumber)}`
                          : ""
                      } · ${String(campaignTargetTotal)} target${
                        campaignTargetTotal === 1 ? "" : "s"
                      }`}
                    </Text>
                    <View
                      style={{
                        backgroundColor: theme.borderSubtle,
                        borderRadius: 999,
                        height: 5,
                        overflow: "hidden",
                        width: "100%",
                      }}
                    >
                      <View
                        style={{
                          backgroundColor: theme.accent,
                          borderRadius: 999,
                          height: 5,
                          width: `${String((campaignDoneCount / 4) * 100)}%` as `${number}%`,
                        }}
                      />
                    </View>
                    <View
                      style={[
                        styles.patientRowFooter,
                        { gap: 5, justifyContent: "flex-start" },
                      ]}
                    >
                      <View
                        style={{
                          backgroundColor: campaignIsLive
                            ? adminFigmaPalette.successSoft
                            : adminFigmaPalette.warningSoft,
                          borderColor: campaignIsLive
                            ? theme.statusChip.success.border
                            : theme.statusChip.warning.border,
                          borderRadius: 999,
                          borderWidth: 1,
                          paddingHorizontal: 8,
                          paddingVertical: 2,
                        }}
                      >
                        <Text
                          style={[
                            styles.pillValueText,
                            {
                              color: campaignIsLive
                                ? theme.statusChip.success.text
                                : theme.statusChip.warning.text,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 9.5,
                              fontWeight: "800",
                              lineHeight: 13,
                            },
                          ]}
                        >
                          {campaignIsLive
                            ? "Live"
                            : `Setup ${String(campaignDoneCount)} of 4`}
                        </Text>
                      </View>
                      {campaignScheduledCount > 0 ? (
                        <View
                          style={{
                            backgroundColor: theme.borderSubtle,
                            borderRadius: 999,
                            paddingHorizontal: 8,
                            paddingVertical: 2,
                          }}
                        >
                          <Text
                            style={[
                              styles.pillValueText,
                              {
                                color: theme.textSecondary,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 9.5,
                                fontWeight: "700",
                                lineHeight: 13,
                              },
                            ]}
                          >
                            {`${String(campaignScheduledCount)} scheduled`}
                          </Text>
                        </View>
                      ) : null}
                    </View>
                  </Pressable>
                );
              })}
              <Pressable
                accessibilityLabel="Create a new outbound campaign"
                disabled={isAdminOutboundCommandSubmitting}
                onPress={openOutboundCreateCampaignEditor}
                style={[
                  adminWebOutlineReset,
                  {
                    alignItems: "center",
                    borderColor: adminFigmaPalette.border,
                    borderRadius: adminOperationalLayout.panelRadius,
                    borderStyle: "dashed",
                    borderWidth: 1.5,
                    justifyContent: "center",
                    minHeight: 60,
                    width: "100%",
                  },
                ]}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: theme.accentStrong,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: "700",
                      lineHeight: 16,
                    },
                  ]}
                >
                  {"+ New campaign"}
                </Text>
              </Pressable>
            </View>

            <View
              style={[
                styles.listGroup,
                {
                  flex: isAdminMobileLayout ? undefined : 1,
                  gap: adminCompact.sectionGap,
                  minWidth: 0,
                  width: isAdminMobileLayout ? "100%" : undefined,
                },
              ]}
            >
              {renderAdminOutboundCampaignReadiness()}
        {liveOutboundDialingReady ? null : (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: liveOutboundDialingReady
                ? adminFigmaPalette.successSoft
                : adminFigmaPalette.warningSoft,
              borderColor: liveOutboundDialingReady
                ? theme.statusChip.success.border
                : theme.statusChip.warning.border,
              borderRadius: adminOperationalLayout.panelRadius,
              gap: tokens.spacing.sm,
              padding: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "center",
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <View
              style={{
                alignItems: "center",
                backgroundColor: adminFigmaPalette.surface,
                borderColor: theme.statusChip.warning.border,
                borderRadius: adminOperationalLayout.controlRadius,
                borderWidth: 1,
                height: 38,
                justifyContent: "center",
                width: 38,
              }}
            >
              <LineIcon
                color={
                  liveOutboundDialingReady
                    ? theme.statusChip.success.text
                    : theme.statusChip.warning.text
                }
                name={liveOutboundDialingReady ? "check" : "info"}
                size={21}
                strokeWidth={2.35}
              />
            </View>
            <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.displayFontFamily,
                    fontSize: adminCompact.rowTitleFontSize,
                    fontWeight: adminTypography.titleWeight,
                    lineHeight: adminCompact.rowTitleLineHeight,
                  },
                ]}
              >
                {liveOutboundDialingReady
                  ? "Live dialing is ready"
                  : "Live dialing is locked"}
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminCompact.bodyFontSize,
                    lineHeight: adminCompact.bodyLineHeight,
                  },
                ]}
              >
                {liveOutboundDialingReady
                  ? "Queued questionnaire attempts can now be started with AmpleCare direct calling."
                  : "Authoring and target records can be saved now. Live AmpleCare calling remains blocked until the scheduler and call flow are explicitly enabled."}
              </Text>
            </View>
            {renderAdminStatusPill(
              liveOutboundDialingReady ? "Ready" : "Blocked",
              { minWidth: 82 },
            )}
          </View>

          {adminOutboundCallsSummary.gate.blockerMessages.length > 0 ? (
            <View style={[styles.listGroup, { gap: 6 }]}>
              {adminOutboundCallsSummary.gate.blockerMessages.map((message) => (
                <View
                  key={message}
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "center",
                      gap: 8,
                    },
                  ]}
                >
                  <LineIcon
                    color={theme.statusChip.warning.text}
                    name="flag"
                    size={15}
                    strokeWidth={2.4}
                  />
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: adminTypography.strongWeight,
                        lineHeight: 15,
                      },
                    ]}
                  >
                    {resolveAdminOutboundCallingBlockerText(message)}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>
        )}

              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "stretch",
                    flexWrap: "wrap",
                    gap: tokens.spacing.sm,
                    width: "100%",
                  },
                ]}
              >
                <View
                  style={[
                    styles.listGroup,
                    {
                      backgroundColor: adminFigmaPalette.surface,
                      borderColor: adminFigmaPalette.border,
                      borderRadius: adminOperationalLayout.panelRadius,
                      borderWidth: 1,
                      flex: 1,
                      gap: 0,
                      minWidth: 260,
                      padding: 12,
                    },
                  ]}
                >
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: adminFigmaPalette.inkMuted,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: "800",
                        letterSpacing: 0,
                        lineHeight: 13,
                        marginBottom: 6,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {`Live performance — ${
                      selectedOutboundCampaign?.displayName ?? "all campaigns"
                    }`}
                  </Text>
                  {outboundMetricCards.map((item, itemIndex) => (
                    <View
                      key={item.labelText}
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          borderTopColor: theme.borderSubtle,
                          borderTopWidth: itemIndex === 0 ? 0 : 1,
                          gap: tokens.spacing.sm,
                          justifyContent: "space-between",
                          paddingVertical: 7,
                        },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: adminFigmaPalette.inkMuted,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11.5,
                            lineHeight: 15,
                          },
                        ]}
                      >
                        {item.labelText}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: adminFigmaPalette.ink,
                            flexShrink: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11.5,
                            fontWeight: adminTypography.strongWeight,
                            lineHeight: 15,
                            textAlign: "right",
                          },
                        ]}
                      >
                        {`${item.valueText} · ${item.supportText}`}
                      </Text>
                    </View>
                  ))}
                </View>
                <View
                  style={[
                    styles.listGroup,
                    {
                      backgroundColor: adminFigmaPalette.surface,
                      borderColor: adminFigmaPalette.border,
                      borderRadius: adminOperationalLayout.panelRadius,
                      borderWidth: 1,
                      flex: 1,
                      gap: 0,
                      minWidth: 260,
                      padding: 12,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: adminFigmaPalette.inkMuted,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        fontWeight: "800",
                        letterSpacing: 0,
                        lineHeight: 13,
                        marginBottom: 6,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    Recent activity
                  </Text>
                  {outboundRecentAuditRows.length > 0 ? (
                    outboundRecentAuditRows.map((event, eventIndex) => (
                      <View
                        key={event.eventId}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            borderTopColor: theme.borderSubtle,
                            borderTopWidth: eventIndex === 0 ? 0 : 1,
                            gap: tokens.spacing.sm,
                            justifyContent: "space-between",
                            paddingVertical: 7,
                          },
                        ]}
                      >
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.metricLabel,
                            {
                              color: adminFigmaPalette.inkMuted,
                              flex: 1,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 11.5,
                              lineHeight: 15,
                            },
                          ]}
                        >
                          {formatOutboundAuditLabel(event.eventKind)}
                        </Text>
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.metricLabel,
                            {
                              color: adminFigmaPalette.ink,
                              fontFamily: layout.bodyFontFamily,
                              fontSize: 10.5,
                              fontWeight: adminTypography.strongWeight,
                              lineHeight: 14,
                              textAlign: "right",
                            },
                          ]}
                        >
                          {formatAdminSummaryTimestamp(event.occurredAt)}
                        </Text>
                      </View>
                    ))
                  ) : (
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11.5,
                          lineHeight: 15,
                          paddingVertical: 7,
                        },
                      ]}
                    >
                      No recent events
                    </Text>
                  )}
                </View>
              </View>
            </View>
          </View>
        ) : null}

        {adminOutboundTab === "activity" ? (
        <View style={[styles.listGroup, { gap: tokens.spacing.md }]}>
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: isMediumLayout ? "center" : "flex-start",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <View style={{ flex: 1, gap: 4 }}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.displayFontFamily,
                    fontSize: 18,
                    lineHeight: 23,
                  },
                ]}
              >
                Reliability watch
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    lineHeight: 16,
                  },
                ]}
              >
                {`${formatAdminSummaryTimestamp(
                  outboundAnalytics.windowStartAt,
                )} - ${formatAdminSummaryTimestamp(outboundAnalytics.windowEndAt)}`}
              </Text>
            </View>
            <View
              style={{
                backgroundColor: theme.backgroundSubtle,
                borderColor: theme.borderStrong,
                borderRadius: adminOperationalLayout.controlRadius,
                borderWidth: 1.5,
                flexDirection: "row",
                gap: 2,
                padding: 3,
              }}
            >
              {(
                [
                  { id: "all", labelText: "All campaigns" },
                  { id: "campaign", labelText: "This campaign" },
                ] as const
              ).map((option) => {
                const isActive = adminOutboundAnalyticsScope === option.id;

                return (
                  <Pressable
                    key={option.id}
                    onPress={() =>
                      setAdminOutboundAnalyticsScope(option.id)
                    }
                    style={[
                      adminWebOutlineReset,
                      {
                        backgroundColor: isActive
                          ? theme.accent
                          : "transparent",
                        borderRadius: 7,
                        paddingHorizontal: 14,
                        paddingVertical: 6,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.badgeText,
                        {
                          color: isActive
                            ? theme.accentContrast
                            : theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                          fontWeight: adminTypography.labelWeight,
                        },
                      ]}
                    >
                      {option.labelText}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: adminFigmaPalette.surface,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                gap: tokens.spacing.xs,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: adminFigmaPalette.inkMuted,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 10,
                  fontWeight: adminTypography.sectionLabelWeight,
                  letterSpacing: 0,
                  textTransform: "uppercase",
                },
              ]}
            >
              {adminOutboundAnalyticsScope === "campaign"
                ? "Selected campaign rollup"
                : "Per-campaign rollup"}
            </Text>
            {(adminOutboundAnalyticsScope === "campaign"
              ? adminOutboundCampaignList.filter(
                  (campaign) =>
                    campaign.campaignId ===
                    selectedOutboundCampaign?.campaignId,
                )
              : adminOutboundCampaignList
            ).map((campaign) => (
              <View
                key={`rollup-${campaign.campaignId}`}
                style={{
                  alignItems: "center",
                  borderTopColor: theme.borderSubtle,
                  borderTopWidth: 1,
                  flexDirection: "row",
                  gap: tokens.spacing.sm,
                  paddingTop: tokens.spacing.xs,
                }}
              >
                <View
                  style={{
                    backgroundColor: resolveCampaignReadinessColor(campaign),
                    borderRadius: 999,
                    height: 8,
                    width: 8,
                  }}
                />
                <Text
                  numberOfLines={1}
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12.5,
                      fontWeight: adminTypography.titleWeight,
                    },
                  ]}
                >
                  {campaign.displayName}
                </Text>
                <View style={[styles.flagRow, { gap: 5 }]}>
                  {(
                    [
                      { key: "scheduled", labelText: "scheduled" },
                      { key: "calling", labelText: "calling" },
                      { key: "review_required", labelText: "review" },
                      { key: "completed", labelText: "completed" },
                    ] as const
                  ).map((item) => (
                    <View
                      key={item.key}
                      style={{
                        backgroundColor: theme.backgroundSubtle,
                        borderRadius: 6,
                        paddingHorizontal: 7,
                        paddingVertical: 2,
                      }}
                    >
                      <Text
                        style={[
                          styles.pillValueText,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            fontWeight: adminTypography.strongWeight,
                          },
                        ]}
                      >
                        {`${String(
                          campaign.targetCounts[item.key] ?? 0,
                        )} ${item.labelText}`}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </View>

          <View style={[styles.patientMetaGrid, { gap: tokens.spacing.sm }]}>
            {outboundAuditRollupRows.map((item) => (
              <View
                key={item.labelText}
                style={[
                  styles.patientMetaCard,
                  {
                    backgroundColor: adminFigmaPalette.surface,
                    borderColor: adminFigmaPalette.border,
                    borderRadius: adminOperationalLayout.panelRadius,
                    gap: 4,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricValue,
                    {
                      color: adminFigmaPalette.accent,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 24,
                      lineHeight: 29,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: adminFigmaPalette.ink,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 12,
                      fontWeight: adminTypography.strongWeight,
                      lineHeight: 15,
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: adminFigmaPalette.inkMuted,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      lineHeight: 13,
                    },
                  ]}
                >
                  {item.supportText}
                </Text>
              </View>
            ))}
          </View>

          <View
            style={[
              {
                alignItems: "stretch",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <View
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: adminOperationalLayout.panelRadius,
                  flex: 1,
                  gap: tokens.spacing.sm,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: adminTypography.strongWeight,
                    lineHeight: 15,
                  },
                ]}
              >
                Daily outcomes
              </Text>
              {outboundAnalyticsSeriesRows.length > 0 ? (
                outboundAnalyticsSeriesRows.map((point) => (
                  <View
                    key={point.dateKey}
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "center",
                        borderTopColor: adminFigmaPalette.border,
                        borderTopWidth: 1,
                        flexDirection: "row",
                        gap: tokens.spacing.sm,
                        paddingTop: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.ink,
                          flex: 1,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          lineHeight: 14,
                        },
                      ]}
                    >
                      {point.dateKey}
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          lineHeight: 14,
                          textAlign: "right",
                        },
                      ]}
                    >
                      {`${String(point.liveAnswerCount)}/${String(
                        point.attemptedCallCount,
                      )} answered`}
                    </Text>
                    <Text
                      numberOfLines={1}
                      style={[
                        styles.metricLabel,
                        {
                          color:
                            point.redFlagEventCount > 0
                              ? adminFigmaPalette.danger
                              : adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          lineHeight: 14,
                          textAlign: "right",
                        },
                      ]}
                    >
                      {`${String(point.completedSessionCount)} done, ${String(
                        point.redFlagEventCount,
                      )} flags`}
                    </Text>
                  </View>
                ))
              ) : (
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: adminFigmaPalette.inkMuted,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      lineHeight: 16,
                    },
                  ]}
                >
                  No call activity in this window.
                </Text>
              )}
            </View>

            <View
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: adminOperationalLayout.panelRadius,
                  flex: 1,
                  gap: tokens.spacing.sm,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 12,
                    fontWeight: adminTypography.strongWeight,
                    lineHeight: 15,
                  },
                ]}
              >
                Recent audit
              </Text>
              {outboundRecentAuditRows.length > 0 ? (
                outboundRecentAuditRows.map((event) => (
                  <View
                    key={event.eventId}
                    style={[
                      {
                        borderTopColor: adminFigmaPalette.border,
                        borderTopWidth: 1,
                        gap: 3,
                        paddingTop: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <View
                      style={[
                        styles.patientRowHeader,
                        {
                          alignItems: "center",
                          flexDirection: "row",
                          gap: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: adminFigmaPalette.ink,
                            flex: 1,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 11,
                            fontWeight: adminTypography.strongWeight,
                            lineHeight: 14,
                          },
                        ]}
                      >
                        {formatOutboundAuditLabel(event.eventKind)}
                      </Text>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.metricLabel,
                          {
                            color: adminFigmaPalette.inkMuted,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            lineHeight: 13,
                            textAlign: "right",
                          },
                        ]}
                      >
                        {formatAdminSummaryTimestamp(event.occurredAt)}
                      </Text>
                    </View>
                    <Text
                      numberOfLines={2}
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          lineHeight: 14,
                        },
                      ]}
                    >
                      {event.eventSummary}
                    </Text>
                  </View>
                ))
              ) : (
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: adminFigmaPalette.inkMuted,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                      lineHeight: 16,
                    },
                  ]}
                >
                  No audit events in this window.
                </Text>
              )}
            </View>
          </View>
        </View>
        ) : null}

        {adminOutboundTab === "patients"
          ? renderAdminOutboundPatientsPanel()
          : null}

        {adminOutboundTab === "questionnaire" ? (
        <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: isMediumLayout ? "center" : "flex-start",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: tokens.spacing.sm,
              },
            ]}
          >
            <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.displayFontFamily,
                    fontSize: adminCompact.rowTitleFontSize,
                    fontWeight: adminTypography.titleWeight,
                    lineHeight: adminCompact.rowTitleLineHeight,
                  },
                ]}
              >
                Questionnaire authoring
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminCompact.bodyFontSize,
                    lineHeight: adminCompact.bodyLineHeight,
                  },
                ]}
              >
                Draft the clinical script, answer types, resume rules, and
                review triggers before any patient is eligible for a call.
              </Text>
            </View>
            {renderAdminStatusPill("Setup gate", { minWidth: 96 })}
          </View>

          {adminOutboundQuestionnaireTemplates.map((template) => {
            const latestVersion = template.latestVersion;
            const questions = latestVersion?.questions ?? [];
            const sectionRows = questions.reduce<
              Array<{
                key: string;
                labelText: string;
                questionCount: number;
                reviewCount: number;
                requiredCount: number;
              }>
            >((rows, question) => {
              const existingRow = rows.find(
                (row) => row.key === question.sectionKey,
              );
              const requiresReview =
                question.clinicianReviewLabel != null ||
                Object.keys(question.redFlagRules).length > 0;

              if (existingRow) {
                existingRow.questionCount += 1;
                existingRow.requiredCount += question.required ? 1 : 0;
                existingRow.reviewCount += requiresReview ? 1 : 0;
                return rows;
              }

              rows.push({
                key: question.sectionKey,
                labelText: question.sectionLabel ?? question.sectionKey,
                questionCount: 1,
                requiredCount: question.required ? 1 : 0,
                reviewCount: requiresReview ? 1 : 0,
              });
              return rows;
            }, []);
            const redFlagQuestionCount = questions.filter((question) => {
              const redFlagRules = readAdminOutboundRecord(
                question.redFlagRules,
              );

              return (
                readAdminOutboundBoolean(
                  redFlagRules.stopRoutineAutomation,
                  false,
                ) ||
                readAdminOutboundBoolean(redFlagRules.treatAsRedFlag, false)
              );
            }).length;
            const yellowFlagQuestionCount = questions.filter(
              (question) =>
                readAdminOutboundRecord(question.redFlagRules)
                  .unsureRequiresReview !== false,
            ).length;
            const customLeadInCount = questions.filter((question) => {
              const voicePhraseConfig = readAdminOutboundRecord(
                question.voicePhraseConfig,
              );

              return [
                voicePhraseConfig.leadIn,
                voicePhraseConfig.promptLeadIn,
                voicePhraseConfig.voiceLeadIn,
              ].some((value) => typeof value === "string" && value.trim());
            }).length;
            const customAnswerHintCount = questions.filter((question) => {
              const voicePhraseConfig = readAdminOutboundRecord(
                question.voicePhraseConfig,
              );

              return [
                voicePhraseConfig.answerHint,
                voicePhraseConfig.answerHintText,
                voicePhraseConfig.binaryAnswerHint,
                voicePhraseConfig.includeAnswerHint,
                voicePhraseConfig.showAnswerHint,
                voicePhraseConfig.repeatAnswerHint,
                voicePhraseConfig.forceAnswerHint,
                voicePhraseConfig.suppressAnswerHint,
                voicePhraseConfig.hideAnswerHint,
                voicePhraseConfig.omitAnswerHint,
              ].some((value) => value != null);
            }).length;
            const medicationDefinitionCount = questions.filter(
              (question) =>
                question.questionKey.toLowerCase().includes("medication") ||
                question.sectionKey.toLowerCase().includes("medication"),
            ).length;
            const resumePolicy = {
              ...adminOutboundDefaultResumePolicy,
              ...readAdminOutboundRecord(
                latestVersion?.resumePolicy ?? template.defaultResumePolicy,
              ),
            };
            const publishedReviewPolicy = readAdminOutboundRecord(
              latestVersion?.reviewPolicy ?? template.defaultReviewPolicy,
            );
            const publishedVoicePolicy = {
              ...adminOutboundDefaultVoicePolicy,
              ...readAdminOutboundRecord(publishedReviewPolicy.voicePolicy),
            };
            const resumeDelayMinutes = readAdminOutboundNumber(
              resumePolicy.defaultResumeDelayMinutes,
              adminOutboundDefaultResumePolicy.defaultResumeDelayMinutes,
            );
            const resumeMaxPausedHours = readAdminOutboundNumber(
              resumePolicy.maxPausedHours,
              adminOutboundDefaultResumePolicy.maxPausedHours,
            );
            const resumeMaxAttempts = readAdminOutboundNumber(
              resumePolicy.maxResumeAttempts,
              adminOutboundDefaultResumePolicy.maxResumeAttempts,
            );
            const voicePolicyRows = [
              {
                iconName: "volume" as const,
                labelText: "Voice tone",
                valueText: `${humanizeWorkspaceKey(
                  readAdminOutboundText(publishedVoicePolicy.tonePreset),
                )}; ${String(customLeadInCount)} custom lead-in${
                  customLeadInCount === 1 ? "" : "s"
                }`,
              },
              {
                iconName: "hospital" as const,
                labelText: "Intro and branding",
                valueText: `${humanizeWorkspaceKey(
                  readAdminOutboundText(publishedVoicePolicy.introMode),
                )}; disclosure ${readAdminOutboundText(
                  publishedVoicePolicy.disclosureVersion,
                )}`,
              },
              {
                iconName: "info" as const,
                labelText: "Answer hints",
                valueText:
                  customAnswerHintCount > 0
                    ? `${String(customAnswerHintCount)} custom hint rule${
                        customAnswerHintCount === 1 ? "" : "s"
                      }`
                    : "First binary prompt teaches answer options; later prompts stay concise",
              },
              {
                iconName: "flag" as const,
                labelText: "Yellow flags",
                valueText: `${String(yellowFlagQuestionCount)} question${
                  yellowFlagQuestionCount === 1 ? "" : "s"
                } route not-sure answers for review`,
              },
              {
                iconName: "document" as const,
                labelText: "Definitions",
                valueText: `${String(sectionRows.length)} sections, ${String(
                  questions.length,
                )} stable answer keys, ${String(
                  medicationDefinitionCount,
                )} medication prompts`,
              },
              {
                iconName: "phone" as const,
                labelText: "Preview and test call",
                valueText: liveOutboundDialingReady
                  ? "Use Campaign start controls after target review"
                  : "Locked until the campaign and call gate are ready",
              },
            ];
            const approvalText =
              latestVersion?.clinicalApprovedAt == null
                ? "Clinical approval required"
                : `Approved ${formatAdminSummaryTimestamp(
                    latestVersion.clinicalApprovedAt,
                  )}`;

            return (
              <View
                key={template.questionnaireTemplateId}
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: adminFigmaPalette.surface,
                    borderColor: adminFigmaPalette.border,
                    borderRadius: adminOperationalLayout.panelRadius,
                    gap: tokens.spacing.md,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      flexDirection: isMediumLayout ? "row" : "column",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={{
                      alignItems: "center",
                      backgroundColor: adminFigmaPalette.panelSubtle,
                      borderColor: adminFigmaPalette.border,
                      borderRadius: adminOperationalLayout.panelRadius,
                      borderWidth: 1,
                      height: 44,
                      justifyContent: "center",
                      width: 44,
                    }}
                  >
                    <LineIcon
                      color={adminFigmaPalette.accent}
                      name="document"
                      size={22}
                      strokeWidth={2.3}
                    />
                  </View>
                  <View
                    style={{
                      flex: isMediumLayout ? 1 : undefined,
                      gap: 4,
                      minWidth: 0,
                      width: isMediumLayout ? undefined : "100%",
                    }}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          fontWeight: adminTypography.sectionLabelWeight,
                          letterSpacing: 0,
                          lineHeight: 13,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      Clinical script
                    </Text>
                    <Text
                      style={[
                        styles.sectionTitle,
                        {
                          color: adminFigmaPalette.ink,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 18,
                          fontWeight: adminTypography.titleWeight,
                          lineHeight: 22,
                        },
                      ]}
                    >
                      {template.displayName}
                    </Text>
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                          lineHeight: 17,
                        },
                      ]}
                    >
                      {template.clinicalPurpose}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.patientRowCounters,
                      {
                        alignSelf: isMediumLayout ? "flex-start" : "stretch",
                        gap: tokens.spacing.xs,
                        justifyContent: isMediumLayout ? "flex-end" : "flex-start",
                      },
                    ]}
                  >
                    {renderAdminStatusPill(humanizeWorkspaceKey(template.status), {
                      minWidth: 70,
                    })}
                    {renderAdminStatusPill(approvalText, { minWidth: 150 })}
                  </View>
                </View>

                <View style={[styles.patientMetaGrid, { gap: tokens.spacing.xs }]}>
                  {[
                    {
                      labelText: "Version",
                      valueText:
                        latestVersion?.versionLabel ??
                        `v${String(latestVersion?.versionNumber ?? 1)}`,
                    },
                    {
                      labelText: "Questions",
                      valueText: String(latestVersion?.questionCount ?? 0),
                    },
                    {
                      labelText: "Estimated call",
                      valueText: `${String(
                        latestVersion?.estimatedMinutes ??
                          template.defaultEstimatedMinutes ??
                          0,
                      )} min`,
                    },
                    {
                      labelText: "Locale",
                      valueText: template.locale,
                    },
                  ].map((item) => (
                    <View
                      key={item.labelText}
                      style={[
                        styles.patientMetaCard,
                        {
                          backgroundColor: adminFigmaPalette.panelSubtle,
                          borderColor: adminFigmaPalette.border,
                          borderRadius: adminOperationalLayout.panelRadius,
                          gap: 2,
                          padding: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: adminFigmaPalette.inkMuted,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            fontWeight: adminTypography.sectionLabelWeight,
                            letterSpacing: 0,
                            lineHeight: 12,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        {item.labelText}
                      </Text>
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: adminFigmaPalette.ink,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 12,
                            fontWeight: adminTypography.strongWeight,
                            lineHeight: 16,
                          },
                        ]}
                      >
                        {item.valueText}
                      </Text>
                    </View>
                  ))}
                </View>

                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "stretch",
                      flexDirection: isMediumLayout ? "row" : "column",
                      gap: tokens.spacing.sm,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.detailBodyCard,
                      {
                        backgroundColor: adminFigmaPalette.panelSubtle,
                        borderColor: adminFigmaPalette.border,
                        borderRadius: adminOperationalLayout.panelRadius,
                        flex: 1.35,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          fontWeight: adminTypography.sectionLabelWeight,
                          letterSpacing: 0,
                          lineHeight: 13,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      Question outline
                    </Text>
                    {sectionRows.map((section) => (
                      <View
                        key={section.key}
                        style={[
                          styles.patientRowHeader,
                          {
                            alignItems: "center",
                            borderColor: adminFigmaPalette.border,
                            borderTopWidth: sectionRows[0] === section ? 0 : 1,
                            gap: tokens.spacing.xs,
                            paddingTop:
                              sectionRows[0] === section ? 0 : tokens.spacing.xs,
                          },
                        ]}
                      >
                        <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
                          <Text
                            style={[
                              styles.navigationText,
                              {
                                color: adminFigmaPalette.ink,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 12,
                                fontWeight: adminTypography.strongWeight,
                                lineHeight: 16,
                              },
                            ]}
                          >
                            {section.labelText}
                          </Text>
                          <Text
                            style={[
                              styles.metricLabel,
                              {
                                color: adminFigmaPalette.inkMuted,
                                fontFamily: layout.bodyFontFamily,
                                fontSize: 10,
                                lineHeight: 13,
                              },
                            ]}
                          >
                            {`${String(section.questionCount)} questions, ${String(
                              section.requiredCount,
                            )} required`}
                          </Text>
                        </View>
                        {section.reviewCount > 0 ? (
                          renderAdminStatusPill("Review", { minWidth: 70 })
                        ) : (
                          <LineIcon
                            color={theme.statusChip.success.text}
                            name="check"
                            size={17}
                            strokeWidth={2.4}
                          />
                        )}
                      </View>
                    ))}
                  </View>

                  <View
                    style={[
                      styles.detailBodyCard,
                      {
                        backgroundColor: adminFigmaPalette.panelSubtle,
                        borderColor: adminFigmaPalette.border,
                        borderRadius: adminOperationalLayout.panelRadius,
                        flex: 1,
                        gap: tokens.spacing.xs,
                        padding: tokens.spacing.sm,
                      },
                    ]}
                  >
                    {[
                      {
                        iconName: "archive" as const,
                        labelText: "Resume behavior",
                        valueText: `${String(
                          resumeDelayMinutes,
                        )} min delay, ${String(
                          resumeMaxPausedHours,
                        )} hr pause, ${String(
                          resumeMaxAttempts,
                        )} resume attempts`,
                      },
                      {
                        iconName: "flag" as const,
                        labelText: "Red flag routing",
                        valueText: `${String(redFlagQuestionCount)} trigger${
                          redFlagQuestionCount === 1 ? "" : "s"
                        } route answers as red-flag review`,
                      },
                      ...voicePolicyRows,
                    ].map((row) => (
                      <View key={row.labelText}>
                        {renderAdminCommandCenterRow({
                          iconName: row.iconName,
                          labelText: row.labelText,
                          valueText: row.valueText,
                        })}
                      </View>
                    ))}
                  </View>
                </View>

                <View
                  style={[
                    styles.patientRowFooter,
                    {
                      gap: tokens.spacing.xs,
                    },
                  ]}
                >
                  {persistedOutboundTemplate == null &&
                  !shouldOpenScaffoldOutboundEditor
                    ? renderAdminPrimaryButton({
                        disabled: isAdminOutboundCommandSubmitting,
                        iconName: "edit",
                        labelText: "Create questionnaire",
                        minWidth: 150,
                        onPress: runCreateDefaultOutboundQuestionnaire,
                        prefixText: "",
                        tone: "surface",
                      })
                    : renderAdminPrimaryButton({
                        disabled: isAdminOutboundCommandSubmitting,
                        iconName: "edit",
                        labelText:
                          persistedOutboundTemplate == null
                            ? "Edit draft"
                            : "Edit questionnaire",
                        minWidth: 150,
                        onPress: () =>
                          openOutboundQuestionnaireRevisionEditor(template),
                        prefixText: "",
                        tone: "surface",
                      })}
                  {!template.questionnaireTemplateId.startsWith("planned-")
                    ? renderAdminPrimaryButton({
                        disabled: isAdminOutboundCommandSubmitting,
                        iconName: "plus",
                        labelText: "Duplicate",
                        minWidth: 120,
                        onPress: () =>
                          runDuplicateOutboundQuestionnaire(template),
                        prefixText: "",
                        tone: "surface",
                      })
                    : null}
                  {template.questionnaireTemplateId.startsWith("planned-")
                    ? renderAdminStatusPill("Create first", {
                        controlHeight: true,
                        minWidth: 120,
                      })
                    : template.activeVersion == null
                    ? renderAdminPrimaryButton({
                        disabled: isAdminOutboundCommandSubmitting,
                        iconName: "check",
                        labelText: "Publish version",
                        minWidth: 138,
                        onPress: () =>
                          runPublishOutboundQuestionnaireVersion(template),
                        prefixText: "",
                        tone: "primary",
                      })
                    : renderAdminStatusPill("Version published", {
                        controlHeight: true,
                        minWidth: 138,
                      })}
                </View>
              </View>
            );
          })}
        </View>
        ) : null}

        {adminOutboundTab === "campaign" ? (
        <View
          style={[
            styles.listGroup,
            { gap: adminCompact.sectionGap, width: "100%" },
          ]}
        >
        {renderAdminOutboundCampaignLibrary()}
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: adminCompact.sectionGap,
            },
          ]}
        >
          <View style={[styles.listGroup, { flex: 1.2, gap: tokens.spacing.sm }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminCompact.rowTitleFontSize,
                  fontWeight: adminTypography.titleWeight,
                  lineHeight: adminCompact.rowTitleLineHeight,
                },
              ]}
            >
              Selected campaign
            </Text>
            {adminOutboundCallsSummary.campaigns
              .filter(
                (campaign) =>
                  campaign.campaignId === selectedOutboundCampaign?.campaignId,
              )
              .map((campaign) => (
              <View
                key={campaign.campaignId}
                style={[
                  styles.detailBodyCard,
                  {
                    backgroundColor: adminFigmaPalette.surface,
                    borderColor: adminFigmaPalette.border,
                    borderRadius: adminOperationalLayout.panelRadius,
                    gap: tokens.spacing.sm,
                    padding: tokens.spacing.md,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      gap: tokens.spacing.sm,
                      justifyContent: "space-between",
                    },
                  ]}
                >
                  <View style={{ flex: 1, gap: 4, minWidth: 0 }}>
                    <Text
                      style={[
                        styles.sectionTitle,
                        {
                          color: adminFigmaPalette.ink,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 18,
                          lineHeight: 22,
                        },
                      ]}
                    >
                      {campaign.displayName}
                    </Text>
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 12,
                          lineHeight: 17,
                        },
                      ]}
                    >
                      {campaign.purposeText}
                    </Text>
                  </View>
                  {renderAdminStatusPill(
                    humanizeWorkspaceKey(campaign.campaignStatus),
                    {
                      minWidth: 72,
                    },
                  )}
                </View>

                <View style={[styles.patientMetaGrid, { gap: tokens.spacing.xs }]}>
                  {[
                    {
                      labelText: "Questionnaire",
                      valueText:
                        campaign.questionnaireDisplayName ?? "Not published",
                    },
                    {
                      labelText: "Targets",
                      valueText: `${String(campaign.targetCounts.scheduled)} scheduled, ${String(campaign.targetCounts.paused)} paused`,
                    },
                    {
                      labelText: "Resume queue",
                      valueText:
                        campaign.targetCounts.paused > 0
                          ? "Callbacks pending"
                          : "No paused sessions",
                    },
                    {
                      labelText: "Review routing",
                      valueText: "Red flags to clinician review",
                    },
                    {
                      labelText: "Dialing",
                      valueText: "Locked",
                    },
                  ].map((item) => (
                    <View
                      key={item.labelText}
                      style={[
                        styles.patientMetaCard,
                        {
                          backgroundColor: adminFigmaPalette.panelSubtle,
                          borderColor: adminFigmaPalette.border,
                          borderRadius: adminOperationalLayout.panelRadius,
                          gap: 2,
                          padding: tokens.spacing.sm,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: adminFigmaPalette.inkMuted,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 10,
                            fontWeight: adminTypography.sectionLabelWeight,
                            letterSpacing: 0,
                            lineHeight: 12,
                            textTransform: "uppercase",
                          },
                        ]}
                      >
                        {item.labelText}
                      </Text>
                      <Text
                        style={[
                          styles.navigationText,
                          {
                            color: adminFigmaPalette.ink,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 12,
                            fontWeight: adminTypography.strongWeight,
                            lineHeight: 16,
                          },
                        ]}
                      >
                        {item.valueText}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}
            {renderAdminOutboundSchedulingControls()}
          </View>

          <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.sm }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminCompact.rowTitleFontSize,
                  fontWeight: adminTypography.titleWeight,
                  lineHeight: adminCompact.rowTitleLineHeight,
                },
              ]}
            >
              Policy defaults
            </Text>
            <View
              style={[
                styles.detailBodyCard,
                {
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: adminOperationalLayout.panelRadius,
                  gap: 2,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              {policyRows.map((row) => (
                <View key={row.labelText}>
                  {renderAdminCommandCenterRow({
                    iconName: row.iconName,
                    labelText: row.labelText,
                    valueText: row.valueText,
                  })}
                </View>
              ))}
            </View>

            <Text
              style={[
                styles.sectionTitle,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminCompact.rowTitleFontSize,
                  fontWeight: adminTypography.titleWeight,
                  lineHeight: adminCompact.rowTitleLineHeight,
                },
              ]}
            >
              Callback scheduler
            </Text>
            <View
              style={[
                styles.detailBodyCard,
                {
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: adminOperationalLayout.panelRadius,
                  gap: tokens.spacing.sm,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <View
                style={{
                  alignItems: "center",
                  flexDirection: "row",
                  gap: tokens.spacing.xs,
                }}
              >
                <View style={{ flex: 1, gap: 1, minWidth: 0 }}>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: adminTypography.strongWeight,
                        lineHeight: 16,
                      },
                    ]}
                  >
                    Automatic callbacks
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: adminFigmaPalette.inkMuted,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        lineHeight: 13,
                      },
                    ]}
                  >
                    Patients who ask to finish later are re-dialed
                  </Text>
                </View>
                {renderAdminPrimaryButton({
                  disabled:
                    isAdminCommandSubmitting ||
                    !adminCallbackSchedulerBaseline.configurable,
                  iconName: adminCallbackSchedulerView.enabled
                    ? "check"
                    : "stop",
                  labelText: adminCallbackSchedulerView.enabled ? "On" : "Off",
                  minWidth: 78,
                  onPress: () =>
                    updateAdminCallbackSchedulerDraft({
                      enabled: !adminCallbackSchedulerView.enabled,
                    }),
                  prefixText: "",
                  tone: adminCallbackSchedulerView.enabled
                    ? "primary"
                    : "surface",
                })}
              </View>
              <View
                style={{
                  alignItems: "flex-end",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: tokens.spacing.xs,
                }}
              >
                {(
                  [
                    {
                      id: "break",
                      labelText: "Break between tries (min)",
                      onChangeText: (value: string) =>
                        updateAdminCallbackSchedulerDraft({
                          breakMinutesText: value.replace(/[^0-9]/gu, ""),
                        }),
                      value: adminCallbackSchedulerView.breakMinutesText,
                    },
                    {
                      id: "attempts",
                      labelText: "Tries per request",
                      onChangeText: (value: string) =>
                        updateAdminCallbackSchedulerDraft({
                          maxAttemptsText: value.replace(/[^0-9]/gu, ""),
                        }),
                      value: adminCallbackSchedulerView.maxAttemptsText,
                    },
                  ] as const
                ).map((field) => (
                  <View
                    key={field.id}
                    style={{ flex: 1, gap: 3, minWidth: 132 }}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: adminFigmaPalette.inkMuted,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          fontWeight: adminTypography.sectionLabelWeight,
                          letterSpacing: 0,
                          lineHeight: 12,
                          textTransform: "uppercase",
                        },
                      ]}
                    >
                      {field.labelText}
                    </Text>
                    <TextInput
                      editable={
                        !isAdminCommandSubmitting &&
                        adminCallbackSchedulerBaseline.configurable &&
                        adminCallbackSchedulerView.enabled
                      }
                      inputMode="numeric"
                      keyboardType="numeric"
                      onChangeText={field.onChangeText}
                      style={{
                        backgroundColor: adminFigmaPalette.surface,
                        borderColor: adminFigmaPalette.border,
                        borderRadius: adminOperationalLayout.controlRadius,
                        borderWidth: 1,
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        opacity:
                          adminCallbackSchedulerView.enabled &&
                          adminCallbackSchedulerBaseline.configurable
                            ? 1
                            : 0.55,
                        paddingHorizontal: 10,
                        paddingVertical: 7,
                      }}
                      value={field.value}
                    />
                  </View>
                ))}
                {renderAdminPrimaryButton({
                  disabled:
                    isAdminCommandSubmitting ||
                    !adminCallbackSchedulerBaseline.configurable,
                  iconName: "sync",
                  labelText: "Save",
                  minWidth: 84,
                  onPress: () => {
                    void saveAdminCallbackSchedulerSettings();
                  },
                  prefixText: "",
                  tone: "primary",
                })}
              </View>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    lineHeight: 13,
                  },
                ]}
              >
                {adminCallbackSchedulerBaseline.configurable
                  ? `Checked every 15 minutes while the platform schedule is enabled. Campaign call windows, caps, and cooldowns still gate every dial.${
                      adminCallbackSchedulerBaseline.updatedAt
                        ? ` Updated ${formatAdminSummaryTimestamp(
                            adminCallbackSchedulerBaseline.updatedAt,
                          )}.`
                        : ""
                    }`
                  : "This environment's tenant database does not carry the callback scheduler settings yet."}
              </Text>
            </View>
          </View>
        </View>
        </View>
        ) : null}
      </View>
    );
  };

  const renderAdminReviewsSurface = () => {
    const reviewQueueCountById = (queueId: string) =>
      rawReviewRows.find((row) => row.id === queueId)?.count ?? 0;
    const reviewSpotlightCards = [
      {
        id: "message-reviews",
        labelText: "Message reviews",
        supportText: "Drafts waiting for clinician approval",
        valueText: String(reviewQueueCountById("pending-reviews")),
        badgeText: "High priority",
        toneColor: theme.warning,
        toneSurface: adminFigmaPalette.warningSoft,
      },
      {
        id: "escalations",
        labelText: "Escalations",
        supportText: "Cases needing care team review",
        valueText: String(reviewQueueCountById("active-escalations")),
        badgeText: "Urgent",
        toneColor: theme.danger,
        toneSurface: adminFigmaPalette.dangerSoft,
      },
      {
        id: "documents",
        labelText: "Documents",
        supportText: "New files available for review",
        valueText: String(reviewQueueCountById("available-documents")),
        badgeText: "Reviewing",
        toneColor: theme.info,
        toneSurface: adminFigmaPalette.infoSoft,
      },
      {
        id: "draft-quality",
        labelText: "Draft quality",
        supportText: "Auto-approved draft guidance",
        valueText: `${Math.max(0, 100 - aiEditedPercent)}%`,
        badgeText:
          aiEditedPercent > 25 ? "Watch edits" : "Healthy trend",
        toneColor: theme.success,
        toneSurface: adminFigmaPalette.successSoft,
      },
    ];
    const reviewHealthRows = rawReviewRows.slice(0, 7);
    const reviewHealthBarMaxHeight = 150;
    const reviewHealthMaxCount = Math.max(
      1,
      ...reviewHealthRows.map((row) => row.count),
    );
    const latestReviewItems = (
      sortAdminReviewQueueRows(
        rawReviewRows.filter((row) => row.count > 0),
        "priority",
      ).length > 0
        ? sortAdminReviewQueueRows(
            rawReviewRows.filter((row) => row.count > 0),
            "priority",
          )
        : rawReviewRows
    ).slice(0, 3);
    const reviewFilterToolbar = (
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.searchSurface,
            {
              ...adminSearchSurfaceStyle,
              alignItems: "center",
              flex: 1,
              flexDirection: "row",
              gap: tokens.spacing.xs,
              ...adminCompactSearchPadding,
              width: "100%",
            },
          ]}
        >
          <TextInput
            onChangeText={setAdminSearchQuery}
            placeholder="Search review queues..."
            placeholderTextColor={theme.textTertiary}
            style={[styles.searchInput, adminSearchInputTextStyle]}
            value={adminSearchQuery}
          />
          <LineIcon
            color={theme.textSecondary}
            name="search"
            size={15}
            strokeWidth={2.2}
          />
        </View>

        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {[
            ["all", "All reviews"],
            ["high", "High"],
            ["medium", "Medium"],
            ["low", "Low"],
          ].map(([value, labelText]) => (
            <View key={value}>
              {renderWorkspaceFilterChip({
                isSelected: adminReviewPriorityFilter === value,
                labelText,
                onPress: () => setAdminReviewPriorityFilter(value),
                theme,
                tokens,
              })}
            </View>
          ))}
        </View>
      </View>
    );

    return (
      <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            flexWrap: "wrap",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.xs }]}>
          <Text
            style={[
              styles.title,
              {
                ...adminCompactTitleStyle,
              },
            ]}
          >
            Review queues
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            Prioritize clinician attention by urgency, queue health, and care program.
          </Text>
        </View>

        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]} />
      </View>

      {isAdminMobileLayout ? reviewFilterToolbar : null}

      <View
        style={[
          styles.patientMetaGrid,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {reviewSpotlightCards.map((item) => (
          <View
            key={item.id}
            style={[
              styles.patientMetaCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: adminOperationalLayout.panelRadius,
                gap: tokens.spacing.sm,
                minHeight: 136,
                padding: tokens.spacing.md,
              },
            ]}
          >
            <View style={[styles.patientRowHeader, { gap: tokens.spacing.sm }]}>
              <View
                style={[
                  styles.patientAvatar,
                  {
                    backgroundColor: item.toneSurface,
                    borderColor: "transparent",
                    borderRadius: adminOperationalLayout.panelRadius,
                    height: 52,
                    width: 52,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.metricValue,
                    {
                      color: item.toneColor,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize.lg,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
              </View>
              <View style={[styles.listGroup, { flex: 1, gap: 3, minWidth: 0 }]}>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize.lg,
                      lineHeight: tokens.typography.lineHeight.lg,
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      lineHeight: tokens.typography.lineHeight.sm,
                    },
                  ]}
                >
                  {item.supportText}
                </Text>
              </View>
            </View>
            <View
              style={[
                styles.inlinePill,
                {
                  alignItems: "center",
                  alignSelf: "flex-start",
                  backgroundColor: item.toneSurface,
                  borderColor: "transparent",
                  borderRadius: 999,
                  height: 30,
                  justifyContent: "center",
                  paddingHorizontal: 14,
                  paddingVertical: 0,
                },
              ]}
            >
              <Text
                numberOfLines={1}
                style={[
                  styles.pillValueText,
                  {
                    color: item.toneColor,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: adminTypography.sectionLabelWeight,
                    lineHeight: 14,
                    textAlign: "center",
                  },
                ]}
              >
                {item.badgeText}
              </Text>
            </View>
          </View>
        ))}
      </View>

      <View
        style={[
          styles.workspaceGrid,
          {
            alignItems: "stretch",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View
          style={[
            styles.surfaceCard,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: adminOperationalLayout.panelRadius,
              flex: 1.65,
              gap: tokens.spacing.md,
              padding: tokens.spacing.lg,
            },
          ]}
        >
          <View style={[styles.listGroup, { gap: 3 }]}>
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: tokens.typography.fontSize.lg,
                },
              ]}
            >
              Queue health
            </Text>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                },
              ]}
            >
              Review load by priority and care program
            </Text>
          </View>
          <View
            style={[
              styles.patientRowFooter,
              {
                alignItems: "flex-end",
                borderBottomColor: theme.borderStrong,
                borderBottomWidth: 2,
                gap: tokens.spacing.md,
                minHeight: reviewHealthBarMaxHeight + 48,
                paddingHorizontal: tokens.spacing.sm,
              },
            ]}
          >
            {reviewHealthRows.map((row, index) => {
              const barHeight = Math.max(
                row.count > 0 ? 22 : 8,
                Math.round(
                  (row.count / reviewHealthMaxCount) * reviewHealthBarMaxHeight,
                ),
              );
              const barColor =
                row.priorityLabel === "High"
                  ? index % 2 === 0
                    ? theme.accent
                    : theme.warning
                  : row.priorityLabel === "Medium"
                    ? theme.info
                    : theme.success;

              return (
                <View
                  key={row.id}
                  style={[
                    styles.listGroup,
                    {
                      alignItems: "center",
                      flex: 1,
                      gap: 8,
                      justifyContent: "flex-end",
                      minWidth: 54,
                    },
                  ]}
                >
                  <View
                    style={{
                      backgroundColor: barColor,
                      borderTopLeftRadius: 10,
                      borderTopRightRadius: 10,
                      height: barHeight,
                      opacity: row.count > 0 ? 1 : 0.28,
                      width: "72%",
                    }}
                  />
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        textAlign: "center",
                      },
                    ]}
                  >
                    {row.sourceArea}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>

        <View
          style={[
            styles.surfaceCard,
            {
              backgroundColor: theme.surface,
              borderColor: theme.borderSubtle,
              borderRadius: adminOperationalLayout.panelRadius,
              flex: 1,
              gap: tokens.spacing.sm,
              padding: tokens.spacing.lg,
            },
          ]}
        >
          <Text
            style={[
              styles.sectionTitle,
              {
                color: theme.textPrimary,
                fontFamily: layout.displayFontFamily,
                fontSize: tokens.typography.fontSize.lg,
              },
            ]}
          >
            Latest review items
          </Text>
          {latestReviewItems.map((row) => (
            <View
              key={row.id}
              style={[
                styles.patientRowHeader,
                {
                  borderTopColor: theme.borderSubtle,
                  borderTopWidth: 1,
                  gap: tokens.spacing.sm,
                  paddingVertical: tokens.spacing.sm,
                },
              ]}
            >
              <View style={[styles.listGroup, { flex: 1, gap: 3, minWidth: 0 }]}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {row.queueLabel}
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                    },
                  ]}
                >
                  {row.supportingText}
                </Text>
              </View>
              {renderAdminStatusPill(row.priorityLabel, { minWidth: 86 })}
            </View>
          ))}
        </View>
      </View>

      {!isAdminMobileLayout ? reviewFilterToolbar : null}

      <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
          {[
            ["priority", "Sort priority"],
            ["count", "Sort count"],
            ["signal", "Sort latest"],
          ].map(([value, labelText]) => (
            <View key={value}>
              {renderWorkspaceFilterChip({
                isSelected: adminReviewSortKey === value,
                labelText,
                onPress: () =>
                  setAdminReviewSortKey(
                    value as "priority" | "count" | "signal",
                  ),
                theme,
                tokens,
              })}
            </View>
          ))}
      </View>

      {isAdminMobileLayout ? (
        renderAdminReviewsMobileList()
      ) : (
      <ScrollView
        horizontal={!isMediumLayout}
        showsHorizontalScrollIndicator={!isMediumLayout}
        style={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}
        contentContainerStyle={{
          minWidth: isMediumLayout ? "100%" : adminReviewTableMinWidth,
        }}
      >
        <View
          style={[
            styles.patientDocumentTable,
            adminTableShellStyle,
            {
              minWidth: isMediumLayout ? undefined : adminReviewTableMinWidth,
              width: "100%",
            },
          ]}
        >
          <View
            style={[
              styles.patientDocumentTableHeader,
              adminTableHeaderStyle,
            ]}
          >
            {[
              { labelText: "Queue", flexValue: 1.8 },
              { labelText: "Count", flexValue: 0.7 },
              { labelText: "Owner", flexValue: 1 },
              { labelText: "Priority", flexValue: 0.8 },
              { labelText: "Last signal", flexValue: 1 },
              { labelText: "Actions", flexValue: 1 },
            ].map((item) => (
              <Text
                key={item.labelText}
                style={[
                  styles.metricLabel,
                  {
                    flex: item.flexValue,
                    ...adminTableHeaderTextStyle,
                  },
                ]}
              >
                {item.labelText}
              </Text>
            ))}
          </View>

          {reviewPageRows.length > 0 ? (
            reviewPageRows.map((row, index) => {
              const isSelectedReviewRow = adminSelectedReviewQueueId === row.id;
              const isReviewQueueLoading =
                adminReviewQueueItemsLoadingId === row.id;

              return (
                <View
                  key={row.id}
                  style={[
                    styles.patientDocumentTableRow,
                    buildAdminTableRowStateStyle(index, {
                      selected: isSelectedReviewRow,
                    }),
                  ]}
                >
                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      { flex: 1.8, gap: 2 },
                    ]}
                  >
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: tokens.typography.fontSize.md,
                        lineHeight: tokens.typography.lineHeight.md,
                      },
                    ]}
                  >
                    {row.queueLabel}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                      },
                    ]}
                  >
                    {`${row.sourceArea} - ${row.supportingText}`}
                  </Text>
                </View>
                <Text
                  style={[
                    styles.metricValue,
                    {
                      color:
                        row.count > 0 ? theme.textPrimary : theme.textSecondary,
                      flex: 0.7,
                      fontFamily: layout.displayFontFamily,
                      fontSize: adminCompact.rowTitleFontSize,
                    },
                  ]}
                >
                  {String(row.count)}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {row.ownerLabel}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color:
                        row.priorityLabel === "High"
                          ? theme.warning
                          : row.priorityLabel === "Medium"
                            ? theme.info
                            : theme.textSecondary,
                      flex: 0.8,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {row.priorityLabel}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {row.latestSignalText}
                </Text>
                <View
                  style={[
                    styles.patientRowFooter,
                    { flex: 1, gap: tokens.spacing.xs },
                  ]}
                >
                  {renderAdminTableActionButton({
                    disabled: row.count < 1 || isReviewQueueLoading,
                    labelText: isReviewQueueLoading ? "Loading..." : "View items",
                    onPress: () => openAdminReviewQueueItems(row),
                    tone: row.priorityLabel === "High" ? "primary" : "surface",
                  })}
                  </View>
                </View>
              );
            })
          ) : (
            <View
              style={[
                styles.detailBodyCard,
                adminTableEmptyStateStyle,
              ]}
            >
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                No review backlog rows match the current admin filters.
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      )}

      {reviewRows.length > 5 ? (
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Prev",
            onPress: () =>
              setAdminReviewPageIndex((current) => Math.max(0, current - 1)),
            theme,
            tokens,
          })}
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
              },
            ]}
          >
            {`Page ${adminReviewPageIndex + 1} of ${Math.max(1, Math.ceil(reviewRows.length / 5))}`}
          </Text>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Next",
            onPress: () =>
              setAdminReviewPageIndex((current) =>
                Math.min(
                  Math.max(0, Math.ceil(reviewRows.length / 5) - 1),
                  current + 1,
                ),
              ),
            theme,
            tokens,
          })}
        </View>
      ) : null}
      </View>
    );
  };

  const renderAdminAiModelToggle = (input: {
    editable: boolean;
    enabled: boolean;
    flag: TenantAdminAiModelContextFlagV1;
    modelKey: TenantAdminAiModelKeyV1;
  }) => {
    const { editable, enabled, flag, modelKey } = input;
    const disabled = !editable || isAdminCommandSubmitting;

    return renderAdminBinaryToggle({
      checked: enabled,
      disabled,
      labelText: editable
        ? enabled
          ? "Enabled"
          : "Disabled"
        : enabled
          ? "Locked on"
          : "Unavailable",
      onPress: () =>
        runAdminAiModelContextFlagTransition(flag, modelKey, !enabled),
    });
  };
  const renderAdminAiArtifactStatusPill = (status: "active" | "archived") => (
    <View
      style={[
        styles.inlinePill,
        {
          backgroundColor:
            status === "active" ? theme.backgroundSubtle : theme.surface,
          borderColor:
            status === "active" ? theme.accentStrong : theme.borderSubtle,
          borderRadius: adminControlRadius,
          paddingHorizontal: tokens.spacing.sm,
          paddingVertical: tokens.spacing.xs,
        },
      ]}
    >
      <Text
        style={[
          styles.pillValueText,
          {
            color: status === "active" ? theme.accentStrong : theme.textSecondary,
            fontFamily: layout.bodyFontFamily,
            fontSize: tokens.typography.fontSize.xs,
          },
        ]}
      >
        {status === "active" ? "Active" : "Archived"}
      </Text>
    </View>
  );
  const renderAdminAiModelArtifactSection = (input: {
    columnLabel: string;
    emptyText: string;
    onAddChat: () => void;
    onAddShared: () => void;
    onAddVoice: () => void;
    rows: Array<{
      id: string;
      modelKey: "chat" | "voice" | "shared";
      primaryText: string;
      secondaryText: string;
      status: "active" | "archived";
      updatedAt: string | null;
      onArchive: () => void;
      onEdit: () => void;
    }>;
    title: string;
  }) => (
    <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.sm,
          },
        ]}
      >
        <Text
          style={[
            styles.sectionTitle,
            {
              color: theme.textPrimary,
              fontFamily: layout.displayFontFamily,
              fontSize: tokens.typography.fontSize.lg,
              lineHeight: tokens.typography.lineHeight.lg,
            },
          ]}
        >
          {input.title}
        </Text>
        <View
          style={[
            styles.patientRowHeader,
            {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: tokens.spacing.xs,
            },
          ]}
        >
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Add chat",
            onPress: input.onAddChat,
            theme,
            tokens,
          })}
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Add voice",
            onPress: input.onAddVoice,
            theme,
            tokens,
          })}
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Add shared",
            onPress: input.onAddShared,
            theme,
            tokens,
          })}
        </View>
      </View>

      <View style={[styles.patientDocumentTable, adminTableShellStyle]}>
        <View style={[styles.patientDocumentTableHeader, adminTableHeaderStyle]}>
          {[
            { labelText: input.columnLabel, flexValue: 2.4 },
            { labelText: "Model", flexValue: 0.7 },
            { labelText: "Status", flexValue: 0.8 },
            { labelText: "Actions", flexValue: 1.2 },
          ].map((item) => (
            <Text
              key={item.labelText}
              style={[
                styles.metricLabel,
                {
                  flex: item.flexValue,
                  ...adminTableHeaderTextStyle,
                },
              ]}
            >
              {item.labelText}
            </Text>
          ))}
        </View>

        {input.rows.length > 0 ? (
          input.rows.map((row, index) => (
            <View
              key={row.id}
              style={[
                styles.patientDocumentTableRow,
                buildAdminTableRowStateStyle(index),
              ]}
            >
              <View
                style={[
                  styles.patientDocumentTableCell,
                  { flex: 2.4, gap: tokens.spacing.xs },
                ]}
              >
                <Text
                  style={[
                    styles.navigationText,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      fontWeight: "700",
                    },
                  ]}
                >
                  {row.primaryText}
                </Text>
                <Text
                  numberOfLines={2}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      lineHeight: tokens.typography.lineHeight.sm,
                    },
                  ]}
                >
                  {row.secondaryText}
                </Text>
              </View>
              <View style={[styles.patientDocumentTableCell, { flex: 0.7 }]}>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {row.modelKey}
                </Text>
              </View>
              <View style={[styles.patientDocumentTableCell, { flex: 0.8 }]}>
                {renderAdminAiArtifactStatusPill(row.status)}
              </View>
              <View
                style={[
                  styles.patientDocumentTableCell,
                  {
                    flex: 1.2,
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: tokens.spacing.xs,
                  },
                ]}
              >
                {renderWorkspaceFilterChip({
                  isSelected: false,
                  labelText: "Edit",
                  onPress: row.onEdit,
                  theme,
                  tokens,
                })}
                {row.status === "active"
                  ? renderWorkspaceFilterChip({
                      isSelected: false,
                      labelText: "Archive",
                      onPress: row.onArchive,
                      theme,
                      tokens,
                    })
                  : null}
              </View>
            </View>
          ))
        ) : (
          <View style={[styles.patientDocumentTableRow, { padding: tokens.spacing.md }]}>
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                },
              ]}
            >
              {input.emptyText}
            </Text>
          </View>
        )}
      </View>
    </View>
  );

  const renderAdminAiModelParametersSurface = () => (
    <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.xs }]}>
          <Text
            style={[
              styles.title,
              {
                ...adminCompactTitleStyle,
              },
            ]}
          >
            AI model parameters
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            Configure which approved data classes are available to Chat and
            Voice assistants.
          </Text>
        </View>
        <View
          style={[
            styles.inlinePill,
            {
              backgroundColor: theme.backgroundSubtle,
              borderColor: theme.borderSubtle,
              borderRadius: adminControlRadius,
              paddingHorizontal: tokens.spacing.md,
              paddingVertical: tokens.spacing.xs,
            },
          ]}
        >
          <Text
            style={[
              styles.pillValueText,
              {
                color: theme.accentStrong,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
              },
            ]}
          >
            Approved policy
          </Text>
        </View>
      </View>

      <View
        style={[
          styles.patientMetaGrid,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {[
          {
            labelText: "Context flags",
            valueText: String(aiModelContextFlags.length),
          },
          {
            labelText: "Chat enabled",
            valueText: String(aiModelChatEnabledCount),
          },
          {
            labelText: "Voice enabled",
            valueText: String(aiModelVoiceEnabledCount),
          },
          {
            labelText: "Guidance items",
            valueText: String(activeAiModelPromptArtifactCount),
          },
          {
            labelText: "Knowledge sources",
            valueText: String(activeAiModelSourceDocumentCount),
          },
          {
            labelText: "Latest change",
            valueText: aiModelLatestChangeText,
          },
        ].map((item) => (
          <View
            key={item.labelText}
            style={[
              styles.patientMetaCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.xs,
                padding: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              {item.labelText}
            </Text>
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                },
              ]}
            >
              {item.valueText}
            </Text>
          </View>
        ))}
      </View>

      {isAdminMobileLayout ? (
        <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
          {aiModelContextFlags.map((flag, index) => (
            <View
              key={flag.flagKey}
              style={[
                buildAdminTableRowStateStyle(index),
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  borderWidth: 1,
                  gap: tokens.spacing.md,
                  padding: tokens.spacing.md,
                },
              ]}
            >
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientDesktopAgentIconButton,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      height: 38,
                      width: 38,
                    },
                  ]}
                >
                  <LineIcon
                    color={theme.accentStrong}
                    name={
                      flag.flagKey === "escalationToClinician"
                        ? "flag"
                        : flag.flagKey === "recentCallHistory"
                          ? "phone"
                        : flag.flagKey === "schedulingInformation"
                          ? "appointment"
                          : flag.flagKey === "billingInformation"
                            ? "document"
                            : flag.flagKey === "doctorNotes"
                              ? "edit"
                              : "case"
                    }
                    size={19}
                  />
                </View>
                <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: tokens.typography.fontSize.md,
                        lineHeight: tokens.typography.lineHeight.md,
                      },
                    ]}
                  >
                    {flag.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        lineHeight: tokens.typography.lineHeight.sm,
                      },
                    ]}
                  >
                    {flag.descriptionText}
                  </Text>
                </View>
              </View>
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <View style={[styles.listGroup, { gap: 4 }]}>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    Chat
                  </Text>
                  {renderAdminAiModelToggle({
                    editable: flag.chatEditable,
                    enabled: flag.chatEnabled,
                    flag,
                    modelKey: "chat",
                  })}
                </View>
                <View style={[styles.listGroup, { gap: 4 }]}>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    Voice
                  </Text>
                  {renderAdminAiModelToggle({
                    editable: flag.voiceEditable,
                    enabled: flag.voiceEnabled,
                    flag,
                    modelKey: "voice",
                  })}
                </View>
              </View>
            </View>
          ))}
        </View>
      ) : (
      <View
        style={[
          styles.patientDocumentTable,
          adminTableShellStyle,
        ]}
      >
        <View
          style={[
            styles.patientDocumentTableHeader,
            adminTableHeaderStyle,
          ]}
        >
          {[
            { labelText: "Data class", flexValue: 2.2 },
            { labelText: "Chat", flexValue: 0.8 },
            { labelText: "Voice", flexValue: 0.8 },
          ].map((item) => (
            <Text
              key={item.labelText}
              style={[
                styles.metricLabel,
                {
                  flex: item.flexValue,
                  ...adminTableHeaderTextStyle,
                },
              ]}
            >
              {item.labelText}
            </Text>
          ))}
        </View>

        {aiModelContextFlags.map((flag, index) => (
          <View
            key={flag.flagKey}
            style={[
              styles.patientDocumentTableRow,
              buildAdminTableRowStateStyle(index),
            ]}
          >
            <View
              style={[
                styles.patientDocumentTableCell,
                { flex: 2.2, gap: tokens.spacing.xs },
              ]}
            >
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: tokens.spacing.sm,
                  },
                ]}
              >
                <View
                  style={[
                    styles.patientDesktopAgentIconButton,
                    {
                      backgroundColor: theme.backgroundSubtle,
                      borderColor: theme.borderSubtle,
                      height: 38,
                      width: 38,
                    },
                  ]}
                >
                  <LineIcon
                    color={theme.accentStrong}
                    name={
                      flag.flagKey === "escalationToClinician"
                        ? "flag"
                        : flag.flagKey === "recentCallHistory"
                          ? "phone"
                        : flag.flagKey === "schedulingInformation"
                          ? "appointment"
                          : flag.flagKey === "billingInformation"
                            ? "document"
                            : flag.flagKey === "doctorNotes"
                              ? "edit"
                              : "case"
                    }
                    size={19}
                  />
                </View>
                <View style={[styles.listGroup, { flex: 1, gap: 2 }]}>
                  <Text
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: tokens.typography.fontSize.md,
                        lineHeight: tokens.typography.lineHeight.md,
                      },
                    ]}
                  >
                    {flag.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        lineHeight: tokens.typography.lineHeight.sm,
                      },
                    ]}
                  >
                    {flag.descriptionText}
                  </Text>
                </View>
              </View>
            </View>
            <View style={[styles.patientDocumentTableCell, { flex: 0.8 }]}>
              {renderAdminAiModelToggle({
                editable: flag.chatEditable,
                enabled: flag.chatEnabled,
                flag,
                modelKey: "chat",
              })}
            </View>
            <View style={[styles.patientDocumentTableCell, { flex: 0.8 }]}>
              {renderAdminAiModelToggle({
                editable: flag.voiceEditable,
                enabled: flag.voiceEnabled,
                flag,
                modelKey: "voice",
              })}
            </View>
          </View>
        ))}
      </View>
      )}

      {renderAdminAiModelArtifactSection({
        columnLabel: "Guidance",
        emptyText: "No response guidance has been saved yet.",
        onAddChat: () => runAdminAiModelPromptArtifactEditor("chat"),
        onAddShared: () => runAdminAiModelPromptArtifactEditor("shared"),
        onAddVoice: () => runAdminAiModelPromptArtifactEditor("voice"),
        rows: aiModelPromptArtifacts.map((artifact) => ({
          id: artifact.promptId,
          modelKey: artifact.modelKey,
          onArchive: () => archiveAdminAiModelPromptArtifact(artifact),
          onEdit: () =>
            runAdminAiModelPromptArtifactEditor(artifact.modelKey, artifact),
          primaryText: resolveAdminAiModelProductText(
            artifact.labelText,
            "Response guidance",
          ),
          secondaryText: resolveAdminAiModelProductText(
            artifact.purposeText ||
              `${artifact.promptText.slice(0, 140)}${artifact.promptText.length > 140 ? "..." : ""}`,
            "Approved response guidance for care-team review.",
          ),
          status: artifact.status,
          updatedAt: artifact.updatedAt,
        })),
        title: "Response guidance",
      })}

      {renderAdminAiModelArtifactSection({
        columnLabel: "Source",
        emptyText: "No AI knowledge sources have been saved yet.",
        onAddChat: () => runAdminAiModelSourceDocumentEditor("chat"),
        onAddShared: () => runAdminAiModelSourceDocumentEditor("shared"),
        onAddVoice: () => runAdminAiModelSourceDocumentEditor("voice"),
        rows: aiModelSourceDocuments.map((document) => ({
          id: document.sourceDocumentId,
          modelKey: document.modelKey,
          onArchive: () => archiveAdminAiModelSourceDocument(document),
          onEdit: () =>
            runAdminAiModelSourceDocumentEditor(document.modelKey, document),
          primaryText: resolveAdminAiModelProductText(
            document.titleText,
            "Knowledge source",
          ),
          secondaryText: resolveAdminAiModelProductText(
            document.summaryText ||
              `${document.sourceText.slice(0, 140)}${document.sourceText.length > 140 ? "..." : ""}`,
            "Approved clinical knowledge source.",
          ),
          status: document.status,
          updatedAt: document.updatedAt,
        })),
        title: "Knowledge sources",
      })}
    </View>
  );

  const renderAdminPatientPortalSurface = () => {
    const previewFrame =
      Platform.OS === "web"
        ? (
            // Phone-width frame so the patient app renders its MOBILE shell
            // (mobile layout triggers below the 900px breakpoint).
            <View style={{ alignItems: "center", width: "100%" }}>
              {createElement("iframe", {
                src: adminPatientPortalPreviewUrl,
                style: {
                  backgroundColor: adminFigmaPalette.surface,
                  border: `1px solid ${adminFigmaPalette.border}`,
                  borderRadius: 28,
                  boxShadow: "0 18px 48px rgba(15, 23, 42, 0.18)",
                  height: isMediumLayout ? 780 : 640,
                  maxWidth: "100%",
                  width: 390,
                } satisfies CSSProperties,
                title: "Patient portal mobile preview",
              })}
            </View>
          )
        : (
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: adminFigmaPalette.inkMuted,
                  fontFamily: layout.bodyFontFamily,
                },
              ]}
            >
              Patient portal preview is available in the web Admin workspace.
            </Text>
          );
    const portalFeatureIconNames: Record<string, LineIconName> = {
      appointments: "appointment",
      calls: "phone",
      documents: "document",
      medications: "medication",
      messages: "bot",
      procedures: "procedure",
    };

    return (
      <View style={{ gap: 16 }}>
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: isMediumLayout ? "center" : "stretch",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: 12,
              justifyContent: "space-between",
            },
          ]}
        >
          <View style={{ flex: 1, gap: 5, minWidth: 0 }}>
            <Text
              style={[
            styles.sectionTitle,
            {
              ...adminCompactTitleStyle,
            },
              ]}
            >
              Patient portal
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: adminFigmaPalette.inkMuted,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 14,
                  lineHeight: 20,
                },
              ]}
            >
              Configure which patient-facing modules are visible, then preview
              the patient experience before saving.
            </Text>
          </View>
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "center",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: isMediumLayout ? "flex-end" : "flex-start",
              },
            ]}
          >
            {renderAdminPrimaryButton({
              disabled:
                isAdminCommandSubmitting || !adminPatientPortalHasUnsavedChanges,
              iconName: "check",
              labelText: "Save modules",
              onPress: saveAdminPatientPortalFeatureFlags,
              prefixText: "",
            })}
            {renderAdminPrimaryButton({
              disabled: !adminPatientPortalHasUnsavedChanges,
              iconName: "sync",
              labelText: "Reset",
              onPress: () => setAdminPatientPortalFeatureDraft(null),
              prefixText: "",
              tone: "surface",
            })}
          </View>
        </View>

        <View
          style={{
            flexDirection: isMediumLayout ? "row" : "column",
            flexWrap: isMediumLayout ? "wrap" : "nowrap",
            gap: 12,
          }}
        >
          <View
            style={[
              styles.surfaceCard,
              {
                backgroundColor: adminFigmaPalette.surface,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                flex: isMediumLayout ? 1 : undefined,
                minWidth: isMediumLayout ? 220 : undefined,
                padding: 16,
              },
            ]}
          >
            <Text style={[styles.metricLabel, { color: adminFigmaPalette.inkMuted }]}>
              Enabled modules
            </Text>
            <Text
              style={[
                styles.metricValue,
                {
                  color: adminFigmaPalette.accent,
                  fontFamily: layout.displayFontFamily,
                },
              ]}
            >
              {adminPatientPortalEnabledCount}/
              {patientPortalFeatureDefinitionsV1.length}
            </Text>
          </View>
          <View
            style={[
              styles.surfaceCard,
              {
                backgroundColor: adminFigmaPalette.surface,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                flex: isMediumLayout ? 1 : undefined,
                minWidth: isMediumLayout ? 220 : undefined,
                padding: 16,
              },
            ]}
          >
            <Text style={[styles.metricLabel, { color: adminFigmaPalette.inkMuted }]}>
              Source of truth
            </Text>
            <Text
              style={[
                styles.metricValue,
                {
                  color: adminFigmaPalette.ink,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 18,
                },
              ]}
            >
              Backend flags
            </Text>
          </View>
          <View
            style={[
              styles.surfaceCard,
              {
                backgroundColor: adminPatientPortalHasUnsavedChanges
                  ? adminFigmaPalette.warningSoft
                  : adminFigmaPalette.successSoft,
                borderColor: adminPatientPortalHasUnsavedChanges
                  ? adminFigmaPalette.warning
                  : adminFigmaPalette.success,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                flex: isMediumLayout ? 1 : undefined,
                minWidth: isMediumLayout ? 220 : undefined,
                padding: 16,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: adminPatientPortalHasUnsavedChanges
                    ? adminFigmaPalette.warning
                    : adminFigmaPalette.success,
                },
              ]}
            >
              Status
            </Text>
            <Text
              style={[
                styles.metricValue,
                {
                  color: adminPatientPortalHasUnsavedChanges
                    ? adminFigmaPalette.warning
                    : adminFigmaPalette.success,
                  fontFamily: layout.displayFontFamily,
                  fontSize: 18,
                },
              ]}
            >
              {adminPatientPortalHasUnsavedChanges ? "Unsaved" : "Saved"}
            </Text>
          </View>
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: isWideLayout ? "row" : "column",
              gap: 16,
            },
          ]}
        >
          <View style={{ flex: 1, gap: 12, minWidth: 0 }}>
            {patientPortalFeatureDefinitionsV1.map((definition) => {
              const enabled =
                adminPatientPortalFeatureFlags[definition.flagKey] === true;

              return (
                <View
                  key={definition.flagKey}
                  style={[
                    styles.surfaceCard,
                    {
                      alignItems: isMediumLayout ? "center" : "stretch",
                      backgroundColor: enabled
                        ? adminFigmaPalette.panelSubtle
                        : adminFigmaPalette.surface,
                      borderColor: enabled
                        ? adminFigmaPalette.accent
                        : adminFigmaPalette.border,
                      borderRadius: adminOperationalLayout.panelRadius,
                      borderWidth: 1,
                      flexDirection: isMediumLayout ? "row" : "column",
                      gap: 12,
                      justifyContent: "space-between",
                      padding: 14,
                    },
                  ]}
                >
                  <View
                    style={{
                      alignItems: "center",
                      flexDirection: "row",
                      flex: 1,
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <View
                      style={{
                        alignItems: "center",
                        backgroundColor: enabled
                          ? adminFigmaPalette.successSoft
                          : adminFigmaPalette.panelSubtle,
                        borderRadius: adminOperationalLayout.panelRadius,
                        height: 42,
                        justifyContent: "center",
                        width: 42,
                      }}
                    >
                      <LineIcon
                        color={
                          enabled
                            ? adminFigmaPalette.success
                            : adminFigmaPalette.inkMuted
                        }
                        name={
                          portalFeatureIconNames[definition.moduleId] ??
                          "settings"
                        }
                        size={20}
                        strokeWidth={2.25}
                      />
                    </View>
                    <View style={{ flex: 1, gap: 3, minWidth: 0 }}>
                      <Text
                        style={[
                          styles.sectionTitle,
                          {
                            color: adminFigmaPalette.ink,
                            fontFamily: layout.displayFontFamily,
                            fontSize: 15,
                            lineHeight: 20,
                          },
                        ]}
                      >
                        {definition.label}
                      </Text>
                      <Text
                        style={[
                          styles.sectionSubtitle,
                          {
                            color: adminFigmaPalette.inkMuted,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: 12,
                            lineHeight: 17,
                          },
                        ]}
                      >
                        {definition.flagKey}
                      </Text>
                    </View>
                  </View>
                  <View
                    style={{
                      alignSelf: isMediumLayout ? "center" : "flex-start",
                    }}
                  >
                    {renderAdminBinaryToggle({
                      checked: enabled,
                      onPress: () =>
                        setAdminPatientPortalFeatureEnabled(
                          definition.flagKey,
                          !enabled,
                        ),
                    })}
                  </View>
                </View>
              );
            })}
          </View>

          <View
            style={[
              styles.surfaceCard,
              {
                backgroundColor: adminFigmaPalette.surface,
                borderColor: adminFigmaPalette.border,
                borderRadius: adminOperationalLayout.panelRadius,
                borderWidth: 1,
                flex: isWideLayout ? 1.15 : undefined,
                gap: 12,
                minWidth: 0,
                overflow: "hidden",
                padding: 14,
              },
            ]}
          >
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "center",
                  justifyContent: "space-between",
                },
              ]}
            >
              <View style={{ gap: 4 }}>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: adminFigmaPalette.ink,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 15,
                      lineHeight: 20,
                    },
                  ]}
                >
                  Live preview
                </Text>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: adminFigmaPalette.inkMuted,
                      fontFamily: layout.bodyFontFamily,
                    },
                  ]}
                >
                  Uses the current patient UI with unsaved module settings.
                </Text>
              </View>
              <LineIcon
                color={adminFigmaPalette.accent}
                name="eye"
                size={20}
                strokeWidth={2.35}
              />
            </View>
            {previewFrame}
          </View>
        </View>
      </View>
    );
  };

  const renderAdminFlagsSurface = () => (
    <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.xs }]}>
          <Text
            style={[
              styles.title,
              {
                ...adminCompactTitleStyle,
              },
            ]}
          >
            Feature and system flags
          </Text>
          <Text
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.sm,
                lineHeight: tokens.typography.lineHeight.md,
              },
            ]}
          >
            Manage feature and system settings.
          </Text>
        </View>

        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderAdminPrimaryButton({
            density: "compact",
            labelText: "Create flag",
            onPress: openCreateFeatureFlagDraft,
            prefixText: "+",
            tone: "primary",
          })}
          {adminFlagStateSummaryRows.map((item) => {
            const palette = resolveAdminFlagStatePalette(item.state);

            return (
              <View
                key={item.labelText}
                style={[
                  styles.inlinePill,
                  {
                    backgroundColor: palette.backgroundColor,
                    borderColor: palette.borderColor,
                    borderRadius: 8,
                    paddingHorizontal: tokens.spacing.sm,
                    paddingVertical: tokens.spacing.xs,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: palette.textColor,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                    },
                  ]}
                >
                  {`${item.labelText}: ${humanizeWorkspaceKey(item.state)}`}
                </Text>
              </View>
            );
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            labelText:
              smsDispatchSystemFlagState === "enabled"
                ? "Pause SMS"
                : "Enable SMS",
            onPress: () =>
              runAdminSystemFlagTransition(
                smsDispatchSystemFlagKey,
                smsDispatchSystemFlagState === "enabled"
                  ? "disabled"
                  : "enabled",
              ),
            prefixText: "",
            tone:
              smsDispatchSystemFlagState === "enabled" ? "surface" : "primary",
          })}
          {renderWorkspaceFilterChip({
            isSelected: smsDispatchSystemFlagState === "unset",
            labelText: "Unset SMS",
            onPress: () =>
              runAdminSystemFlagTransition(
                smsDispatchSystemFlagKey,
                "unset",
              ),
            theme,
            tokens,
          })}
        </View>
      </View>

      <View
        style={[
          styles.patientMetaGrid,
          {
            gap: tokens.spacing.sm,
          },
        ]}
      >
        {[
          {
            labelText: "Visible flags",
            valueText: String(featureFlagRows.length),
          },
          { labelText: "Enabled", valueText: String(enabledFeatureFlagCount) },
          {
            labelText: "Disabled",
            valueText: String(disabledFeatureFlagCount),
          },
          {
            labelText: "Unset",
            valueText: String(unsetFeatureFlagCount),
          },
          {
            labelText: "System flags",
            valueText: String(systemFeatureFlagCount),
          },
          { labelText: "Latest change", valueText: latestFeatureFlagText },
        ].map((item) => (
          <View
            key={item.labelText}
            style={[
              styles.patientMetaCard,
              {
                backgroundColor: theme.surface,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.xs,
                padding: tokens.spacing.sm,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                  textTransform: "uppercase",
                },
              ]}
            >
              {item.labelText}
            </Text>
            <Text
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.md,
                },
              ]}
            >
              {item.valueText}
            </Text>
          </View>
        ))}
      </View>

      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: "center",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.sm,
          },
        ]}
      >
        <View
          style={[
            styles.searchSurface,
            {
              ...adminSearchSurfaceStyle,
              alignItems: "center",
              flex: 1,
              flexDirection: "row",
              gap: tokens.spacing.xs,
              ...adminCompactSearchPadding,
              width: "100%",
            },
          ]}
        >
          <TextInput
            onChangeText={setAdminSearchQuery}
            placeholder="Search flags..."
            placeholderTextColor={theme.textTertiary}
            style={[
              styles.searchInput,
              adminSearchInputTextStyle,
            ]}
            value={adminSearchQuery}
          />
          <LineIcon
            color={theme.textSecondary}
            name="search"
            size={15}
            strokeWidth={2.2}
          />
        </View>

        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {[
            ["all", "All flags"],
            ["enabled", "Enabled"],
            ["disabled", "Disabled"],
            ["unset", "Unset"],
            ["system", "System"],
            ["feature", "Feature"],
          ].map(([value, labelText]) =>
            renderWorkspaceFilterChip({
              isSelected: adminFeatureFlagStatusFilter === value,
              labelText,
              onPress: () =>
                setAdminFeatureFlagStatusFilter(
                  value as
                    | "all"
                    | "enabled"
                    | "disabled"
                    | "unset"
                    | "system"
                    | "feature",
                ),
              theme,
              tokens,
            }),
          )}
        </View>
      </View>

      <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
        {[
          ["updated", "Sort updated"],
          ["key", "Sort key"],
          ["scope", "Sort scope"],
        ].map(([value, labelText]) =>
          renderWorkspaceFilterChip({
            isSelected: adminFeatureFlagSortKey === value,
            labelText,
            onPress: () =>
              setAdminFeatureFlagSortKey(value as "key" | "scope" | "updated"),
            theme,
            tokens,
          }),
        )}
      </View>

      <View
        style={[
          styles.patientDocumentTable,
          adminTableShellStyle,
        ]}
      >
        <View
          style={[
            styles.patientDocumentTableHeader,
            adminTableHeaderStyle,
          ]}
        >
          {[
            { labelText: "Flag", flexValue: 2 },
            { labelText: "Scope", flexValue: 1.1 },
            { labelText: "Status", flexValue: 0.9 },
            { labelText: "Updated", flexValue: 1 },
            { labelText: "Actions", flexValue: 1.4 },
          ].map((item) => (
            <Text
              key={item.labelText}
              style={[
                styles.metricLabel,
                {
                  flex: item.flexValue,
                  ...adminTableHeaderTextStyle,
                },
              ]}
            >
              {item.labelText}
            </Text>
          ))}
        </View>

        {featureFlagPageRows.length > 0 ? (
          featureFlagPageRows.map((flag, index) => (
            <View
              key={flag.flagId}
              style={[
                styles.patientDocumentTableRow,
                buildAdminTableRowStateStyle(index),
              ]}
            >
              <View
                style={[styles.patientDocumentTableCell, { flex: 2, gap: 2 }]}
              >
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: tokens.typography.fontSize.md,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {flag.flagKey}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                    },
                  ]}
                >
                  {[
                    flag.isSystemFlag ? "System flag" : "Feature flag",
                    flag.flagVariant ? `variant ${flag.flagVariant}` : null,
                    flag.rolloutNote,
                  ]
                    .filter((part): part is string => Boolean(part))
                    .join(" | ")}
                </Text>
              </View>
              <View
                style={[styles.patientDocumentTableCell, { flex: 1.1, gap: 2 }]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  {flag.scopeLabel}
                </Text>
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.xs,
                    },
                  ]}
                >
                  {flag.scopeKey ?? humanizeWorkspaceKey(flag.scope)}
                </Text>
              </View>
              <View style={[styles.patientDocumentTableCell, { flex: 0.9 }]}>
                {(() => {
                  const flagState = resolveAdminFeatureFlagState(flag);
                  const pillBackgroundColor =
                    flagState === "enabled"
                      ? theme.backgroundSubtle
                      : flagState === "disabled"
                        ? theme.surfaceMuted
                        : theme.surface;
                  const pillBorderColor =
                    flagState === "enabled"
                      ? theme.info
                      : flagState === "disabled"
                        ? theme.warning
                        : theme.borderSubtle;
                  const pillTextColor =
                    flagState === "enabled"
                      ? theme.info
                      : flagState === "disabled"
                        ? theme.warning
                        : theme.textSecondary;

                  return (
                    <View
                      style={[
                        styles.inlinePill,
                        {
                          backgroundColor: pillBackgroundColor,
                          borderColor: pillBorderColor,
                          borderRadius: 8,
                          paddingHorizontal: tokens.spacing.sm,
                          paddingVertical: tokens.spacing.xs,
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.pillValueText,
                          {
                            color: pillTextColor,
                            fontFamily: layout.bodyFontFamily,
                          },
                        ]}
                      >
                        {humanizeWorkspaceKey(flagState)}
                      </Text>
                    </View>
                  );
                })()}
              </View>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textPrimary,
                    flex: 1,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                  },
                ]}
              >
                {formatAdminSummaryTimestamp(flag.updatedAt)}
              </Text>
              <View
                style={[
                  styles.patientRowFooter,
                  {
                    flex: 1.6,
                    flexWrap: "wrap",
                    gap: tokens.spacing.xs,
                    justifyContent: "flex-start",
                  },
                ]}
              >
                {(
                  [
                    ["enabled", "Enable"],
                    ["disabled", "Disable"],
                    ["unset", "Unset"],
                  ] as const
                ).map(([value, labelText]) =>
                  renderWorkspaceFilterChip({
                    isSelected: resolveAdminFeatureFlagState(flag) === value,
                    labelText,
                    onPress: () => runAdminFeatureFlagTransition(flag, value),
                    theme,
                    tokens,
                  }),
                )}
              </View>
            </View>
          ))
        ) : (
          <View
            style={[
              styles.detailBodyCard,
              adminTableEmptyStateStyle,
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              No feature or system flags match the current admin filters.
            </Text>
          </View>
        )}
      </View>

      {featureFlagRows.length > 8 ? (
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Prev",
            onPress: () =>
              setAdminFeatureFlagPageIndex((current) =>
                Math.max(0, current - 1),
              ),
            theme,
            tokens,
          })}
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
              },
            ]}
          >
            {`Page ${adminFeatureFlagPageIndex + 1} of ${Math.max(1, Math.ceil(featureFlagRows.length / 8))}`}
          </Text>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Next",
            onPress: () =>
              setAdminFeatureFlagPageIndex((current) =>
                Math.min(
                  Math.max(0, Math.ceil(featureFlagRows.length / 8) - 1),
                  current + 1,
                ),
              ),
            theme,
            tokens,
          })}
        </View>
      ) : null}
    </View>
  );

  const renderAdminPatientDetailPanel = (row: AdminPreviewPatientRow) => {
    const isArchived = isAdminPatientArchived(row);
    const portalRoutes: Array<{
      labelText: string;
      route: TenantAdminPatientPortalRoute;
    }> = [
      { labelText: "Overview", route: "overview" },
      { labelText: "Messages", route: "messages" },
      { labelText: "Documents", route: "documents" },
      { labelText: "Appointments", route: "appointments" },
      { labelText: "Medications", route: "medications" },
      { labelText: "Onboarding", route: "onboarding" },
    ];
    const summaryStatusText = row.summaryRefreshStatus
      ? humanizeWorkspaceKey(row.summaryRefreshStatus)
      : "Not generated";
    const summaryGeneratedText = row.summaryGeneratedAt
      ? formatAdminSummaryTimestamp(row.summaryGeneratedAt)
      : "No generated summary";
    const portalUpdatedText = row.portalStateUpdatedAt
      ? formatAdminSummaryTimestamp(row.portalStateUpdatedAt)
      : "No portal change recorded";
    const lastUpdatedText = row.lastUpdatedAt
      ? formatAdminSummaryTimestamp(row.lastUpdatedAt)
      : "No update recorded";
    const reminderWakeTime = row.reminderWakeUpTime ?? "07:00";
    const reminderScheduleValue = row.remindersEnabled ? reminderWakeTime : "off";
    const patientStateText = isArchived
      ? "Archived"
      : row.requiresAttention
        ? "Needs attention"
        : row.statusLabel;
    const headerSubtitleText = [
      row.identifier ?? "No identifier",
      patientStateText,
      row.leadingClinicianLabel ?? "No clinician assigned",
    ]
      .filter((part) => part.trim().length > 0)
      .join(" - ");
    const portalAccessText = humanizeWorkspaceKey(row.portalAuthStatus);
    const portalRouteText = humanizeWorkspaceKey(row.portalEntryPointRoute);
    const patientDecisionCards = [
      {
        labelText: "Current state",
        valueText: patientStateText,
      },
      {
        labelText: "Leading clinician",
        valueText: row.leadingClinicianLabel ?? "No clinician assigned",
      },
      {
        labelText: "Portal access",
        valueText: portalAccessText,
      },
      {
        labelText: "Open documents",
        valueText: String(row.pendingDocumentCount),
      },
      {
        labelText: "Unread messages",
        valueText: String(row.unreadMessageCount),
      },
      {
        labelText: "Contact window",
        valueText: row.contactWindowText ?? "No window recorded",
      },
      {
        labelText: "Access sync",
        valueText: row.accessSyncStatus
          ? humanizeWorkspaceKey(row.accessSyncStatus)
          : "Not requested",
      },
    ];
    const portalStateRows = [
      {
        labelText: "Onboarding",
        valueText: row.onboardingInitialFlowComplete
          ? "Complete"
          : "Open",
      },
      {
        labelText: "Entry point",
        valueText: portalRouteText,
      },
      {
        labelText: "Reminders",
        valueText: row.remindersEnabled
          ? `${row.reminderWakeUpTime ?? "Wake time unset"}`
          : "Off",
      },
      {
        labelText: "Last portal update",
        valueText: portalUpdatedText,
      },
    ];
    const patientAuditRows = [
      {
        labelText: "Patient reference",
        valueText: row.patientId ?? "No patient reference",
      },
      {
        labelText: "Source summary",
        valueText: row.supportText,
      },
      {
        labelText: "Summary generated",
        valueText: summaryGeneratedText,
      },
      {
        labelText: "Policy version",
        valueText: row.summaryPolicyVersion ?? "Policy pending",
      },
      {
        labelText: "Source updated",
        valueText: lastUpdatedText,
      },
      {
        labelText: "Lifecycle version",
        valueText: String(row.lifecycleVersion),
      },
      ...(row.accessSyncLastErrorMessage
        ? [
            {
              labelText: "Access sync error",
              valueText: row.accessSyncLastErrorMessage,
            },
          ]
        : []),
      ...(isArchived
        ? [
            {
              labelText: "Archived at",
              valueText: row.archivedAt
                ? formatAdminSummaryTimestamp(row.archivedAt)
                : "Timestamp unavailable",
            },
            {
              labelText: "Archived by",
              valueText: row.archivedByReference ?? "Actor unavailable",
            },
            {
              labelText: "Archive reason",
              valueText: row.archiveReasonKey
                ? humanizeWorkspaceKey(row.archiveReasonKey)
                : "Reason unavailable",
            },
            {
              labelText: "Archive notes",
              valueText: row.archiveNotes ?? "No archive notes",
            },
            {
              labelText: "Previous state",
              valueText: row.preArchiveStatus
                ? humanizeWorkspaceKey(row.preArchiveStatus)
                : "Not recorded",
            },
          ]
        : []),
    ].filter((item) => item.valueText.trim().length > 0);

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminOperationalLayout.panelRadius,
            borderWidth: 0,
            gap: adminCompact.sectionGap,
            padding: adminModalDensity.cardPadding,
          },
        ]}
      >
        {renderAdminModalHeader({
          actions: (
            <>
              {!isArchived
                ? renderAdminPrimaryButton({
                    density: "compact",
                    labelText: "Edit patient",
                    onPress: () => runUpdatePatientCommand(row),
                    prefixText: "",
                    tone: "surface",
                  })
                : null}
              {!isArchived
                ? renderAdminPrimaryButton({
                    density: "compact",
                    labelText: row.onboardingInitialFlowComplete
                      ? "Reset onboarding"
                      : "Complete onboarding",
                    onPress: () =>
                      runAdminPatientPortalControl(
                        row,
                        row.onboardingInitialFlowComplete
                          ? {
                              actionKind: "reset-onboarding",
                              entryPointRoute: "onboarding",
                              notes:
                                "Onboarding was reopened from the patient workspace.",
                            }
                          : {
                              actionKind: "complete-onboarding",
                              entryPointRoute: "overview",
                              notes:
                                "Onboarding was completed from the patient workspace.",
                            },
                        row.onboardingInitialFlowComplete
                          ? "Onboarding reset"
                          : "Onboarding completion",
                      ),
                    prefixText: "",
                    tone: row.onboardingInitialFlowComplete
                      ? "surface"
                      : "primary",
                  })
                : null}
              {row.accessSyncStatus != null &&
              row.accessSyncStatus !== "completed"
                ? renderAdminPrimaryButton({
                    density: "compact",
                    labelText: "Retry access sync",
                    onPress: () => retryAdminPatientAccessSync(row),
                    prefixText: "",
                    tone: "surface",
                  })
                : null}
              {renderAdminPrimaryButton({
                density: "compact",
                labelText: isArchived ? "Restore patient" : "Archive patient",
                onPress: () =>
                  openAdminPatientLifecycleDialog(
                    row,
                    isArchived ? "restore" : "archive",
                  ),
                prefixText: "",
                tone: isArchived ? "primary" : "danger",
              })}
            </>
          ),
          eyebrowText: "Patient workspace",
          onClose: () => setAdminSelectedPatientRowId(null),
          subtitleText: headerSubtitleText,
          titleText: row.displayName,
        })}

        <View style={[styles.patientMetaGrid, { gap: tokens.spacing.sm }]}>
          {patientDecisionCards.map((item) => (
            <View
              key={item.labelText}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.surfaceElevated,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  flexBasis: isWideLayout ? 108 : 132,
                  gap: tokens.spacing.xs,
                  padding: adminModalDensity.cardPadding,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaValueFontSize,
                    lineHeight: adminModalDensity.bodyLineHeight,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: "column",
              gap: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Portal and reminders
            </Text>
            <View style={[styles.listGroup, { gap: tokens.spacing.xs }]}>
              {portalStateRows.map((item) => (
                <View
                  key={item.labelText}
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      borderTopColor: theme.borderSubtle,
                      borderTopWidth: item.labelText === "Onboarding" ? 0 : 1,
                      gap: tokens.spacing.md,
                      justifyContent: "space-between",
                      paddingTop:
                        item.labelText === "Onboarding" ? 0 : tokens.spacing.xs,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminModalDensity.metaLabelFontSize,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {item.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminModalDensity.bodyFontSize,
                        lineHeight: adminModalDensity.bodyLineHeight,
                        textAlign: "right",
                      },
                    ]}
                  >
                    {item.valueText}
                  </Text>
                </View>
              ))}
            </View>
            <View
              style={[
                styles.patientRowHeader,
                {
                  alignItems: "stretch",
                  flexDirection: "column",
                  gap: tokens.spacing.sm,
                },
              ]}
            >
              {renderAdminModalSelectField({
                fullWidth: true,
                labelText: "Entry point",
                onChange: (nextRoute) =>
                  runAdminPatientPortalControl(
                    row,
                    {
                      actionKind: "set-entry-route",
                      entryPointRoute: nextRoute as TenantAdminPatientPortalRoute,
                      notes: `Admin routed patient portal to ${nextRoute}.`,
                    },
                      "Portal route update",
                    ),
                options: portalRoutes.map((item) => ({
                  labelText: item.labelText,
                  value: item.route,
                })),
                value: row.portalEntryPointRoute,
              })}
              {renderAdminModalSelectField({
                fullWidth: true,
                labelText: "Reminders",
                onChange: (nextValue) =>
                  runAdminPatientPortalControl(
                    row,
                    {
                      actionKind: "set-reminder-preference",
                      notes:
                        "Admin updated reminder preference from patient detail.",
                      remindersEnabled: nextValue !== "off",
                      wakeUpTime: nextValue === "off" ? reminderWakeTime : nextValue,
                    },
                    "Reminder preference",
                  ),
                options: [
                  { labelText: "Off", value: "off" },
                  {
                    labelText: `${reminderWakeTime} reminders`,
                    value: reminderWakeTime,
                  },
                ],
                value: reminderScheduleValue,
              })}
            </View>
            <View
              style={[
                styles.detailBodyCard,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Access recovery
              </Text>
              <View
                style={[
                  styles.patientRowFooter,
                  {
                    alignItems: "center",
                    gap: adminModalDensity.actionGap,
                    justifyContent: "space-between",
                  },
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      flex: 1,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminModalDensity.bodyFontSize,
                      lineHeight: adminModalDensity.bodyLineHeight,
                    },
                  ]}
                >
                  Use only when the patient cannot access their portal.
                </Text>
                {renderAdminPrimaryButton({
                  density: "compact",
                  labelText: "Reset auth",
                  onPress: () =>
                    runAdminPatientPortalControl(
                      row,
                      {
                        actionKind: "reset-auth",
                        notes:
                          "Portal access was reset from the patient workspace.",
                      },
                      "Portal auth reset",
                    ),
                  prefixText: "",
                  tone: "danger",
                })}
              </View>
            </View>
          </View>

          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Audit details
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Support-only references are kept here so the primary workspace can
              stay focused on care and access decisions.
            </Text>
            <View style={[styles.listGroup, { gap: tokens.spacing.xs }]}>
              {patientAuditRows.map((item) => (
                <View
                  key={item.labelText}
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "flex-start",
                      borderTopColor: theme.borderSubtle,
                      borderTopWidth:
                        item.labelText === "Patient reference" ? 0 : 1,
                      gap: tokens.spacing.md,
                      justifyContent: "space-between",
                      paddingTop:
                        item.labelText === "Patient reference"
                          ? 0
                          : tokens.spacing.xs,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminModalDensity.metaLabelFontSize,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    {item.labelText}
                  </Text>
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminModalDensity.bodyFontSize,
                        lineHeight: adminModalDensity.bodyLineHeight,
                        textAlign: "right",
                      },
                    ]}
                  >
                    {item.valueText}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>
    );
  };

  const renderAdminPatientDetailModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: selectedAdminPatientRow
        ? `${selectedAdminPatientRow.displayName} patient workspace`
        : "Patient workspace",
      children: selectedAdminPatientRow
        ? renderAdminPatientDetailPanel(selectedAdminPatientRow)
        : null,
      contentPadding: 0,
      maxWidth: isWideLayout ? 1120 : 980,
      onClose: () => setAdminSelectedPatientRowId(null),
      rail: selectedAdminPatientRow
        ? renderAdminModalRail({
            avatarText: resolvePatientInitials(selectedAdminPatientRow.displayName),
            eyebrowText: "Patient",
            glanceRows: [
              {
                labelText: "Documents",
                valueText: String(selectedAdminPatientRow.pendingDocumentCount),
              },
              {
                labelText: "Unread",
                valueText: String(selectedAdminPatientRow.unreadMessageCount),
              },
              isAdminPatientArchived(selectedAdminPatientRow)
                ? {
                    labelText: "Status",
                    valueText: "Archived",
                  }
                : selectedAdminPatientRow.requiresAttention
                ? {
                    labelText: "Attention",
                    valueText: "Needs attention",
                  }
                : {
                    labelText: "Status",
                    valueText: selectedAdminPatientRow.statusLabel,
                  },
            ],
            overviewRows: [
              {
                labelText: "Identifier",
                valueText:
                  selectedAdminPatientRow.identifier ?? "No identifier",
              },
              {
                labelText: "Phone",
                valueText:
                  selectedAdminPatientRow.phoneNumber ?? "No phone on record",
              },
              {
                labelText: "Leading clinician",
                valueText:
                  selectedAdminPatientRow.leadingClinicianLabel ??
                  "No clinician assigned",
              },
              {
                labelText: "Contact window",
                valueText:
                  selectedAdminPatientRow.contactWindowText ??
                  "No window recorded",
              },
            ],
            pills: [
              isAdminPatientArchived(selectedAdminPatientRow)
                ? "Archived"
                : selectedAdminPatientRow.requiresAttention
                ? "Needs attention"
                : selectedAdminPatientRow.statusLabel,
              selectedAdminPatientRow.portalAuthStatus,
            ],
            subtitleText:
              selectedAdminPatientRow.identifier ??
              selectedAdminPatientRow.statusLabel,
            titleText: selectedAdminPatientRow.displayName,
          })
        : null,
      visible: selectedAdminPatientRow != null,
    });

  const renderAdminPatientsMobileList = () =>
    previewPatientPageRows.length > 0 ? (
      <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
        {previewPatientPageRows.map((row) => {
          const lastUpdatedText = row.lastUpdatedAt
            ? formatAdminSummaryTimestamp(row.lastUpdatedAt)
            : "No update";
          const statusText = isAdminPatientArchived(row)
            ? "Archived"
            : row.requiresAttention
              ? "Needs attention"
              : row.statusLabel;

          return renderAdminMobileRecordCard({
            actions: renderAdminTableActionButton({
              labelText: "View",
              onPress: () => {
                setAdminSelectedPatientRowId(row.id);
                setAdminOpsNotice(
                  `Patient detail workspace opened for ${row.displayName}.`,
                );
              },
              tone: "surface",
              width: 94,
            }),
            badge: renderAdminStatusPill(statusText, { minWidth: 86 }),
            cardKey: row.id,
            metaRows: [
              renderAdminMobileMetaRow(
                "Identifier",
                row.identifier ?? "No patient identifier",
              ),
              renderAdminMobileMetaRow(
                "Clinician",
                row.leadingClinicianLabel ?? "No clinician assigned",
              ),
              renderAdminMobileMetaRow("Last updated", lastUpdatedText),
              renderAdminMobileMetaRow("Care type", row.careTypeLabel),
            ],
            subtitleText: row.phoneNumber ?? "No phone on record",
            titleText: row.displayName,
          });
        })}
      </View>
    ) : (
      <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          No patients match the current admin filters.
        </Text>
      </View>
    );

  const renderAdminPatientsSurface = () => (
    <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View
          style={{
            flex: isMediumLayout ? 1 : undefined,
            gap: 3,
            minWidth: 0,
            width: isMediumLayout ? undefined : "100%",
          }}
        >
          <Text
            style={[
              styles.title,
              {
                ...adminCompactTitleStyle,
              },
            ]}
          >
            Patients
          </Text>
          <Text
            numberOfLines={1}
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                fontWeight: adminTypography.labelWeight,
                lineHeight: 16,
              },
            ]}
          >
            {adminPatientQuickFilter === "archived"
              ? `${previewPatientArchivedCount} archived patient${previewPatientArchivedCount === 1 ? "" : "s"}`
              : `${adminPatientOperationsTotalCount} total · ${previewPatientAttentionCount} need attention`}
          </Text>
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              gap: tokens.spacing.sm,
              justifyContent: isMediumLayout ? "flex-end" : "flex-start",
              marginLeft: isMediumLayout ? "auto" : 0,
            },
          ]}
        >
          {renderAdminPrimaryButton({
            density: "compact",
            labelText: "Create patient",
            onPress: runCreatePatientCommand,
            width: isMediumLayout ? 148 : undefined,
          })}
          {renderAdminPrimaryButton({
            density: "compact",
            labelText: "Import CSV",
            onPress: runImportPatientsCsvCommand,
            prefixText: "",
            tone: "surface",
            width: isMediumLayout ? 136 : undefined,
          })}
        </View>
      </View>

      <View
        style={[
          styles.flagRow,
          {
            alignItems: "center",
            gap: 8,
          },
        ]}
      >
        {renderAdminOptionStatusCard({
          accentColor: adminFigmaPalette.accent,
          countText: String(adminPatientOperationsTotalCount),
          isSelected: adminPatientQuickFilter === "all",
          labelText: "All",
          onPress: () => selectAdminPatientQuickFilter("all"),
        })}
        {renderAdminOptionStatusCard({
          accentColor: theme.warning,
          countText: String(previewPatientAttentionCount),
          isSelected: adminPatientQuickFilter === "attention",
          labelText: "Needs attention",
          onPress: () => selectAdminPatientQuickFilter("attention"),
        })}
        {renderAdminOptionStatusCard({
          accentColor: theme.success,
          countText: String(previewPatientActiveCount),
          isSelected: adminPatientQuickFilter === "active",
          labelText: "Active",
          onPress: () => selectAdminPatientQuickFilter("active"),
        })}
        {renderAdminOptionStatusCard({
          accentColor: theme.textTertiary,
          countText: String(previewPatientInactiveCount),
          isSelected: adminPatientQuickFilter === "inactive",
          labelText: "Inactive",
          onPress: () => selectAdminPatientQuickFilter("inactive"),
        })}
        {renderAdminOptionStatusCard({
          accentColor: theme.textSecondary,
          countText:
            adminPatientQuickFilter === "archived"
              ? String(previewPatientArchivedCount)
              : "View",
          isSelected: adminPatientQuickFilter === "archived",
          labelText: "Archived",
          onPress: () => selectAdminPatientQuickFilter("archived"),
        })}
        <View style={{ flex: isMediumLayout ? 1 : 0, minWidth: isMediumLayout ? 20 : 0 }} />
        <View
          style={[
            styles.searchSurface,
            {
              ...adminSearchSurfaceStyle,
              alignItems: "center",
              flexDirection: "row",
              gap: tokens.spacing.xs,
              minWidth: isMediumLayout ? 190 : undefined,
              ...adminCompactSearchPadding,
              width: isMediumLayout ? 230 : "100%",
            },
          ]}
        >
          <LineIcon
            color={theme.textTertiary}
            name="search"
            size={14}
            strokeWidth={2.25}
          />
          <TextInput
            onChangeText={setAdminSearchQuery}
            placeholder="Search patients..."
            placeholderTextColor={theme.textTertiary}
            style={[
              styles.searchInput,
              adminSearchInputTextStyle,
            ]}
            value={adminSearchQuery}
          />
        </View>
        {renderAdminOptionSelectControl({
          accessibilityLabel: "Filter patients by status",
          minWidth: 126,
          onChange: (value) =>
            selectAdminPatientQuickFilter(value as AdminPatientQuickFilter),
          options: adminPatientQuickFilterOptions,
          value: adminPatientQuickFilter,
        })}
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              gap: 8,
              marginLeft: isMediumLayout ? "auto" : 0,
            },
          ]}
        >
          <Text
            style={[
              styles.metricLabel,
              {
                color: adminFigmaPalette.inkMuted,
                fontFamily: layout.bodyFontFamily,
                fontSize: 11,
                fontWeight: adminTypography.labelWeight,
              },
            ]}
          >
            Sort: {adminPatientSortKey === "access" ? "Needs attention" : "Name"}
          </Text>
          {renderAdminOptionIconButton({
            iconName: "document",
            labelText: "Sort patient rows",
            onPress: () =>
              setAdminPatientSortKey((current) =>
                current === "name"
                  ? "access"
                  : "name",
              ),
          })}
        </View>
      </View>

      {adminPatientOperationsStatusText ? (
        <Text
          style={[
            styles.metricLabel,
            {
              color: hasAdminPatientOperationsError
                ? theme.warning
                : theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: 12,
              fontWeight: adminTypography.labelWeight,
            },
          ]}
        >
          {adminPatientOperationsStatusText}
        </Text>
      ) : null}

      {isAdminMobileLayout ? (
        renderAdminPatientsMobileList()
      ) : (
      <ScrollView
        horizontal={!isMediumLayout}
        showsHorizontalScrollIndicator={!isMediumLayout}
        style={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}
        contentContainerStyle={{
          minWidth: isMediumLayout ? "100%" : adminPatientTableMinWidth,
        }}
      >
        <View
          style={[
            styles.patientDocumentTable,
            adminTableShellStyle,
            {
              minWidth: isMediumLayout ? undefined : adminPatientTableMinWidth,
              width: "100%",
            },
          ]}
        >
          <View
            style={[
              styles.patientDocumentTableHeader,
              adminTableHeaderStyle,
            ]}
          >
            {[
              { labelText: "Patient", flexValue: 1.45 },
              { labelText: "Identifier", flexValue: 1 },
              { labelText: "Leading clinician", flexValue: 1.25 },
              { labelText: "Status", flexValue: 0.9 },
              { labelText: "Last updated", flexValue: 1.05 },
              { labelText: "Actions", flexValue: 0.75 },
            ].map((item) => (
              <Text
                key={item.labelText}
                style={[
                  styles.metricLabel,
                  {
                    flex: item.flexValue,
                    ...adminTableHeaderTextStyle,
                  },
                ]}
              >
                {item.labelText}
              </Text>
            ))}
          </View>

          {previewPatientPageRows.length > 0 ? (
            previewPatientPageRows.map((row, index) => {
              const isSelected = selectedAdminPatientRow?.id === row.id;
              const lastUpdatedText = row.lastUpdatedAt
                ? formatAdminSummaryTimestamp(row.lastUpdatedAt)
                : "No update";
              const isArchived = isAdminPatientArchived(row);
              const statusText = isArchived
                ? "Archived"
                : row.requiresAttention
                  ? "Needs attention"
                  : row.statusLabel;
              const statusPalette = isArchived
                ? {
                    backgroundColor: theme.surfaceMuted,
                    borderColor: theme.borderStrong,
                    textColor: theme.textSecondary,
                  }
                : row.requiresAttention
                ? {
                    backgroundColor: theme.statusChip.warning.background,
                    borderColor: theme.statusChip.warning.border,
                    textColor: theme.statusChip.warning.text,
                  }
                : row.statusLabel.toLowerCase() === "inactive"
                  ? {
                      backgroundColor: theme.surfaceMuted,
                      borderColor: theme.borderSubtle,
                      textColor: theme.textSecondary,
                    }
                  : {
                      backgroundColor: theme.statusChip.success.background,
                      borderColor: theme.statusChip.success.border,
                      textColor: theme.statusChip.success.text,
                    };

            return (
              <Pressable
                key={row.id}
                onPress={() => {
                  setAdminSelectedPatientRowId(row.id);
                  setAdminOpsNotice(
                    `Patient detail workspace opened for ${row.displayName}.`,
                  );
                }}
                style={(state) =>
                  buildAdminInteractiveTableRowStyle(index, isSelected, state)
                }
              >
                <View
                  style={[
                    styles.patientRowHeader,
                    { alignItems: "center", flex: 1.45, gap: 8, minWidth: 0 },
                  ]}
                >
                  <View
                    style={[
                      styles.patientAvatar,
                      {
                        backgroundColor: adminFigmaPalette.infoSoft,
                        borderColor: adminFigmaPalette.border,
                        borderRadius: 14,
                        height: 28,
                        width: 28,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.patientAvatarText,
                        {
                          color: adminFigmaPalette.accent,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: adminTypography.strongWeight,
                        },
                      ]}
                    >
                      {resolvePatientInitials(row.displayName)}
                    </Text>
                  </View>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminCompact.rowTitleFontSize,
                        fontWeight: adminTypography.strongWeight,
                        lineHeight: adminCompact.rowTitleLineHeight,
                      },
                    ]}
                  >
                    {row.displayName}
                  </Text>
                </View>
                <View
                  style={[
                    styles.patientDocumentTableCell,
                    { flex: 1, gap: 2 },
                  ]}
                >
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: row.phoneNumber
                          ? theme.textPrimary
                          : theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: adminTypography.bodyWeight,
                      },
                    ]}
                  >
                    {row.phoneNumber ?? "No phone"}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        fontWeight: adminTypography.bodyWeight,
                      },
                    ]}
                  >
                    {row.identifier}
                  </Text>
                </View>
                <View style={[styles.patientDocumentTableCell, { flex: 1.25 }]}>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: adminTypography.bodyWeight,
                      },
                    ]}
                  >
                    {row.leadingClinicianLabel ?? "No clinician assigned"}
                  </Text>
                </View>
                <View style={[styles.patientDocumentTableCell, { flex: 0.9 }]}>
                  <View
                    style={[
                      styles.inlinePill,
                      {
                        alignItems: "center",
                        backgroundColor: statusPalette.backgroundColor,
                        borderColor: statusPalette.borderColor,
                        borderRadius: layout.controlRadius,
                        flexDirection: "row",
                        gap: 6,
                        minWidth: 72,
                        paddingHorizontal: 10,
                        paddingVertical: tokens.spacing.xs,
                      },
                    ]}
                  >
                    <View
                      style={{
                        backgroundColor: statusPalette.textColor,
                        borderRadius: 999,
                        height: 5,
                        width: 5,
                      }}
                    />
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: statusPalette.textColor,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                          fontWeight: adminTypography.strongWeight,
                          textAlign: "center",
                        },
                      ]}
                    >
                      {statusText}
                    </Text>
                  </View>
                </View>
                <View style={[styles.patientDocumentTableCell, { flex: 1.05 }]}>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 12,
                        fontWeight: adminTypography.bodyWeight,
                      },
                    ]}
                  >
                    {lastUpdatedText}
                  </Text>
                  <Text
                    numberOfLines={1}
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.xs,
                        fontWeight: adminTypography.bodyWeight,
                      },
                    ]}
                  >
                    {row.careTypeLabel}
                  </Text>
                </View>
                <View
                  style={[
                    styles.patientRowFooter,
                    { flex: 0.75, gap: tokens.spacing.xs, justifyContent: "flex-end" },
                  ]}
                >
                  {renderAdminTableActionButton({
                    labelText: "View",
                    onPress: () => {
                      setAdminSelectedPatientRowId(row.id);
                      setAdminOpsNotice(
                        `Patient detail workspace opened for ${row.displayName}.`,
                      );
                    },
                    tone: "surface",
                    width: 74,
                  })}
                </View>
              </Pressable>
            );
            })
          ) : (
            <View
              style={[
                styles.detailBodyCard,
                adminTableEmptyStateStyle,
              ]}
            >
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.sm,
                    lineHeight: tokens.typography.lineHeight.md,
                  },
                ]}
              >
                No patients match the current admin filters.
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
      )}

      {adminPatientOperationsFilteredTotalCount > 0 ? (
        renderAdminOptionPagination({
          currentPageIndex: adminPatientPageIndex,
          itemLabel: "patients",
          onNext: () =>
            setAdminPatientPageIndex((current) =>
              Math.min(
                Math.max(0, adminPatientOperationsPageCount - 1),
                current + 1,
              ),
            ),
          onPrevious: () =>
            setAdminPatientPageIndex((current) => Math.max(0, current - 1)),
          pageCount: adminPatientOperationsPageCount,
          totalCount: adminPatientOperationsFilteredTotalCount,
          visibleCount: previewPatientPageRows.length,
        })
      ) : null}

    </View>
  );

  const renderAdminCliniciansMobileList = () =>
    previewClinicianPageRows.length > 0 ? (
      <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
        {previewClinicianPageRows.map((row) =>
          renderAdminMobileRecordCard({
            actions: renderAdminTableActionButton({
              labelText: "Edit",
              onPress: () => runUpdateClinicianCommand(row),
              tone: "primary",
              width: 94,
            }),
            badge: renderAdminStatusPill(
              row.statusKey === "inactive" ? "retired" : row.statusKey,
              { minWidth: 76 },
            ),
            cardKey: row.id,
            metaRows: [
              ...(row.isProtectedOwner
                ? [
                    renderAdminMobileMetaRow(
                      "Access",
                      row.protectedOwnerLabel ?? "Platform owner",
                    ),
                  ]
                : []),
              renderAdminMobileMetaRow(
                "Patients",
                String(row.assignedPatientCount),
              ),
              renderAdminMobileMetaRow("Department", row.departmentName),
              renderAdminMobileMetaRow("Team", row.teamName),
              renderAdminMobileMetaRow("Contact", row.phoneText),
            ],
            subtitleText: row.isProtectedOwner
              ? `Protected ${(row.protectedOwnerLabel ?? "platform owner").toLowerCase()}`
              : row.roleLabel,
            titleText: row.legacyDisplayName,
          }),
        )}
      </View>
    ) : (
      <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
              lineHeight: tokens.typography.lineHeight.md,
            },
          ]}
        >
          No clinicians match the current admin filters.
        </Text>
      </View>
    );

  const renderAdminCliniciansSurface = () => (
    <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View
          style={{
            flex: isMediumLayout ? 1 : undefined,
            gap: 3,
            minWidth: 0,
            width: isMediumLayout ? undefined : "100%",
          }}
        >
          <Text
            style={[
              styles.title,
              {
                ...adminCompactTitleStyle,
              },
            ]}
          >
            Clinicians
          </Text>
          <Text
            numberOfLines={1}
            style={[
              styles.sectionSubtitle,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: 12,
                fontWeight: adminTypography.labelWeight,
                lineHeight: 16,
              },
            ]}
          >
            {`${adminClinicianOperationsTotalCount} total · ${clinicianSiteCount} site scopes`}
          </Text>
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "center",
              flexDirection: isMediumLayout ? "row" : "column",
              flexWrap: "wrap",
              gap: tokens.spacing.xs,
              justifyContent: isMediumLayout ? "flex-end" : "flex-start",
              marginLeft: isMediumLayout ? "auto" : 0,
              maxWidth: isMediumLayout ? 540 : undefined,
              minWidth: 0,
              width: isMediumLayout ? undefined : "100%",
            },
          ]}
        >
          {renderAdminPrimaryButton({
            density: "compact",
            labelText: "Create clinician",
            onPress: runCreateClinicianCommand,
            width: isMediumLayout ? 152 : undefined,
          })}

          <View
            style={[
              styles.searchSurface,
              {
                ...adminSearchSurfaceStyle,
                alignItems: "center",
                flex: isMediumLayout ? 1 : undefined,
                flexDirection: "row",
                gap: tokens.spacing.xs,
                maxWidth: isMediumLayout ? 300 : undefined,
                minWidth: isMediumLayout ? 220 : undefined,
                ...adminCompactSearchPadding,
                width: "100%",
              },
            ]}
          >
            <TextInput
              onChangeText={setAdminSearchQuery}
              placeholder="Enter clinician name..."
              placeholderTextColor={theme.textTertiary}
              style={[
                styles.searchInput,
                adminSearchInputTextStyle,
              ]}
              value={adminSearchQuery}
            />
            <LineIcon
              color={theme.textSecondary}
              name="search"
              size={15}
              strokeWidth={2.2}
            />
          </View>
        </View>
      </View>

      <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
        {[
          ["name", "Sort name"],
                ["department", "Sort department"],
                ["role", "Sort role"],
              ].map(([value, labelText]) =>
          renderWorkspaceFilterChip({
            isSelected: adminClinicianSortKey === value,
            labelText,
            onPress: () =>
              setAdminClinicianSortKey(
                value as "name" | "department" | "role",
              ),
            theme,
            tokens,
                  }),
                )}
                {renderWorkspaceFilterChip({
                  isSelected: adminShowInactiveClinicians,
                  labelText: "Include inactive",
                  onPress: () =>
                    setAdminShowInactiveClinicians((current) => !current),
                  theme,
                  tokens,
                })}
              </View>

      {adminClinicianOperationsStatusText ? (
        <Text
          style={[
            styles.metricLabel,
            {
              color: hasAdminClinicianOperationsError
                ? theme.warning
                : theme.textSecondary,
              fontFamily: layout.bodyFontFamily,
              fontSize: tokens.typography.fontSize.sm,
            },
          ]}
        >
          {adminClinicianOperationsStatusText}
        </Text>
      ) : null}

      {isAdminMobileLayout ? (
        renderAdminCliniciansMobileList()
      ) : (
      <ScrollView
        horizontal={!isMediumLayout}
        showsHorizontalScrollIndicator={!isMediumLayout}
        style={{ maxWidth: "100%", overflow: "hidden", width: "100%" }}
        contentContainerStyle={{
          minWidth: isMediumLayout ? "100%" : adminClinicianTableMinWidth,
        }}
      >
        <View
          style={[
            styles.patientDocumentTable,
            adminTableShellStyle,
            {
              minWidth: isMediumLayout ? undefined : adminClinicianTableMinWidth,
              width: "100%",
            },
          ]}
        >
          <View
            style={[
              styles.patientDocumentTableHeader,
              adminTableHeaderStyle,
            ]}
          >
            {[
              { labelText: "Status", flexValue: 0.7 },
              { labelText: "Clinician", flexValue: 1.7 },
              { labelText: "Patients", flexValue: 0.7 },
              { labelText: "Department", flexValue: 1.15 },
              { labelText: "Team", flexValue: 1.1 },
              { labelText: "Contact", flexValue: 1.45 },
              { labelText: "Actions", flexValue: 1 },
          ].map((item) => (
            <Text
              key={item.labelText}
              style={[
                styles.metricLabel,
                {
                  flex: item.flexValue,
                  ...adminTableHeaderTextStyle,
                },
              ]}
            >
              {item.labelText}
            </Text>
          ))}
          </View>

        {previewClinicianPageRows.length > 0 ? (
          previewClinicianPageRows.map((row, index) => (
            <View
              key={row.id}
              style={[
                styles.patientDocumentTableRow,
                buildAdminTableRowStateStyle(index),
              ]}
            >
              <View style={[styles.patientDocumentTableCell, { flex: 0.7 }]}>
                {renderAdminStatusPill(
                  row.statusKey === "inactive" ? "retired" : row.statusKey,
                  { minWidth: 64 },
                )}
              </View>
              <View
                style={[
                  styles.patientDocumentTableCell,
                  { flex: 1.7, gap: 2 },
                ]}
              >
                <View
                  style={{
                    alignItems: "center",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: tokens.spacing.xs,
                    minWidth: 0,
                  }}
                >
                  <Text
                    numberOfLines={2}
                    style={[
                      styles.sectionTitle,
                      {
                        color: theme.textPrimary,
                        fontFamily: layout.displayFontFamily,
                        fontSize: adminCompact.rowTitleFontSize,
                        flexShrink: 1,
                        lineHeight: adminCompact.rowTitleLineHeight,
                        minWidth: 0,
                      },
                    ]}
                  >
                    {row.legacyDisplayName}
                  </Text>
                  {row.isProtectedOwner
                    ? renderAdminProtectedOwnerPill(row.protectedOwnerLabel)
                    : null}
                </View>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminCompact.bodyFontSize,
                    },
                  ]}
                >
                  {row.roleLabel}
                </Text>
              </View>
              <View style={[styles.patientDocumentTableCell, { flex: 0.7 }]}>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: adminCompact.rowTitleFontSize,
                      lineHeight: adminCompact.rowTitleLineHeight,
                    },
                  ]}
                >
                  {String(row.assignedPatientCount)}
                </Text>
              </View>
              <View style={[styles.patientDocumentTableCell, { flex: 1.15 }]}>
                <View
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: adminDepartmentPalette.backgroundColor,
                      borderColor: adminDepartmentPalette.borderColor,
                      borderRadius: layout.controlRadius,
                      minWidth: 72,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: adminDepartmentPalette.textColor,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminCompact.bodyFontSize,
                        maxWidth: 104,
                        textAlign: "center",
                      },
                    ]}
                  >
                    {row.departmentName}
                  </Text>
                </View>
              </View>
              <View style={[styles.patientDocumentTableCell, { flex: 1.1 }]}>
                <View
                  style={[
                    styles.inlinePill,
                    {
                      backgroundColor: row.teamPalette.backgroundColor,
                      borderColor: row.teamPalette.borderColor,
                      borderRadius: layout.controlRadius,
                      minWidth: 72,
                      paddingHorizontal: 8,
                      paddingVertical: 4,
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.navigationText,
                      {
                        color: row.teamPalette.textColor,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: adminCompact.bodyFontSize,
                        maxWidth: 104,
                        textAlign: "center",
                      },
                    ]}
                  >
                    {row.teamName}
                  </Text>
                </View>
              </View>
              <View
                style={[
                  styles.patientDocumentTableCell,
                  { flex: 1.45, gap: 2 },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminCompact.bodyFontSize,
                    },
                  ]}
                >
                  {row.phoneText}
                </Text>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.metricLabel,
                    {
                      color:
                        row.emailAddressText === "No email on record"
                          ? theme.textSecondary
                          : theme.textPrimary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: adminCompact.bodyFontSize,
                    },
                  ]}
                >
                  {row.emailAddressText}
                </Text>
              </View>
              <View
                style={[
                  styles.patientRowFooter,
                  {
                    flex: 1,
                    flexWrap: "nowrap",
                    gap: tokens.spacing.xs,
                    justifyContent: "flex-start",
                  },
                ]}
              >
                {renderAdminTableActionButton({
                  labelText: "Edit",
                  onPress: () => runUpdateClinicianCommand(row),
                  tone: "primary",
                  width: adminTableActionButtonWidth,
                })}
              </View>
            </View>
          ))
        ) : (
          <View
            style={[
              styles.detailBodyCard,
              adminTableEmptyStateStyle,
            ]}
          >
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              No clinicians match the current admin filters.
            </Text>
          </View>
        )}
      </View>
      </ScrollView>
      )}

      {adminClinicianOperationsFilteredTotalCount >
      adminClinicianOperationsPageSize ? (
        <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Prev",
            onPress: () =>
              setAdminClinicianPageIndex((current) => Math.max(0, current - 1)),
            theme,
            tokens,
          })}
          <Text
            style={[
              styles.metricLabel,
              {
                color: theme.textSecondary,
                fontFamily: layout.bodyFontFamily,
                fontSize: tokens.typography.fontSize.xs,
              },
            ]}
          >
            {`Page ${adminClinicianPageIndex + 1} of ${adminClinicianOperationsPageCount}`}
          </Text>
          {renderWorkspaceFilterChip({
            isSelected: false,
            labelText: "Next",
            onPress: () =>
              setAdminClinicianPageIndex((current) =>
                Math.min(
                  Math.max(0, adminClinicianOperationsPageCount - 1),
                  current + 1,
                ),
              ),
            theme,
            tokens,
          })}
        </View>
      ) : null}
    </View>
  );

  const renderAdminDepartmentsSurface = () => (
    <View
      style={[
        styles.workspaceGrid,
        {
          flexDirection: usesAdminCommandRailLayout ? "row" : "column",
          gap: adminCompact.sectionGap,
          minWidth: 0,
        },
      ]}
    >
      {[
        {
          actionLabelText: "Create department",
          items: previewDepartmentRows,
          titleText: "Departments",
          tone: "department" as const,
        },
        {
          actionLabelText: "Create team",
          items: previewTeamRows,
          titleText: "Teams",
          tone: "team" as const,
        },
      ].map((group) => (
        <View
          key={group.titleText}
          style={[
            styles.listGroup,
            {
              flex: 1,
              gap: adminCompact.tableGap,
              minWidth: 0,
            },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: isAdminMobileLayout ? "stretch" : "center",
                flexDirection: isAdminMobileLayout ? "column" : "row",
                flexWrap: "wrap",
                gap: tokens.spacing.md,
              },
            ]}
          >
            <Text
              style={[
                styles.title,
                {
                  color: theme.textPrimary,
                  flex: 1,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminCompact.headingFontSize,
                  lineHeight: adminCompact.headingLineHeight,
                  marginTop: 0,
                  minWidth: isAdminMobileLayout ? 0 : 180,
                },
              ]}
            >
              {group.titleText}
            </Text>
            {renderAdminPrimaryButton({
              labelText: group.actionLabelText,
              onPress:
                group.tone === "department"
                  ? runCreateDepartmentCommand
                  : runCreateTeamCommand,
              stretch: isAdminMobileLayout,
              width: adminToolbarActionButtonWidth,
            })}
          </View>

          {isAdminMobileLayout ? (
            group.items.length > 0 ? (
              <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
                {group.items.map((item) =>
                  renderAdminMobileRecordCard({
                    actions: (
                      <>
                        {renderAdminTableActionButton({
                          labelText: "Edit",
                          onPress: () =>
                            group.tone === "department"
                              ? runUpdateDepartmentCommand(item)
                              : runUpdateTeamCommand(item),
                          tone: "primary",
                          width: 94,
                        })}
                        {renderAdminTableActionButton({
                          labelText: "Retire",
                          onPress: () =>
                            group.tone === "department"
                              ? runRetireDepartmentCommand(item)
                              : runRetireTeamCommand(item),
                          tone: "danger",
                          width: 94,
                        })}
                      </>
                    ),
                    badge: renderAdminStatusPill(item.status),
                    cardKey: `${group.tone}-${item.name}`,
                    metaRows: [
                      renderAdminMobileMetaRow(
                        "Clinicians",
                        String(item.clinicianCount),
                      ),
                    ],
                    titleText: item.name,
                  }),
                )}
              </View>
            ) : (
              <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {`No ${group.tone === "department" ? "department" : "team"} records are available yet.`}
                </Text>
              </View>
            )
          ) : (
            <View
              style={[
                styles.patientDocumentTable,
                adminTableShellStyle,
              ]}
            >
            <View
              style={[
                styles.patientDocumentTableHeader,
                adminTableHeaderStyle,
              ]}
            >
              {[
                { labelText: "Name", flexValue: 1.55 },
                { labelText: "Status", flexValue: 0.85 },
                { labelText: "Clinicians", flexValue: 0.6 },
                { labelText: "Actions", flexValue: 1.9 },
              ].map((item) => (
                <Text
                  key={item.labelText}
                  style={[
                    styles.metricLabel,
                    {
                      flex: item.flexValue,
                      ...adminTableHeaderTextStyle,
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
              ))}
            </View>

            {group.items.length > 0 ? (
              group.items.map((item, index) => {
                const palette =
                  group.tone === "department"
                    ? adminDepartmentPalette
                    : "palette" in item
                      ? item.palette
                      : adminDepartmentPalette;

                return (
                  <View
                    key={item.name}
                    style={[
                      styles.patientDocumentTableRow,
                      buildAdminTableRowStateStyle(index),
                      {
                        alignItems: "center",
                      },
                    ]}
                  >
                    <View
                      style={[styles.patientDocumentTableCell, { flex: 1.55 }]}
                    >
                      <Text
                        numberOfLines={2}
                        style={[
                          styles.sectionTitle,
                          {
                            color: palette.textColor,
                            fontFamily: layout.displayFontFamily,
                            fontSize: adminCompact.rowTitleFontSize,
                            lineHeight: adminCompact.rowTitleLineHeight,
                          },
                        ]}
                      >
                        {item.name}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.patientDocumentTableCell,
                        {
                          flex: 0.85,
                        },
                      ]}
                    >
                      {renderAdminStatusPill(item.status, { minWidth: 68 })}
                    </View>
                    <Text
                      style={[
                        styles.sectionTitle,
                        {
                          color: theme.textPrimary,
                          flex: 0.6,
                          fontFamily: layout.displayFontFamily,
                          fontSize: adminCompact.rowTitleFontSize,
                          lineHeight: adminCompact.rowTitleLineHeight,
                        },
                      ]}
                    >
                      {String(item.clinicianCount)}
                    </Text>
                    <View
                      style={[
                        styles.patientRowFooter,
                        {
                          flex: 1.9,
                          flexWrap: "wrap",
                          gap: tokens.spacing.xs,
                          justifyContent: "flex-start",
                          minWidth: 0,
                        },
                      ]}
                    >
                      {renderAdminTableActionButton({
                        labelText: "Edit",
                        onPress: () =>
                          group.tone === "department"
                            ? runUpdateDepartmentCommand(item)
                            : runUpdateTeamCommand(item),
                        tone: "primary",
                        width: adminNarrowTableActionButtonWidth,
                      })}
                      {renderAdminTableActionButton({
                        labelText: "Retire",
                        onPress: () =>
                          group.tone === "department"
                            ? runRetireDepartmentCommand(item)
                            : runRetireTeamCommand(item),
                        tone: "danger",
                        width: adminNarrowTableActionButtonWidth,
                      })}
                    </View>
                  </View>
                );
              })
            ) : (
              <View
                style={[
                  styles.detailBodyCard,
                  adminTableEmptyStateStyle,
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                      lineHeight: tokens.typography.lineHeight.md,
                    },
                  ]}
                >
                  {`No ${group.tone === "department" ? "department" : "team"} records are available yet.`}
                </Text>
              </View>
            )}
            </View>
          )}
        </View>
      ))}
    </View>
  );

  const renderAdminProvisioningRunDetailPanel = (
    run: NonNullable<typeof selectedAdminProvisioningRun>,
  ) => {
    const startedTime = run.startedAt ? new Date(run.startedAt).getTime() : null;
    const completedTime = run.completedAt
      ? new Date(run.completedAt).getTime()
      : null;
    const hasDuration =
      typeof startedTime === "number" &&
      Number.isFinite(startedTime) &&
      typeof completedTime === "number" &&
      Number.isFinite(completedTime) &&
      completedTime >= startedTime;
    const durationText = hasDuration
      ? `${Math.max(1, Math.round((completedTime - startedTime) / 60000))} min`
      : run.completedAt == null &&
          (run.runStatus === "running" || run.runStatus === "provisioning")
        ? "Still running"
        : "Not available";
    const completedText = run.completedAt
      ? formatAdminSummaryTimestamp(run.completedAt)
      : run.runStatus === "running" || run.runStatus === "provisioning"
        ? "Still running"
        : "Not completed";
    const runStatusText = humanizeWorkspaceKey(run.runStatus);
    const statusHelperText =
      run.runStatus === "failed" || run.runStatus === "cancelled"
        ? "Review the target resources and request context before retrying."
        : run.runStatus === "running" || run.runStatus === "provisioning"
          ? "This run is still in progress."
          : "No action is needed unless related resources look out of date.";
    const targetRows = [
      { labelText: "Target", valueText: run.targetLabel },
      {
        labelText: "Database",
        valueText: run.databaseName ?? "No database attached",
      },
      {
        labelText: "Cluster",
        valueText: run.clusterIdentifier ?? "No cluster attached",
      },
      { labelText: "Environment", valueText: run.environmentKey },
    ];
    const requestRows = [
      { labelText: "Requested by", valueText: run.requestedBy ?? "System" },
      { labelText: "Created", valueText: formatAdminSummaryTimestamp(run.createdAt) },
      { labelText: "Run reference", valueText: run.runId },
    ];
    const renderProvisioningFactRows = (
      rows: ReadonlyArray<{ labelText: string; valueText: string }>,
    ) => (
      <View style={[styles.listGroup, { gap: 6 }]}>
        {rows.map((row) => (
          <View
            key={row.labelText}
            style={[
              styles.patientRowHeader,
              {
                borderTopColor: theme.borderSubtle,
                borderTopWidth: 1,
                gap: tokens.spacing.sm,
                paddingTop: 6,
              },
            ]}
          >
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.metaLabelFontSize,
                  textTransform: "uppercase",
                },
              ]}
            >
              {row.labelText}
            </Text>
            <Text
              numberOfLines={2}
              style={[
                styles.navigationText,
                {
                  color: theme.textPrimary,
                  flexShrink: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.metaValueFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                  textAlign: "right",
                },
              ]}
            >
              {row.valueText}
            </Text>
          </View>
        ))}
      </View>
    );

    return (
      <View
        style={[
          styles.detailBodyCard,
          {
            backgroundColor: theme.surface,
            borderColor: theme.borderStrong,
            borderRadius: adminOperationalLayout.panelRadius,
            borderWidth: 0,
            gap: tokens.spacing.md,
            padding: adminModalDensity.cardPadding,
          },
        ]}
      >
        {renderAdminModalHeader({
          actions: renderAdminPrimaryButton({
            density: "compact",
            labelText: "Close",
            onPress: () => setAdminSelectedProvisioningRunId(null),
            prefixText: "",
            tone: "surface",
          }),
          eyebrowText: "Provisioning run",
          onClose: () => setAdminSelectedProvisioningRunId(null),
          subtitleText: `${run.targetLabel} in ${run.environmentKey}. ${statusHelperText}`,
          titleText: humanizeWorkspaceKey(run.runKind),
        })}
        <View style={[styles.patientMetaGrid, { gap: tokens.spacing.sm }]}>
          {[
            {
              helperText: statusHelperText,
              labelText: "Run status",
              valueText: runStatusText,
            },
            {
              helperText: "When the run started.",
              labelText: "Started",
              valueText: formatAdminSummaryTimestamp(run.startedAt),
            },
            {
              helperText: "Completion state for this run.",
              labelText: "Completed",
              valueText: completedText,
            },
            {
              helperText: "Elapsed time when available.",
              labelText: "Duration",
              valueText: durationText,
            },
          ].map((item) => (
            <View
              key={item.labelText}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.surfaceElevated,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  flexBasis: isWideLayout ? 180 : 156,
                  gap: tokens.spacing.xs,
                  padding: adminModalDensity.cardPadding,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaValueFontSize,
                    lineHeight: adminModalDensity.bodyLineHeight,
                  },
                ]}
              >
                {item.valueText}
              </Text>
              <Text
                numberOfLines={2}
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: adminModalDensity.metaLabelFontSize,
                    lineHeight: 15,
                  },
                ]}
              >
                {item.helperText}
              </Text>
            </View>
          ))}
        </View>

        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: "stretch",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: tokens.spacing.md,
            },
          ]}
        >
          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Target resources
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Confirm the tenant resources this run was expected to update.
            </Text>
            {renderProvisioningFactRows(targetRows)}
          </View>

          <View
            style={[
              styles.detailBodyCard,
              {
                backgroundColor: theme.surfaceElevated,
                borderColor: theme.borderSubtle,
                borderRadius: layout.controlRadius,
                flex: 1,
                gap: tokens.spacing.sm,
                padding: adminModalDensity.cardPadding,
              },
            ]}
          >
            <Text
              style={[
                styles.sectionTitle,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminModalDensity.sectionTitleFontSize,
                  lineHeight: adminModalDensity.sectionTitleLineHeight,
                },
              ]}
            >
              Request and audit
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: adminModalDensity.bodyFontSize,
                  lineHeight: adminModalDensity.bodyLineHeight,
                },
              ]}
            >
              Keep this context available for support without crowding the run
              summary.
            </Text>
            {renderProvisioningFactRows(requestRows)}
          </View>
        </View>
      </View>
    );
  };

  const renderAdminProvisioningRunDetailModal = () =>
    renderAdminModalFrame({
      accessibilityLabel: selectedAdminProvisioningRun
        ? `${selectedAdminProvisioningRun.runId} provisioning run`
        : "Provisioning run",
      children: selectedAdminProvisioningRun
        ? renderAdminProvisioningRunDetailPanel(selectedAdminProvisioningRun)
        : null,
      contentPadding: 0,
      maxWidth: 900,
      onClose: () => setAdminSelectedProvisioningRunId(null),
      rail: selectedAdminProvisioningRun
        ? renderAdminModalRail({
            avatarText: "RUN",
            eyebrowText: "Provisioning",
            glanceRows: [
              {
                labelText: "Started",
                valueText: formatAdminSummaryTimestamp(
                  selectedAdminProvisioningRun.startedAt,
                ),
              },
              {
                labelText: "Completed",
                valueText: formatAdminSummaryTimestamp(
                  selectedAdminProvisioningRun.completedAt,
                ),
              },
              {
                labelText: "Created",
                valueText: formatAdminSummaryTimestamp(
                  selectedAdminProvisioningRun.createdAt,
                ),
              },
            ],
            overviewRows: [
              {
                labelText: "Target",
                valueText: selectedAdminProvisioningRun.targetLabel,
              },
              {
                labelText: "Environment",
                valueText: selectedAdminProvisioningRun.environmentKey,
              },
              {
                labelText: "Database",
                valueText:
                  selectedAdminProvisioningRun.databaseName ?? "No database",
              },
              {
                labelText: "Cluster",
                valueText:
                  selectedAdminProvisioningRun.clusterIdentifier ??
                  "No cluster",
              },
            ],
            pills: [
              humanizeWorkspaceKey(selectedAdminProvisioningRun.runStatus),
            ],
            subtitleText: "Run status and target resources.",
            titleText: humanizeWorkspaceKey(selectedAdminProvisioningRun.runKind),
          })
        : null,
      visible: selectedAdminProvisioningRun != null,
    });

  const renderAdminProvisioningSurface = () => {
    const statusColor = (status: string) =>
      status === "failed" || status === "cancelled"
        ? theme.danger
        : status === "running" ||
            status === "provisioning" ||
            status === "planned"
          ? theme.warning
          : status === "succeeded" || status === "active"
            ? theme.info
            : theme.textSecondary;
    const showsRuns =
      adminProvisioningViewFilter === "all" ||
      adminProvisioningViewFilter === "runs" ||
      adminProvisioningViewFilter === "failed";
    const showsAllocations =
      adminProvisioningViewFilter === "all" ||
      adminProvisioningViewFilter === "allocations" ||
      adminProvisioningViewFilter === "failed";
    const showsClusters =
      adminProvisioningViewFilter === "all" ||
      adminProvisioningViewFilter === "clusters" ||
      adminProvisioningViewFilter === "failed";

    return (
      <View style={[styles.listGroup, { gap: adminCompact.sectionGap }]}>
        <View
          style={[
            styles.patientRowHeader,
            {
              alignItems: isMediumLayout ? "center" : "flex-start",
              flexDirection: isMediumLayout ? "row" : "column",
              gap: tokens.spacing.md,
            },
          ]}
        >
          <View style={[styles.listGroup, { flex: 1, gap: tokens.spacing.xs }]}>
            <Text
              style={[
                styles.title,
                {
                  color: theme.textPrimary,
                  fontFamily: layout.displayFontFamily,
                  fontSize: adminCompact.headingFontSize,
                  lineHeight: adminCompact.headingLineHeight,
                  marginTop: 0,
                },
              ]}
            >
              Provisioning
            </Text>
            <Text
              style={[
                styles.sectionSubtitle,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.sm,
                  lineHeight: tokens.typography.lineHeight.md,
                },
              ]}
            >
              Review provisioning status, allocations, and active clusters.
            </Text>
          </View>

          <View
            style={[
              styles.searchSurface,
              {
                ...adminSearchSurfaceStyle,
                alignItems: "center",
                flexDirection: "row",
                gap: tokens.spacing.xs,
                maxWidth: 420,
                ...adminCompactSearchPadding,
                width: "100%",
              },
            ]}
          >
            <TextInput
              onChangeText={setAdminSearchQuery}
              placeholder="Search provisioning..."
              placeholderTextColor={theme.textTertiary}
              style={[
                styles.searchInput,
                adminSearchInputTextStyle,
              ]}
              value={adminSearchQuery}
            />
            <LineIcon
              color={theme.textSecondary}
              name="search"
              size={15}
              strokeWidth={2.2}
            />
          </View>
        </View>

        <View style={[styles.flagRow, { gap: tokens.spacing.xs }]}>
          {[
            ["all", "All"],
            ["runs", "Runs"],
            ["allocations", "Allocations"],
            ["clusters", "Clusters"],
            ["failed", "Failed only"],
          ].map(([value, labelText]) =>
            renderWorkspaceFilterChip({
              isSelected: adminProvisioningViewFilter === value,
              labelText,
              onPress: () =>
                setAdminProvisioningViewFilter(
                  value as
                    | "all"
                    | "runs"
                    | "allocations"
                    | "clusters"
                    | "failed",
                ),
              theme,
              tokens,
            }),
          )}
        </View>

        <View style={[styles.patientMetaGrid, { gap: tokens.spacing.sm }]}>
          {[
            {
              labelText: "Recent runs",
              valueText: String(summary?.provisioning.recentRunCount ?? 0),
            },
            {
              labelText: "Active allocations",
              valueText: String(
                summary?.provisioning.activeAllocationCount ?? 0,
              ),
            },
            {
              labelText: "Active clusters",
              valueText: String(summary?.provisioning.activeClusterCount ?? 0),
            },
            {
              labelText: "Failed rows",
              valueText: String(
                failedProvisioningRunCount +
                  failedDatabaseAllocationCount +
                  failedDatabaseClusterCount,
              ),
            },
            {
              labelText: "Latest run",
              valueText: latestProvisioningRun
                ? humanizeWorkspaceKey(latestProvisioningRun!.runStatus)
                : "No run",
            },
          ].map((item) => (
            <View
              key={item.labelText}
              style={[
                styles.patientMetaCard,
                {
                  backgroundColor: theme.surface,
                  borderColor: theme.borderSubtle,
                  borderRadius: layout.controlRadius,
                  gap: tokens.spacing.xs,
                  padding: tokens.spacing.sm,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: theme.textSecondary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.xs,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: theme.textPrimary,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: tokens.typography.fontSize.md,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>

        {showsRuns ? (
          isAdminMobileLayout ? (
            <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.accent,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: adminTypography.sectionLabelWeight,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Recent runs
              </Text>
              {provisioningRunPageRows.length > 0 ? (
                provisioningRunPageRows.map((run) =>
                  renderAdminMobileRecordCard({
                    actions: renderAdminTableActionButton({
                      labelText: "Open",
                      onPress: () => {
                        setAdminSelectedProvisioningRunId(run.runId);
                        setAdminOpsNotice(
                          `Provisioning run selected for ${run.targetLabel}.`,
                        );
                      },
                      tone: "surface",
                      width: adminTableActionButtonWidth,
                    }),
                    badge: renderAdminStatusPill(
                      humanizeWorkspaceKey(run.runStatus),
                    ),
                    cardKey: run.runId,
                    metaRows: [
                      renderAdminMobileMetaRow("Target", run.targetLabel),
                      renderAdminMobileMetaRow(
                        "Started",
                        formatAdminSummaryTimestamp(run.startedAt),
                      ),
                      renderAdminMobileMetaRow(
                        "Completed",
                        formatAdminSummaryTimestamp(run.completedAt),
                      ),
                    ],
                    subtitleText: run.environmentKey,
                    titleText: humanizeWorkspaceKey(run.runKind),
                  }),
                )
              ) : (
                <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    No provisioning runs match the current admin filters.
                  </Text>
                </View>
              )}
            </View>
          ) : (
          <View
            style={[
              styles.patientDocumentTable,
              adminTableShellStyle,
            ]}
          >
            <View
              style={[
                styles.patientDocumentTableHeader,
                adminTableHeaderStyle,
              ]}
            >
              {[
                { labelText: "Run", flexValue: 1.4 },
                { labelText: "Target", flexValue: 1.4 },
                { labelText: "Status", flexValue: 0.8 },
                { labelText: "Started", flexValue: 1 },
                { labelText: "Completed", flexValue: 1 },
              ].map((item) => (
                <Text
                  key={item.labelText}
                  style={[
                    styles.metricLabel,
                    {
                      flex: item.flexValue,
                      ...adminTableHeaderTextStyle,
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
              ))}
            </View>
            {provisioningRunPageRows.length > 0 ? (
              provisioningRunPageRows.map((run, index) => {
                const isSelected =
                  selectedAdminProvisioningRun?.runId === run.runId;

                return (
                  <Pressable
                    key={run.runId}
                    onPress={() => {
                      setAdminSelectedProvisioningRunId(run.runId);
                      setAdminOpsNotice(
                        `Provisioning run selected for ${run.targetLabel}.`,
                      );
                    }}
                    style={(state) =>
                      buildAdminInteractiveTableRowStyle(index, isSelected, state)
                    }
                  >
                    <View
                      style={[
                        styles.patientDocumentTableCell,
                        { flex: 1.4, gap: 2 },
                      ]}
                    >
                      <Text
                        style={[
                          styles.sectionTitle,
                          {
                            color: theme.textPrimary,
                            fontFamily: layout.displayFontFamily,
                            fontSize: tokens.typography.fontSize.md,
                            lineHeight: tokens.typography.lineHeight.md,
                          },
                        ]}
                      >
                        {humanizeWorkspaceKey(run.runKind)}
                      </Text>
                      <Text
                        style={[
                          styles.metricLabel,
                          {
                            color: theme.textSecondary,
                            fontFamily: layout.bodyFontFamily,
                            fontSize: tokens.typography.fontSize.xs,
                          },
                        ]}
                      >
                        {run.environmentKey}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textPrimary,
                          flex: 1.4,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {run.targetLabel}
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: statusColor(run.runStatus),
                          flex: 0.8,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {humanizeWorkspaceKey(run.runStatus)}
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textPrimary,
                          flex: 1,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {formatAdminSummaryTimestamp(run.startedAt)}
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textPrimary,
                          flex: 1,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {formatAdminSummaryTimestamp(run.completedAt)}
                    </Text>
                  </Pressable>
                );
              })
            ) : (
              <View
                style={[
                  styles.detailBodyCard,
                  adminTableEmptyStateStyle,
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  No provisioning runs match the current admin filters.
                </Text>
              </View>
            )}
          </View>
          )
        ) : null}

        {showsRuns && provisioningRuns.length > 8 ? (
          <View style={[styles.patientRowFooter, { gap: tokens.spacing.xs }]}>
            {renderWorkspaceFilterChip({
              isSelected: false,
              labelText: "Prev",
              onPress: () =>
                setAdminProvisioningPageIndex((current) =>
                  Math.max(0, current - 1),
                ),
              theme,
              tokens,
            })}
            <Text
              style={[
                styles.metricLabel,
                {
                  color: theme.textSecondary,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: tokens.typography.fontSize.xs,
                },
              ]}
            >
              {`Page ${adminProvisioningPageIndex + 1} of ${Math.max(1, Math.ceil(provisioningRuns.length / 8))}`}
            </Text>
            {renderWorkspaceFilterChip({
              isSelected: false,
              labelText: "Next",
              onPress: () =>
                setAdminProvisioningPageIndex((current) =>
                  Math.min(
                    Math.max(0, Math.ceil(provisioningRuns.length / 8) - 1),
                    current + 1,
                  ),
                ),
              theme,
              tokens,
            })}
          </View>
        ) : null}

        {showsAllocations ? (
          isAdminMobileLayout ? (
            <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.accent,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: adminTypography.sectionLabelWeight,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Database allocations
              </Text>
              {databaseAllocationRows.length > 0 ? (
                databaseAllocationRows.map((allocation) =>
                  renderAdminMobileRecordCard({
                    badge: renderAdminStatusPill(
                      humanizeWorkspaceKey(allocation.allocationStatus),
                    ),
                    cardKey: allocation.allocationId,
                    metaRows: [
                      renderAdminMobileMetaRow(
                        "Cluster",
                        `${allocation.clusterIdentifier} | ${allocation.awsRegion}`,
                      ),
                      renderAdminMobileMetaRow(
                        "Migration",
                        allocation.lastMigrationVersion ??
                          allocation.migrationBundleKey,
                      ),
                      renderAdminMobileMetaRow(
                        "Provisioned",
                        formatAdminSummaryTimestamp(
                          allocation.lastProvisionedAt,
                        ),
                      ),
                    ],
                    titleText: allocation.databaseName,
                  }),
                )
              ) : (
                <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    No allocations match the current admin filters.
                  </Text>
                </View>
              )}
            </View>
          ) : (
          <View
            style={[
              styles.patientDocumentTable,
              adminTableShellStyle,
            ]}
          >
            <View
              style={[
                styles.patientDocumentTableHeader,
                adminTableHeaderStyle,
              ]}
            >
              {[
                { labelText: "Database", flexValue: 1.4 },
                { labelText: "Status", flexValue: 0.8 },
                { labelText: "Cluster", flexValue: 1.4 },
                { labelText: "Migration", flexValue: 1.1 },
                { labelText: "Provisioned", flexValue: 1 },
              ].map((item) => (
                <Text
                  key={item.labelText}
                  style={[
                    styles.metricLabel,
                    {
                      flex: item.flexValue,
                      ...adminTableHeaderTextStyle,
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
              ))}
            </View>
            {databaseAllocationRows.length > 0 ? (
              databaseAllocationRows.map((allocation, index) => (
                <View
                  key={allocation.allocationId}
                  style={[
                    styles.patientDocumentTableRow,
                    buildAdminTableRowStateStyle(index),
                  ]}
                >
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1.4,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {allocation.databaseName}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: statusColor(allocation.allocationStatus),
                        flex: 0.8,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {humanizeWorkspaceKey(allocation.allocationStatus)}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1.4,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {`${allocation.clusterIdentifier} | ${allocation.awsRegion}`}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1.1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {allocation.lastMigrationVersion ??
                      allocation.migrationBundleKey}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {formatAdminSummaryTimestamp(allocation.lastProvisionedAt)}
                  </Text>
                </View>
              ))
            ) : (
              <View
                style={[
                  styles.detailBodyCard,
                  adminTableEmptyStateStyle,
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  No allocations match the current admin filters.
                </Text>
              </View>
            )}
          </View>
          )
        ) : null}

        {showsClusters ? (
          isAdminMobileLayout ? (
            <View style={[styles.listGroup, { gap: tokens.spacing.sm }]}>
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.accent,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: adminTypography.sectionLabelWeight,
                    textTransform: "uppercase",
                  },
                ]}
              >
                Database clusters
              </Text>
              {databaseClusterRows.length > 0 ? (
                databaseClusterRows.map((cluster) =>
                  renderAdminMobileRecordCard({
                    badge: renderAdminStatusPill(
                      humanizeWorkspaceKey(cluster.clusterStatus),
                    ),
                    cardKey: cluster.clusterId,
                    metaRows: [
                      renderAdminMobileMetaRow(
                        "Role",
                        humanizeWorkspaceKey(cluster.clusterRole),
                      ),
                      renderAdminMobileMetaRow(
                        "Engine",
                        `${cluster.engineFamily} ${cluster.engineVersion}`,
                      ),
                      renderAdminMobileMetaRow(
                        "Allocations",
                        String(cluster.allocationCount),
                      ),
                    ],
                    subtitleText: `${cluster.environmentKey} | ${cluster.awsRegion}`,
                    titleText: cluster.clusterIdentifier,
                  }),
                )
              ) : (
                <View style={[styles.detailBodyCard, adminTableEmptyStateStyle]}>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: theme.textSecondary,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    No clusters match the current admin filters.
                  </Text>
                </View>
              )}
            </View>
          ) : (
          <View
            style={[
              styles.patientDocumentTable,
              adminTableShellStyle,
            ]}
          >
            <View
              style={[
                styles.patientDocumentTableHeader,
                adminTableHeaderStyle,
              ]}
            >
              {[
                { labelText: "Cluster", flexValue: 1.5 },
                { labelText: "Role", flexValue: 1.1 },
                { labelText: "Engine", flexValue: 1 },
                { labelText: "Status", flexValue: 0.8 },
                { labelText: "Allocations", flexValue: 0.8 },
              ].map((item) => (
                <Text
                  key={item.labelText}
                  style={[
                    styles.metricLabel,
                    {
                      flex: item.flexValue,
                      ...adminTableHeaderTextStyle,
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
              ))}
            </View>
            {databaseClusterRows.length > 0 ? (
              databaseClusterRows.map((cluster, index) => (
                <View
                  key={cluster.clusterId}
                  style={[
                    styles.patientDocumentTableRow,
                    buildAdminTableRowStateStyle(index),
                  ]}
                >
                  <View
                    style={[
                      styles.patientDocumentTableCell,
                      { flex: 1.5, gap: 2 },
                    ]}
                  >
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textPrimary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                        },
                      ]}
                    >
                      {cluster.clusterIdentifier}
                    </Text>
                    <Text
                      style={[
                        styles.metricLabel,
                        {
                          color: theme.textSecondary,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.xs,
                        },
                      ]}
                    >
                      {`${cluster.environmentKey} | ${cluster.awsRegion}`}
                    </Text>
                  </View>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1.1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {humanizeWorkspaceKey(cluster.clusterRole)}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 1,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {`${cluster.engineFamily} ${cluster.engineVersion}`}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: statusColor(cluster.clusterStatus),
                        flex: 0.8,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {humanizeWorkspaceKey(cluster.clusterStatus)}
                  </Text>
                  <Text
                    style={[
                      styles.metricLabel,
                      {
                        color: theme.textPrimary,
                        flex: 0.8,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: tokens.typography.fontSize.sm,
                      },
                    ]}
                  >
                    {String(cluster.allocationCount)}
                  </Text>
                </View>
              ))
            ) : (
              <View
                style={[
                  styles.detailBodyCard,
                  adminTableEmptyStateStyle,
                ]}
              >
                <Text
                  style={[
                    styles.sectionSubtitle,
                    {
                      color: theme.textSecondary,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: tokens.typography.fontSize.sm,
                    },
                  ]}
                >
                  No clusters match the current admin filters.
                </Text>
              </View>
            )}
          </View>
          )
        ) : null}
      </View>
    );
  };

  const renderAdminDateInput = (
    labelText: string,
    value: string,
    onChange: (nextValue: string) => void,
  ): ReactNode => {
    if (Platform.OS === "web") {
      const inputStyle: CSSProperties = {
        background: adminFigmaPalette.surface,
        border: `1px solid ${adminFigmaPalette.border}`,
        borderRadius: adminControlRadius,
        boxSizing: "border-box",
        color: adminFigmaPalette.ink,
        fontFamily: layout.bodyFontFamily,
        fontSize: 12,
        height: 34,
        outline: "none",
        padding: "6px 10px",
        width: "100%",
      };

      return createElement("input", {
        "aria-label": labelText,
        onChange: (event: SyntheticEvent) => {
          const target = event.target as { value?: string };

          onChange(target.value ?? "");
        },
        style: inputStyle,
        type: "date",
        value,
      });
    }

    return (
      <TextInput
        onChangeText={onChange}
        placeholder="YYYY-MM-DD"
        placeholderTextColor={theme.textTertiary}
        style={[
          styles.searchInput,
          {
            backgroundColor: adminFigmaPalette.surface,
            borderColor: adminFigmaPalette.border,
            borderRadius: adminControlRadius,
            borderWidth: 1,
            color: adminFigmaPalette.ink,
            fontFamily: layout.bodyFontFamily,
            fontSize: 12,
            minHeight: 34,
            paddingHorizontal: 10,
            paddingVertical: 6,
          },
        ]}
        value={value}
      />
    );
  };

  const applyAdminAnalyticsCustomRange = () => {
    const startDate = parseAdminDateInputValue(
      adminAnalyticsCustomRangeDraft.startDate,
    );
    const endDate = parseAdminDateInputValue(adminAnalyticsCustomRangeDraft.endDate);

    if (!startDate || !endDate) {
      setAdminOpsNotice("Choose a valid start and end date for analytics.");
      return;
    }

    const orderedStartDate =
      startDate.getTime() <= endDate.getTime() ? startDate : endDate;
    const orderedEndDate =
      startDate.getTime() <= endDate.getTime() ? endDate : startDate;

    onUpdateAdminAnalyticsRange({
      departmentCode: adminAnalyticsRangeSelection.departmentCode ?? null,
      endDate: formatAdminDateInputValue(orderedEndDate),
      rangeKey: "custom",
      startDate: formatAdminDateInputValue(orderedStartDate),
    });
    setIsAdminAnalyticsCustomRangeOpen(false);
    setAdminOpsNotice("Analytics range updated.");
  };

  const renderAdminAnalyticsCustomRangeModal = () => {
    if (!isAdminAnalyticsCustomRangeOpen) {
      return null;
    }

    return renderAdminModalFrame({
      accessibilityLabel: "Custom analytics range",
      children: (
        <View
          style={[
            styles.detailBodyCard,
            {
              backgroundColor: adminFigmaPalette.surface,
              borderColor: adminFigmaPalette.border,
              borderRadius: adminControlRadius,
              borderWidth: 0,
              gap: tokens.spacing.md,
              padding: adminModalDensity.cardPadding,
              width: "100%",
            },
          ]}
        >
          {renderAdminModalHeader({
            eyebrowText: "Analytics",
            onClose: () => setIsAdminAnalyticsCustomRangeOpen(false),
            subtitleText: "Choose the reporting window for the dashboard metrics.",
            titleText: "Custom analytics range",
          })}

          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "stretch",
                flexDirection: isMediumLayout ? "row" : "column",
                gap: 10,
              },
            ]}
          >
            {[
              {
                field: "startDate" as const,
                labelText: "Start date",
                value: adminAnalyticsCustomRangeDraft.startDate,
              },
              {
                field: "endDate" as const,
                labelText: "End date",
                value: adminAnalyticsCustomRangeDraft.endDate,
              },
            ].map((item) => (
              <View
                key={item.field}
                style={[styles.listGroup, { flex: 1, gap: 6 }]}
              >
                <Text
                  style={[
                    styles.metricLabel,
                    {
                      color: adminFigmaPalette.inkMuted,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 10,
                      textTransform: "uppercase",
                    },
                  ]}
                >
                  {item.labelText}
                </Text>
                {renderAdminDateInput(item.labelText, item.value, (nextValue) =>
                  setAdminAnalyticsCustomRangeDraft((current) => ({
                    ...current,
                    [item.field]: nextValue,
                  })),
                )}
              </View>
            ))}
          </View>

          <View
            style={[
              styles.patientRowFooter,
              {
                gap: 8,
                justifyContent: "flex-end",
              },
            ]}
          >
            {renderAdminPrimaryButton({
              density: "compact",
              labelText: "Cancel",
              onPress: () => setIsAdminAnalyticsCustomRangeOpen(false),
              prefixText: "",
              tone: "surface",
            })}
            {renderAdminPrimaryButton({
              density: "compact",
              labelText: "Apply range",
              onPress: applyAdminAnalyticsCustomRange,
              prefixText: "",
              tone: "primary",
            })}
          </View>
        </View>
      ),
      contentPadding: 0,
      maxWidth: 760,
      onClose: () => setIsAdminAnalyticsCustomRangeOpen(false),
      rail: null,
      visible: isAdminAnalyticsCustomRangeOpen,
    });
  };

  const renderAdminAnalyticsSurface = () => (
    <View style={[styles.listGroup, { gap: 16 }]}>
      <View
        style={[
          styles.patientRowHeader,
          {
            alignItems: isMediumLayout ? "center" : "flex-start",
            flexDirection: isMediumLayout ? "row" : "column",
            gap: 12,
          },
        ]}
      >
        <Text
          style={[
            styles.title,
            {
              ...adminCompactTitleStyle,
            },
          ]}
        >
          Analytics
        </Text>

        <View
          style={[
            styles.patientRowFooter,
            {
              flexWrap: "wrap",
              gap: 8,
              marginLeft: isMediumLayout ? "auto" : 0,
            },
          ]}
        >
          {[
            ["today", "Today"],
            ["7d", "Last 7 days"],
            ["30d", "Last 30 days"],
          ].map(([value, labelText]) => {
            const rangeKey = value as AdminAnalyticsRangeKey;
            const isSelected = adminAnalyticsRangeSelection.rangeKey === value;

            return (
              <Pressable
                key={value}
                onPress={() =>
                  onUpdateAdminAnalyticsRange(
                    resolveAdminAnalyticsRangeSelection(
                      rangeKey,
                      new Date(),
                      adminAnalyticsRangeSelection.departmentCode ?? null,
                    ),
                  )
                }
                style={[
                  styles.inlinePill,
                  {
                    backgroundColor: isSelected
                      ? adminFigmaPalette.accent
                      : adminFigmaPalette.surface,
                    borderColor: isSelected
                      ? adminFigmaPalette.accent
                      : adminFigmaPalette.border,
                    borderRadius: adminControlRadius,
                    paddingHorizontal: 12,
                    paddingVertical: 5,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.pillValueText,
                    {
                      color: isSelected
                        ? adminFigmaPalette.surface
                        : adminFigmaPalette.accent,
                      fontFamily: layout.bodyFontFamily,
                      fontSize: 11,
                    },
                  ]}
                >
                  {labelText}
                </Text>
              </Pressable>
            );
          })}
          <Pressable
            onPress={() => setIsAdminAnalyticsCustomRangeOpen(true)}
            style={[
              styles.inlinePill,
              {
                backgroundColor:
                  adminAnalyticsRangeSelection.rangeKey === "custom"
                    ? adminFigmaPalette.accent
                    : adminFigmaPalette.surface,
                borderColor:
                  adminAnalyticsRangeSelection.rangeKey === "custom"
                    ? adminFigmaPalette.accent
                    : adminFigmaPalette.border,
                borderRadius: adminControlRadius,
                paddingHorizontal: 14,
                paddingVertical: 5,
              },
            ]}
          >
            <Text
              style={[
                styles.pillValueText,
                {
                  color:
                    adminAnalyticsRangeSelection.rangeKey === "custom"
                      ? adminFigmaPalette.surface
                      : adminFigmaPalette.accent,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 11,
                },
              ]}
            >
              {analyticsDateRangeLabel}
            </Text>
          </Pressable>
          {renderAdminOptionSelectControl({
            accessibilityLabel: "Filter analytics by department",
            minWidth: 188,
            onChange: (value) => {
              onUpdateAdminAnalyticsRange({
                ...adminAnalyticsRangeSelection,
                departmentCode: value === "all" ? null : value,
              });
              setAdminOpsNotice(
                value === "all"
                  ? "Analytics now shows all departments."
                  : "Analytics department filter updated.",
              );
            },
            options: adminAnalyticsDepartmentOptions,
            value: adminAnalyticsSelectedDepartmentCode,
          })}
        </View>
      </View>

      <View style={[styles.listGroup, { gap: 10 }]}>
        <Text
          style={[
            styles.navigationText,
            {
              color: adminFigmaPalette.ink,
              fontFamily: layout.bodyFontFamily,
              fontSize: 13,
              fontWeight: "700",
            },
          ]}
        >
          {adminAnalyticsSelectedDepartmentLabel}
        </Text>
        <View
          style={[
            styles.metricGroupRow,
            {
              flexDirection: isMediumLayout ? "row" : "column",
              flexWrap: isMediumLayout ? "wrap" : "nowrap",
              gap: 14,
            },
          ]}
        >
          {organizationAnalyticsSummaryCards.map((item) => (
            <View
              key={item.id}
              style={[
                styles.metricGroupCard,
                {
                  backgroundColor: theme.surface,
                  borderColor: "transparent",
                  borderRadius: adminControlRadius,
                  flexBasis: isMediumLayout ? 148 : undefined,
                  flexGrow: 1,
                  gap: 4,
                  minHeight: 62,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  shadowOpacity: 0,
                },
              ]}
            >
              <Text
                style={[
                  styles.navigationText,
                  {
                    color: adminFigmaPalette.ink,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    fontWeight: "500",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <View
                style={[
                  styles.patientRowHeader,
                  {
                    alignItems: "center",
                    gap: 6,
                    justifyContent: "flex-start",
                  },
                ]}
              >
                <Text
                  style={[
                    styles.title,
                    {
                      color: theme.textPrimary,
                      fontFamily: layout.displayFontFamily,
                      fontSize: 22,
                      lineHeight: 26,
                      marginTop: 0,
                    },
                  ]}
                >
                  {item.valueText}
                </Text>
                {renderAdminDeltaPill(item.delta, {
                  suffix: item.id === "avg-response-time" ? "s" : "%",
                })}
              </View>
            </View>
          ))}
        </View>
        <Text
          style={[
            styles.sectionSubtitle,
            {
              color: adminFigmaPalette.inkMuted,
              fontFamily: layout.bodyFontFamily,
              fontSize: 11,
              lineHeight: 15,
            },
          ]}
        >
          Voice minutes are estimated from available transcript timing for this
          reporting window.
        </Text>
        <View
          style={[
            styles.metricGroupRow,
            {
              flexDirection: isMediumLayout ? "row" : "column",
              flexWrap: isMediumLayout ? "wrap" : "nowrap",
              gap: 10,
            },
          ]}
        >
          {analyticsDefinitionItems.map((item) => (
            <View
              key={item.id}
              style={[
                styles.metricGroupCard,
                {
                  backgroundColor: adminFigmaPalette.surface,
                  borderColor: adminFigmaPalette.border,
                  borderRadius: adminControlRadius,
                  flexBasis: isMediumLayout ? 210 : undefined,
                  flexGrow: 1,
                  gap: 4,
                  minHeight: 68,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  shadowOpacity: 0,
                },
              ]}
            >
              <Text
                style={[
                  styles.metricLabel,
                  {
                    color: adminFigmaPalette.accent,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 10,
                    fontWeight: adminTypography.sectionLabelWeight,
                    textTransform: "uppercase",
                  },
                ]}
              >
                {item.labelText}
              </Text>
              <Text
                style={[
                  styles.sectionSubtitle,
                  {
                    color: adminFigmaPalette.inkMuted,
                    fontFamily: layout.bodyFontFamily,
                    fontSize: 11,
                    lineHeight: 15,
                  },
                ]}
              >
                {item.valueText}
              </Text>
            </View>
          ))}
        </View>
      </View>

      <View
        style={[
          styles.workspaceGrid,
          {
            flexDirection: isWideLayout ? "row" : "column",
            gap: tokens.spacing.md,
          },
        ]}
      >
        <View
          style={[
            styles.listGroup,
            { flex: isWideLayout ? 0.82 : 1, gap: 12 },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: adminFigmaPalette.ink,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                fontWeight: "700",
              },
            ]}
          >
            Overall draft quality
          </Text>
          <View
            style={[
              styles.metricGroupCard,
              {
                alignItems: "center",
                backgroundColor: theme.surface,
                borderColor: "transparent",
                borderRadius: adminControlRadius,
                justifyContent: "center",
                minHeight: 150,
                padding: 12,
                shadowOpacity: 0,
              },
            ]}
          >
            {renderAdminAnalyticsRing({
              background:
                `conic-gradient(${adminFigmaPalette.successBright} 0 30%, ${adminFigmaPalette.warningBright} 30% 54%, ${adminFigmaPalette.warning} 54% 77%, ${adminFigmaPalette.dangerBright} 77% 100%)`,
              diameter: 122,
              ringWidth: 14,
              children: (
                <View style={{ alignItems: "center", gap: 0 }}>
                  <Text
                    style={[
                      styles.title,
                      {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.displayFontFamily,
                        fontSize: 24,
                        lineHeight: 28,
                        marginTop: 0,
                      },
                    ]}
                  >
                    {analyticsAiConfidencePercent == null
                      ? "N/A"
                      : resolveAdminPercentText(analyticsAiConfidencePercent, 1)}
                  </Text>
                  {analyticsAiConfidencePercent == null ? null : (
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: adminFigmaPalette.ink,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "700",
                        },
                      ]}
                    >
                      %
                    </Text>
                  )}
                  {renderAdminDeltaPill(summaryAnalytics?.aiConfidenceDeltaPercent)}
                </View>
              ),
            })}
          </View>
        </View>

        <View
          style={[
            styles.listGroup,
            { flex: isWideLayout ? 2.12 : 1, gap: 12 },
          ]}
        >
          <View
            style={[
              styles.patientRowHeader,
              {
                alignItems: "center",
                gap: 12,
              },
            ]}
          >
            <Text
              style={[
                styles.navigationText,
                {
                  color: adminFigmaPalette.ink,
                  flex: 1,
                  fontFamily: layout.bodyFontFamily,
                  fontSize: 13,
                  fontWeight: "700",
                },
              ]}
            >
              New patients conversion funnel
            </Text>
            <View style={[styles.patientRowFooter, { gap: 0 }]}>
              {[
                ["overall", "Overall"],
                ["step-by-step", "Step-by-step"],
              ].map(([value, labelText]) => {
                const isSelected = adminAnalyticsFunnelMode === value;

                return (
                  <Pressable
                    key={value}
                    onPress={() =>
                      setAdminAnalyticsFunnelMode(
                        value as "overall" | "step-by-step",
                      )
                    }
                    style={[
                      styles.inlinePill,
                      {
                        backgroundColor: isSelected
                          ? adminFigmaPalette.accent
                          : adminFigmaPalette.surface,
                        borderColor: adminFigmaPalette.border,
                        borderRadius: adminControlRadius,
                        paddingHorizontal: 12,
                        paddingVertical: 4,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: isSelected
                            ? adminFigmaPalette.surface
                            : adminFigmaPalette.ink,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                        },
                      ]}
                    >
                      {labelText}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View
            style={[
              styles.metricGroupCard,
              {
                backgroundColor: theme.surface,
                borderColor: "transparent",
                borderRadius: adminControlRadius,
                gap: 10,
                minHeight: 150,
                paddingHorizontal: 12,
                paddingVertical: 12,
                shadowOpacity: 0,
              },
            ]}
          >
            <View
              style={[
                styles.metricGroupRow,
                {
                  flexDirection: isMediumLayout ? "row" : "column",
                  gap: 12,
                },
              ]}
            >
              {[
                {
                  delta: null,
                  id: "invited",
                  labelText: "Received invitation",
                  supportText: analyticsInvitationCount > 0 ? "100%" : "0.0%",
                  valueText: String(analyticsInvitationCount),
                },
                {
                  delta: summaryAnalytics?.loggedInDeltaCount ?? null,
                  id: "logged-in",
                  labelText: "Logged in to app",
                  supportText: `${formatAnalyticsInvitationRate(analyticsLoggedInCount)}% of ${analyticsInvitationCount}`,
                  valueText: String(analyticsLoggedInCount),
                },
                {
                  delta: summaryAnalytics?.engagedDeltaCount ?? null,
                  id: "engaged",
                  labelText: "Engaged AI Agent",
                  supportText: `${formatAnalyticsInvitationRate(analyticsEngagedCount)}% of ${analyticsInvitationCount}`,
                  valueText: String(analyticsEngagedCount),
                },
              ].map((item) => (
                <View
                  key={item.id}
                  style={[
                    styles.listGroup,
                    {
                      borderLeftColor: adminFigmaPalette.border,
                      borderLeftWidth:
                        item.id === "invited" || !isMediumLayout ? 0 : 1,
                      flex: 1,
                      gap: 2,
                      paddingLeft:
                        item.id === "invited" || !isMediumLayout ? 0 : 14,
                    },
                  ]}
                >
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "center",
                        gap: 4,
                        justifyContent: "flex-start",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.navigationText,
                        {
                          color: adminFigmaPalette.ink,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "500",
                        },
                      ]}
                    >
                      {item.labelText}
                    </Text>
                    <Text
                      style={[
                        styles.pillValueText,
                        {
                          color: adminFigmaPalette.accent,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: 10,
                          fontWeight: "700",
                        },
                      ]}
                    >
                      i
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.patientRowHeader,
                      {
                        alignItems: "center",
                        gap: 6,
                        justifyContent: "flex-start",
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.title,
                        {
                          color: adminFigmaPalette.ink,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 22,
                          lineHeight: 26,
                          marginTop: 0,
                        },
                      ]}
                    >
                      {item.valueText}
                    </Text>
                    {renderAdminDeltaPill(item.delta, { suffix: "" })}
                  </View>
                  <Text
                    style={[
                      styles.sectionSubtitle,
                      {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 10,
                        lineHeight: 13,
                      },
                    ]}
                  >
                    {item.supportText}
                  </Text>
                </View>
              ))}
            </View>

            {renderAdminAnalyticsBars()}
          </View>
        </View>
      </View>

      <View
        style={[
          styles.workspaceGrid,
          {
            flexDirection: isWideLayout ? "row" : "column",
            gap: 16,
          },
        ]}
      >
        <View
          style={[
            styles.listGroup,
            { flex: isWideLayout ? 0.92 : 1, gap: 12 },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: adminFigmaPalette.ink,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                fontWeight: "700",
              },
            ]}
          >
            Language Model accuracy
          </Text>
          <View
            style={[
              styles.metricGroupCard,
              {
                alignItems: "center",
                backgroundColor: adminFigmaPalette.surface,
                borderColor: "transparent",
                borderRadius: adminControlRadius,
                flexDirection: isMediumLayout ? "row" : "column",
                gap: 12,
                minHeight: 154,
                paddingHorizontal: 12,
                paddingVertical: 12,
                shadowOpacity: 0,
              },
            ]}
          >
            {renderAdminAnalyticsRing({
              background: `conic-gradient(${adminFigmaPalette.successBright} 0 ${analyticsLanguageModel.autonomousResponsePercent}%, ${adminFigmaPalette.warning} ${analyticsLanguageModel.autonomousResponsePercent}% ${
                analyticsLanguageModel.autonomousResponsePercent +
                analyticsLanguageModel.clinicianApprovedPercent
              }%, ${adminFigmaPalette.dangerBright} ${
                analyticsLanguageModel.autonomousResponsePercent +
                analyticsLanguageModel.clinicianApprovedPercent
              }% 100%)`,
              diameter: 112,
              ringWidth: 13,
              children: (
                <View style={{ alignItems: "center", gap: 0 }}>
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 9,
                        fontWeight: adminTypography.sectionLabelWeight,
                        textTransform: "uppercase",
                      },
                    ]}
                  >
                    AI Rating
                  </Text>
                  <Text
                    style={[
                      styles.title,
                      {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.displayFontFamily,
                        fontSize: 24,
                        lineHeight: 27,
                        marginTop: 0,
                      },
                    ]}
                  >
                    {resolveAdminPercentText(analyticsLanguageModel.aiRating, 1)}
                  </Text>
                  <Text
                    style={[
                      styles.pillValueText,
                      {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.bodyFontFamily,
                        fontSize: 11,
                        fontWeight: "700",
                      },
                    ]}
                  >
                    /5
                  </Text>
                  {renderAdminDeltaPill(analyticsLanguageModel.aiRatingDelta, {
                    suffix: "",
                  })}
                </View>
              ),
            })}
            <View style={[styles.listGroup, { flex: 1, gap: 12 }]}>
              {[
                {
                  color: adminFigmaPalette.successBright,
                  delta: analyticsLanguageModel.autonomousResponseDeltaPercent,
                  labelText: "Autonomous response",
                  value: analyticsLanguageModel.autonomousResponsePercent,
                },
                {
                  color: adminFigmaPalette.warning,
                  delta: analyticsLanguageModel.clinicianApprovedDeltaPercent,
                  labelText: "Clinician approved",
                  value: analyticsLanguageModel.clinicianApprovedPercent,
                },
                {
                  color: adminFigmaPalette.dangerBright,
                  delta: analyticsLanguageModel.manualEntryDeltaPercent,
                  labelText: "Manual entry",
                  value: analyticsLanguageModel.manualEntryPercent,
                },
              ].map((item) => (
                <View
                  key={item.labelText}
                  style={[
                    styles.patientRowHeader,
                    {
                      alignItems: "center",
                      gap: 8,
                      justifyContent: "space-between",
                    },
                  ]}
                >
                  <View
                    style={{
                      alignItems: "center",
                      flexDirection: "row",
                      gap: 8,
                    }}
                  >
                    <View
                      style={{
                        borderColor: item.color,
                        borderRadius: 999,
                        borderWidth: 4,
                        height: 18,
                        width: 18,
                      }}
                    />
                    <Text
                      style={[
                        styles.navigationText,
                        {
                        color: adminFigmaPalette.ink,
                        fontFamily: layout.bodyFontFamily,
                          fontSize: 11,
                          fontWeight: "500",
                        },
                      ]}
                    >
                      {item.labelText}
                    </Text>
                  </View>
                  <View
                    style={[
                      styles.patientRowHeader,
                      { alignItems: "center", gap: 6 },
                    ]}
                  >
                    <Text
                      style={[
                        styles.title,
                        {
                          color: adminFigmaPalette.ink,
                          fontFamily: layout.displayFontFamily,
                          fontSize: 20,
                          lineHeight: 24,
                          marginTop: 0,
                        },
                      ]}
                    >
                      {resolveAdminPercentText(item.value, 1)}%
                    </Text>
                    {renderAdminDeltaPill(item.delta)}
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>

        <View
          style={[
            styles.listGroup,
            { flex: isWideLayout ? 1.68 : 1, gap: 12 },
          ]}
        >
          <Text
            style={[
              styles.navigationText,
              {
                color: adminFigmaPalette.ink,
                fontFamily: layout.bodyFontFamily,
                fontSize: 13,
                fontWeight: "700",
              },
            ]}
          >
            Language Model accuracy change within selected timeframe
          </Text>
          {renderAdminAccuracyTrend()}
        </View>
      </View>

      </View>
  );

  const isAnyAdminModalOpen =
    adminCrudForm != null ||
    adminFeatureFlagDraft != null ||
    adminAiModelArtifactEditor != null ||
    adminOutboundCreateDraft != null ||
    adminOutboundDialerDraft != null ||
    adminOutboundCampaignQuestionnaireImpactDraft != null ||
    adminOutboundQuestionnaireRevisionDraft != null ||
    adminOutboundDrawerCandidateId != null ||
    selectedAdminConnector != null ||
    selectedAdminPatientRow != null ||
    selectedAdminReviewQueue != null ||
    selectedAdminProvisioningRun != null ||
    isAdminAnalyticsCustomRangeOpen;

  return (
    <View
      style={[
        styles.root,
        showsLegacyWebAssets ? createWebViewportMinHeightStyle() : null,
        {
          backgroundColor: showsLegacyWebAssets
            ? theme.background
            : "transparent",
          position: "relative",
        },
      ]}
    >
      <View
        pointerEvents="none"
        style={[
          styles.backgroundWash,
          {
            backgroundColor: theme.background,
          },
        ]}
      />
      <ScrollView
        accessibilityElementsHidden={isAnyAdminModalOpen}
        aria-hidden={isAnyAdminModalOpen}
        importantForAccessibility={
          isAnyAdminModalOpen ? "no-hide-descendants" : "auto"
        }
        style={
          showsLegacyWebAssets
            ? {
                flex: 1,
                height: Math.max(0, viewportHeight - legacyWorkbenchFooterHeight),
                maxHeight: Math.max(
                  0,
                  viewportHeight - legacyWorkbenchFooterHeight,
                ),
              }
            : undefined
        }
        contentContainerStyle={[
          styles.scrollContent,
          {
            gap: showsLegacyWebAssets ? 0 : tokens.spacing.xl,
            minHeight: showsLegacyWebAssets
              ? Math.max(0, viewportHeight - legacyWorkbenchFooterHeight)
              : undefined,
            padding: showsLegacyWebAssets ? 0 : tokens.spacing.lg,
            paddingBottom: showsLegacyWebAssets
              ? legacyWorkbenchFooterHeight + tokens.spacing.xl
              : tokens.spacing.lg,
          },
        ]}
      >
        <View
          style={[
            styles.pageFrame,
            {
              backgroundColor: showsLegacyWebAssets
                ? adminFigmaPalette.surface
                : "transparent",
              borderColor: showsLegacyWebAssets
                ? adminFigmaPalette.border
                : "transparent",
              borderRadius: 0,
              borderWidth: 0,
              gap: showsLegacyWebAssets ? 0 : tokens.spacing.lg,
              maxWidth: pageMaxWidth,
              overflow: showsLegacyWebAssets ? "hidden" : "visible",
              shadowColor: showsLegacyWebAssets
                ? theme.accentStrong
                : undefined,
              shadowOffset: { height: 0, width: 0 },
              shadowOpacity: 0,
              shadowRadius: 0,
            },
          ]}
        >
          <View
            style={{
              alignSelf: "stretch",
              backgroundColor:
                !showsLegacyWebAssets && usesAdminWideCommandLayout
                  ? adminFigmaPalette.surface
                  : "transparent",
              borderColor:
                !showsLegacyWebAssets && usesAdminWideCommandLayout
                  ? adminFigmaPalette.border
                  : "transparent",
              borderRadius:
                0,
              borderWidth:
                !showsLegacyWebAssets && usesAdminWideCommandLayout ? 1 : 0,
              gap: usesAdminWideCommandLayout ? 0 : tokens.spacing.lg,
              overflow:
                !showsLegacyWebAssets && usesAdminWideCommandLayout
                  ? "hidden"
                  : "visible",
              shadowColor: theme.accentStrong,
              shadowOffset: { height: 0, width: 0 },
              shadowOpacity: 0,
              shadowRadius: 0,
              width: "100%",
            }}
          >
            {renderAdminHeaderBar()}
            {renderAdminMetricGroups()}
            {!usesAdminWideCommandLayout ? renderAdminTabRail() : null}

            <View
              style={[
                {
                  alignItems: "stretch",
                  flexDirection: usesAdminWideCommandLayout ? "row" : "column",
                  gap: usesAdminWideCommandLayout ? 0 : 12,
                  minWidth: 0,
                  width: "100%",
                },
              ]}
            >
              {usesAdminWideCommandLayout ? renderAdminSideNav() : null}
              <View
                style={{
                  alignItems: "stretch",
                  flex: 1,
                  flexDirection: usesAdminCommandRailLayout ? "row" : "column",
                  gap: usesAdminWideCommandLayout ? 0 : 12,
                  minWidth: 0,
                }}
              >
                <View
                  style={[
                    styles.surfaceCard,
                    {
                      backgroundColor: adminFigmaPalette.surface,
                      borderColor: usesAdminWideCommandLayout
                        ? "transparent"
                        : adminFigmaPalette.border,
                      borderRadius: usesAdminWideCommandLayout
                        ? 0
                        : adminOperationalLayout.panelRadius,
                      borderWidth: usesAdminWideCommandLayout ? 0 : 1,
                      flex: 1,
                      gap: adminCompact.sectionGap,
                      minHeight: legacyAdminSurfaceMinHeight,
                      minWidth: 0,
                      padding: isMediumLayout
                        ? adminOperationalLayout.pagePaddingWide
                        : adminOperationalLayout.pagePaddingCompact,
                      shadowColor: theme.accentStrong,
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0,
                      shadowRadius: 0,
                    },
                  ]}
                >
                  {adminOpsNotice ? (
                    <Text
                      style={[
                        styles.sectionSubtitle,
                        {
                          color: theme.info,
                          fontFamily: layout.bodyFontFamily,
                          fontSize: tokens.typography.fontSize.sm,
                          lineHeight: tokens.typography.lineHeight.md,
                        },
                      ]}
                    >
                      {adminOpsNotice}
                    </Text>
                  ) : null}

                  {adminDashboardTabId === "patients"
                    ? renderAdminPatientsSurface()
                    : adminDashboardTabId === "clinicians"
                      ? renderAdminCliniciansSurface()
                      : adminDashboardTabId === "connectors"
                        ? renderAdminConnectorsSurface()
                        : adminDashboardTabId === "reviews"
                          ? renderAdminReviewsSurface()
                          : adminDashboardTabId === "outbound-calls"
                            ? renderAdminOutboundCallsSurface()
                            : adminDashboardTabId === "ai-models"
                              ? renderAdminAiModelParametersSurface()
                              : adminDashboardTabId === "patient-portal"
                                ? renderAdminPatientPortalSurface()
                                : adminDashboardTabId === "flags"
                                  ? renderAdminFlagsSurface()
                                  : adminDashboardTabId === "provisioning"
                                    ? renderAdminProvisioningSurface()
                                    : adminDashboardTabId === "departments"
                                      ? renderAdminDepartmentsSurface()
                                      : renderAdminAnalyticsSurface()}
                </View>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
      {showsLegacyWebAssets
        ? renderLegacyWorkbenchFooter({
            layout,
            palette: {
              ...patientDesktopPalette,
              darkBlue: theme.headerBand,
              shellGradient: theme.shellGradient,
            } as unknown as typeof patientDesktopPalette,
            position: "relative",
            viewportWidth,
          })
        : null}
      {adminActivityPopoverOpen ? (
        <>
          <Pressable
            accessibilityLabel="Close activity panel"
            onPress={() => setAdminActivityPopoverOpen(false)}
            style={{
              bottom: 0,
              left: 0,
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 90,
            }}
          />
          <View
            style={{
              backgroundColor: theme.surface,
              borderColor: theme.borderStrong,
              borderRadius: adminOperationalLayout.panelRadius,
              borderWidth: 1,
              maxHeight: 560,
              overflow: "hidden",
              padding: 14,
              position: "absolute",
              right: 16,
              shadowColor: theme.textPrimary,
              shadowOffset: { height: 16, width: 0 },
              shadowOpacity: 0.2,
              shadowRadius: 34,
              top: 56,
              zIndex: 95,
            }}
          >
            <ScrollView showsVerticalScrollIndicator={false}>
              {renderAdminCommandCenterRail()}
            </ScrollView>
          </View>
        </>
      ) : null}
      {renderAdminCrudFormModal()}
      {renderAdminPatientLifecycleModal()}
      {renderAdminFeatureFlagDraftModal()}
      {renderAdminAiModelArtifactEditorModal()}
      {renderAdminOutboundQuestionnaireRevisionModal()}
      {renderAdminOutboundDialerConfigModal()}
      {renderAdminOutboundCreateCampaignModal()}
      {renderAdminOutboundCampaignQuestionnaireImpactModal()}
      {renderAdminConnectorDetailModal()}
      {renderAdminPatientDetailModal()}
      {renderAdminReviewQueueDetailModal()}
      {renderAdminProvisioningRunDetailModal()}
      {renderAdminAnalyticsCustomRangeModal()}
    </View>
  );
}
